import { useState, useRef, useCallback, useEffect, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import { ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════════ */
const T = {
  bg:"#0B0E14", surface:"#111520", raised:"#181D2A", border:"#1E2535", borderHi:"#2A3450",
  accent:"#2563EB", accentLt:"#3B82F6", green:"#10B981", amber:"#F59E0B", red:"#EF4444",
  muted:"#4B5680", mutedLt:"#6B7AA0", text:"#E2E8F4", textSub:"#94A3C0",
  sf:"#2563EB", legal:"#8B5CF6", ihf:"#10B981", tax:"#f97316",
  fontMono:"'IBM Plex Mono',monospace", fontSans:"'IBM Plex Sans',sans-serif",
  fontSerif:"'Libre Baskerville',Georgia,serif",
};

// v2.6
const GCSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500&family=Libre+Baskerville:wght@700&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{background:${T.bg};color:${T.text};font-family:${T.fontSans};}
::-webkit-scrollbar{width:4px;height:4px;}
::-webkit-scrollbar-track{background:${T.surface};}
::-webkit-scrollbar-thumb{background:${T.border};border-radius:2px;}
select option{background:${T.raised};color:${T.text};}
input[type=text]::placeholder,input[type=email]::placeholder{color:${T.muted};}
.tbtn:hover{color:${T.text}!important;}
.rh:hover td{background:${T.raised}!important;}
@keyframes fi{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
.fi{animation:fi 0.25s ease both;}
input[type=checkbox]{accent-color:${T.accent};width:15px;height:15px;cursor:pointer;}
.tbl-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch;}
.tbl-wrap table{min-width:480px;}
@media(max-width:768px){.fi{padding:12px!important;} .card-pad{padding:12px!important;}}
`;

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS & DEFAULTS
═══════════════════════════════════════════════════════════════ */
const BA_OPTIONS  = ["Strategic Finance","Legal","In-House Finance","Tax"];



const DEFAULT_REFERRALS = [
  { id:1, name:"Steve" },
  { id:2, name:"Felix" },
  { id:3, name:"Word of Mouth" },
  { id:4, name:"LinkedIn" },
  { id:5, name:"Existing Client" },
];

/* ═══════════════════════════════════════════════════════════════
   REAL SEED DATA — Lvl Up Partners LLP (Apr 2026 pack)
   Replaced on each Excel upload; persists for the session.
═══════════════════════════════════════════════════════════════ */
const SEED_PL_ROWS = [
  {period:"Apr 2026",year:2026,month:3,sf_revenue:45303.65,sf_cos:-25788.44,legal_revenue:66854.42,legal_cos:-25903.43,ihf_revenue:11757.09,ihf_cos:-11767.14,total_revenue:123915.16,total_cos:-63459.01,ebitda:56278.01,profit_before_drawings:29049.5,debts_over_30:31587.93,accrued_income:22282.5,accumulated_profits:70012.0},
  {period:"Mar 2026",year:2026,month:2,sf_revenue:48584.42,sf_cos:-25788.44,legal_revenue:56706.3,legal_cos:-25792.43,ihf_revenue:8987.8,ihf_cos:-10023.05,total_revenue:114278.52,total_cos:-61603.92,ebitda:45909.94,profit_before_drawings:22731.04,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Feb 2026",year:2026,month:1,sf_revenue:34692.32,sf_cos:-24788.44,legal_revenue:58855.53,legal_cos:-25792.43,ihf_revenue:8812.5,ihf_cos:-9359.87,total_revenue:102360.35,total_cos:-59940.74,ebitda:37893.49,profit_before_drawings:20099.42,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Jan 2026",year:2026,month:0,sf_revenue:36818.66,sf_cos:-25788.44,legal_revenue:49139.36,legal_cos:-25375.34,ihf_revenue:6260.0,ihf_cos:-7410.87,total_revenue:92218.02,total_cos:-58574.65,ebitda:27482.35,profit_before_drawings:15417.28,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Dec 2025",year:2025,month:11,sf_revenue:33205.18,sf_cos:-24824.95,legal_revenue:48416.77,legal_cos:-24527.35,ihf_revenue:6043.75,ihf_cos:-6886.9,total_revenue:87665.7,total_cos:-56239.2,ebitda:26946.94,profit_before_drawings:11940.31,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Nov 2025",year:2025,month:10,sf_revenue:53737.02,sf_cos:-24500.0,legal_revenue:41215.24,legal_cos:-24166.67,ihf_revenue:5420.0,ihf_cos:-6687.51,total_revenue:100372.26,total_cos:-55354.18,ebitda:39699.95,profit_before_drawings:19384.02,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Oct 2025",year:2025,month:9,sf_revenue:53734.85,sf_cos:-24648.76,legal_revenue:52649.01,legal_cos:-15000.0,ihf_revenue:7035.0,ihf_cos:-6562.51,total_revenue:113418.86,total_cos:-46211.27,ebitda:63237.64,profit_before_drawings:32233.6,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Sep 2025",year:2025,month:8,sf_revenue:49003.95,sf_cos:-24625.0,legal_revenue:36768.52,legal_cos:-15000.0,ihf_revenue:4425.0,ihf_cos:-6562.51,total_revenue:113743.58,total_cos:-46187.51,ebitda:62539.3,profit_before_drawings:31753.44,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Aug 2025",year:2025,month:7,sf_revenue:34219.53,sf_cos:-21000.0,legal_revenue:26738.56,legal_cos:-15000.0,ihf_revenue:4775.31,ihf_cos:-5149.68,total_revenue:65733.4,total_cos:-41149.68,ebitda:13045.96,profit_before_drawings:6169.75,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Jul 2025",year:2025,month:6,sf_revenue:50975.77,sf_cos:-26500.0,legal_revenue:33492.1,legal_cos:-15000.0,ihf_revenue:1274.99,ihf_cos:0.0,total_revenue:85742.86,total_cos:-41500.0,ebitda:41554.67,profit_before_drawings:18772.83,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Jun 2025",year:2025,month:5,sf_revenue:48929.42,sf_cos:-24500.0,legal_revenue:38134.2,legal_cos:-15000.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:87063.62,total_cos:-39500.0,ebitda:44507.59,profit_before_drawings:20779.08,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"May 2025",year:2025,month:4,sf_revenue:50824.06,sf_cos:-25650.0,legal_revenue:35062.88,legal_cos:-15000.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:85886.94,total_cos:-40650.0,ebitda:42040.85,profit_before_drawings:18288.99,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Apr 2025",year:2025,month:3,sf_revenue:45078.39,sf_cos:-24471.33,legal_revenue:30939.0,legal_cos:-15000.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:76017.39,total_cos:-39471.33,ebitda:33608.51,profit_before_drawings:16920.59,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Mar 2025",year:2025,month:2,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:66692.45,total_cos:0.0,ebitda:31404.79,profit_before_drawings:2599.34,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Feb 2025",year:2025,month:1,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:70901.77,total_cos:0.0,ebitda:37994.62,profit_before_drawings:12070.27,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Jan 2025",year:2025,month:0,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:86640.5,total_cos:0.0,ebitda:51833.07,profit_before_drawings:17420.99,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Dec 2024",year:2024,month:11,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:68138.17,total_cos:0.0,ebitda:46948.28,profit_before_drawings:13209.0,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Nov 2024",year:2024,month:10,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:101817.37,total_cos:0.0,ebitda:78854.02,profit_before_drawings:26722.95,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Oct 2024",year:2024,month:9,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:81427.75,total_cos:0.0,ebitda:60973.91,profit_before_drawings:23473.19,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Sep 2024",year:2024,month:8,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:80610.9,total_cos:0.0,ebitda:59976.67,profit_before_drawings:23079.9,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Aug 2024",year:2024,month:7,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:210074.4,total_cos:0.0,ebitda:188671.6,profit_before_drawings:88714.32,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Jul 2024",year:2024,month:6,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:104299.75,total_cos:0.0,ebitda:83806.12,profit_before_drawings:35011.62,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"Jun 2024",year:2024,month:5,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:70582.0,total_cos:0.0,ebitda:50453.78,profit_before_drawings:19696.07,debts_over_30:0,accrued_income:0,accumulated_profits:0},
  {period:"May 2024",year:2024,month:4,sf_revenue:0.0,sf_cos:0.0,legal_revenue:0.0,legal_cos:0.0,ihf_revenue:0.0,ihf_cos:0.0,total_revenue:88812.25,total_cos:0.0,ebitda:67774.08,profit_before_drawings:27469.15,debts_over_30:0,accrued_income:0,accumulated_profits:0},
];

const SEED_CLIENT_ROWS = [
  {period:"Apr 2026",client:"Secret Mode",sf:8527.49,legal:12992.5,ihf:977.5,total:22497.49},
  {period:"Apr 2026",client:"Marmalade",sf:6437.49,legal:5709.38,ihf:0,total:12146.87},
  {period:"Apr 2026",client:"Radical Forge",sf:8325.0,legal:1537.5,ihf:712.5,total:10575.0},
  {period:"Apr 2026",client:"Flix Interactive",sf:1983.01,legal:0,ihf:5922.09,total:7905.1},
  {period:"Apr 2026",client:"Panic Stations Ltd",sf:104.0,legal:5554.18,ihf:0,total:5658.18},
  {period:"Apr 2026",client:"Futurlab",sf:125.0,legal:5322.5,ihf:0,total:5447.5},
  {period:"Apr 2026",client:"Pretty Cool Games Ltd",sf:0,legal:4750.0,ihf:0,total:4750.0},
  {period:"Apr 2026",client:"Rocket Science",sf:0,legal:4166.66,ihf:0,total:4166.66},
  {period:"Apr 2026",client:"Splash Damage",sf:4139.99,legal:0,ihf:0,total:4139.99},
  {period:"Apr 2026",client:"Omeda",sf:3000.0,legal:1000.0,ihf:0,total:4000.0},
  {period:"Apr 2026",client:"Meaning Machine Games",sf:0,legal:2649.99,ihf:0,total:2649.99},
  {period:"Apr 2026",client:"Skeleton Crew Syndicate",sf:2024.67,legal:437.5,ihf:0,total:2462.17},
  {period:"Apr 2026",client:"Agora Gaming Partners",sf:0,legal:2416.68,ihf:0,total:2416.68},
  {period:"Apr 2026",client:"Simbio Ltd",sf:0,legal:1755.0,ihf:450.0,total:2205.0},
  {period:"Apr 2026",client:"Open Policy",sf:2141.99,legal:0,ihf:0,total:2141.99},
  {period:"Apr 2026",client:"Brainstash",sf:2065.0,legal:0,ihf:0,total:2065.0},
  {period:"Apr 2026",client:"Auroch Digital Ltd",sf:0,legal:2010.01,ihf:0,total:2010.01},
  {period:"Apr 2026",client:"Torn Banner Studios",sf:0,legal:2000.0,ihf:0,total:2000.0},
  {period:"Apr 2026",client:"Rekindled",sf:496.66,legal:500.0,ihf:965.0,total:1961.66},
  {period:"Apr 2026",client:"Treesplease",sf:1944.01,legal:0,ihf:0,total:1944.01},
  {period:"Apr 2026",client:"Bearhug",sf:810.0,legal:0,ihf:1130.0,total:1940.0},
  {period:"Apr 2026",client:"Mythwright",sf:1122.67,legal:612.5,ihf:0,total:1735.17},
  {period:"Apr 2026",client:"Paul Croft - Payload",sf:706.67,legal:1000.0,ihf:0,total:1706.67},
  {period:"Apr 2026",client:"Ammobox Studio",sf:0,legal:1500.0,ihf:0,total:1500.0},
  {period:"Apr 2026",client:"Amplified Games",sf:0,legal:1080.0,ihf:0,total:1080.0},
  {period:"Apr 2026",client:"Half Moon",sf:0,legal:1033.34,ihf:0,total:1033.34},
  {period:"Apr 2026",client:"YRS Truly",sf:0,legal:984.18,ihf:0,total:984.18},
  {period:"Apr 2026",client:"Salto",sf:900.0,legal:0,ihf:0,total:900.0},
  {period:"Apr 2026",client:"Tanglewood",sf:360.0,legal:390.01,ihf:0,total:750.01},
  {period:"Apr 2026",client:"Alinea Analytics",sf:0,legal:737.5,ihf:0,total:737.5},
  {period:"Apr 2026",client:"Arrowhead",sf:0,legal:725.0,ihf:0,total:725.0},
  {period:"Apr 2026",client:"Fumb Games",sf:0,legal:625.0,ihf:0,total:625.0},
  {period:"Apr 2026",client:"Day Off Interactive",sf:90.0,legal:450.0,ihf:0,total:540.0},
  {period:"Apr 2026",client:"Anorak",sf:180.0,legal:59.99,ihf:300.0,total:539.99},
  {period:"Apr 2026",client:"Talewind",sf:0,legal:500.0,ihf:0,total:500.0},
  {period:"Apr 2026",client:"Sassy Ghost",sf:0,legal:500.0,ihf:0,total:500.0},
  {period:"Apr 2026",client:"Yamii Games",sf:0,legal:500.0,ihf:0,total:500.0},
  {period:"Apr 2026",client:"Spellgarden Games",sf:0,legal:489.99,ihf:0,total:489.99},
  {period:"Apr 2026",client:"Kevin Green",sf:0,legal:405.0,ihf:0,total:405.0},
  {period:"Apr 2026",client:"Enigma Studios",sf:0,legal:375.0,ihf:0,total:375.0},
  {period:"Apr 2026",client:"Nap Club BV",sf:0,legal:375.0,ihf:0,total:375.0},
  {period:"Apr 2026",client:"Old Skull Games",sf:0,legal:375.0,ihf:0,total:375.0},
  {period:"Apr 2026",client:"Ginger Samba Sports",sf:0,legal:0,ihf:300.0,total:300.0},
  {period:"Apr 2026",client:"Get Birding",sf:300.0,legal:0,ihf:0,total:300.0},
  {period:"Apr 2026",client:"Bippinbits",sf:0,legal:291.68,ihf:0,total:291.68},
  {period:"Apr 2026",client:"10six",sf:270.0,legal:0,ihf:0,total:270.0},
  {period:"Apr 2026",client:"Pantaloon",sf:0,legal:230.0,ihf:0,total:230.0},
  {period:"Apr 2026",client:"Divide the Plunder",sf:0,legal:208.33,ihf:0,total:208.33},
  {period:"Apr 2026",client:"Slug Disco",sf:0,legal:202.5,ihf:0,total:202.5},
  {period:"Apr 2026",client:"Sounding.io",sf:200.0,legal:0,ihf:0,total:200.0},
  {period:"Apr 2026",client:"Second Star Games",sf:0,legal:125.0,ihf:50.0,total:175.0},
  {period:"Apr 2026",client:"Killer Filler",sf:0,legal:125.0,ihf:0,total:125.0},
  {period:"Apr 2026",client:"Interior Night",sf:112.5,legal:0,ihf:0,total:112.5},
  {period:"Apr 2026",client:"Vince Farquharson",sf:0,legal:40.0,ihf:0,total:40.0},
];

const SEED_TIME_ROWS = [
  {client_name:'Secret Mode Limited',chargeable_hours:112.6,rate:0,revenue:19205.01,total_cost:14012.52,non_partner_charge:11877.48},
  {client_name:'Radical Forge Limited',chargeable_hours:114.6,rate:0,revenue:14974.2,total_cost:12435.03,non_partner_charge:13774.18},
  {client_name:'Marmalade Game Studio Limited',chargeable_hours:63.5,rate:0,revenue:11662.54,total_cost:11072.54,non_partner_charge:975.0},
  {client_name:'Flix Interactive Ltd',chargeable_hours:111.3,rate:0,revenue:8339.65,total_cost:5934.99,non_partner_charge:7454.65},
  {client_name:'Panic Stations Ltd',chargeable_hours:23.9,rate:0,revenue:5858.18,total_cost:4188.76,non_partner_charge:379.0},
  {client_name:'Pretty Cool Games Ltd',chargeable_hours:21.2,rate:0,revenue:5312.5,total_cost:3825.0,non_partner_charge:0.0},
  {client_name:'Futurlab Limited',chargeable_hours:19.5,rate:0,revenue:4822.5,total_cost:3468.75,non_partner_charge:135.0},
  {client_name:'Rocket Science Corporation (UK) Ltd',chargeable_hours:21.8,rate:0,revenue:4346.66,total_cost:2669.16,non_partner_charge:4346.66},
  {client_name:'Omeda Studios Limited',chargeable_hours:14.8,rate:0,revenue:3982.5,total_cost:2655.0,non_partner_charge:0.0},
  {client_name:'Rekindled Studios Ltd',chargeable_hours:49.2,rate:0,revenue:3672.76,total_cost:2631.67,non_partner_charge:2922.76},
  {client_name:'Mythwright Limited',chargeable_hours:18.8,rate:0,revenue:3175.17,total_cost:2414.59,non_partner_charge:2862.67},
  {client_name:'Skeleton Crew Syndicate Limited',chargeable_hours:15.7,rate:0,revenue:2942.16,total_cost:2225.82,non_partner_charge:1754.66},
  {client_name:'Meaning Machine Games Ltd',chargeable_hours:14.3,rate:0,revenue:2649.99,total_cost:1846.66,non_partner_charge:2399.99},
  {client_name:'Simbio Limited',chargeable_hours:17.5,rate:0,revenue:2247.5,total_cost:1502.5,non_partner_charge:492.5},
  {client_name:'Ammobox Studios Sdn Bhd',chargeable_hours:8.6,rate:0,revenue:2145.85,total_cost:1545.01,non_partner_charge:0.0},
  {client_name:'Open Policy Limited',chargeable_hours:7.9,rate:0,revenue:2141.99,total_cost:1074.16,non_partner_charge:1736.99},
  {client_name:'Auroch Digital Ltd',chargeable_hours:11.2,rate:0,revenue:2010.01,total_cost:1900.01,non_partner_charge:360.0},
  {client_name:'Torn Banner Studios Inc.',chargeable_hours:6.7,rate:0,revenue:2000.01,total_cost:1200.01,non_partner_charge:0.0},
  {client_name:'Treesplease Games Ltd',chargeable_hours:7.7,rate:0,revenue:1989.01,total_cost:1303.76,non_partner_charge:324.0},
  {client_name:'Agora Gaming Partners Management Consultancies - FZCO',chargeable_hours:6.0,rate:0,revenue:1500.0,total_cost:1080.0,non_partner_charge:0.0},
  {client_name:'Brainstash Inc',chargeable_hours:5.6,rate:0,revenue:1245.0,total_cost:1004.99,non_partner_charge:0.0},
  {client_name:'Anorak Games Limited',chargeable_hours:17.9,rate:0,revenue:1149.16,total_cost:856.66,non_partner_charge:909.16},
  {client_name:'Bear Hug Games Limited',chargeable_hours:7.9,rate:0,revenue:1134.59,total_cost:1032.09,non_partner_charge:324.59},
  {client_name:'Amplified Games Ltd',chargeable_hours:6.0,rate:0,revenue:1080.0,total_cost:750.0,non_partner_charge:1080.0},
  {client_name:'Salto Games Limited',chargeable_hours:3.8,rate:0,revenue:1012.5,total_cost:675.0,non_partner_charge:0.0},
  {client_name:'YRS Truly Ltd',chargeable_hours:4.4,rate:0,revenue:984.17,total_cost:685.01,non_partner_charge:380.0},
  {client_name:'Tanglewood Games Ltd',chargeable_hours:4.2,rate:0,revenue:840.01,total_cost:630.84,non_partner_charge:390.01},
  {client_name:'Half Moon Studios Ltd',chargeable_hours:4.0,rate:0,revenue:800.0,total_cost:720.0,non_partner_charge:0.0},
  {client_name:'Alinea Analytics AS',chargeable_hours:2.5,rate:0,revenue:737.5,total_cost:450.0,non_partner_charge:0.0},
  {client_name:'Arrowhead Game Studios AB',chargeable_hours:2.5,rate:0,revenue:725.0,total_cost:450.0,non_partner_charge:0.0},
  {client_name:'Fumb Games Ltd',chargeable_hours:2.5,rate:0,revenue:625.0,total_cost:450.0,non_partner_charge:0.0},
  {client_name:'Sassy Ghost Limited',chargeable_hours:2.0,rate:0,revenue:520.0,total_cost:360.0,non_partner_charge:0.0},
  {client_name:'Day Off Interactive Limited',chargeable_hours:2.5,rate:0,revenue:520.0,total_cost:450.0,non_partner_charge:0.0},
  {client_name:'Yamii Games',chargeable_hours:2.0,rate:0,revenue:500.0,total_cost:360.0,non_partner_charge:0.0},
  {client_name:'Spellgarden Games UK Ltd',chargeable_hours:2.3,rate:0,revenue:489.99,total_cost:346.66,non_partner_charge:239.99},
  {client_name:'Ginger Samba Sports Limited',chargeable_hours:11.3,rate:0,revenue:485.42,total_cost:462.92,non_partner_charge:485.42},
  {client_name:'Talewind Limited',chargeable_hours:2.3,rate:0,revenue:419.99,total_cost:291.66,non_partner_charge:419.99},
  {client_name:'Kevin Green',chargeable_hours:1.5,rate:0,revenue:405.0,total_cost:270.0,non_partner_charge:0.0},
  {client_name:'Old Skull Games SAS',chargeable_hours:1.5,rate:0,revenue:375.0,total_cost:270.0,non_partner_charge:0.0},
  {client_name:'Nap Club BV',chargeable_hours:1.5,rate:0,revenue:375.0,total_cost:270.0,non_partner_charge:0.0},
  {client_name:'Get Birding',chargeable_hours:1.7,rate:0,revenue:300.01,total_cost:300.01,non_partner_charge:0.0},
  {client_name:'Bippinbits GmbH',chargeable_hours:1.2,rate:0,revenue:291.68,total_cost:210.01,non_partner_charge:0.0},
  {client_name:'10six Games Ltd',chargeable_hours:1.5,rate:0,revenue:270.0,total_cost:270.0,non_partner_charge:0.0},
  {client_name:'Pantaloon Interactive Ltd',chargeable_hours:0.9,rate:0,revenue:229.18,total_cost:165.01,non_partner_charge:0.0},
  {client_name:'Divide The Plunder',chargeable_hours:0.8,rate:0,revenue:208.32,total_cost:149.99,non_partner_charge:0.0},
  {client_name:'Slug Disco Studios Limited',chargeable_hours:0.8,rate:0,revenue:202.5,total_cost:135.0,non_partner_charge:0.0},
  {client_name:'Sounding Inc',chargeable_hours:1.0,rate:0,revenue:180.0,total_cost:180.0,non_partner_charge:0.0},
  {client_name:'Killer Filler Ltd',chargeable_hours:0.5,rate:0,revenue:125.0,total_cost:90.0,non_partner_charge:0.0},
  {client_name:'Second Star Games Limited',chargeable_hours:0.5,rate:0,revenue:125.0,total_cost:90.0,non_partner_charge:0.0},
  {client_name:'Interior Night Limited',chargeable_hours:0.8,rate:0,revenue:112.5,total_cost:135.0,non_partner_charge:0.0},
  {client_name:'Vincent Farquharson',chargeable_hours:0.2,rate:0,revenue:41.67,total_cost:30.01,non_partner_charge:0.0},
];

const SEED_SERVICE_REV_ROWS = [
  {period:"Apr 2026",service:"Strategic Finance Support",revenue:32151.67,ba:"sf"},
  {period:"Apr 2026",service:"M&A - Finance",revenue:11579.48,ba:"sf"},
  {period:"Apr 2026",service:"Financial Model Build",revenue:2635.0,ba:"sf"},
  {period:"Apr 2026",service:"Legal Advice",revenue:65741.92,ba:"legal"},
  {period:"Apr 2026",service:"M&A - Legal",revenue:1000.0,ba:"legal"},
  {period:"Apr 2026",service:"In-House Finance",revenue:8895.0,ba:"ihf"},
  {period:"Apr 2026",service:"In-House Finance (out of scope)",revenue:1797.09,ba:"ihf"},
  {period:"Apr 2026",service:"In-House Finance (payroll)",revenue:65.0,ba:"ihf"},
  {period:"Apr 2026",service:"Recharging Fees",revenue:50.0,ba:"legal"},
];


const SEED_MAPPING = [
  {time_name:"Secret Mode Limited",revenue_client:"Secret Mode",tag:"SECRET_MODE",referral:"",origBA:"",equityCont:false},
  {time_name:"Radical Forge Limited",revenue_client:"Radical Forge",tag:"RADICAL_FORGE",referral:"",origBA:"",equityCont:false},
  {time_name:"Marmalade Game Studio Limited",revenue_client:"Marmalade",tag:"MARMALADE",referral:"",origBA:"",equityCont:false},
  {time_name:"Flix Interactive Ltd",revenue_client:"Flix Interactive",tag:"FLIX_INTERACTIVE",referral:"",origBA:"",equityCont:false},
  {time_name:"Panic Stations Ltd",revenue_client:"Panic Stations Ltd",tag:"PANIC_STATIONS_LTD",referral:"",origBA:"",equityCont:false},
  {time_name:"Pretty Cool Games Ltd",revenue_client:"Pretty Cool Games Ltd",tag:"PRETTY_COOL_GAMES_LTD",referral:"",origBA:"",equityCont:false},
  {time_name:"Futurlab Limited",revenue_client:"Futurlab",tag:"FUTURLAB",referral:"",origBA:"",equityCont:false},
  {time_name:"Rocket Science Corporation (UK) Ltd",revenue_client:"Rocket Science",tag:"ROCKET_SCIENCE",referral:"",origBA:"",equityCont:false},
  {time_name:"Omeda Studios Limited",revenue_client:"Omeda",tag:"OMEDA",referral:"",origBA:"",equityCont:false},
  {time_name:"Rekindled Studios Ltd",revenue_client:"Rekindled",tag:"REKINDLED",referral:"",origBA:"",equityCont:false},
  {time_name:"Mythwright Limited",revenue_client:"Mythwright",tag:"MYTHWRIGHT",referral:"",origBA:"",equityCont:false},
  {time_name:"Skeleton Crew Syndicate Limited",revenue_client:"Skeleton Crew Syndicate",tag:"SKELETON_CREW_SYNDICATE",referral:"",origBA:"",equityCont:false},
  {time_name:"Meaning Machine Games Ltd",revenue_client:"Meaning Machine Games",tag:"MEANING_MACHINE_GAMES",referral:"",origBA:"",equityCont:false},
  {time_name:"Simbio Limited",revenue_client:"Simbio Ltd",tag:"SIMBIO_LTD",referral:"",origBA:"",equityCont:false},
  {time_name:"Ammobox Studios Sdn Bhd",revenue_client:"Ammobox Studio",tag:"AMMOBOX_STUDIO",referral:"",origBA:"",equityCont:false},
  {time_name:"Open Policy Limited",revenue_client:"Open Policy",tag:"OPEN_POLICY",referral:"",origBA:"",equityCont:false},
  {time_name:"Auroch Digital Ltd",revenue_client:"Auroch Digital Ltd",tag:"AUROCH_DIGITAL_LTD",referral:"",origBA:"",equityCont:false},
  {time_name:"Torn Banner Studios Inc.",revenue_client:"Torn Banner Studios",tag:"TORN_BANNER_STUDIOS",referral:"",origBA:"",equityCont:false},
  {time_name:"Treesplease Games Ltd",revenue_client:"Treesplease",tag:"TREESPLEASE",referral:"",origBA:"",equityCont:false},
  {time_name:"Agora Gaming Partners Management Consultancies - FZCO",revenue_client:"Agora Gaming Partners",tag:"AGORA_GAMING_PARTNERS",referral:"",origBA:"",equityCont:false},
  {time_name:"Brainstash Inc",revenue_client:"Brainstash",tag:"BRAINSTASH",referral:"",origBA:"",equityCont:false},
  {time_name:"Anorak Games Limited",revenue_client:"Anorak",tag:"ANORAK",referral:"",origBA:"",equityCont:false},
  {time_name:"Bear Hug Games Limited",revenue_client:"Bearhug",tag:"BEARHUG",referral:"",origBA:"",equityCont:false},
  {time_name:"Amplified Games Ltd",revenue_client:"Amplified Games",tag:"AMPLIFIED_GAMES",referral:"",origBA:"",equityCont:false},
  {time_name:"Salto Games Limited",revenue_client:"Salto",tag:"SALTO",referral:"",origBA:"",equityCont:false},
  {time_name:"YRS Truly Ltd",revenue_client:"YRS Truly",tag:"YRS_TRULY",referral:"",origBA:"",equityCont:false},
  {time_name:"Tanglewood Games Ltd",revenue_client:"Tanglewood",tag:"TANGLEWOOD",referral:"",origBA:"",equityCont:false},
  {time_name:"Half Moon Studios Ltd",revenue_client:"Half Moon",tag:"HALF_MOON",referral:"",origBA:"",equityCont:false},
  {time_name:"Alinea Analytics AS",revenue_client:"Alinea Analytics",tag:"ALINEA_ANALYTICS",referral:"",origBA:"",equityCont:false},
  {time_name:"Arrowhead Game Studios AB",revenue_client:"Arrowhead",tag:"ARROWHEAD",referral:"",origBA:"",equityCont:false},
  {time_name:"Fumb Games Ltd",revenue_client:"Fumb Games",tag:"FUMB_GAMES",referral:"",origBA:"",equityCont:false},
  {time_name:"Sassy Ghost Limited",revenue_client:"Sassy Ghost",tag:"SASSY_GHOST",referral:"",origBA:"",equityCont:false},
  {time_name:"Day Off Interactive Limited",revenue_client:"Day Off Interactive",tag:"DAY_OFF_INTERACTIVE",referral:"",origBA:"",equityCont:false},
  {time_name:"Yamii Games",revenue_client:"Yamii Games",tag:"YAMII_GAMES",referral:"",origBA:"",equityCont:false},
  {time_name:"Spellgarden Games UK Ltd",revenue_client:"Spellgarden Games",tag:"SPELLGARDEN_GAMES",referral:"",origBA:"",equityCont:false},
  {time_name:"Ginger Samba Sports Limited",revenue_client:"Ginger Samba Sports",tag:"GINGER_SAMBA_SPORTS",referral:"",origBA:"",equityCont:false},
  {time_name:"Talewind Limited",revenue_client:"Talewind",tag:"TALEWIND",referral:"",origBA:"",equityCont:false},
  {time_name:"Kevin Green",revenue_client:"Kevin Green",tag:"KEVIN_GREEN",referral:"",origBA:"",equityCont:false},
  {time_name:"Old Skull Games SAS",revenue_client:"Old Skull Games",tag:"OLD_SKULL_GAMES",referral:"",origBA:"",equityCont:false},
  {time_name:"Nap Club BV",revenue_client:"Nap Club BV",tag:"NAP_CLUB_BV",referral:"",origBA:"",equityCont:false},
  {time_name:"Get Birding",revenue_client:"Get Birding",tag:"GET_BIRDING",referral:"",origBA:"",equityCont:false},
  {time_name:"Bippinbits GmbH",revenue_client:"Bippinbits",tag:"BIPPINBITS",referral:"",origBA:"",equityCont:false},
  {time_name:"10six Games Ltd",revenue_client:"10six",tag:"10SIX",referral:"",origBA:"",equityCont:false},
  {time_name:"Pantaloon Interactive Ltd",revenue_client:"Pantaloon",tag:"PANTALOON",referral:"",origBA:"",equityCont:false},
  {time_name:"Divide The Plunder",revenue_client:"Divide the Plunder",tag:"DIVIDE_THE_PLUNDER",referral:"",origBA:"",equityCont:false},
  {time_name:"Slug Disco Studios Limited",revenue_client:"Slug Disco",tag:"SLUG_DISCO",referral:"",origBA:"",equityCont:false},
  {time_name:"Sounding Inc",revenue_client:"Sounding.io",tag:"SOUNDINGIO",referral:"",origBA:"",equityCont:false},
  {time_name:"Killer Filler Ltd",revenue_client:"Killer Filler",tag:"KILLER_FILLER",referral:"",origBA:"",equityCont:false},
  {time_name:"Second Star Games Limited",revenue_client:"Second Star Games",tag:"SECOND_STAR_GAMES",referral:"",origBA:"",equityCont:false},
  {time_name:"Interior Night Limited",revenue_client:"Interior Night",tag:"INTERIOR_NIGHT",referral:"",origBA:"",equityCont:false},
  {time_name:"Vincent Farquharson",revenue_client:"Vince Farquharson",tag:"VINCE_FARQUHARSON",referral:"",origBA:"",equityCont:false},
];


const DEFAULT_EMPLOYEES = [
  { name:"Steve Tinkler",         email:"steve@firm.com",    ba:"Strategic Finance", role:"Partner" },
  { name:"Felix Wong",            email:"felix@firm.com",    ba:"Legal",             role:"Partner" },
  { name:"Carter Rich",           email:"carter@firm.com",   ba:"Legal",             role:"Associate" },
  { name:"Neil Bancroft-Jones",   email:"neil@firm.com",     ba:"Strategic Finance", role:"Associate" },
  { name:"Victoria Harrison",     email:"victoria@firm.com", ba:"In-House Finance",  role:"Associate" },
  { name:"Ruth Manio",            email:"ruth@firm.com",     ba:"In-House Finance",  role:"Associate" },
  { name:"John Ivan De Jesus",    email:"john@firm.com",     ba:"In-House Finance",  role:"Associate" },
];

const SEED_STAFF_TIME = [
  {period:"Apr 2026",staff:"Carter Rich",       ba:"Legal",             hours:124.0, charge:23469.13, cost:15499.98},
  {period:"Apr 2026",staff:"Felix Wong",        ba:"Legal",             hours:190.4, charge:44702.35, cost:34275.04},
  {period:"Apr 2026",staff:"John Ivan De Jesus",ba:"In-House Finance",  hours:79.0,  charge:1295.0,   cost:1580.0},
  {period:"Apr 2026",staff:"Neil Bancroft-Jones",ba:"Strategic Finance",hours:131.6, charge:21238.36, cost:16445.85},
  {period:"Apr 2026",staff:"Ruth Manio",        ba:"In-House Finance",  hours:102.5, charge:3022.5,   cost:2050.0},
  {period:"Apr 2026",staff:"Steve Tinkler",     ba:"Strategic Finance", hours:128.2, charge:26754.5,  cost:23070.11},
  {period:"Apr 2026",staff:"Victoria Harrison", ba:"In-House Finance",  hours:110.3, charge:8587.52,  cost:7171.68},
];

const SEED_AGED_DEBTORS = [
  {client:"Zenos Interactive Limited",           over30:18846.68},
  {client:"Zenos Technology Limited",            over30:7646.40},
  {client:"Rocket Science Corporation (UK) Ltd", over30:4168.01},
  {client:"Caketown Interactive AB",             over30:437.50},
  {client:"Ginger Samba Sports Limited",         over30:360.00},
  {client:"Syrin Studios Limited",               over30:234.00},
  {client:"AGORA GAMING PARTNERS(HK) SERVICE CO., LIMITED", over30:187.50},
  {client:"Subcult Joint Ltd",                   over30:150.00},
];

const SEED_ACCRUED_CLIENTS = [
  {period:"Apr 2026",client:"Omeda",              accrued:7000.00},
  {period:"Apr 2026",client:"Wired Productions",  accrued:5200.00},
  {period:"Apr 2026",client:"Pretty Cool Games Ltd",accrued:5050.00},
  {period:"Apr 2026",client:"Sounding.io",        accrued:1650.00},
  {period:"Apr 2026",client:"Pretty Cool Games",  accrued:880.00},
  {period:"Apr 2026",client:"Twin Atlas LLC",     accrued:750.00},
  {period:"Apr 2026",client:"Get Birding",        accrued:650.00},
  {period:"Apr 2026",client:"Electric Saint",     accrued:520.00},
  {period:"Apr 2026",client:"Atipica Ltd",        accrued:500.00},
  {period:"Apr 2026",client:"Sassy Ghost",        accrued:500.00},
  {period:"Apr 2026",client:"Pantaloon",          accrued:480.00},
  {period:"Apr 2026",client:"Vince",              accrued:102.50},
];

// Service → BA mapping defaults
const DEFAULT_SERVICE_MAP = [
  { service:"Strategic Finance Support",       ba:"Strategic Finance" },
  { service:"Financial Model Build",           ba:"Strategic Finance" },
  { service:"M&A - Finance",                  ba:"Strategic Finance" },
  { service:"Legal Advice",                   ba:"Legal" },
  { service:"M&A - Legal",                    ba:"Legal" },
  { service:"Recharging Fees",                ba:"Legal" },
  { service:"In-House Finance",               ba:"In-House Finance" },
  { service:"In-House Finance (payroll)",     ba:"In-House Finance" },
  { service:"In-House Finance (out of scope)","ba":"In-House Finance" },
];


/* ═══════════════════════════════════════════════════════════════
   FORMATTERS & HELPERS
═══════════════════════════════════════════════════════════════ */
const fmtGBP = n => {
  if (n === undefined || n === null || isNaN(n)) return "—";
  const a = Math.abs(n);
  const s = a >= 1e6 ? `£${(a/1e6).toFixed(1)}m` : a >= 1000 ? `£${Math.round(a/1000)}k` : `£${Math.round(a).toLocaleString("en-GB")}`;
  return n < 0 ? `(${s})` : s;
};
const fmtPct = n => n === undefined || n === null || isNaN(n) ? "—" : `${(n*100).toFixed(1)}%`;
const fmtNum = n => n === undefined || n === null || isNaN(n) ? "—" : n.toLocaleString("en-GB");

// plRows is stored newest-first (index 0 = most recent).
// All LTM helpers reverse to chronological order so that the 12 months
// ending at selectedIdx are the selected month plus the 11 months before it.
const MSHORT_SORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function periodSortKey(period) {
  const parts = (period||'').split(' ');
  // Prefix match so "Sept" sorts as September (an exact indexOf returned -1,
  // which would have sorted September before January of the same year).
  const mIdx = MSHORT_SORT.findIndex(m => (parts[0] || '').startsWith(m));
  const yr   = parseInt(parts[1]) || 0;
  return yr * 12 + mIdx;
}
function sortedPlRows(rows) {
  // Deduplicate by period, keeping first occurrence
  const seen = new Set();
  const deduped = rows.filter(r => { if (seen.has(r.period)) return false; seen.add(r.period); return true; });
  return deduped.sort((a,b) => periodSortKey(b.period) - periodSortKey(a.period));
}
function ltmSum(rows, selectedIdx, field) {
  const sorted = sortedPlRows(rows);
  const chrono = [...sorted].reverse();
  const ci = sorted.length - 1 - selectedIdx;
  const s  = Math.max(0, ci - 11);
  return chrono.slice(s, ci + 1).reduce((a, r) => a + (r[field] ?? 0), 0);
}
function last12(rows, selectedIdx, field) {
  const sorted = sortedPlRows(rows);
  const chrono = [...sorted].reverse();
  const ci = sorted.length - 1 - selectedIdx;
  const s  = Math.max(0, ci - 11);
  return chrono.slice(s, ci + 1).map(r => ({
    period: r.period, short: r.period.split(" ")[0], value: r[field] ?? 0,
  }));
}
function trailingLTM(rows, selectedIdx, field) {
  const sorted = sortedPlRows(rows);
  const chrono = [...sorted].reverse(); // full history oldest->newest
  const ci = sorted.length - 1 - selectedIdx; // index of selected month in chrono
  const s  = Math.max(0, ci - 11); // start of visible 12 bars
  // For each of the 12 visible bars, calculate its trailing 12-month sum
  // using the FULL chrono array (not just the visible slice)
  return chrono.slice(s, ci + 1).map((_, i) => {
    const barIdx   = s + i;          // index of this bar in full chrono
    const ltmStart = barIdx - 11;    // 12 months back from this bar
    if (ltmStart < 0) return { ltm: null }; // not enough history
    const window = chrono.slice(ltmStart, barIdx + 1);
    // Only show LTM when all 12 months have valid revenue data
    const hasValidData = window.every(r => (r.total_revenue ?? 0) > 0);
    if (!hasValidData) return { ltm: null };
    return { ltm: window.reduce((a, r) => a + (r[field] ?? 0), 0) };
  });
}
function chartData(rows, selectedIdx, field) {
  const bars = last12(rows, selectedIdx, field);
  const ltms = trailingLTM(rows, selectedIdx, field);
  return bars.map((b, i) => ({ ...b, ltm: ltms[i].ltm }));
}

/* ═══════════════════════════════════════════════════════════════
   UI PRIMITIVES
═══════════════════════════════════════════════════════════════ */
const Card    = ({ children, style={} }) => <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:"18px 20px", ...style }}>{children}</div>;
const Div     = ({ my=24 }) => <div style={{ height:1, background:T.border, margin:`${my}px 0` }}/>;
const Badge   = ({ label, color=T.accent }) => <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:4, fontSize:10, fontWeight:700, background:color+"22", color, letterSpacing:"0.06em", fontFamily:T.fontMono }}>{label}</span>;
const Btn     = ({ children, onClick, variant="primary", style={} }) => (
  <button onClick={onClick} style={{ background:variant==="primary"?T.accent:T.raised, color:T.text, border:variant==="primary"?"none":`1px solid ${T.border}`, padding:"7px 16px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:T.fontSans, ...style }}>
    {children}
  </button>
);
const Inp     = ({ value, onChange, placeholder, style={} }) => (
  <input type="text" value={value} onChange={onChange} placeholder={placeholder}
    style={{ background:T.raised, border:`1px solid ${T.border}`, color:T.text, padding:"7px 11px", borderRadius:6, fontSize:12, fontFamily:T.fontSans, outline:"none", width:"100%", ...style }}/>
);
const Sel     = ({ value, onChange, children, style={} }) => (
  <select value={value} onChange={onChange}
    style={{ background:T.raised, border:`1px solid ${T.border}`, color:T.text, padding:"7px 11px", borderRadius:6, fontSize:12, fontFamily:T.fontSans, cursor:"pointer", outline:"none", ...style }}>
    {children}
  </select>
);
const TH      = ({ children, right, center }) => (
  <th style={{ textAlign:center?"center":right?"right":"left", padding:"8px 12px", fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:T.muted, borderBottom:`1px solid ${T.border}`, fontFamily:T.fontMono, whiteSpace:"nowrap" }}>{children}</th>
);
const TD      = ({ children, right, center, color, bold, style={} }) => (
  <td style={{ textAlign:center?"center":right?"right":"left", padding:"8px 12px", fontSize:12, color:color||T.text, fontWeight:bold?700:400, borderBottom:`1px solid ${T.border}22`, fontFamily:bold?T.fontMono:T.fontSans, ...style }}>{children}</td>
);
const EmptyRow = ({ cols, msg="Upload data pack to populate" }) => (
  <tr><td colSpan={cols} style={{ padding:"20px 12px", textAlign:"center", color:T.muted, fontSize:12, fontStyle:"italic" }}>{msg}</td></tr>
);
const SH = ({ label, accent=T.accent, action }) => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ width:3, height:16, background:accent, borderRadius:2 }}/>
      <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.14em", textTransform:"uppercase", color:T.mutedLt, fontFamily:T.fontMono }}>{label}</span>
    </div>
    {action}
  </div>
);
const FL = { fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:T.muted, marginBottom:6, fontFamily:T.fontMono };

// Tick icon for equity/cont fee and non-Steve/Felix
const Tick = ({ color=T.green, title="" }) => (
  <span title={title} style={{ color, fontSize:14, fontWeight:700 }}>✓</span>
);

/* ═══════════════════════════════════════════════════════════════
   CHARTS
═══════════════════════════════════════════════════════════════ */
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.raised, border:`1px solid ${T.borderHi}`, borderRadius:6, padding:"8px 12px", fontSize:11, fontFamily:T.fontMono }}>
      <div style={{ color:T.mutedLt, marginBottom:5, fontSize:10 }}>{label}</div>
      {payload.map((p,i) => <div key={i} style={{ color:p.color, lineHeight:"20px" }}>{p.name}: {fmtGBP(p.value)}</div>)}
    </div>
  );
};

function BarLTMChart({ data, barColor, h=150, barName="Monthly", ltmName="Trailing LTM", hideLine=false }) {
  return (
    <ResponsiveContainer width="100%" height={h}>
      <ComposedChart data={data} margin={{ top:4, right:hideLine?4:44, left:-8, bottom:0 }}>
        <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false}/>
        <XAxis dataKey="short" tick={{ fill:T.muted, fontSize:9, fontFamily:T.fontMono }} axisLine={false} tickLine={false}/>
        <YAxis yAxisId="bars" orientation="left"
          tick={{ fill:T.muted, fontSize:9, fontFamily:T.fontMono }} axisLine={false} tickLine={false}
          tickFormatter={v => v===0?"0":`£${Math.round(Math.abs(v)/1000)}k`} width={38}/>
        {!hideLine && <YAxis yAxisId="ltm" orientation="right"
          tick={{ fill:barColor, fontSize:9, fontFamily:T.fontMono, opacity:0.7 }} axisLine={false} tickLine={false}
          tickFormatter={v => v===0?"0":`£${(Math.abs(v)/1000000).toFixed(1)}m`} width={44}/>}
        <Tooltip content={<ChartTip/>}/>
        <Bar yAxisId="bars" dataKey="value" name={barName} fill={barColor} radius={[2,2,0,0]} maxBarSize={32}>
          {data.map((d,i) => <Cell key={i} fill={d.value<0?T.red:barColor} fillOpacity={0.85}/>)}
        </Bar>
        {!hideLine && <Line yAxisId="ltm" dataKey="ltm" name={ltmName} stroke={barColor} strokeWidth={1.5} strokeDasharray="5 3" dot={false}/>}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/* ═══════════════════════════════════════════════════════════════
   REFERRAL SOURCE BY REVENUE TABLE (reusable)
═══════════════════════════════════════════════════════════════ */
function ReferralRevenueTable({ monthClients, mapping, referrals, baKey=null, currentBA=null }) {
  // For BA tabs: if a client's origBA differs from currentBA, group under origBA (cross-referral).
  // Otherwise group under the referral source name as normal.
  const refMap = {};
  const unref = { name:"Unassigned", rev:0 };

  monthClients.forEach(c => {
    const rev = baKey ? (c[baKey]??0) : (c.total??0);
    if (rev <= 0) return;

    // Fuzzy match — try exact revenue_client first, then partial match on time_name
    const lower = (c.client||'').toLowerCase();
    const mp = mapping.find(m => m.revenue_client === c.client)
      || mapping.find(m =>
          (m.revenue_client||'').toLowerCase() === lower ||
          lower.includes((m.revenue_client||'').toLowerCase()) ||
          (m.revenue_client||'').toLowerCase().includes(lower) ||
          lower.includes((m.time_name||'').toLowerCase()) ||
          (m.time_name||'').toLowerCase().includes(lower)
        );

    let groupKey;
    if (currentBA && mp?.origBA && mp.origBA !== currentBA) {
      groupKey = mp.origBA;
    } else {
      groupKey = mp?.referral || null;
    }

    if (!groupKey) { unref.rev += rev; return; }
    refMap[groupKey] = (refMap[groupKey]??0) + rev;
  });

  const rows = Object.entries(refMap).map(([name,rev]) => ({ name, rev }))
    .sort((a,b) => b.rev - a.rev);
  if (unref.rev > 0) rows.push(unref);

  if (rows.length === 0) return (
    <div style={{ fontSize:11, color:T.muted, fontStyle:"italic", padding:"10px 0" }}>
      No referral data — assign referral sources and originating BA in Standing Data
    </div>
  );

  const total = rows.reduce((s,r) => s+r.rev, 0);
  // Determine if a row is a cross-referred BA (to style differently)
  const baNames = new Set(["Strategic Finance","Legal","In-House Finance","Tax"]);
  return (
    <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
      <thead><tr style={{ background:T.raised }}>
        <TH>Referral / Originating BA</TH>
        <TH right>Revenue</TH>
        <TH right>% of Total</TH>
      </tr></thead>
      <tbody>
        {rows.map((r,i) => {
          const isCrossBA = baNames.has(r.name) && r.name !== currentBA;
          const isUnassigned = r.name === "Unassigned";
          return (
            <tr key={i} className="rh">
              <TD>
                {isUnassigned
                  ? <span style={{ color:T.muted, fontStyle:"italic" }}>Unassigned</span>
                  : isCrossBA
                  ? <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:9, fontFamily:T.fontMono, fontWeight:700,
                        letterSpacing:"0.08em", textTransform:"uppercase", padding:"2px 6px",
                        borderRadius:4, background:T.accent+"22", color:T.accentLt }}>
                        CROSS-REFER
                      </span>
                      {r.name}
                    </span>
                  : r.name}
              </TD>
              <TD right bold>{fmtGBP(r.rev)}</TD>
              <TD right color={T.mutedLt}>{total>0 ? fmtPct(r.rev/total) : "—"}</TD>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TOP BAR
═══════════════════════════════════════════════════════════════ */
function TopBar({ plRows, selectedIdx, setSelectedIdx, xeroConnected, xeroSyncing, onXeroSync, onUpload, uploading, currentUser, onSignOut }) {
  const ref = useRef();
  return (
    <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`, display:"flex",
      alignItems:"center", justifyContent:"space-between", padding:"0 28px", height:58,
      position:"sticky", top:0, zIndex:200 }}>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ fontFamily:T.fontSerif, fontSize:18, fontWeight:700, color:T.text }}>Firm Intelligence</div>
        <div style={{ width:1, height:22, background:T.border }}/>
        <div style={{ fontSize:10, color:T.muted, fontFamily:T.fontMono, letterSpacing:"0.08em", textTransform:"uppercase" }}>Management Reporting</div>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ fontSize:10, color:T.muted, fontFamily:T.fontMono }}>PERIOD</div>
        <Sel value={selectedIdx} onChange={e => setSelectedIdx(Number(e.target.value))} style={{ width:140 }}>
          {sortedPlRows(plRows).filter((r,i,arr) => arr.findIndex(x=>x.period===r.period)===i).map((r,i) => <option key={i} value={i}>{r.period}</option>)}
        </Sel>
        <input ref={ref} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }}
          onChange={e => { if(e.target.files[0]) onUpload(e.target.files[0]); e.target.value=""; }}/>
        <Btn onClick={() => !uploading && ref.current.click()}
          style={{ opacity: uploading ? 0.6 : 1, cursor: uploading ? "wait" : "pointer" }}>
          {uploading ? "⏳ Parsing…" : "↑ Upload P&L"}
        </Btn>
        {xeroConnected ? (
          <Btn onClick={onXeroSync} variant="secondary"
            style={{ opacity: xeroSyncing ? 0.6 : 1, cursor: xeroSyncing ? "wait" : "pointer",
              borderColor: "#13B5EA", color: "#13B5EA" }}>
            {xeroSyncing ? "⏳ Syncing…" : "↻ Sync Xero"}
          </Btn>
        ) : (
          <Btn onClick={() => window.location.href='/api/xero/auth'} variant="secondary"
            style={{ borderColor:"#13B5EA", color:"#13B5EA" }}>
            Connect Xero
          </Btn>
        )}
        <div style={{width:1,height:22,background:T.border}}/>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:1}}>
          {currentUser && <span style={{fontSize:10,color:T.muted,fontFamily:T.fontMono}}>{currentUser.email.split("@")[0]}</span>}
          <span onClick={onSignOut} style={{fontSize:10,color:T.red,cursor:"pointer",fontFamily:T.fontMono,
            letterSpacing:"0.06em",textTransform:"uppercase"}}
            onMouseEnter={e=>e.currentTarget.style.opacity="0.7"}
            onMouseLeave={e=>e.currentTarget.style.opacity="1"}>Sign out</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB BAR
═══════════════════════════════════════════════════════════════ */
const TABS = [
  { id:"exec",     label:"Executive Summary" },
  { id:"sf",       label:"Strategic Finance" },
  { id:"legal",    label:"Legal" },
  { id:"ihf",      label:"In-House Finance" },
  { id:"tax",      label:"Tax" },
  { id:"objectives", label:"Objectives" },
  { id:"crm",      label:"CRM" },
  { id:"onboarding", label:"Onboarding" },
  { id:"billing",  label:"Billing" },
  { id:"utilisation", label:"Weekly Perf" },
  { id:"standing", label:"Standing Data" },
  { id:"admin",    label:"Admin" },
];
function TabBar({ active, setActive, currentUser }) {
  const visibleTabs = TABS.filter(t => {
    if (!currentUser || currentUser.isPartner) return true;
    // Use tab_access array from Supabase if set
    if (currentUser.tab_access?.length) return currentUser.tab_access.includes(t.id);
    // Fallback to BA-based default
    const baTabMap = { 'Strategic Finance':'sf', 'Legal':'legal', 'In-House Finance':'ihf' };
    const userTab = baTabMap[currentUser.ba];
    return t.id === userTab;
  });
  return (
    <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`, display:"flex", padding:"0 28px", overflowX:"auto" }}>
      {visibleTabs.map(t => {
        const on = t.id === active;
        return (
          <button key={t.id} className="tbtn" onClick={() => setActive(t.id)}
            style={{ padding:"13px 20px", fontSize:11, fontWeight:600, letterSpacing:"0.06em",
              textTransform:"uppercase", cursor:"pointer", color:on?T.text:T.muted, background:"none",
              border:"none", borderBottom:on?`2px solid ${T.accent}`:"2px solid transparent",
              whiteSpace:"nowrap", fontFamily:T.fontSans, transition:"color 0.15s" }}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   REUSABLE CHART CARDS
═══════════════════════════════════════════════════════════════ */
function PLCard({ label, month, ltm, data, color }) {
  const vc = v => v < 0 ? T.red : color;
  return (
    <Card>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:T.muted, marginBottom:14, fontFamily:T.fontMono }}>{label}</div>
      <div style={{ display:"flex", gap:28, marginBottom:16 }}>
        <div>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4, fontFamily:T.fontMono }}>Month</div>
          <div style={{ fontSize:30, fontWeight:700, color:vc(month), fontFamily:T.fontMono, letterSpacing:"-0.03em" }}>{fmtGBP(month)}</div>
        </div>
        <div style={{ width:1, background:T.border }}/>
        <div>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4, fontFamily:T.fontMono }}>LTM</div>
          <div style={{ fontSize:30, fontWeight:700, color:vc(ltm), fontFamily:T.fontMono, letterSpacing:"-0.03em" }}>{fmtGBP(ltm)}</div>
        </div>
      </div>
      <BarLTMChart data={data} barColor={color} h={140}/>
    </Card>
  );
}

function BABlock({ label, color, revM, revLTM, cosM, cosLTM, gpData }) {
  const gpM = Math.round((revM+cosM)/1000)*1000, gpL = Math.round((revLTM+cosLTM)/1000)*1000;
  return (
    <Card>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
        <div style={{ width:8, height:8, borderRadius:"50%", background:color }}/>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color, fontFamily:T.fontMono }}>{label}</div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", marginBottom:14, borderRadius:6, overflow:"hidden", border:`1px solid ${T.border}` }}>
        {[{l:"Revenue",m:revM,ltm:revLTM,c:T.text},{l:"Cost",m:cosM,ltm:cosLTM,c:T.amber},{l:"GP",m:gpM,ltm:gpL,c:gpM>=0?T.green:T.red}].map((x,i) => (
          <div key={i} style={{ padding:"12px 14px", borderRight:i<2?`1px solid ${T.border}`:"none", background:T.raised }}>
            <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:5, fontFamily:T.fontMono }}>{x.l}</div>
            <div style={{ fontSize:18, fontWeight:700, color:x.c, fontFamily:T.fontMono, letterSpacing:"-0.02em" }}>{fmtGBP(x.m)}</div>
            <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>LTM {fmtGBP(x.ltm)}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6, fontFamily:T.fontMono }}>Gross profit — last 12 months</div>
      <BarLTMChart data={gpData} barColor={T.green} h={130}/>
    </Card>
  );
}

function QCard({ label, mVal, ltmVal, data, color, fmt=fmtGBP }) {
  return (
    <Card>
      <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:T.muted, marginBottom:14, fontFamily:T.fontMono }}>{label}</div>
      <div style={{ display:"flex", gap:20, marginBottom:14 }}>
        <div>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3, fontFamily:T.fontMono }}>Month</div>
          <div style={{ fontSize:26, fontWeight:700, color, fontFamily:T.fontMono, letterSpacing:"-0.02em" }}>{fmt(mVal)}</div>
        </div>
        <div style={{ width:1, background:T.border }}/>
        <div>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3, fontFamily:T.fontMono }}>LTM</div>
          <div style={{ fontSize:26, fontWeight:700, color:T.textSub, fontFamily:T.fontMono, letterSpacing:"-0.02em" }}>{fmt(ltmVal)}</div>
        </div>
      </div>
      <BarLTMChart data={data} barColor={color} h={100}/>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════
   EXECUTIVE SUMMARY
═══════════════════════════════════════════════════════════════ */
function ExecSummary({ plRows, clientRows, timeRows, staffTimeRows, mapping, referrals, delegateThreshold, agedDebtors, accruedClients, selectedIdx, crmOpps }) {
  const sorted = sortedPlRows(plRows);
  const row = sorted[selectedIdx] ?? sorted[0];
  const idx = selectedIdx;

  const rev    = row.total_revenue, revLTM = ltmSum(plRows, idx, "total_revenue");
  const ebi    = row.ebitda,        ebiLTM = ltmSum(plRows, idx, "ebitda");
  const pbd    = row.profit_before_drawings, pbdLTM = ltmSum(plRows, idx, "profit_before_drawings");

  function baGpChart(rf, cf) {
    // Work in chronological order (oldest first) matching the LTM helpers
    const chrono = [...plRows].reverse();
    const ci = plRows.length - 1 - idx;
    const s  = Math.max(0, ci - 11);
    const bars = chrono.slice(s, ci+1).map(r => ({ period:r.period, short:r.period.split(" ")[0], value:(r[rf]??0)+(r[cf]??0) }));
    const ltms = bars.map((_,i) => {
      const ltmStart = (s+i) - 11;
      if (ltmStart < 0) return null;
      const window = chrono.slice(ltmStart, s+i+1);
      if (window.length < 12) return null;
      if (!window.every(r => (r.total_revenue ?? 0) > 0)) return null;
      return window.reduce((a,r) => a+(r[rf]??0)+(r[cf]??0), 0);
    });
    return bars.map((b,i) => ({ ...b, ltm:ltms[i] }));
  }

  // Client metrics
  const monthClients = clientRows.filter(c => c.period === row.period);
  // Count unique client codes that appear in revenue transactions (incl. accruals, zero-net)
  const billed = monthClients; // all client codes in the period, not just invoiced
  const billedWithRev = monthClients.filter(c => (c.total??0) > 0);
  const top10        = [...billedWithRev].sort((a,b) => (b.total??0)-(a.total??0)).slice(0,10);
  const top10Rev     = top10.reduce((s,c) => s+(c.total??0), 0);
  const pct10m       = rev > 0 ? top10Rev/rev : 0;
  const avgRevM      = billed.length > 0 ? rev/billed.length : 0;

  // LTM client metrics — use chronological slice matching the LTM helpers
  const chronoPL     = [...plRows].reverse();
  const ci           = plRows.length - 1 - idx;
  const ltmS         = Math.max(0, ci - 11);
  const ltmPeriods   = new Set(chronoPL.slice(ltmS, ci+1).map(r => r.period));
  const periodsWithCR = new Set(clientRows.map(c => c.period));
  const ltmCR        = clientRows.filter(c => ltmPeriods.has(c.period) && periodsWithCR.has(c.period));
  const cMap         = {};
  ltmCR.forEach(c => { cMap[c.client] = (cMap[c.client]??0) + (c.total??0); });
  const ltmClients   = Object.keys(cMap); // all codes that appeared in LTM, incl. accruals
  const ltmTop10Rev  = Object.values(cMap).sort((a,b)=>b-a).slice(0,10).reduce((s,v)=>s+v,0);
  const pct10LTM     = revLTM > 0 ? ltmTop10Rev/revLTM : 0;
  const avgRevLTM    = ltmClients.length > 0 ? revLTM/ltmClients.length : 0;

  function qTrend(fn) {
    return chronoPL.slice(ltmS, ci+1).map((r) => {
      // All client codes in period (incl. accruals) for "billed" count;
      // top10 still uses revenue > 0 for concentration metric
      const mc    = clientRows.filter(c => c.period===r.period);
      const mcRev = clientRows.filter(c => c.period===r.period && (c.total??0)>0);
      const t10   = [...mcRev].sort((a,b)=>(b.total??0)-(a.total??0)).slice(0,10).reduce((s,c)=>s+(c.total??0),0);
      return { period:r.period, short:r.period.split(" ")[0], value:fn({r,mc,mcRev,t10}), ltm:0 };
    });
  }
  const pct10Data  = qTrend(({r,t10}) => r.total_revenue>0 ? t10/r.total_revenue : 0);
  const billedData = qTrend(({mc}) => mc.length); // mc = all client codes
  const avgData    = qTrend(({r,mc}) => mc.length>0 ? r.total_revenue/mc.length : 0); // mc = all codes

  // Project margins — map time Contact (full name) to revenue_client (short name) via mapping
  const timeByClient = {};
  const COST_RATES = { 'Steve Tinkler':150, 'Felix Wong':150 };
  const DEFAULT_COST = 45;
  // Only use summary rows for the selected period to avoid double-counting person_client rows
  const periodTimeRows = timeRows.filter(t => t.period === row.period && t.charge_type === 'summary');
  const hasCostData = periodTimeRows.some(t => Number(t.cost||0) > 0);
  periodTimeRows.forEach(t => {
    if (!t.client_name || t.client_name === '__staff_summary__') return;
    const lower = (t.client_name||'').toLowerCase();
    const m = mapping.find(x =>
      (x.time_name||'').toLowerCase() === lower ||
      (x.revenue_client||'').toLowerCase() === lower ||
      lower.includes((x.time_name||'').toLowerCase()) ||
      (x.time_name||'').toLowerCase().includes(lower)
    );
    const cName = m ? m.revenue_client : t.client_name;
    if (!timeByClient[cName]) timeByClient[cName] = { totalCost:0, timeCharge:0 };
    const actualCost = Number(t.cost||0);
    const hrs = Number(t.hours||0);
    const costRate = COST_RATES[t.staff] ?? DEFAULT_COST;
    timeByClient[cName].totalCost  += hasCostData ? actualCost : hrs * costRate;
    timeByClient[cName].timeCharge += Number(t.charge||0);
  });
  // Build margins for all billed clients (not just top10)
  const margins = monthClients
    .filter(c => (c.total??0) > 0)
    .map(c => {
      const td = timeByClient[c.client];
      const revBilled = c.total ?? 0;
      const totalCost = td?.totalCost ?? null;
      const marginAbs = totalCost !== null ? revBilled - totalCost : null;
      const marginPct = totalCost !== null && revBilled !== 0 ? marginAbs / revBilled : null;
      return { client:c.client, revenue:revBilled, totalCost, marginAbs, marginPct };
    })
    .filter(r => r.totalCost !== null)
    .sort((a, b) => (a.marginAbs??0) - (b.marginAbs??0)); // sort ascending by £ value
  const lowMargin = margins.filter(r => r.marginPct !== null && r.marginPct < 0.2 && r.revenue >= 500);

  // Helper: get client metadata from mapping
  const clientMeta = (clientName) => {
    const exact = mapping.find(m => m.revenue_client === clientName);
    if (exact) return exact;
    const lower = (clientName||'').toLowerCase();
    const partial = mapping.find(m =>
      lower.includes((m.revenue_client||'').toLowerCase()) ||
      (m.revenue_client||'').toLowerCase().includes(lower) ||
      lower.includes((m.time_name||'').toLowerCase()) ||
      (m.time_name||'').toLowerCase().includes(lower)
    );
    return partial || {};
  };

  // ★ flag: non-partner billable charge for this client > threshold
  // Uses non_partner_charge field stored directly on each timeRow (from detailed time sheet)
  const isBigNonPartner = (clientName) => {
    const mapped = mapping.find(m => m.revenue_client === clientName);
    const timeName = (mapped?.time_name || "").toLowerCase();
    const clientTimeRow = timeRows.find(t => {
      const tn = (t.client_name || "").toLowerCase();
      return tn === timeName || (timeName && tn === timeName) ||
             (mapped?.revenue_client || "").toLowerCase() === tn;
    });
    if (!clientTimeRow) return false;
    return (clientTimeRow.non_partner_charge ?? 0) > delegateThreshold;
  };

  return (
    <div style={{ padding:"28px", maxWidth:1600, margin:"0 auto" }} className="fi">

      {/* P&L METRICS */}
      <SH label="Financial Performance"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18, marginBottom:28 }}>
        <PLCard label="Revenue"               month={rev} ltm={revLTM} data={chartData(plRows,idx,"total_revenue")}         color={T.accentLt}/>
        <PLCard label="EBITDA"                month={ebi} ltm={ebiLTM} data={chartData(plRows,idx,"ebitda")}                color={T.green}/>
        <PLCard label="Profit Before Drawings" month={pbd} ltm={pbdLTM} data={chartData(plRows,idx,"profit_before_drawings")} color={T.amber}/>
      </div>

      <Div/>
      {/* BUSINESS AREA */}
      <SH label="Performance by Business Area"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:18, marginBottom:28 }}>
        <BABlock label="Strategic Finance" color={T.sf}   revM={row.sf_revenue}    revLTM={ltmSum(plRows,idx,"sf_revenue")}    cosM={row.sf_cos}    cosLTM={ltmSum(plRows,idx,"sf_cos")}    gpData={baGpChart("sf_revenue","sf_cos")}/>
        <BABlock label="Legal"             color={T.legal} revM={row.legal_revenue} revLTM={ltmSum(plRows,idx,"legal_revenue")} cosM={row.legal_cos} cosLTM={ltmSum(plRows,idx,"legal_cos")} gpData={baGpChart("legal_revenue","legal_cos")}/>
        <BABlock label="In-House Finance"  color={T.ihf}  revM={row.ihf_revenue}   revLTM={ltmSum(plRows,idx,"ihf_revenue")}   cosM={row.ihf_cos}   cosLTM={ltmSum(plRows,idx,"ihf_cos")}   gpData={baGpChart("ihf_revenue","ihf_cos")}/>
        <BABlock label="Tax"                color={T.tax}  revM={row.tax_revenue||0} revLTM={ltmSum(plRows,idx,"tax_revenue")}  cosM={row.tax_cos||0} cosLTM={ltmSum(plRows,idx,"tax_cos")}   gpData={baGpChart("tax_revenue","tax_cos")}/>
      </div>

      <Div/>
      {/* QUALITY METRICS */}
      <SH label="Revenue Quality Metrics"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:18, marginBottom:28 }}>
        <QCard label="% Revenue from Top 10 Clients" mVal={pct10m}        ltmVal={pct10LTM}         data={pct10Data}  color={T.accentLt} fmt={fmtPct}/>
        <QCard label="Active Clients (Unique Codes)"                 mVal={billed.length} ltmVal={ltmClients.length} data={billedData} color={T.amber}    fmt={fmtNum}/>
        <QCard label="Avg Revenue per Client"          mVal={avgRevM}       ltmVal={avgRevLTM}         data={avgData}    color={T.green}/>
        <Card style={{padding:'12px 14px'}}>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.12em',textTransform:'uppercase',color:T.muted,fontFamily:T.fontMono,marginBottom:6}}>CRM Pipeline — By Week</div>
          {(() => {
            const now = new Date();
            const mon = new Date(now); mon.setHours(0,0,0,0);
            const d = mon.getDay(); mon.setDate(mon.getDate()-(d===0?6:d-1));
            const cutoff = new Date(mon);
            const BAS = ['Strat Fin','Legal','IHF','Tax'];
            const BC = {'Strat Fin':T.sf,'Legal':T.legal,'IHF':T.ihf,'Tax':T.tax};
            const weeks = Array.from({length:20},(_,i)=>{
              const ms = mon.getTime()-(19-i)*7*86400000;
              const s = new Date(ms); const e = new Date(ms+7*86400000);
              return {label:s.toLocaleDateString('en-GB',{day:'2-digit',month:'short'}),start:s,end:e};
            });
            const cd = weeks.map(w=>{
              const o={label:w.label};
              BAS.forEach(ba=>{
                o[ba]=(crmOpps||[]).filter(x=>x.created_at&&new Date(x.created_at)>=cutoff&&new Date(x.created_at)>=w.start&&new Date(x.created_at)<w.end&&(x.business_areas||[]).includes(ba)).reduce((s,x)=>s+Number(x.value||0),0);
              });
              return o;
            });
            if (!cd.some(w=>BAS.some(b=>w[b]>0))) return <div style={{fontSize:11,color:T.border,fontStyle:'italic',padding:'20px 0',textAlign:'center'}}>No opps logged this week yet</div>;
            return (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={cd} margin={{top:2,right:4,left:-24,bottom:24}}>
                  <XAxis dataKey="label" tick={{fontSize:7,fill:T.muted}} angle={-45} textAnchor="end" axisLine={false} tickLine={false}/>
                  <YAxis tickFormatter={v=>v>=1000?'£'+(v/1000).toFixed(0)+'k':''} tick={{fontSize:7,fill:T.muted}} axisLine={false} tickLine={false}/>
                  <Tooltip formatter={(v,n)=>['£'+Number(v).toLocaleString('en-GB'),n]} contentStyle={{background:T.raised,border:'1px solid '+T.border,borderRadius:6,fontSize:10}}/>
                  {BAS.map(ba=><Bar key={ba} dataKey={ba} stackId="a" fill={BC[ba]} radius={ba==='Tax'?[2,2,0,0]:[0,0,0,0]}/>)}
                </BarChart>
              </ResponsiveContainer>
            );
          })()}
        </Card>
      </div>


      <Div/>
      {/* TOP 10 CLIENTS */}
      <SH label="Top 10 Clients & Project Margin"/>
      <div style={{ display:"grid", gridTemplateColumns:"2.85fr 1fr", gap:18, marginBottom:28 }}>

        {/* Top 10 table — with margin column */}
        <Card style={{ padding:0, overflow:"hidden" }}>
          <div style={{ padding:"13px 16px 11px", borderBottom:`1px solid ${T.border}`, fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:T.muted, fontFamily:T.fontMono }}>
            Revenue — Top 10 Clients (Month)
          </div>
          <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:T.raised }}>
              <TH>Client</TH>
              <TH>Referral</TH>
              <TH right>Strat. Fin.</TH>
              <TH right>Legal</TH>
              <TH right>IHF</TH>
              <TH right>Total</TH>
              <TH right>Margin</TH>
              <TH center>E/CF</TH>
              <TH center>★</TH>
            </tr></thead>
            <tbody>
              {top10.length === 0 ? <EmptyRow cols={9}/> :
                top10.map((c,i) => {
                  const meta = clientMeta(c.client);
                  const bigNP = isBigNonPartner(c.client);
                  const mRow = margins.find(m => m.client === c.client);
                  const mg = mRow?.marginPct ?? null;
                  return (
                    <tr key={i} className="rh">
                      <TD style={{ fontWeight:500 }}>{c.client}</TD>
                      <TD><span style={{ fontSize:11, color:T.mutedLt }}>{meta.referral || <span style={{ color:T.muted, fontStyle:"italic" }}>—</span>}</span></TD>
                      <TD right color={T.sf}>{fmtGBP(Math.round((c.sf??0)/1000)*1000)}</TD>
                      <TD right color={T.legal}>{fmtGBP(Math.round((c.legal??0)/1000)*1000)}</TD>
                      <TD right color={T.ihf}>{fmtGBP(Math.round((c.ihf??0)/1000)*1000)}</TD>
                      <TD right bold>{fmtGBP(Math.round((c.total??0)/1000)*1000)}</TD>
                      <TD right bold color={mg===null?T.muted:mg<0.2?T.red:T.green}>{mg===null?"—":fmtPct(mg)}</TD>
                      <TD center>{meta.equityCont ? <Tick color={T.accent} title="Equity / Cont Fee"/> : <span style={{ color:T.border }}>—</span>}</TD>
                      <TD center>{bigNP ? <Tick color={T.amber} title={`£${(delegateThreshold/1000).toFixed(0)}k+ non-partner billable time`}/> : <span style={{ color:T.border }}>—</span>}</TD>
                    </tr>
                  );
                })}
            </tbody>
          </table></div>
          <div style={{ padding:"8px 14px", borderTop:`1px solid ${T.border}`, display:"flex", gap:16, fontSize:10, color:T.muted, fontFamily:T.fontMono }}>
            <span><span style={{ color:T.green }}>✓</span> Margin = (Revenue − Est. Cost) / Revenue</span>
            <span><span style={{ color:T.accent }}>✓</span> E/CF = Equity / Cont Fee</span>
            <span><span style={{ color:T.amber }}>✓</span> ★ = Non-partner billable time &gt; £{(delegateThreshold/1000).toFixed(0)}k</span>
          </div>
        </Card>

        {/* Low margin */}
        <Card style={{ padding:0, overflow:"hidden" }}>
          <div style={{ padding:"13px 16px 11px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:lowMargin.length>0?T.red:T.green }}/>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:T.muted, fontFamily:T.fontMono }}>Low Margin (&lt;20%)</div>
          </div>
          {lowMargin.length === 0 ? (
            <div style={{ padding:"24px 16px", textAlign:"center" }}>
              <div style={{ fontSize:22, color:T.green }}>✓</div>
              <div style={{ fontSize:12, color:T.green, marginTop:6, fontWeight:600 }}>No low-margin projects</div>
            </div>
          ) : (
            <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ background:T.raised }}>
                <TH>Client</TH><TH right>Revenue</TH><TH right>Margin %</TH>
              </tr></thead>
              <tbody>
                {lowMargin.map((p,i) => (
                  <tr key={i} className="rh">
                    <TD style={{ maxWidth:110, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.client}</TD>
                    <TD right>{fmtGBP(p.revenue)}</TD>
                    <TD right bold color={p.marginPct<0?T.red:T.amber}>{fmtPct(p.marginPct)}</TD>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      </div>

      <Div/>
      {/* REVENUE BY REFERRAL SOURCE */}
      <SH label="Revenue by Referral Source (Month)"/>
      <Card style={{ marginBottom:28 }}>
        <ReferralRevenueTable monthClients={monthClients} mapping={mapping} referrals={referrals}/>
      </Card>

      <Div/>
      {/* BALANCE SHEET */}
      <SH label="Balance Sheet Health"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18, marginBottom:28 }}>
        {[
          { label:"Debts Over 30 Days",  val:row.debts_over_30,      color:T.red,   icon:"⚠" },
          { label:"Accrued Income",       val:row.accrued_income,     color:T.amber, icon:"◎" },
          { label:"Accumulated Profits",  val:row.accumulated_profits,color:T.green, icon:"▲" },
        ].map(({ label, val, color, icon }) => (
          <Card key={label} style={{ borderLeft:`3px solid ${color}` }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:T.muted, marginBottom:12, fontFamily:T.fontMono, display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ color }}>{icon}</span>{label}
            </div>
            <div style={{ fontSize:34, fontWeight:700, color, fontFamily:T.fontMono, letterSpacing:"-0.03em" }}>{fmtGBP(val??0)}</div>
            <div style={{ fontSize:11, color:T.muted, marginTop:6 }}>At end of {row.period}</div>
          </Card>
        ))}
      </div>

      <Div/>
      {/* AGED DEBTORS */}
      <SH label="Debts Over 30 Days — Client Breakdown"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18, marginBottom:28 }}>
        <Card style={{ padding:0, overflow:"hidden" }}>
          <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:T.raised }}>
              <TH>Client</TH><TH right>Amount Over 30 Days</TH>
            </tr></thead>
            <tbody>
              {(agedDebtors||[]).length === 0 ? <EmptyRow cols={2} msg="Upload data pack to populate"/> :
                [...(agedDebtors||[])].filter(d => d.over30 > 0).sort((a,b) => b.over30 - a.over30).map((d,i) => (
                  <tr key={i} className="rh">
                    <TD>{d.client}</TD>
                    <TD right bold color={T.red}>{fmtGBP(d.over30)}</TD>
                  </tr>
                ))}
              {(agedDebtors||[]).length > 0 && (
                <tr style={{ background:T.raised }}>
                  <TD bold color={T.mutedLt}>Total</TD>
                  <TD right bold color={T.red}>{fmtGBP((agedDebtors||[]).reduce((s,d)=>s+d.over30,0))}</TD>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </Card>

        {/* ACCRUED REVENUE */}
        <Card style={{ padding:0, overflow:"hidden" }}>
          <div style={{ padding:"13px 16px 11px", borderBottom:`1px solid ${T.border}`, fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:T.muted, fontFamily:T.fontMono }}>
            Accrued Revenue — {row.period}
          </div>
          <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:T.raised }}>
              <TH>Client</TH><TH right>Accrued Revenue</TH>
            </tr></thead>
            <tbody>
              {(() => {
                const periodAccrued = (accruedClients||[]).filter(a => a.period === row.period);
                if (periodAccrued.length === 0) return <EmptyRow cols={2} msg="Upload data pack to populate"/>;
                const total = periodAccrued.reduce((s,a) => s + (a.amount||0), 0);
                return <>
                  {[...periodAccrued].sort((a,b) => (b.amount||0) - (a.amount||0)).map((a,i) => (
                    <tr key={i} className="rh">
                      <TD>{a.client}</TD>
                      <TD right bold color={T.amber}>{fmtGBP(a.amount||0)}</TD>
                    </tr>
                  ))}
                  <tr style={{ background:T.raised }}>
                    <TD bold color={T.mutedLt}>Total</TD>
                    <TD right bold color={T.amber}>{fmtGBP(total)}</TD>
                  </tr>
                </>;
              })()}
            </tbody>
          </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   BUSINESS AREA DETAIL TAB
═══════════════════════════════════════════════════════════════ */
function BADetailTab({ label, color, rf, cf, baKey, plRows, clientRows, timeRows, staffTimeRows, mapping, referrals, delegateThreshold, serviceRevRows, employees, selectedIdx }) {
  const sortedRows = sortedPlRows(plRows);
  const row    = sortedRows[selectedIdx] ?? sortedRows[0];
  const idx    = selectedIdx;
  const rev    = row[rf]??0, cos = row[cf]??0, gp = Math.round((rev+cos)/1000)*1000;
  const revLTM = ltmSum(plRows,idx,rf), cosLTM = ltmSum(plRows,idx,cf), gpLTM = Math.round((revLTM+cosLTM)/1000)*1000;

  // Work in chronological order to correctly compute GP trend + trailing LTM
  const chronoPLba = [...plRows].reverse();
  const ciba       = plRows.length - 1 - idx;
  const sba        = Math.max(0, ciba - 11);
  const gpBars     = chronoPLba.slice(sba, ciba+1).map(r => ({ period:r.period, short:r.period.split(" ")[0], value:(r[rf]??0)+(r[cf]??0) }));
  const gpData     = gpBars.map((_, i) => {
    const ltmStart = Math.max(0, (sba+i) - 11);
    const ltm = chronoPLba.slice(ltmStart, sba+i+1).reduce((a,r) => a+(r[rf]??0)+(r[cf]??0), 0);
    return { ...gpBars[i], ltm };
  });

  const mc = clientRows.filter(c => c.period===row.period && (c[baKey]??0)>0).sort((a,b)=>(b[baKey]??0)-(a[baKey]??0));
  const fmtK = v => v === 0 ? '—' : '£' + (Math.abs(v)/1000).toFixed(1) + 'k';

  // Build revenue by person for this BA from timeRows
  // Build per-client per-person charge map from person_client rows
  const baStaffSet = new Set((employees||[]).filter(e => e.ba === label).map(e => e.name.toLowerCase()));
  const personClientRows2 = (timeRows||[]).filter(t =>
    t.period === row.period &&
    t.charge_type === 'person_client' &&
    baStaffSet.has((t.staff||'').toLowerCase())
  );
  const clientPersonMap = {};
  const baPersons = [];
  personClientRows2.forEach(t => {
    const lower = (t.client_name||'').toLowerCase();
    const m = mapping.find(x =>
      (x.time_name||'').toLowerCase() === lower ||
      lower.includes((x.time_name||'').toLowerCase()) ||
      (x.time_name||'').toLowerCase().includes(lower)
    );
    const clientKey = m ? m.revenue_client : t.client_name;
    if (!clientPersonMap[clientKey]) clientPersonMap[clientKey] = {};
    if (!clientPersonMap[clientKey][t.staff]) clientPersonMap[clientKey][t.staff] = 0;
    clientPersonMap[clientKey][t.staff] += t.charge||0;
    if (!baPersons.includes(t.staff)) baPersons.push(t.staff);
  });

  const clientMeta = (clientName) => {
    const exact = mapping.find(m => m.revenue_client === clientName);
    if (exact) return exact;
    const lower = (clientName||'').toLowerCase();
    const partial = mapping.find(m =>
      lower.includes((m.revenue_client||'').toLowerCase()) ||
      (m.revenue_client||'').toLowerCase().includes(lower) ||
      lower.includes((m.time_name||'').toLowerCase()) ||
      (m.time_name||'').toLowerCase().includes(lower)
    );
    return partial || {};
  };

  return (
    <div style={{ padding:"28px", maxWidth:1600, margin:"0 auto" }} className="fi">
      <SH label={`${label} — Monthly Overview`} accent={color}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18, marginBottom:28 }}>
        {[{l:"Revenue",m:rev,ltm:revLTM,c:T.text},{l:"Cost of Sales",m:cos,ltm:cosLTM,c:T.amber},{l:"Gross Profit",m:gp,ltm:gpLTM,c:gp>=0?T.green:T.red}].map(x => (
          <Card key={x.l} style={{ borderTop:`2px solid ${x.c}` }}>
            <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:8, fontFamily:T.fontMono }}>{x.l}</div>
            <div style={{ fontSize:32, fontWeight:700, color:x.c, fontFamily:T.fontMono, letterSpacing:"-0.03em" }}>{fmtGBP(x.m)}</div>
            <div style={{ fontSize:12, color:T.muted, marginTop:5 }}>LTM  {fmtGBP(x.ltm)}</div>
          </Card>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18, marginBottom:28 }}>
        <Card>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10, fontFamily:T.fontMono }}>Monthly Revenue + Trailing LTM</div>
          <BarLTMChart data={chartData(plRows,idx,rf)} barColor={color} h={180} hideLine={true}/>
        </Card>
        <Card>
          <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10, fontFamily:T.fontMono }}>Monthly Gross Profit + Trailing LTM</div>
          <BarLTMChart data={gpData} barColor={T.green} h={180} hideLine={true}/>
        </Card>
      </div>

      <Div/>
      <SH label={`${label} — Revenue by Service (${row.period})`} accent={color}/>
      {(() => {
        // Match period - serviceRevRows.ba is stored as short key (sf/legal/ihf)
        const isThisBa = s => s.ba === baKey || s.ba === (baKey==='sf'?'Strategic Finance':baKey==='legal'?'Legal':baKey==='ihf'?'In-House Finance':'Tax');
        const svcRows = serviceRevRows.filter(s =>
          s.period === row.period && isThisBa(s) &&
          (s.amount||s.revenue||0) > 0
        ).sort((a,b) => (b.amount||b.revenue||0) - (a.amount||a.revenue||0));
        // Build last-12-months grouped chart: one entry per period, one key per service
        const last12periods = (() => { const s = sortedPlRows(plRows); const c = [...s].reverse(); const ci = s.length-1-selectedIdx; return c.slice(Math.max(0,ci-11), ci+1).map(r=>r.period); })();
        // Collect all services for this BA from the full serviceRevRows dataset
        const allBaSvcs = [...new Set(serviceRevRows.filter(isThisBa).map(s => s.service))];
        // Chart data: one object per period with each service as a key
        const svcChartData = last12periods.map(period => {
          const obj = { period, short: period.split(" ")[0] };
          allBaSvcs.forEach(svc => {
            // Must also belong to THIS tab: without the BA check, a service stored under In-House Finance in one
            // month and Tax in another was plotted on both tabs in every month (double counting).
            const r = serviceRevRows.find(s => s.period===period && s.service===svc && isThisBa(s));
            obj[svc] = r ? (r.amount||r.revenue||0) : 0;
          });
          return obj;
        });
        // Assign distinct shades of the BA colour by lightening

        const baColorMap = baKey==="sf"
          ? ["#2563EB","#3B82F6","#60A5FA","#93C5FD","#BFDBFE"]
          : baKey==="legal"
          ? ["#7C3AED","#8B5CF6","#A78BFA","#C4B5FD","#DDD6FE"]
          : ["#059669","#10B981","#34D399","#6EE7B7","#A7F3D0"];
        return (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:18, marginBottom:28 }}>
            {/* Service revenue table for the month */}
            <Card style={{ padding:0, overflow:"hidden" }}>
              <div style={{ padding:"13px 16px 11px", borderBottom:`1px solid ${T.border}`,
                fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase",
                color:T.muted, fontFamily:T.fontMono }}>Revenue by Service — {row.period}</div>
              {svcRows.length === 0 ? (
                <div style={{ padding:"20px 16px", fontSize:12, color:T.muted, fontStyle:"italic" }}>
                  Upload data pack to populate
                </div>
              ) : (
                <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr style={{ background:T.raised }}>
                    <TH>Service</TH><TH right>Revenue</TH><TH right>% of BA</TH>
                  </tr></thead>
                  <tbody>
                    {(() => {
                      const baRevField = baKey==='sf'?'sf_revenue':baKey==='legal'?'legal_revenue':baKey==='ihf'?'ihf_revenue':'tax_revenue';
                      const total = Math.abs(row[baRevField] || svcRows.reduce((s,r) => s + (r.amount||r.revenue||0), 0));
                      return svcRows.map((s,i) => (
                        <tr key={i} className="rh">
                          <TD style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:8, height:8, borderRadius:2, flexShrink:0,
                              background: baColorMap[i % baColorMap.length] }}/>
                            {s.service}
                          </TD>
                          <TD right bold>{fmtGBP(s.amount||s.revenue||0)}</TD>
                          <TD right color={T.mutedLt}>{total>0?fmtPct((s.amount||s.revenue||0)/total):"—"}</TD>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
                </div>
              )}
              {(() => {
                const plTotal = row[baKey==='sf'?'sf_revenue':baKey==='legal'?'legal_revenue':baKey==='ihf'?'ihf_revenue':'tax_revenue'] || 0;
                const svcTotal = serviceRevRows.filter(s => s.period === row.period && isThisBa(s)).reduce((n, s) => n + (s.amount||s.revenue||0), 0);
                if (!svcTotal && !plTotal) return null;
                const diff = svcTotal - plTotal, ok = Math.abs(diff) < 1;
                return (
                  <div style={{ padding:"9px 16px", borderTop:`1px solid ${T.border}`, fontSize:10, fontFamily:T.fontMono, color: ok ? T.green : T.amber }}>
                    {ok ? '✓' : '⚠'} Services {fmtGBP(svcTotal)} · P&L revenue {fmtGBP(plTotal)}
                    {!ok && ` — differs by ${fmtGBP(Math.abs(diff))}. Months uploaded before this fix used the old service assignment: re-upload this month's report to rebuild it.`}
                  </div>
                );
              })()}
            </Card>
            {/* Grouped bar chart — last 12 months by service */}
            <Card>
              <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.1em", textTransform:"uppercase",
                marginBottom:12, fontFamily:T.fontMono }}>Revenue by Service — Last 12 Months</div>
              {allBaSvcs.length === 0 ? (
                <div style={{ fontSize:12, color:T.muted, fontStyle:"italic" }}>Upload data pack to populate</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={svcChartData} margin={{ top:4, right:4, left:-8, bottom:0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke={T.border} vertical={false}/>
                    <XAxis dataKey="short" tick={{ fill:T.muted, fontSize:9, fontFamily:T.fontMono }}
                      axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fill:T.muted, fontSize:9, fontFamily:T.fontMono }}
                      axisLine={false} tickLine={false}
                      tickFormatter={v => v===0?"0":`£${Math.round(Math.abs(v)/1000)}k`} width={38}/>
                    <Tooltip content={<ChartTip/>}/>
                    <Legend wrapperStyle={{ fontSize:10, fontFamily:T.fontMono, color:T.muted, paddingTop:8 }}/>
                    {allBaSvcs.map((svc,i) => (
                      <Bar key={svc} dataKey={svc} name={svc}
                        fill={baColorMap[i % baColorMap.length]} fillOpacity={0.85}
                        radius={[2,2,0,0]} maxBarSize={20} stackId="a"/>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
        );
      })()}

      <Div/>
      <SH label={`${label} — Client Revenue (${row.period})`} accent={color}/>
      <Card style={{ padding:0, overflow:"hidden", marginBottom:28 }}>
        <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr style={{ background:T.raised }}>
            <TH>#</TH><TH>Client</TH><TH>Referral / Orig. BA</TH>
            {baPersons.map(p => <TH key={p} right style={{fontSize:9,color:T.mutedLt}}>{p.split(' ')[0]}</TH>)}
            {baPersons.length > 0 && <TH right style={{fontSize:9,color:T.mutedLt}}>Unalloc.</TH>}
            <TH right>{label} Revenue</TH>
          </tr></thead>
          <tbody>
            {mc.length===0 ? <EmptyRow cols={7}/> :
              mc.map((c,i) => {
                const meta  = clientMeta(c.client);
                return (
                  <tr key={i} className="rh">
                    <TD color={T.muted}>{i+1}</TD>
                    <TD bold>{c.client}</TD>
                    <TD><span style={{ fontSize:11, color:T.mutedLt }}>
                      {(() => {
                        // If origBA ≠ this BA → show origBA, otherwise show referral source
                        if (meta.origBA && meta.origBA !== label) return <span style={{color:T.legal}}>{meta.origBA}</span>;
                        return meta.referral || <span style={{fontStyle:"italic",color:T.muted}}>—</span>;
                      })()}
                    </span></TD>
                    {baPersons.map(p => (
                      <TD key={p} right color={T.mutedLt} style={{fontSize:11}}>
                        {clientPersonMap[c.client]?.[p] > 0 ? fmtK(clientPersonMap[c.client][p]) : '—'}
                      </TD>
                    ))}
                    {baPersons.length > 0 && (() => {
                      const allocated = baPersons.reduce((s,p) => s + (clientPersonMap[c.client]?.[p]||0), 0);
                      const unalloc = (c[baKey]??0) - allocated;
                      const absU = Math.abs(unalloc);
                      if (absU < 1) return <TD right color={T.muted} style={{fontSize:11}}>—</TD>;
                      const uStr = unalloc < 0 ? `(${fmtK(absU)})` : fmtK(unalloc);
                      return <TD right color={unalloc < 0 ? T.red : T.amber} style={{fontSize:11}}>{uStr}</TD>;
                    })()}
                    <TD right bold color={color}>{fmtK(c[baKey]??0)}</TD>
                  </tr>
                );
              })}
          </tbody>
        </table>
        </div>
        <div style={{ padding:"8px 14px", borderTop:`1px solid ${T.border}`, display:"flex", gap:16, fontSize:10, color:T.muted, fontFamily:T.fontMono }}>
          <span style={{color:T.mutedLt}}>Referral / Orig. BA: shows originating BA if cross-referred, otherwise referral source</span>
        </div>
      </Card>

      <Div/>
      <SH label={`${label} — Revenue by Referral Source`} accent={color}/>
      <Card style={{ marginBottom:28 }}>
        <ReferralRevenueTable monthClients={mc} mapping={mapping} referrals={referrals} baKey={baKey} currentBA={label}/>
      </Card>

      <Div/>
      <SH label={`${label} — Billable Time by Person (${row.period})`} accent={color}/>
      <Card style={{ padding:0, overflow:"hidden" }}>
        {(() => {
          // Show staff assigned to this BA who have time in this period
          // Use employees state (not the ba field on staffTimeRows) so that
          // BA reassignments in Standing Data take effect immediately
          const baStaff = (employees || []).filter(e => e.ba === label);
          const baStaffNames = new Set(baStaff.map(e => e.name.toLowerCase()));
          const allStaffRows = staffTimeRows.filter(s => s.period === row.period);
          const periodStaffTime = allStaffRows
            .filter(s => baStaffNames.has(s.staff.toLowerCase()))
            .sort((a,b) => b.charge - a.charge);
          return periodStaffTime.length === 0 ? (
            <div style={{ padding:"20px 18px", fontSize:12, color:T.muted, fontStyle:"italic" }}>
              No billable time recorded — upload data pack or assign staff in Standing Data
            </div>
          ) : (
            <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ background:T.raised }}>
                <TH>Name</TH><TH right>Billable Hours</TH><TH right>Billable Revenue</TH>
              </tr></thead>
              <tbody>
                {periodStaffTime.map((s,i) => {
                  return (
                    <tr key={i} className="rh">
                      <TD bold>{s.staff}</TD>
                      <TD right>{s.hours.toFixed(1)}h</TD>
                      <TD right>{fmtGBP(s.charge)}</TD>
                    </tr>
                  );
                })}
                {(() => {
                  const tot = periodStaffTime.reduce((a,s) => ({charge:a.charge+s.charge, hours:a.hours+s.hours}), {charge:0,hours:0});
                  return (
                    <tr style={{ background:T.raised }}>
                      <TD bold color={T.mutedLt}>Total</TD>
                      <TD right bold>{tot.hours.toFixed(1)}h</TD>
                      <TD right bold>{fmtGBP(tot.charge)}</TD>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
            </div>
          );
        })()}
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STANDING DATA TAB
═══════════════════════════════════════════════════════════════ */
function AddEmployeeForm({ employees, setEmployees }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Associate");
  const [ba, setBa] = useState("Strategic Finance");
  const add = () => {
    if (!name.trim()) return;
    setEmployees([...(employees||[]), { name:name.trim(), email:email.trim(), role, ba }]);
    setName(""); setEmail(""); setRole("Associate"); setBa("Strategic Finance");
  };
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 0.8fr 1fr auto", gap:10, alignItems:"flex-end", marginTop:14 }}>
      <div><div style={FL}>Full Name</div><Inp value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Jane Smith"/></div>
      <div><div style={FL}>Email</div><Inp value={email} onChange={e=>setEmail(e.target.value)} placeholder="jane@firm.com"/></div>
      <div><div style={FL}>Role</div>
        <Sel value={role} onChange={e=>setRole(e.target.value)} style={{width:"100%"}}>
          <option>Partner</option><option>Associate</option><option>Consultant</option>
        </Sel>
      </div>
      <div><div style={FL}>Business Area</div>
        <Sel value={ba} onChange={e=>setBa(e.target.value)} style={{width:"100%"}}>
          {BA_OPTIONS.map(b=><option key={b} value={b}>{b}</option>)}
        </Sel>
      </div>
      <Btn onClick={add}>Add</Btn>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// CRM TAB
// ─────────────────────────────────────────────────────────────────────────────


const CrmField = ({label, children}) => (
  <div style={{marginBottom:12}}>
    <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:T.muted,fontFamily:T.fontMono,
      textTransform:"uppercase",marginBottom:4}}>{label}</div>
    {children}
  </div>
);

const BLANK_OPP = {
  id:null, client_name:"", project:"", owner:"Steve", status:"Early Discussions",
  date_initiated:new Date().toISOString().slice(0,10),
  business_areas:[], priority:"High", next_steps:"",
  last_contact:new Date().toISOString().slice(0,10),
  value:null, is_prospect:false, el_signed:false, ye_date:'', priority_date:'', discuss_team:false, created_at:new Date().toISOString(),
};
const CRM_OWNERS    = ["Steve","Felix","Neil","Victoria","Dan","Carter"];
const CRM_STATUSES  = ["Early Discussions","Quoting","Verbally Approved","Won","Lost","On Hold","N/A"];
const CRM_BAS       = ["Strat Fin","Legal","IHF","Tax","Steve/Felix"];
const CRM_PRIORITIES= ["High","Medium","Low"]; // v3.2

// Separate component so typing doesn't re-render the whole table

/* ═══════════════════════════════════════════════════════════════
   OBJECTIVES TAB
═══════════════════════════════════════════════════════════════ */
const SEED_OBJECTIVES = [
  { owner:"Both", theme:"Bandwidth & Billable Time",  text:"Reduce billable time — we need space to move the business forward" },
  { owner:"FW",   theme:"Bandwidth & Billable Time",  text:"Still going wide — need more bandwidth for life and non-billable work" },
  { owner:"Both", theme:"Bandwidth & Billable Time",  text:"Set reducing revenue targets for ourselves year on year" },
  { owner:"Both", theme:"Client Focus & Quality",     text:"Prioritise clients — focus on where there's real value and opportunity for the wider group" },
  { owner:"ST",   theme:"Client Focus & Quality",     text:"Nail the clients you work on — we can't have a drop in quality, too damaging for reputation" },
  { owner:"Both", theme:"Client Focus & Quality",     text:"Identify who are the next Secret Mode — target large strategic clients" },
  { owner:"FW",   theme:"Growth & Marketing",         text:"Hire a third lawyer" },
  { owner:"FW",   theme:"Growth & Marketing",         text:"Execute broader marketing around the triple lock proposition" },
  { owner:"ST",   theme:"Growth & Marketing",         text:"Drive the business forward — focus on tax clients and supporting the team" },
  { owner:"ST",   theme:"Growth & Marketing",         text:"Ensure IHF is set up to scale" },
  { owner:"Both", theme:"Team & People",              text:"Carefully manage the team's personal development" },
  { owner:"Both", theme:"Long-term Target",           text:"By March 2028 — a sustainable business not reliant on our time. No working evenings." },
  { owner:"Both", theme:"Long-term Target",           text:"Implement a quarterly dinner to revisit these targets and genuinely hold ourselves to account" },
];

const THEME_COLORS = {
  "Bandwidth & Billable Time": "#f97316",
  "Client Focus & Quality":    "#3b82f6",
  "Growth & Marketing":        "#10b981",
  "Team & People":             "#a855f7",
  "Long-term Target":          "#eab308",
};

function ObjectivesTab() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(null);

  useEffect(() => {
    supabase.from("objectives").select("*").order("sort_order").then(({ data }) => {
      if (data?.length) setRows(data);
      else {
        // Seed initial objectives
        supabase.from("objectives").insert(
          SEED_OBJECTIVES.map((o,i) => ({...o, status:"", sort_order:i}))
        ).select().then(({ data:ins }) => { if (ins) setRows(ins); });
      }
      setLoading(false);
    });
  }, []);

  const updateStatus = async (id, status) => {
    setSaving(id);
    setRows(prev => prev.map(r => r.id===id ? {...r,status} : r));
    await supabase.from("objectives").update({status}).eq("id", id);
    setSaving(null);
  };

  const ownerColor = o => o==="ST"?T.blue:o==="FW"?"#10b981":T.amber;

  // Group by theme
  const themes = [...new Set(rows.map(r=>r.theme))];

  return (
    <div style={{padding:"28px 24px",maxWidth:1100,margin:"0 auto"}}>
      <SH label="Partner Objectives — Confidential"/>
      <p style={{fontSize:12,color:T.muted,marginBottom:24,fontStyle:"italic"}}>
        Steve & Felix only · Reviewed quarterly
      </p>

      {loading ? <div style={{color:T.muted,fontSize:13}}>Loading...</div> : <>
        {themes.map(theme => (
          <div key={theme} style={{marginBottom:24}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <div style={{width:4,height:18,borderRadius:2,background:THEME_COLORS[theme]||T.muted}}/>
              <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",
                color:THEME_COLORS[theme]||T.muted,fontFamily:T.fontMono}}>{theme}</span>
            </div>
            <div style={{borderRadius:8,border:`1px solid ${T.border}`,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{background:T.raised}}>
                    <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:T.muted,fontFamily:T.fontMono,textTransform:"uppercase",width:70,textAlign:"center"}}>OWNER</th>
                    <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:T.muted,fontFamily:T.fontMono,textTransform:"uppercase",textAlign:"left"}}>OBJECTIVE</th>
                    <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:T.muted,fontFamily:T.fontMono,textTransform:"uppercase",width:280,textAlign:"left"}}>STATUS / NOTES</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.filter(r=>r.theme===theme).map((r,i)=>(
                    <tr key={r.id} style={{borderTop:i===0?"none":`1px solid ${T.border}`,background:i%2===0?T.card:T.bg}}>
                      <td style={{padding:"10px 12px",textAlign:"center"}}>
                        <span style={{fontSize:11,fontWeight:700,color:ownerColor(r.owner),
                          background:ownerColor(r.owner)+"22",padding:"3px 8px",borderRadius:4,
                          fontFamily:T.fontMono}}>{r.owner}</span>
                      </td>
                      <td style={{padding:"10px 12px",fontSize:13,color:T.text,lineHeight:1.5}}>{r.text}</td>
                      <td style={{padding:"8px 12px"}}>
                        <textarea value={r.status||""} onChange={e=>setRows(prev=>prev.map(x=>x.id===r.id?{...x,status:e.target.value}:x))}
                          onBlur={e=>updateStatus(r.id,e.target.value)}
                          placeholder="Add status or notes..."
                          rows={2}
                          style={{width:"100%",background:saving===r.id?T.raised:T.bg,
                            border:`1px solid ${saving===r.id?T.blue:T.border}`,borderRadius:5,
                            padding:"6px 8px",color:T.text,fontSize:12,fontFamily:T.fontSans,
                            resize:"none",boxSizing:"border-box",transition:"border 0.2s"}}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        <div style={{marginTop:24,padding:"14px 20px",background:T.raised,borderRadius:8,
          border:`1px solid ${T.border}`,textAlign:"center"}}>
          <div style={{fontSize:11,color:T.muted,fontFamily:T.fontMono,letterSpacing:"0.1em",marginBottom:3}}>NEXT REVIEW</div>
          <div style={{fontSize:13,fontWeight:700,color:T.amber}}>Quarterly — bring food, be honest</div>
        </div>
      </>}
    </div>
  );
}

function CRMPanel({ initial, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (field, val) => setForm(f => ({...f, [field]:val}));
  const fmtVal = v => v ? "£"+Number(v).toLocaleString("en-GB") : null;
  const iStyle = {width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:6,
    padding:"10px 12px",color:T.text,fontSize:14,fontFamily:T.fontSans,boxSizing:"border-box"};
  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };
  return (
    <div style={{position:"fixed",inset:0,zIndex:999,display:"flex"}}>
      <div style={{flex:1,background:"rgba(0,0,0,0.6)"}} onClick={onClose}/>
      <div style={{width:"min(440px,100vw)",background:"#0f1117",borderLeft:`1px solid ${T.border}`,
        boxShadow:"-8px 0 32px rgba(0,0,0,0.6)",overflowY:"auto",padding:"20px 16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontSize:13,fontWeight:700,color:T.text,fontFamily:T.fontMono,letterSpacing:"0.08em"}}>
            {form.id?"EDIT OPPORTUNITY":"NEW OPPORTUNITY"}
          </span>
          <div style={{display:"flex",gap:8}}>
            {form.id && <Btn variant="secondary" onClick={()=>onDelete(form.id)} style={{color:T.red,fontSize:12}}>Delete</Btn>}
            <Btn variant="secondary" onClick={onClose} style={{fontSize:12}}>Cancel</Btn>
          </div>
        </div>
        <CrmField label="Client Name"><input value={form.client_name} onChange={e=>set("client_name",e.target.value)} placeholder="e.g. Radical Forge" style={iStyle}/></CrmField>
        <CrmField label="Project"><input value={form.project||""} onChange={e=>set("project",e.target.value)} placeholder="e.g. Series A Fundraise" style={iStyle}/></CrmField>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <CrmField label="Owner"><select value={form.owner} onChange={e=>set("owner",e.target.value)} style={iStyle}>{CRM_OWNERS.map(o=><option key={o}>{o}</option>)}</select></CrmField>
          <CrmField label="Priority"><select value={form.priority} onChange={e=>set("priority",e.target.value)} style={iStyle}>{CRM_PRIORITIES.map(o=><option key={o}>{o}</option>)}</select></CrmField>
        </div>
        <CrmField label="Status"><select value={form.status} onChange={e=>set("status",e.target.value)} style={iStyle}>{CRM_STATUSES.map(o=><option key={o}>{o}</option>)}</select></CrmField>
        <CrmField label="Business Areas">
          <div style={{display:"flex",gap:8,flexWrap:"wrap",padding:"4px 0"}}>
            {CRM_BAS.map(ba=>{
              const active=(form.business_areas||[]).includes(ba);
              return <span key={ba} onClick={()=>set("business_areas",active?(form.business_areas||[]).filter(x=>x!==ba):[...(form.business_areas||[]),ba])}
                style={{cursor:"pointer",fontSize:13,fontWeight:active?700:400,color:active?T.blue:T.muted,
                  background:active?T.blue+"22":T.raised,padding:"6px 12px",borderRadius:6,
                  border:`1px solid ${active?T.blue:T.border}`,userSelect:"none",WebkitUserSelect:"none"}}>{ba}</span>;
            })}
          </div>
        </CrmField>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <CrmField label="EL Signed">
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0"}}>
              <input type="checkbox" checked={form.el_signed||false} onChange={e=>set("el_signed",e.target.checked)} style={{width:20,height:20,cursor:"pointer",accentColor:T.blue}}/>
              <span style={{fontSize:13,color:form.el_signed?T.green:T.muted}}>{form.el_signed?"Signed ✓":"Not yet"}</span>
            </div>
          </CrmField>
          <CrmField label="Discuss w/ Team">
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0"}}>
              <input type="checkbox" checked={form.discuss_team||false} onChange={e=>set("discuss_team",e.target.checked)} style={{width:20,height:20,cursor:"pointer",accentColor:T.amber}}/>
              <span style={{fontSize:13,color:form.discuss_team?T.amber:T.muted}}>{form.discuss_team?"Yes 🗣":"No"}</span>
            </div>
          </CrmField>
        </div>
        <CrmField label="Y/E Date (MMM-YY e.g. Mar-26)"><input value={form.ye_date||""} onChange={e=>set("ye_date",e.target.value)} placeholder="e.g. Mar-26" style={iStyle}/></CrmField>
        <CrmField label="Value (£)">
          <input value={form.value||""} onChange={e=>set("value",e.target.value.replace(/[^0-9]/g,""))} placeholder="e.g. 25000" style={iStyle}/>
          {form.value && <div style={{fontSize:12,color:T.green,marginTop:4}}>{fmtVal(form.value)}</div>}
        </CrmField>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <CrmField label="Date Initiated"><input value={form.date_initiated||""} onChange={e=>set("date_initiated",e.target.value)} style={iStyle}/></CrmField>
          <CrmField label="Last Contact"><input value={form.last_contact||""} onChange={e=>set("last_contact",e.target.value)} style={iStyle}/></CrmField>
        </div>
        <CrmField label="Next Steps">
          <textarea value={form.next_steps||""} onChange={e=>set("next_steps",e.target.value)} placeholder="What needs to happen next..." style={{...iStyle,minHeight:100,resize:"vertical"}}/>
        </CrmField>
        <CrmField label="Priority Date">
          <input type="date" value={form.priority_date||""} onChange={e=>set("priority_date",e.target.value||null)} style={{...iStyle,colorScheme:"dark"}}/>
        </CrmField>
        <Btn onClick={handleSave} disabled={saving||!form.client_name?.trim()} style={{marginTop:12,width:"100%",justifyContent:"center",padding:14,fontSize:14}}>
          {saving?"Saving...":(form.id?"Save Changes":"Add Opportunity")}
        </Btn>
      </div>
    </div>
  );
}

// Inline editable cell - stays in the row
function CRMCell({ id, field, val, opts, width, onSave, multiline, dateInput }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(val||"");
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const commit = async () => {
    setEditing(false);
    if (editVal !== (val||"")) await onSave(id, field, editVal || null);
  };

  if (editing) {
    if (dateInput) return (
      <input ref={ref} type="date" value={editVal||""} onChange={e=>setEditVal(e.target.value)}
        onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape")setEditing(false);}}
        style={{background:T.raised,border:`1px solid ${T.blue}`,borderRadius:4,padding:"4px 6px",
          color:T.text,fontSize:12,width:width||"auto",maxWidth:"100%",colorScheme:"dark"}}/>
    );
    if (opts) return (
      <select ref={ref} value={editVal} onChange={e=>setEditVal(e.target.value)} onBlur={commit}
        style={{background:T.raised,border:`1px solid ${T.blue}`,borderRadius:4,padding:"4px 6px",
          color:T.text,fontSize:12,width:width||"auto",maxWidth:"100%"}}>
        {opts.map(o=><option key={o}>{o}</option>)}
      </select>
    );
    if (multiline) return (
      <textarea ref={ref} value={editVal} onChange={e=>setEditVal(e.target.value)}
        onBlur={commit} rows={3}
        style={{background:T.raised,border:`1px solid ${T.blue}`,borderRadius:4,padding:"4px 6px",
          color:T.text,fontSize:12,width:"100%",resize:"vertical",fontFamily:T.fontSans}}/>
    );
    return (
      <input ref={ref} value={editVal} onChange={e=>setEditVal(e.target.value)}
        onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape")setEditing(false);}}
        style={{background:T.raised,border:`1px solid ${T.blue}`,borderRadius:4,padding:"4px 6px",
          color:T.text,fontSize:12,width:width||"auto",maxWidth:"100%"}}/>
    );
  }
  const displayVal = dateInput && val
    ? new Date(val).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : val;
  return (
    <div onClick={()=>{setEditVal(val||"");setEditing(true);}}
      style={{cursor:"text",minHeight:22,padding:"2px 4px",borderRadius:3,fontSize:12,color:T.text,
        minWidth:width||60}}
      onMouseEnter={e=>e.currentTarget.style.background=T.raised}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
      {displayVal||<span style={{color:T.border,fontSize:11,fontStyle:"italic"}}>—</span>}
    </div>
  );
}

function CRMTab({ opps, setOpps, canSeePartners }) {
  const statusColor = s => ({"Early Discussions":T.muted,"Quoting":T.blue,"Verbally Approved":T.amber,"Won":T.green,"Lost":T.red,"On Hold":"#a855f7"}[s]||T.muted);
  const priorityColor = p => p==="High"?T.red:p==="Medium"?T.amber:T.green;
  const [filters,    setFilters]  = useState({});
  const [search,     setSearch]   = useState("");
  const [panelOpp,   setPanelOpp] = useState(null);
  const [scanning,   setScanning] = useState(false);
  const [showProspects, setShowProspects] = useState(false);
  const [sortKey,    setSortKey]   = useState("priority");
  const [sortDir,    setSortDir]   = useState("asc");


  // filters is now { owners:Set, statuses:Set, priorities:Set, bas:Set, discuss_team:bool }
  const filtered = useMemo(() => opps.filter(o => {
    if (o.is_prospect) return false;
    if (!canSeePartners && (o.business_areas||[]).includes("Steve/Felix")) return false;
    if (search && !o.client_name?.toLowerCase().includes(search.toLowerCase()) &&
        !o.project?.toLowerCase().includes(search.toLowerCase()) &&
        !o.next_steps?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filters.owners?.size    && !filters.owners.has(o.owner))                                    return false;
    if (filters.statuses?.size  && !filters.statuses.has(o.status))                                 return false;
    if (filters.priorities?.size && !filters.priorities.has(o.priority))                            return false;
    if (filters.bas?.size       && !(o.business_areas||[]).some(b => filters.bas.has(b)))           return false;
    if (filters.discuss_team    && !o.discuss_team)                                                 return false;
    return true;
  }), [opps, search, filters, canSeePartners]);

  const pendingProspects = useMemo(() => opps.filter(o => o.is_prospect), [opps]);

  const sortedFiltered = [...filtered].sort((a,b) => {
    let av=a[sortKey], bv=b[sortKey];
    if (sortKey==="value") { av=Number(av||0); bv=Number(bv||0); }
    else if (sortKey==="priority") { const P={High:0,Medium:1,Low:2}; av=P[av]??9; bv=P[bv]??9; }
    else if (sortKey==="status") { const S={"Early Discussions":0,"Quoting":1,"Verbally Approved":2,"Won":3,"Lost":4}; av=S[av]??9; bv=S[bv]??9; }
    else { av=(av||"").toString().toLowerCase(); bv=(bv||"").toString().toLowerCase(); }
    if (av<bv) return sortDir==="asc"?-1:1;
    if (av>bv) return sortDir==="asc"?1:-1;
    return 0;
  });

  const toggleSort = key => { if (sortKey===key) setSortDir(d=>d==="asc"?"desc":"asc"); else { setSortKey(key); setSortDir("asc"); }};
  const SortIcon = ({col}) => <span style={{marginLeft:4,opacity:sortKey===col?1:0.3,fontSize:10,color:sortKey===col?T.blue:T.muted}}>{sortKey===col&&sortDir==="desc"?"↓":"↑"}</span>;

  // Save a single field inline
  const saveField = async (id, field, val) => {
    setOpps(prev => prev.map(o => o.id===id ? {...o,[field]:val} : o));
    await supabase.from("crm_opportunities").update({[field]:val}).eq("id", id);
  };

  // Toggle boolean fields
  const toggleBool = async (id, field, current) => {
    const val = !current;
    setOpps(prev => prev.map(o => o.id===id ? {...o,[field]:val} : o));
    await supabase.from("crm_opportunities").update({[field]:val}).eq("id", id);
  };

  const handleSave = async (form) => {
    const payload = {client_name:form.client_name,project:form.project||"",owner:form.owner,status:form.status,
      date_initiated:form.date_initiated,business_areas:form.business_areas||[],priority:form.priority,
      next_steps:form.next_steps||"",last_contact:form.last_contact,value:form.value||null,
      is_prospect:false,el_signed:form.el_signed||false,ye_date:form.ye_date||"",priority_date:form.priority_date||null,discuss_team:form.discuss_team||false};
    if (form.id) {
      await supabase.from("crm_opportunities").update(payload).eq("id", form.id);
      setOpps(prev => prev.map(o => o.id===form.id ? {...o,...payload} : o));
    } else {
      const newPayload = {...payload, created_at: new Date().toISOString()};
      const { data } = await supabase.from("crm_opportunities").insert([newPayload]).select();
      if (data?.[0]) setOpps(prev => [data[0],...prev]);
    }
    setPanelOpp(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    await supabase.from("crm_opportunities").delete().eq("id", id);
    setOpps(prev => prev.filter(o => o.id !== id));
    setPanelOpp(null);
  };

  const approveProspect = async (id) => { setOpps(prev=>prev.map(o=>o.id===id?{...o,is_prospect:false}:o)); await supabase.from("crm_opportunities").update({is_prospect:false}).eq("id",id); };
  const dismissProspect = async (id) => { await supabase.from("crm_opportunities").delete().eq("id",id); setOpps(prev=>prev.filter(o=>o.id!==id)); };

  const scanEmails = async () => {
    setScanning(true);
    try {
      const existing = [...new Set(opps.map(o=>o.client_name.toLowerCase()))];
      const res = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:800,system:`You are a CRM assistant for Lvl Up Partners LLP, a games industry advisory firm. Respond ONLY with a valid JSON array, no markdown. Each item: client_name, project, owner (Steve/Felix/Neil/Victoria/Dan/Carter), status ("Early Discussions"), business_areas (array from: Strat Fin,Legal,IHF,Tax,Steve/Felix), priority (High/Medium/Low), next_steps, date_initiated (${new Date().toISOString().slice(0,10)}), last_contact (${new Date().toISOString().slice(0,10)}), value (number or null), is_prospect (true). Exclude: ${JSON.stringify(existing)}. If none, return [].`,messages:[{role:"user",content:"Suggest 2-3 realistic new business opportunities for a games industry advisory firm based on current industry trends."}]})});
      const data = await res.json();
      const parsed = JSON.parse((data.content?.[0]?.text||"[]").replace(/```json|```/g,"").trim());
      if (parsed.length>0) { const {data:ins}=await supabase.from("crm_opportunities").insert(parsed).select(); if(ins?.length){setOpps(prev=>[...ins,...prev]);setShowProspects(true);} alert(`${parsed.length} prospect(s) added`); } else alert("No new opportunities found");
    } catch(e) { alert("Scan failed: "+e.message); }
    setScanning(false);
  };

  return (
    <div style={{padding:"16px 12px",maxWidth:1600,margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <SH label="CRM — Clients & Opportunities"/>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {pendingProspects.length>0 && <Btn variant="secondary" onClick={()=>setShowProspects(v=>!v)} style={{background:"#a855f722",color:"#a855f7",border:"1px solid #a855f7",fontSize:12,padding:"7px 12px"}}>✦ {pendingProspects.length} to Review</Btn>}
          <Btn variant="secondary" onClick={scanEmails} disabled={scanning} style={{fontSize:12,padding:"7px 12px"}}>{scanning?"Scanning...":"✦ AI Scan"}</Btn>
          <Btn onClick={()=>setPanelOpp({...BLANK_OPP})} style={{fontSize:12,padding:"7px 14px"}}>+ New</Btn>
        </div>
      </div>

      {showProspects && pendingProspects.length>0 && (
        <div style={{background:"#a855f711",border:"1px solid #a855f744",borderRadius:8,padding:14,marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:"#a855f7",fontFamily:T.fontMono,letterSpacing:"0.1em",marginBottom:10}}>✦ AI PROSPECTS — REVIEW BEFORE ADDING</div>
          {pendingProspects.map(o=>(
            <div key={o.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:"1px solid #a855f722",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:140}}><div style={{fontWeight:700,color:T.text,fontSize:13}}>{o.client_name}</div><div style={{fontSize:11,color:T.muted}}>{o.project} · {(o.business_areas||[]).join(", ")}</div><div style={{fontSize:11,color:T.muted,marginTop:2}}>{o.next_steps}</div></div>
              <div style={{display:"flex",gap:6}}><Btn onClick={()=>approveProspect(o.id)} style={{fontSize:11,padding:"4px 12px"}}>✓ Add</Btn><Btn variant="secondary" onClick={()=>dismissProspect(o.id)} style={{fontSize:11,padding:"4px 10px",color:T.red}}>✕</Btn></div>
            </div>
          ))}
        </div>
      )}

      <div style={{marginBottom:10}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search client / project / notes..."
          style={{background:T.raised,border:`1px solid ${T.border}`,borderRadius:6,padding:"7px 10px",
            color:T.text,fontSize:12,width:"100%",maxWidth:300,marginBottom:8,boxSizing:"border-box"}}/>
        {[
          {label:"Status",   fkey:"statuses",   opts:CRM_STATUSES,   color:T.blue},
          {label:"Owner",    fkey:"owners",     opts:CRM_OWNERS,     color:T.muted},
          {label:"Priority", fkey:"priorities", opts:CRM_PRIORITIES, color:T.amber},
          {label:"BA",       fkey:"bas",        opts:CRM_BAS,        color:T.blue},
        ].map(({label,fkey,opts,color})=>(
          <div key={fkey} style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:9,fontWeight:700,color:T.muted,fontFamily:T.fontMono,letterSpacing:"0.1em",
              textTransform:"uppercase",minWidth:55}}>{label}</span>
            {opts.map(o=>{
              const active = filters[fkey]?.has(o);
              return <span key={o} onClick={()=>setFilters(f=>{
                  const s = new Set(f[fkey]||[]);
                  active ? s.delete(o) : s.add(o);
                  return {...f,[fkey]:s.size?s:undefined};
                })}
                style={{cursor:"pointer",fontSize:11,padding:"3px 10px",borderRadius:4,
                  border:`1px solid ${active?color:T.border}`,
                  background:active?color+"22":"transparent",
                  color:active?color:T.muted,fontWeight:active?700:400,
                  userSelect:"none",WebkitUserSelect:"none",transition:"all 0.1s"}}>
                {o}
              </span>;
            })}
          </div>
        ))}
        <div style={{display:"flex",gap:8,alignItems:"center",marginTop:4}}>
          <span onClick={()=>setFilters(f=>({...f,discuss_team:!f.discuss_team}))}
            style={{cursor:"pointer",fontSize:11,padding:"3px 10px",borderRadius:4,
              border:`1px solid ${filters.discuss_team?T.amber:T.border}`,
              background:filters.discuss_team?T.amber+"22":"transparent",
              color:filters.discuss_team?T.amber:T.muted,fontWeight:filters.discuss_team?700:400,
              userSelect:"none"}}>
            🗣 Discuss w/ Team
          </span>
          {(Object.values(filters).some(v=>v?.size||v===true)) &&
            <Btn variant="secondary" onClick={()=>setFilters({})} style={{fontSize:11,padding:"3px 10px"}}>Clear all</Btn>}
        </div>
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <span style={{fontSize:11,color:T.muted}}>{sortedFiltered.length} record{sortedFiltered.length!==1?"s":""}</span>
        {sortedFiltered.some(o=>o.value) && <span style={{fontSize:13,fontWeight:700,color:T.green,background:T.green+"18",padding:"6px 14px",borderRadius:6,border:"1px solid "+T.green+"44"}}>Pipeline: £{sortedFiltered.reduce((s,o)=>s+Number(o.value||0),0).toLocaleString("en-GB")}</span>}
      </div>

      <div style={{overflowX:"auto",borderRadius:8,border:`1px solid ${T.border}`}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:900}}>
          <thead>
            <tr style={{background:T.raised}}>
              {[{h:"Client",k:"client_name"},{h:"Project",k:"project"},{h:"Owner",k:"owner"},{h:"Status",k:"status"},{h:"BAs",k:null},{h:"Priority",k:"priority"},{h:"Value",k:"value"},{h:"EL",k:null},{h:"Team",k:null},{h:"Y/E",k:"ye_date"},{h:"Next Steps",k:null},{h:"Priority Date",k:"priority_date"},{h:"",k:null}].map(({h,k},i)=>(
                <th key={i} onClick={k?()=>toggleSort(k):undefined}
                  style={{padding:"9px 10px",fontSize:9,fontWeight:700,letterSpacing:"0.1em",
                    color:sortKey===k?T.blue:T.muted,fontFamily:T.fontMono,textTransform:"uppercase",
                    textAlign:i===0||i===1||i===9?"left":"center",whiteSpace:"nowrap",
                    cursor:k?"pointer":"default",userSelect:"none"}}>
                  {h}{k&&<SortIcon col={k}/>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.length===0 && <tr><td colSpan={11} style={{padding:24,textAlign:"center",color:T.muted,fontSize:13}}>No opportunities — tap + New to add one</td></tr>}
            {sortedFiltered.map((o,i)=>(
              <tr key={o.id||i} style={{borderTop:`1px solid ${T.border}`,background:i%2===0?T.card:T.bg}}>
                <td style={{padding:"6px 10px",fontWeight:700,minWidth:120}}>
                  <CRMCell id={o.id} field="client_name" val={o.client_name} onSave={saveField} width={140}/>
                </td>
                <td style={{padding:"6px 10px",minWidth:120}}>
                  <CRMCell id={o.id} field="project" val={o.project} onSave={saveField} width={130}/>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center"}}>
                  <CRMCell id={o.id} field="owner" val={o.owner} opts={CRM_OWNERS} onSave={saveField} width={90}/>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center"}}>
                  <div onClick={()=>{}} style={{display:"inline-block"}}>
                    <select value={o.status} onChange={async e=>{
                        const v=e.target.value;
                        setOpps(prev=>prev.map(x=>x.id===o.id?{...x,status:v}:x));
                        await supabase.from("crm_opportunities").update({status:v}).eq("id",o.id);
                      }}
                      style={{background:statusColor(o.status)+"22",border:"1px solid "+statusColor(o.status),
                        borderRadius:4,padding:"3px 8px",color:statusColor(o.status),fontSize:11,
                        fontWeight:700,cursor:"pointer",fontFamily:T.fontSans}}>
                      {CRM_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center"}}>
                  <div style={{display:"flex",gap:3,flexWrap:"wrap",justifyContent:"center"}}>
                    {(o.business_areas||[]).map(ba=><span key={ba} style={{fontSize:10,color:T.blue,background:T.blue+"22",padding:"2px 5px",borderRadius:3,whiteSpace:"nowrap"}}>{ba}</span>)}
                  </div>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center"}}>
                  <select value={o.priority} onChange={async e=>{
                      const v=e.target.value;
                      setOpps(prev=>prev.map(x=>x.id===o.id?{...x,priority:v}:x));
                      await supabase.from("crm_opportunities").update({priority:v}).eq("id",o.id);
                    }}
                    style={{background:"transparent",border:"none",color:priorityColor(o.priority),
                      fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:T.fontSans}}>
                    {CRM_PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center",fontWeight:700,color:T.green}}>
                  <CRMCell id={o.id} field="value"
                    val={o.value ? "£"+Number(o.value).toLocaleString("en-GB") : ""}
                    onSave={(id,f,v)=>saveField(id,f,v.replace(/[^0-9]/g,"")||null)}
                    width={90}/>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center",cursor:"pointer"}} onClick={()=>toggleBool(o.id,"el_signed",o.el_signed)}>
                  <span style={{fontSize:18}}>{o.el_signed?"✅":"⬜"}</span>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center",cursor:"pointer"}} onClick={()=>toggleBool(o.id,"discuss_team",o.discuss_team)}>
                  <span style={{fontSize:18}}>{o.discuss_team?"✅":"⬜"}</span>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center"}}>
                  <CRMCell id={o.id} field="ye_date" val={o.ye_date} onSave={saveField} width={70}/>
                </td>
                <td style={{padding:"6px 10px",minWidth:180,maxWidth:300}}>
                  <CRMCell id={o.id} field="next_steps" val={o.next_steps} onSave={saveField} width={250} multiline/>
                </td>
                <td style={{padding:"6px 8px"}}>
                  <CRMCell id={o.id} field="priority_date" val={o.priority_date} onSave={saveField} width={100} dateInput/>
                </td>
                <td style={{padding:"6px 10px",textAlign:"center"}}>
                  <span onClick={()=>setPanelOpp({...o,business_areas:o.business_areas||[]})}
                    style={{cursor:"pointer",fontSize:11,color:T.muted,padding:"3px 8px",borderRadius:4,border:`1px solid ${T.border}`}}
                    onMouseEnter={e=>e.currentTarget.style.color=T.blue}
                    onMouseLeave={e=>e.currentTarget.style.color=T.muted}>⋯</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{fontSize:10,color:T.border,marginTop:6}}>Click any cell to edit · Click ✅/⬜ to toggle · ⋯ to edit all fields</div>
      {panelOpp && <CRMPanel initial={panelOpp} onSave={handleSave} onDelete={handleDelete} onClose={()=>setPanelOpp(null)}/>}
    </div>
  );
}


const BUSINESS_AREA_OPTIONS = ["Strategic Finance", "Legal", "Tax", "In-House Finance", "In-House Finance (Out of Scope)"];
const BA_NOMINAL_DEFAULTS   = { "Strategic Finance":"201","Legal":"202","In-House Finance":"203","Tax":"204","In-House Finance (Out of Scope)":"203" };
const BA_NOMINAL_LABELS     = { "201":"201 – Strat Fin","202":"202 – Legal","203":"203 – IHF","204":"204 – Tax","260":"260 – Contingent" };

function TaskBusinessAreaSection() {
  const [tasks, setTasks] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  async function load() {
    setLoading(true);
    const [{ data: t }, { data: tracking }] = await Promise.all([
      supabase.from("task_business_area_mapping").select("*").order("task_name"),
      supabase.from("xero_tracking_options_cache").select("*").eq("tracking_category_name","Service").order("option_name"),
    ]);
    setTasks(t || []);
    setServiceOptions((tracking || []).map(r => ({ id: r.option_id, name: r.option_name })));
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function updateTask(taskName, patch) {
    setSaving(s => ({ ...s, [taskName]: true }));
    await supabase.from("task_business_area_mapping").update(patch).eq("task_name", taskName);
    setTasks(ts => ts.map(t => t.task_name === taskName ? { ...t, ...patch } : t));
    setSaving(s => ({ ...s, [taskName]: false }));
  }

  async function assignBA(taskName, ba) {
    await updateTask(taskName, { business_area: ba, nominal_code: BA_NOMINAL_DEFAULTS[ba] || null });
  }

  const unmapped = tasks.filter(t => !t.business_area);
  const mapped = tasks.filter(t => t.business_area);
  const sel = { background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: "5px 8px", fontSize: 11, fontFamily: T.fontSans };

  return (
    <>
      <SH label="Task → Business Area Mapping" accent={unmapped.length > 0 ? T.red : T.accent}/>
      {serviceOptions.length === 0 && (
        <div style={{ fontSize: 11, color: T.amber, fontFamily: T.fontMono, marginBottom: 8 }}>
          ⚠ No Service tracking options — click "Refresh Tracking" in the Billing tab first
        </div>
      )}
      <Card style={{ marginBottom: 28, padding: 0, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 16, color: T.muted, fontSize: 12 }}>Loading...</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead><tr style={{ background: T.raised }}>
                <TH>Task Name</TH>
                <TH>Business Area</TH>
                <TH>Nominal Code</TH>
                <TH>Service Line</TH>
                <TH center>Billing Type</TH>
              </tr></thead>
              <tbody>
                {unmapped.map(t => (
                  <tr key={t.task_name} style={{ background: `${T.red}08` }}>
                    <TD color={T.red} style={{ fontSize: 11 }}>{t.task_name}</TD>
                    <TD>
                      <select value="" disabled={saving[t.task_name]} onChange={e => assignBA(t.task_name, e.target.value)} style={sel}>
                        <option value="" disabled>Assign BA...</option>
                        {BUSINESS_AREA_OPTIONS.map(ba => <option key={ba} value={ba}>{ba}</option>)}
                      </select>
                    </TD>
                    <TD color={T.muted} style={{ fontSize: 10 }}>auto on assign</TD>
                    <TD></TD><TD></TD>
                  </tr>
                ))}
                {mapped.map(t => {
                  const autoCode = BA_NOMINAL_DEFAULTS[t.business_area];
                  const displayCode = t.nominal_code || autoCode || "";
                  return (
                    <tr key={t.task_name} className="rh">
                      <TD style={{ fontSize: 11 }}>{t.task_name}</TD>
                      <TD>
                        <select value={t.business_area || ""} disabled={saving[t.task_name]} onChange={e => assignBA(t.task_name, e.target.value)} style={sel}>
                          {BUSINESS_AREA_OPTIONS.map(ba => <option key={ba} value={ba}>{ba}</option>)}
                        </select>
                      </TD>
                      {/* Auto-suggested from BA but editable for edge cases */}
                      <TD>
                        <select value={displayCode} disabled={saving[t.task_name]}
                          onChange={e => updateTask(t.task_name, { nominal_code: e.target.value })}
                          style={{ ...sel, width: 130 }}>
                          <option value="">—</option>
                          <option value="201">201 – Strat Fin</option>
                          <option value="202">202 – Legal</option>
                          <option value="203">203 – IHF</option>
                          <option value="204">204 – Tax</option>
                          <option value="260">260 – Contingent</option>
                        </select>
                        {displayCode !== autoCode && autoCode && (
                          <span style={{ fontSize: 9, color: T.muted, display: "block", marginTop: 1 }}
                            title={`Auto from BA: ${BA_NOMINAL_LABELS[autoCode]}`}>
                            overridden (auto: {autoCode})
                          </span>
                        )}
                      </TD>
                      <TD>
                        {serviceOptions.length > 0 ? (
                          <select value={t.service_line || ""} disabled={saving[t.task_name]}
                            onChange={e => updateTask(t.task_name, { service_line: e.target.value || null })}
                            style={{ ...sel, width: 180 }}>
                            <option value="">-- not set --</option>
                            {serviceOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                          </select>
                        ) : (
                          <span style={{ fontSize: 10, color: T.muted }}>Fetch tracking first</span>
                        )}
                      </TD>
                      <TD center>
                        <button onClick={() => updateTask(t.task_name, { billing_type: t.billing_type === "fixed_fee" ? "time_and_materials" : "fixed_fee" })}
                          style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, border: `1px solid ${T.border}`, cursor: "pointer",
                            background: t.billing_type === "fixed_fee" ? T.accent : T.raised,
                            color: t.billing_type === "fixed_fee" ? "#fff" : T.text }}>
                          {t.billing_type === "fixed_fee" ? "Fixed Fee" : "T&M"}
                        </button>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// Simple fuzzy match: normalize strings and check overlap
function smartMatchDimension(contactName, options) {
  const norm = s => s.toLowerCase()
    .replace(/\b(limited|ltd|llp|llc|inc|plc|gmbh|pvt|co\b|corp|group|games|studio|studios|interactive|digital|entertainment|software)\b\.?/g, "")
    .replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
  const nc = norm(contactName);
  let best = null, bestScore = 0;
  for (const opt of options) {
    const no = norm(opt.name);
    if (!nc || !no) continue;
    // Containment check
    if (nc.includes(no) || no.includes(nc)) {
      const score = Math.min(nc.length, no.length) / Math.max(nc.length, no.length);
      if (score > bestScore) { bestScore = score; best = opt; }
    }
    // Word overlap
    const cw = new Set(nc.split(" ").filter(w => w.length > 2));
    const ow = new Set(no.split(" ").filter(w => w.length > 2));
    const overlap = [...cw].filter(w => ow.has(w)).length;
    if (overlap > 0) {
      const score = (2 * overlap) / (cw.size + ow.size);
      if (score > bestScore) { bestScore = score; best = opt; }
    }
  }
  return bestScore >= 0.25 ? best : null;
}

function ContactBillingConfigSection() {
  const [contacts, setContacts] = useState([]);
  const [vatGroups, setVatGroups] = useState([]);
  const [clientOptions, setClientOptions] = useState([]); // from Xero tracking cache
  const [clientCatId, setClientCatId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [addingNew, setAddingNew] = useState({}); // contact_name -> bool (pushing to Xero)
  const [suggestions, setSuggestions] = useState({}); // contact_name -> suggested option
  const [emails, setEmails] = useState({}); // contact_name -> [{id, email, role}]

  async function load() {
    setLoading(true);
    const [{ data: cfg }, { data: vg }, { data: cached }, { data: tracking }, { data: emailRows }] = await Promise.all([
      supabase.from("contact_billing_config").select("*"),
      supabase.from("vat_groups").select("*"),
      // Paged: a plain read stops at 1,000 rows, so clients whose rows fell beyond that were missing from this list
      fetchAllRows(() => supabase.from("unbilled_time_cache").select("contact_name, month, task_name").order("contact_name").order("month").order("task_name")),
      supabase.from("xero_tracking_options_cache").select("*").eq("tracking_category_name","Client").order("option_name"),
      supabase.from("contact_emails").select("*").eq("active", true).order("contact_name"),
    ]);
    const emailMap = {};
    (emailRows || []).forEach(e => {
      if (!emailMap[e.contact_name]) emailMap[e.contact_name] = [];
      emailMap[e.contact_name].push(e);
    });
    setEmails(emailMap);
    const configMap = {};
    (cfg || []).forEach(c => { configMap[c.contact_name] = c; });
    // Union of contacts with logged time AND contacts with a config row
    // already (e.g. freshly onboarded, no billing activity yet) - previously
    // only cached time contacts showed here, so a new client couldn't have
    // their invoice email etc set up until after their first billing cycle.
    const allNames = [...new Set([
      ...(cached || []).map(r => r.contact_name),
      ...Object.keys(configMap),
    ])].filter(Boolean).sort();
    const merged = allNames.map(name => configMap[name] || {
      contact_name: name, vat_group: null, client_dimension: null, xero_contact_id: null, payment_terms_days: 7,
    });
    const opts = (tracking || []).map(r => ({ id: r.option_id, name: r.option_name, catId: r.tracking_category_id }));
    setContacts(merged);
    setVatGroups(vg || []);
    setClientOptions(opts);
    if (opts.length > 0) setClientCatId(opts[0].catId);
    // Auto-suggest matches for contacts without a dimension set
    const sugg = {};
    for (const c of merged) {
      if (!c.client_dimension) {
        const match = smartMatchDimension(c.contact_name, opts);
        if (match) sugg[c.contact_name] = match;
      }
    }
    setSuggestions(sugg);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save(contactName, patch) {
    setSaving(s => ({ ...s, [contactName]: true }));
    // Keep the client's CURRENT payment terms. This used to write payment_terms_days: 7 on every save, so
    // editing any other field (VAT group, dimension, ...) silently reset that client's terms to 7 days -
    // and invoice due dates read those terms.
    const existing = contacts.find(c => c.contact_name === contactName);
    await supabase.from("contact_billing_config").upsert(
      { contact_name: contactName, payment_terms_days: existing?.payment_terms_days ?? 7, ...patch },
      { onConflict: "contact_name" }
    );
    setContacts(cs => cs.map(c => c.contact_name === contactName ? { ...c, ...patch } : c));
    if (patch.client_dimension) setSuggestions(s => { const n = {...s}; delete n[contactName]; return n; });
    setSaving(s => ({ ...s, [contactName]: false }));
  }

  async function acceptSuggestion(contactName, optionName) {
    await save(contactName, { client_dimension: optionName });
  }

  async function addEmail(contactName, email) {
    const clean = (email || '').trim().toLowerCase();
    if (!clean || !clean.includes('@')) return;
    const { data, error } = await supabase.from('contact_emails')
      .insert({ contact_name: contactName, email: clean, role: 'primary', active: true })
      .select().single();
    if (error) {
      console.error('[addEmail] failed:', error);
      alert(`Could not save email: ${error.message}\n\nIf this mentions a missing column or table, the contact_emails table needs creating in Supabase.`);
      return;
    }
    setEmails(m => ({ ...m, [contactName]: [...(m[contactName] || []), data] }));
  }

  async function removeEmail(contactName, id) {
    await supabase.from('contact_emails').update({ active: false }).eq('id', id);
    setEmails(m => ({ ...m, [contactName]: (m[contactName] || []).filter(e => e.id !== id) }));
  }

  async function addToXero(contactName) {
    if (!clientCatId) { alert("No Client tracking category found in Xero — click Refresh Tracking in Billing tab first"); return; }
    setAddingNew(s => ({ ...s, [contactName]: true }));
    try {
      const res = await fetch("/api/xero/add-tracking-option", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categoryId: clientCatId, categoryName: "Client", optionName: contactName }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { alert(`Failed: ${data.error}`); return; }
      // Add to local options list and save
      const newOpt = { id: data.option?.TrackingOptionID || contactName, name: contactName, catId: clientCatId };
      setClientOptions(o => [...o, newOpt]);
      await save(contactName, { client_dimension: contactName });
    } catch (err) { alert(err.message); }
    setAddingNew(s => ({ ...s, [contactName]: false }));
  }

  const unconfigured = contacts.filter(c => !c.client_dimension);
  const sel = { background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: "5px 8px", fontSize: 11, fontFamily: T.fontSans };
  const inputStyle = { ...sel, outline: "none" };

  return (
    <>
      <SH label="Contact Billing Config" accent={unconfigured.length > 0 ? T.amber : T.accent} action={
        <button onClick={async () => {
          try {
            const res = await fetch('/api/xero/sync-contacts');
            const data = await res.json();
            if (!res.ok) { alert(`Sync failed: ${data.error}`); return; }
            alert(`✓ Matched ${data.matched} contacts from Xero. Unmatched: ${data.unmatched.length > 0 ? data.unmatched.join(', ') : 'none'}`);
            await load();
          } catch (err) { alert(err.message); }
        }} style={{ padding: '6px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
          background: T.accent, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>
          Sync IDs from Xero
        </button>
      } />
      {unconfigured.length > 0 && (
        <div style={{ fontSize: 11, color: T.amber, fontFamily: T.fontMono, marginBottom: 8 }}>
          {unconfigured.length} contacts need VAT/dimension config
          {clientOptions.length === 0 ? " — click Refresh Tracking in Billing tab first" : ""}
        </div>
      )}
      <Card style={{ marginBottom: 28, padding: 0, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 16, color: T.muted, fontSize: 12 }}>Loading...</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1050 }}>
              <thead><tr style={{ background: T.raised }}>
                <TH>Contact</TH>
                <TH>Billing Notes</TH>
                <TH>VAT Group</TH>
                <TH>Client Dimension</TH>
                <TH>Xero Contact ID</TH>
                <TH center>Terms (days)</TH>
                <TH>Invoice Emails</TH>
              </tr></thead>
              <tbody>
                {contacts.map(c => {
                  const suggestion = suggestions[c.contact_name];
                  const existsInXero = clientOptions.some(o => o.name === c.contact_name);
                  return (
                    <tr key={c.contact_name} className="rh">
                      <TD style={{ fontSize: 11 }}>{c.contact_name}</TD>
                      <TD>
                        <BillingNotesCell value={c.billing_notes} disabled={saving[c.contact_name]}
                          onSave={v => save(c.contact_name, { billing_notes: v })} />
                      </TD>
                      <TD>
                        <select value={c.vat_group || ""} disabled={saving[c.contact_name]}
                          onChange={e => save(c.contact_name, { vat_group: e.target.value || null })}
                          style={{ ...sel, width: 160 }}>
                          <option value="">-- not set --</option>
                          {vatGroups.map(v => <option key={v.code} value={v.code}>{v.label}</option>)}
                        </select>
                      </TD>
                      <TD>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {clientOptions.length > 0 ? (
                            <select value={c.client_dimension || ""} disabled={saving[c.contact_name]}
                              onChange={e => save(c.contact_name, { client_dimension: e.target.value || null })}
                              style={{ ...sel, width: 140 }}>
                              <option value="">-- not set --</option>
                              {clientOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                            </select>
                          ) : (
                            <input defaultValue={c.client_dimension || ""} placeholder="e.g. Secret Mode"
                              onBlur={e => { if (e.target.value !== (c.client_dimension || "")) save(c.contact_name, { client_dimension: e.target.value || null }); }}
                              style={{ ...inputStyle, width: 140 }} />
                          )}
                          {/* Suggest button */}
                          {!c.client_dimension && suggestion && (
                            <button onClick={() => acceptSuggestion(c.contact_name, suggestion.name)}
                              title={`Smart match: ${suggestion.name}`}
                              style={{ fontSize: 9, padding: "2px 7px", background: `${T.accent}22`, color: T.accentLt, border: `1px solid ${T.accent}44`, borderRadius: 3, cursor: "pointer", whiteSpace: "nowrap" }}>
                              ✓ {suggestion.name}?
                            </button>
                          )}
                          {/* Add to Xero button for contacts with no matching option */}
                          {!c.client_dimension && !suggestion && clientOptions.length > 0 && !existsInXero && (
                            <button onClick={() => addToXero(c.contact_name)} disabled={addingNew[c.contact_name]}
                              title="Create this contact as a new Client tracking option in Xero"
                              style={{ fontSize: 9, padding: "2px 7px", background: `${T.green}22`, color: T.green, border: `1px solid ${T.green}44`, borderRadius: 3, cursor: "pointer", whiteSpace: "nowrap" }}>
                              {addingNew[c.contact_name] ? "Adding..." : "+ Add to Xero"}
                            </button>
                          )}
                        </div>
                      </TD>
                      <TD>
                        <input defaultValue={c.xero_contact_id || ""} placeholder="GUID from Xero"
                          onBlur={e => { if (e.target.value !== (c.xero_contact_id || "")) save(c.contact_name, { xero_contact_id: e.target.value || null }); }}
                          style={{ ...inputStyle, width: 130, fontFamily: T.fontMono, fontSize: 10 }} />
                      </TD>
                      <TD center>
                        <input type="number" defaultValue={c.payment_terms_days ?? 7} min="0" max="365"
                          onBlur={e => { const v = parseInt(e.target.value); if (!isNaN(v)) save(c.contact_name, { payment_terms_days: v }); }}
                          style={{ ...inputStyle, width: 55, textAlign: "center" }} />
                      </TD>
                      <TD>
                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          {(emails[c.contact_name] || []).map(em => (
                            <span key={em.id} style={{ display: "inline-flex", alignItems: "center", gap: 5,
                              background: T.raised, border: `1px solid ${T.border}`, borderRadius: 3,
                              padding: "2px 6px", fontSize: 10, fontFamily: T.fontMono, color: T.textSub }}>
                              {em.email}
                              <button onClick={() => removeEmail(c.contact_name, em.id)}
                                style={{ background: "none", border: "none", color: T.red, cursor: "pointer", fontSize: 10, padding: 0 }}>×</button>
                            </span>
                          ))}
                          <input placeholder="+ add email, press Enter"
                            onKeyDown={e => {
                              if (e.key === "Enter" && e.target.value.trim()) {
                                addEmail(c.contact_name, e.target.value);
                                e.target.value = "";
                              }
                            }}
                            style={{ ...inputStyle, width: 190, fontSize: 10 }} />
                        </div>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function FixedFeeSection() {
  const [fees, setFees] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [vatGroups, setVatGroups] = useState([]);
  const [saving, setSaving] = useState({});
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ contact_name:'', description:'', monthly_amount:'', service_line:'', vat_group:'20_standard', nominal_code:'203' });

  useEffect(() => {
    Promise.all([
      supabase.from('contact_fixed_fees').select('*').eq('active', true).order('contact_name'),
      fetchAllRows(() => supabase.from('unbilled_time_cache').select('contact_name').order('contact_name')),
      supabase.from('xero_tracking_options_cache').select('*').eq('tracking_category_name','Service').order('option_name'),
      supabase.from('vat_groups').select('*'),
    ]).then(([{ data: f }, { data: c }, { data: s }, { data: v }]) => {
      setFees(f || []);
      setContacts([...new Set((c || []).map(r => r.contact_name))].sort());
      setServiceOptions((s || []).map(r => ({ id: r.option_id, name: r.option_name })));
      setVatGroups(v || []);
    });
  }, []);

  async function addFee() {
    if (!newRow.contact_name || !newRow.description || !newRow.monthly_amount) return;
    setSaving(s => ({ ...s, new: true }));
    const row = { ...newRow, monthly_amount: parseFloat(newRow.monthly_amount), active: true, effective_from: new Date().toISOString().slice(0,10) };
    const { data } = await supabase.from('contact_fixed_fees').insert(row).select().single();
    if (data) setFees(f => [...f, data]);
    setNewRow({ contact_name:'', description:'', monthly_amount:'', service_line:'', vat_group:'20_standard', nominal_code:'203' });
    setAdding(false);
    setSaving(s => ({ ...s, new: false }));
  }

  async function removeFee(id) {
    await supabase.from('contact_fixed_fees').update({ active: false }).eq('id', id);
    setFees(f => f.filter(r => r.id !== id));
  }

  async function updateFee(id, patch) {
    setSaving(s => ({ ...s, [id]: true }));
    await supabase.from('contact_fixed_fees').update(patch).eq('id', id);
    setFees(f => f.map(r => r.id === id ? { ...r, ...patch } : r));
    setSaving(s => ({ ...s, [id]: false }));
  }

  const sel = { background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: "5px 8px", fontSize: 11, fontFamily: T.fontSans };
  const inp = { ...sel, outline: "none" };

  // Group by contact
  const byContact = {};
  fees.forEach(f => { if (!byContact[f.contact_name]) byContact[f.contact_name] = []; byContact[f.contact_name].push(f); });

  return (
    <>
      <SH label="Fixed Fee Schedules (IHF Retainers)" accent={T.ihf}/>
      <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginBottom: 8 }}>
        For In-House Finance clients — fixed monthly amounts that bill regardless of hours logged
      </div>
      <Card style={{ marginBottom: 28, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
            <thead><tr style={{ background: T.raised }}>
              <TH>Contact</TH>
              <TH>Invoice Description</TH>
              <TH right>Monthly Amount</TH>
              <TH>Service Line</TH>
              <TH>Nominal</TH>
              <TH>VAT</TH>
              <TH center>Remove</TH>
            </tr></thead>
            <tbody>
              {Object.entries(byContact).map(([contact, rows]) => (
                rows.map((f, i) => (
                  <tr key={f.id} className="rh">
                    <TD style={{ fontSize: 11 }}>{i === 0 ? contact : ''}</TD>
                    <TD>
                      <input defaultValue={f.description} onBlur={e => { if (e.target.value !== f.description) updateFee(f.id, { description: e.target.value }); }}
                        style={{ ...ONB_INP, width: 220 }} />
                    </TD>
                    <TD right>
                      <input type="number" step="0.01" defaultValue={f.monthly_amount} onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== f.monthly_amount) updateFee(f.id, { monthly_amount: v }); }}
                        style={{ ...ONB_INP, width: 80, textAlign: "right" }} />
                    </TD>
                    <TD>
                      {serviceOptions.length > 0 ? (
                        <select value={f.service_line || ""} onChange={e => updateFee(f.id, { service_line: e.target.value || null })} style={{ ...sel, width: 160 }}>
                          <option value="">—</option>
                          {serviceOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                        </select>
                      ) : (
                        <input defaultValue={f.service_line || ""} onBlur={e => updateFee(f.id, { service_line: e.target.value || null })} style={{ ...ONB_INP, width: 140 }} />
                      )}
                    </TD>
                    <TD>
                      <select value={f.nominal_code || "203"} onChange={e => updateFee(f.id, { nominal_code: e.target.value })} style={{ ...sel, width: 120 }}>
                        <option value="201">201 – Strat Fin</option>
                        <option value="202">202 – Legal</option>
                        <option value="203">203 – IHF</option>
                        <option value="204">204 – Tax</option>
                        <option value="260">260 – Contingent</option>
                      </select>
                    </TD>
                    <TD>
                      <select value={f.vat_group || "20_standard"} onChange={e => updateFee(f.id, { vat_group: e.target.value })} style={{ ...sel, width: 130 }}>
                        {vatGroups.map(v => <option key={v.code} value={v.code}>{v.label}</option>)}
                      </select>
                    </TD>
                    <TD center>
                      <button onClick={() => removeFee(f.id)} style={{ fontSize: 10, color: T.red, background: "none", border: `1px solid ${T.red}44`, borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>✕</button>
                    </TD>
                  </tr>
                ))
              ))}
              {/* Add row */}
              {adding ? (
                <tr style={{ background: `${T.accent}08` }}>
                  <TD>
                    <select value={newRow.contact_name} onChange={e => setNewRow(r => ({ ...r, contact_name: e.target.value }))} style={{ ...sel, width: 160 }}>
                      <option value="">Select contact...</option>
                      {contacts.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </TD>
                  <TD><input value={newRow.description} onChange={e => setNewRow(r => ({ ...r, description: e.target.value }))} placeholder="e.g. In-House Finance Team" style={{ ...ONB_INP, width: 220 }} /></TD>
                  <TD right><input type="number" step="0.01" value={newRow.monthly_amount} onChange={e => setNewRow(r => ({ ...r, monthly_amount: e.target.value }))} placeholder="0.00" style={{ ...ONB_INP, width: 80, textAlign: "right" }} /></TD>
                  <TD>
                    {serviceOptions.length > 0 ? (
                      <select value={newRow.service_line} onChange={e => setNewRow(r => ({ ...r, service_line: e.target.value }))} style={{ ...sel, width: 160 }}>
                        <option value="">—</option>
                        {serviceOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                      </select>
                    ) : (
                      <input value={newRow.service_line} onChange={e => setNewRow(r => ({ ...r, service_line: e.target.value }))} placeholder="Service line" style={{ ...ONB_INP, width: 140 }} />
                    )}
                  </TD>
                  <TD>
                    <select value={newRow.nominal_code} onChange={e => setNewRow(r => ({ ...r, nominal_code: e.target.value }))} style={{ ...sel, width: 120 }}>
                      <option value="201">201 – Strat Fin</option>
                      <option value="202">202 – Legal</option>
                      <option value="203">203 – IHF</option>
                      <option value="204">204 – Tax</option>
                      <option value="260">260 – Contingent</option>
                    </select>
                  </TD>
                  <TD>
                    <select value={newRow.vat_group} onChange={e => setNewRow(r => ({ ...r, vat_group: e.target.value }))} style={{ ...sel, width: 130 }}>
                      {vatGroups.map(v => <option key={v.code} value={v.code}>{v.label}</option>)}
                    </select>
                  </TD>
                  <TD center style={{ display: "flex", gap: 4 }}>
                    <button onClick={addFee} disabled={saving.new} style={{ fontSize: 10, color: "#fff", background: T.green, border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>✓</button>
                    <button onClick={() => setAdding(false)} style={{ fontSize: 10, color: T.muted, background: T.raised, border: `1px solid ${T.border}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>✕</button>
                  </TD>
                </tr>
              ) : (
                <tr>
                  <TD colSpan={7} style={{ padding: "8px 14px" }}>
                    <button onClick={() => setAdding(true)} style={{ fontSize: 11, color: T.accent, background: "none", border: `1px solid ${T.accent}44`, borderRadius: 5, padding: "4px 12px", cursor: "pointer" }}>
                      + Add Fixed Fee Row
                    </button>
                  </TD>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function StandingData({ mapping, setMapping, referrals, setReferrals, serviceMap, setServiceMap, delegateThreshold, setDelegateThreshold, timeRows, employees, setEmployees }) {

  // ── Referral Sources ──────────────────────────────────
  const [newRef, setNewRef] = useState("");
  const addRef = () => {
    if (!newRef.trim()) return;
    const id = Date.now();
    setReferrals([...referrals, { id, name: newRef.trim() }]);
    setNewRef("");
  };
  const rmRef = (id) => setReferrals(referrals.filter(r => r.id !== id));

  // ── Client Mapping ────────────────────────────────────
  const [tn, setTn]   = useState("");
  const [rc, setRc]   = useState("");
  const [nRef, setNRef]   = useState("");
  const [nBA,  setNBA]    = useState("");
  const [nEq,  setNEq]    = useState(false);
  const autoTag = n => n.toUpperCase().replace(/[^A-Z0-9]/g,"_").replace(/_+/g,"_");

  const addMapping = () => {
    if (!rc.trim()) return;
    // Auto-match time_name if not selected: find closest match in timeRows
    const resolvedTn = tn.trim() || (() => {
      const rcl = rc.trim().toLowerCase();
      const match = timeRows.find(t => t.client_name.toLowerCase().includes(rcl) || rcl.includes(t.client_name.toLowerCase()));
      return match?.client_name || rc.trim();
    })();
    const newRow = { time_name:resolvedTn, revenue_client:rc.trim(), tag:autoTag(rc.trim()), referral:nRef, origBA:nBA, equityCont:nEq };
    setMapping([...mapping, newRow]);
    setTn(""); setRc(""); setNRef(""); setNBA(""); setNEq(false);
  };
  const rmMapping = i => setMapping(mapping.filter((_,j)=>j!==i));

  const updateMapping = (i, key, val) => setMapping(mapping.map((m,j) => j===i ? {...m,[key]:val} : m));

  // ── Service Area Map ──────────────────────────────────


  const rmSvc = i => {
    const s = serviceMap[i];
    setServiceMap(prev => prev.filter((_,j)=>j!==i));
    if (s._id) supabase.from("service_map").delete().eq("id", s._id);
    else supabase.from("service_map").delete().eq("service", s.service);
  };
  const updSvc = (i, key, val) => {
    const updated = serviceMap.map((s,j) => j===i ? {...s,[key]:val} : s);
    setServiceMap(updated);
    const s = serviceMap[i];
    if (s._id) supabase.from("service_map").update({[key]:val}).eq("id", s._id);
    else supabase.from("service_map").update({[key]:val}).eq("service", s.service);
  };



  return (
    <div style={{ padding:"28px", maxWidth:1600, margin:"0 auto" }} className="fi">

      {/* ── TASK → BUSINESS AREA MAPPING ── */}
      <TaskBusinessAreaSection/>

      {/* ── CONTACT BILLING CONFIG ── */}
      <ContactBillingConfigSection/>

      {/* ── FIXED FEE SCHEDULES ── */}
      <FixedFeeSection/>

      {/* ── FIRM SETTINGS ── */}
      <SH label="Firm Settings"/>
      <Card style={{ marginBottom:28 }}>
        <div style={{ display:"grid", gridTemplateColumns:"320px 1fr", gap:28, alignItems:"flex-start" }}>
          <div>
            <div style={FL}>Value of Client Delegated Work (£)</div>
            <div style={{ fontSize:11, color:T.muted, marginBottom:10, lineHeight:1.6 }}>
              The ★ flag on the Top 10 Clients table triggers when billable time charged
              by non-partner staff (anyone other than Steve or Felix) exceeds this threshold
              for a client in the month.
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontFamily:T.fontMono, fontSize:13, color:T.mutedLt }}>£</span>
              <input
                type="number"
                min={0}
                step={1000}
                value={delegateThreshold}
                onChange={e => {
                  const v = Math.round(Number(e.target.value) / 1000) * 1000;
                  setDelegateThreshold(isNaN(v) || v < 0 ? 0 : v);
                }}
                style={{ background:T.raised, border:`1px solid ${T.border}`, color:T.text,
                  padding:"7px 11px", borderRadius:6, fontSize:13, fontFamily:T.fontMono,
                  outline:"none", width:140 }}
              />
              <span style={{ fontFamily:T.fontMono, fontSize:12, color:T.muted }}>
                = £{(delegateThreshold/1000).toFixed(0)}k threshold
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* ── REFERRAL SOURCES ── */}
      <SH label="Referral Sources"
        action={
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <Inp value={newRef} onChange={e=>setNewRef(e.target.value)} placeholder="e.g. LinkedIn" style={{ width:200 }}/>
            <Btn onClick={addRef}>+ Add Referral Source</Btn>
          </div>
        }
      />
      <Card style={{ marginBottom:28 }}>
        {referrals.length === 0 ? (
          <div style={{ fontSize:12, color:T.muted, fontStyle:"italic" }}>No referral sources defined yet. Add one above.</div>
        ) : (
          <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
            {referrals.map(r => (
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:8, background:T.raised,
                border:`1px solid ${T.border}`, borderRadius:6, padding:"6px 12px" }}>
                <span style={{ fontSize:12, color:T.text }}>{r.name}</span>
                <button onClick={() => rmRef(r.id)}
                  style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:13, lineHeight:1, padding:0 }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── CLIENT MAPPING ── */}
      <SH label="Client Mapping — Revenue Transactions → Time Report"/>
      <Card style={{ marginBottom:28 }}>
        <p style={{ fontSize:12, color:T.textSub, marginBottom:18, lineHeight:1.7 }}>
          Each row shows a client code from the <strong style={{ color:T.text }}>Revenue Transactions</strong> sheet with its matched name in the <strong style={{ color:T.text }}>Detailed Time Report</strong>. Auto-mapped on upload — use the dropdown to correct any mismatches.
        </p>

        {/* Add row form */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 0.8fr 1fr auto auto", gap:10, alignItems:"flex-end", marginBottom:20 }}>
          <div><div style={FL}>Revenue Client (from transactions)</div><Inp value={rc} onChange={e=>setRc(e.target.value)} placeholder="e.g. Smith & Co"/></div>
          <div>
            <div style={FL}>Time Report Name</div>
            <Sel value={tn} onChange={e=>setTn(e.target.value)} style={{ width:"100%" }}>
              <option value="">— Select or type below —</option>
              {[...new Set([
                ...timeRows.map(t => t.client_name),
                ...mapping.map(m => m.time_name),
              ])].filter(Boolean).sort().map(n => <option key={n} value={n}>{n}</option>)}
            </Sel>
          </div>
          <div>
            <div style={FL}>Referral Source</div>
            <Sel value={nRef} onChange={e=>setNRef(e.target.value)} style={{ width:"100%" }}>
              <option value="">— None —</option>
              {referrals.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
            </Sel>
          </div>
          <div>
            <div style={FL}>Orig. Bus. Area</div>
            <Sel value={nBA} onChange={e=>setNBA(e.target.value)} style={{ width:"100%" }}>
              <option value="">— None —</option>
              {BA_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
            </Sel>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={FL}>E/CF</div>
            <div style={{ paddingTop:6 }}>
              <input type="checkbox" checked={nEq} onChange={e=>setNEq(e.target.checked)}/>
            </div>
          </div>
          <Btn onClick={addMapping} style={{ alignSelf:"flex-end" }}>Add</Btn>
        </div>

        {/* Mapping table */}
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
            <thead><tr style={{ background:T.raised }}>
              <TH>#</TH>
              <TH>Revenue Client (Transactions)</TH>
              <TH>Time Report Name</TH>
              <TH>Referral Source</TH>
              <TH>Orig. Bus. Area</TH>
              <TH center>E/CF</TH>
              <TH></TH>
            </tr></thead>
            <tbody>
              {mapping.length === 0 ? <EmptyRow cols={7} msg="No mappings — upload data pack or add manually"/> :
                mapping.map((m,i) => {
                  // Build the full list of time names for this row's dropdown
                  const allTimeNames = [...new Set([
                    ...timeRows.map(t => t.client_name),
                    ...mapping.map(x => x.time_name),
                  ])].filter(Boolean).sort();
                  return (
                    <tr key={i} className="rh">
                      <TD color={T.muted}>{i+1}</TD>
                      <TD bold>{m.revenue_client}</TD>
                      <TD>
                        <Sel value={m.time_name||""} onChange={e=>updateMapping(i,"time_name",e.target.value)} style={{ width:"100%", fontSize:11, padding:"5px 8px" }}>
                          <option value="">— Unmatched —</option>
                          {allTimeNames.map(n => <option key={n} value={n}>{n}</option>)}
                        </Sel>
                      </TD>
                      <TD>
                        <Sel value={m.referral||""} onChange={e=>updateMapping(i,"referral",e.target.value)} style={{ width:"100%", fontSize:11, padding:"5px 8px" }}>
                          <option value="">— None —</option>
                          {referrals.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                        </Sel>
                      </TD>
                      <TD>
                        <Sel value={m.origBA||""} onChange={e=>updateMapping(i,"origBA",e.target.value)} style={{ width:"100%", fontSize:11, padding:"5px 8px" }}>
                          <option value="">— None —</option>
                          {BA_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                        </Sel>
                      </TD>
                      <TD center>
                        <input type="checkbox" checked={!!m.equityCont}
                          onChange={e=>updateMapping(i,"equityCont",e.target.checked)}/>
                      </TD>
                      <TD>
                        <Btn variant="secondary" onClick={()=>rmMapping(i)} style={{ padding:"4px 10px", fontSize:11, color:T.red }}>Remove</Btn>
                      </TD>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── SERVICE AREA MAP ── */}
      <SH label="Service Area → Business Area Mapping"/>
      <Card style={{ marginBottom:28 }}>
        <p style={{ fontSize:12, color:T.textSub, marginBottom:18, lineHeight:1.7 }}>
          Every unique value from the <strong style={{ color:T.text }}>Service</strong> column in the Revenue Transactions sheet
          is listed below (e.g. <em>Strategic Finance Support</em>, <em>Legal Advice</em>, <em>M&A - Finance</em>).
          Auto-mapped to a business area on upload — use the dropdown to correct any mismatches.
          Revenue allocation to business areas across all reports is driven by this table.
        </p>
        <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr style={{ background:T.raised }}>
            <TH>#</TH><TH>Service (from Revenue Transactions)</TH><TH>Business Area</TH><TH></TH>
          </tr></thead>
          <tbody>
            <tr style={{background:T.raised}}>
              <TD color={T.muted} style={{fontSize:11}} colSpan={2}>
                <div style={{display:"flex",gap:8,alignItems:"center",padding:"4px 0"}}>
                  <input id="svc-add-input" placeholder="Add service manually (e.g. Tax Advisory)"
                    style={{flex:1,background:T.bg,border:'1px solid '+T.border,borderRadius:5,padding:'5px 8px',color:T.text,fontSize:12,fontFamily:T.fontSans}}/>
                  <select id="svc-add-ba" defaultValue="Tax"
                    style={{background:T.bg,border:'1px solid '+T.border,borderRadius:5,padding:'5px 8px',color:T.text,fontSize:12}}>
                    {BA_OPTIONS.map(b=><option key={b}>{b}</option>)}
                  </select>
                  <Btn style={{fontSize:11,padding:'4px 10px'}} onClick={()=>{
                    const inp = document.getElementById('svc-add-input');
                    const sel = document.getElementById('svc-add-ba');
                    const svc = inp?.value?.trim();
                    const ba = sel?.value;
                    if (!svc) return;
                    const exists = serviceMap.find(x=>x.service===svc);
                    if (!exists) {
                      const newRow = {service:svc, ba, _id:null};
                      setServiceMap(prev=>[...prev,newRow]);
                      supabase.from('service_map').insert([{service:svc,ba}]).then(({data})=>{
                        if(data?.[0]) setServiceMap(prev=>prev.map(x=>x.service===svc&&!x._id?{...x,_id:data[0].id}:x));
                      });
                      if(inp) inp.value='';
                    }
                  }}>Add</Btn>
                </div>
              </TD>
              <TD color={T.muted} style={{fontSize:11}}>Use dropdown to set BA</TD>
            </tr>
            {serviceMap.length === 0 ? <EmptyRow cols={4} msg="Upload a data pack — services will be auto-populated from the Revenue Transactions sheet"/> :
              serviceMap.map((s,i) => (
                <tr key={i} className="rh">
                  <TD color={T.muted}>{i+1}</TD>
                  <TD bold>{s.service}</TD>
                  <TD>
                    <Sel value={s.ba} onChange={e=>updSvc(i,"ba",e.target.value)} style={{ width:"100%", fontSize:11, padding:"5px 8px" }}>
                      {BA_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                    </Sel>
                  </TD>
                  <TD>
                    <Btn variant="secondary" onClick={()=>rmSvc(i)} style={{ padding:"4px 10px", fontSize:11, color:T.red }}>Remove</Btn>
                  </TD>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
      </Card>

      {/* ── BA DEFINITIONS ── */}
      <SH label="Business Area Definitions"/>
      <Card style={{ marginBottom:28 }}>
        <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr style={{ background:T.raised }}>
            <TH>Business Area</TH><TH>P&amp;L Revenue Field</TH><TH>P&amp;L COS Field</TH><TH>Client Rev Key</TH><TH>Colour</TH>
          </tr></thead>
          <tbody>
            {[{n:"Strategic Finance",rf:"sf_revenue",cf:"sf_cos",k:"sf",c:T.sf},
              {n:"Legal",rf:"legal_revenue",cf:"legal_cos",k:"legal",c:T.legal},
              {n:"In-House Finance",rf:"ihf_revenue",cf:"ihf_cos",k:"ihf",c:T.ihf},
              {n:"Tax",rf:"tax_revenue",cf:"tax_cos",k:"tax",c:T.tax}].map(x => (
              <tr key={x.n} className="rh">
                <TD bold>{x.n}</TD>
                <TD><Badge label={x.rf} color={x.c}/></TD>
                <TD><Badge label={x.cf} color={x.c}/></TD>
                <TD><Badge label={x.k} color={x.c}/></TD>
                <TD><div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:14, height:14, borderRadius:3, background:x.c }}/>
                  <span style={{ fontFamily:T.fontMono, fontSize:11, color:T.muted }}>{x.c}</span>
                </div></TD>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      {/* ── EXCEL SPEC ── */}
      {/* ── EMPLOYEES ── */}
      <SH label="Employees & Business Area Assignment"/>
      <Card style={{ marginBottom:28 }}>
        <p style={{ fontSize:12, color:T.textSub, marginBottom:18, lineHeight:1.7 }}>
          Assign each employee to a business area. This controls which staff appear in each
          BA tab's billable time table. The ★ delegate flag excludes anyone marked as a Partner.
        </p>
        <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr style={{ background:T.raised }}>
            <TH>#</TH><TH>Name</TH><TH>Email</TH><TH>Role</TH><TH>Business Area</TH><TH></TH>
          </tr></thead>
          <tbody>
            {(employees||[]).map((e,i) => (
              <tr key={i} className="rh">
                <TD color={T.muted}>{i+1}</TD>
                <TD bold>{e.name}</TD>
                <TD color={T.mutedLt}>{e.email}</TD>
                <TD><span style={{ fontSize:11, color:T.muted }}>{e.role}</span></TD>
                <TD>
                  <Sel value={e.ba||""} onChange={ev => { const upd = (employees||[]).map((x,j) => j===i ? {...x,ba:ev.target.value} : x); setEmployees(upd); }} style={{ width:"100%", fontSize:11, padding:"5px 8px" }}>
                    {BA_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                  </Sel>
                </TD>
                <TD>
                  <Btn variant="secondary" onClick={() => setEmployees((employees||[]).filter((_,j)=>j!==i))}
                    style={{ padding:"4px 10px", fontSize:11, color:T.red }}>Remove</Btn>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <AddEmployeeForm employees={employees} setEmployees={setEmployees}/>
      </Card>

      <SH label="Expected Excel Data Pack — Sheet Structure"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:18 }}>
        {[{
          sheet:"Monthly P&L", icon:"📊",
          cols:["Period (e.g. Jan 2024)","sf_revenue","sf_cos","legal_revenue","legal_cos","ihf_revenue","ihf_cos","total_revenue","total_cos","ebitda","profit_before_drawings","debts_over_30","accrued_income","accumulated_profits"],
        },{
          sheet:"Client Revenue", icon:"👥",
          cols:["Period","Client (canonical name)","sf","legal","ihf","total","service (used for BA allocation)"],
        },{
          sheet:"Detailed Time Report", icon:"⏱",
          cols:["Period","client_name (as billed)","chargeable_hours","rate (£/hr)"],
        }].map(s => (
          <Card key={s.sheet}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:T.accentLt, marginBottom:12, fontFamily:T.fontMono }}>{s.icon} {s.sheet}</div>
            {s.cols.map(c => <div key={c} style={{ fontSize:11, color:T.textSub, padding:"4px 0", borderBottom:`1px solid ${T.border}22`, fontFamily:T.fontMono }}>{c}</div>)}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ADMIN TAB
═══════════════════════════════════════════════════════════════ */


const ALL_TABS = [
  {id:"exec",        label:"Exec"},
  {id:"sf",          label:"Strat. Fin."},
  {id:"legal",       label:"Legal"},
  {id:"ihf",         label:"IHF"},
  {id:"tax",         label:"Tax"},
  {id:"objectives",  label:"Objectives"},
  {id:"crm",         label:"CRM"},
  {id:"onboarding",  label:"Onboarding"},
  {id:"billing",     label:"Billing"},
  {id:"utilisation", label:"Weekly Perf"},
  {id:"standing",    label:"Standing"},
  {id:"admin",       label:"Admin"},
];
// Partners always see all tabs - locked on in the matrix (not toggleable)
// Felix is intentionally NOT listed here so he can be used to test
// tab visibility changes before rolling out to the wider team.
const PARTNER_EMAILS = ["steve.tinkler@lvlup.partners"];

function AccessMatrixTab({ employees }) {
  const [access, setAccess] = useState({}); // { email: Set<tabId> }
  const [saving, setSaving] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Load existing tab_access from employees table
  useEffect(() => {
    const init = {};
    employees.forEach(e => {
      if (!e.email) return;
      const allowed = e.tab_access
        ? new Set(e.tab_access)
        : new Set(defaultTabs(e));
      init[e.email.toLowerCase()] = allowed;
    });
    setAccess(init);
    setLoaded(true);
  }, [employees]);

  function defaultTabs(emp) {
    if (PARTNER_EMAILS.includes(emp.email?.toLowerCase())) return ALL_TABS.map(t=>t.id);
    // billing and utilisation (Weekly Perf) are partners-only by default
    if (emp.ba === "Strategic Finance") return ["exec","sf","crm"];
    if (emp.ba === "Legal")             return ["exec","legal","crm"];
    if (emp.ba === "In-House Finance")  return ["exec","ihf","crm"];
    return ["exec"];
  }

  const [crmPartners, setCrmPartners] = useState({});

  useEffect(() => {
    const init = {};
    employees.forEach(e => {
      if (e.email) init[e.email.toLowerCase()] = e.crm_see_partners || false;
    });
    setCrmPartners(init);
  }, [employees]);

  const toggle = async (email, tabId) => {
    const key = email.toLowerCase();
    if (PARTNER_EMAILS.includes(key)) return;
    const cur = new Set(access[key]||[]);
    cur.has(tabId) ? cur.delete(tabId) : cur.add(tabId);
    setAccess(prev => ({...prev, [key]: cur}));
    setSaving(key);
    const arr = [...cur];
    await supabase.from("employees").update({tab_access: arr}).ilike("email", email);
    setSaving(null);
  };

  const toggleCrmPartners = async (email) => {
    const key = email.toLowerCase();
    const val = !crmPartners[key];
    setCrmPartners(prev => ({...prev, [key]: val}));
    await supabase.from("employees").update({crm_see_partners: val}).ilike("email", email);
  };

  const empsWithEmail = employees.filter(e => e.email);

  return (
    <div>
      <SH label="Tab Access Matrix"/>
      <Card style={{padding:0,overflow:"hidden",marginBottom:16}}>
        <div className="tbl-wrap">
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:T.raised}}>
                <TH>Name</TH>
                <TH>Email</TH>
                {ALL_TABS.map(t=><TH key={t.id}>{t.label}</TH>)}
                <TH>Steve/Felix CRM</TH>
              </tr>
            </thead>
            <tbody>
              {!loaded && <EmptyRow cols={ALL_TABS.length+2} msg="Loading..."/>}
              {loaded && empsWithEmail.length===0 && <EmptyRow cols={ALL_TABS.length+2} msg="No employees with email set — add emails in Standing Data"/>}
              {loaded && empsWithEmail.map((emp,i) => {
                const key = emp.email.toLowerCase();
                const isPartner = PARTNER_EMAILS.includes(key);
                const tabs = isPartner ? new Set(ALL_TABS.map(t=>t.id)) : (access[key]||new Set());
                return (
                  <tr key={i} className="rh" style={{opacity:saving===key?0.6:1}}>
                    <TD bold>{emp.name}</TD>
                    <TD color={T.muted} style={{fontSize:11}}>{emp.email}</TD>
                    {ALL_TABS.map(t => (
                      <TD key={t.id}>
                        {isPartner
                          ? <span style={{color:T.green,fontSize:16}}>●</span>
                          : <span onClick={()=>toggle(emp.email,t.id)}
                              style={{cursor:"pointer",color:tabs.has(t.id)?T.green:T.border,fontSize:18,
                                display:"block",textAlign:"center",lineHeight:1}}>
                              {tabs.has(t.id)?"✅":"⬜"}
                            </span>}
                      </TD>
                    ))}
                    <TD>
                      {isPartner
                        ? <span style={{color:T.green,fontSize:16}}>●</span>
                        : <span onClick={()=>toggleCrmPartners(emp.email)}
                            style={{cursor:"pointer",fontSize:18,display:"block",textAlign:"center"}}>
                            {crmPartners[emp.email?.toLowerCase()]?"✅":"⬜"}
                          </span>}
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <Card>
        <p style={{fontSize:12,color:T.textSub,lineHeight:1.7}}>
          Click tab checkboxes to toggle access. Steve/Felix CRM controls whether the user sees CRM rows tagged with Steve/Felix BA. Changes take effect next time the user logs in.
        </p>
      </Card>
    </div>
  );
}

function AdminTab({ employees }) {
  const [adminSubTab, setAdminSubTab] = useState("access");
  const [authUsers, setAuthUsers]   = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [inviteEmail, setInviteEmail]   = useState("");
  const [inviteStatus, setInviteStatus] = useState(null); // null | "sending" | "ok" | "err"
  const [inviteMsg, setInviteMsg]       = useState("");

  // Load existing auth users via API
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res  = await fetch("/api/admin/users");
      const data = await res.json();
      setAuthUsers(data.users || []);
    } catch (e) {
      setAuthUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => { if (adminSubTab === "access") fetchUsers(); }, [adminSubTab, fetchUsers]);

  const inviteUser = async () => {
    if (!inviteEmail.trim()) return;
    setInviteStatus("sending");
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: inviteEmail.trim(),
        options: { emailRedirectTo: window.location.origin }
      });
      if (error) throw error;
      setInviteStatus("ok");
      setInviteMsg(`Magic link sent to ${inviteEmail.trim()}`);
      setInviteEmail("");
      fetchUsers();
    } catch (e) {
      setInviteStatus("err");
      setInviteMsg(e.message);
    }
    setTimeout(() => { setInviteStatus(null); setInviteMsg(""); }, 5000);
  };

  const removeUser = async (userId, email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    await fetch("/api/admin/remove", {
      method:"POST",
      headers:{ "Content-Type":"application/json" },
      body: JSON.stringify({ userId })
    });
    fetchUsers();
  };

  return (
    <div style={{ padding:"28px", maxWidth:1600, margin:"0 auto" }} className="fi">
      <div style={{ display:"flex", gap:4, marginBottom:24, background:T.raised, borderRadius:8,
        border:`1px solid ${T.border}`, padding:4, width:"fit-content" }}>
        {[{id:"access",label:"Team Access"},{id:"matrix",label:"Access Matrix"}].map(t => (
          <button key={t.id} onClick={()=>setAdminSubTab(t.id)}
            style={{ padding:"7px 18px", borderRadius:6, border:"none", cursor:"pointer",
              fontSize:11, fontWeight:600, letterSpacing:"0.06em", fontFamily:T.fontSans,
              background:adminSubTab===t.id?T.accent:"none",
              color:adminSubTab===t.id?T.text:T.muted }}>
            {t.label}
          </button>
        ))}
      </div>

      {adminSubTab === "access" && <>
        <SH label="Team Access Management"/>
        <Card style={{ marginBottom:28 }}>
          <p style={{ fontSize:12, color:T.textSub, marginBottom:18, lineHeight:1.7 }}>
            Invite team members by email. They will receive a login link. Access is controlled by their Business Area in the Employees section of Standing Data — Partners (Steve, Felix) see all tabs; others see only their BA tab.
          </p>
          <div style={{ display:"flex", gap:12, alignItems:"flex-end", marginBottom:8 }}>
            <div style={{ flex:1 }}>
              <div style={FL}>Email address</div>
              <Inp value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)}
                placeholder="jane@lvlup.partners"
                onKeyDown={e=>e.key==="Enter" && inviteUser()}/>
            </div>
            <Btn onClick={inviteUser} style={{ opacity: inviteStatus==="sending"?0.6:1 }}>
              {inviteStatus==="sending" ? "Sending…" : "Send Invite"}
            </Btn>
          </div>
          {inviteMsg && <div style={{ fontSize:12, color: inviteStatus==="err"?T.red:T.green, marginBottom:12 }}>
            {inviteMsg}
          </div>}
          <div style={{ marginTop:24 }}>
            <div style={{ fontSize:11, color:T.muted, fontFamily:T.fontMono, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:12 }}>Current Users</div>
            {loadingUsers ? <div style={{ color:T.muted, fontSize:12 }}>Loading…</div> :
            <div className="tbl-wrap"><table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ background:T.raised }}><TH>Email</TH><TH>BA / Access</TH><TH>Last Sign In</TH><TH></TH></tr></thead>
              <tbody>
                {authUsers.length === 0 ? <EmptyRow cols={4} msg="No users found"/> :
                  authUsers.map((u,i) => {
                    const emp = employees.find(e => e.email?.toLowerCase() === u.email?.toLowerCase());
                    const isPartner = PARTNER_EMAILS.includes(u.email?.toLowerCase());
                    const lastSign = u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString("en-GB") : "Never";
                    return (
                      <tr key={i} className="rh">
                        <TD bold>{u.email}</TD>
                        <TD>
                          {isPartner
                            ? <Badge label="Partner — Full Access" color={T.accent}/>
                            : emp ? <Badge label={emp.ba} color={emp.ba==="Strategic Finance"?T.sf:emp.ba==="Legal"?T.legal:T.ihf}/>
                            : <Badge label="No employee record" color={T.muted}/>}
                        </TD>
                        <TD color={T.muted}>{lastSign}</TD>
                        <TD>
                          {!isPartner && <Btn variant="secondary" onClick={()=>removeUser(u.id, u.email)}
                            style={{ padding:"4px 10px", fontSize:11, color:T.red }}>Remove</Btn>}
                        </TD>
                      </tr>
                    );
                  })}
              </tbody>
            </table></div>}
          </div>
        </Card>
      </>}

      {adminSubTab === "matrix" && <AccessMatrixTab employees={employees}/>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TOAST
═══════════════════════════════════════════════════════════════ */
// Shown when a user arrives via an invite or password-reset link. Supabase signs
// them in automatically on that link, but without this screen there's nowhere to
// actually choose a password - they'd land on the dashboard and be locked out again
// next time. Detected via the PASSWORD_RECOVERY event or the recovery hash in the URL.
function SetPasswordScreen({ email, onDone }) {
  const [password, setPassword]   = useState("");
  const [confirm,  setConfirm]    = useState("");
  const [error,    setError]      = useState("");
  const [loading,  setLoading]    = useState(false);

  const save = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true); setError("");
    const { error } = await supabase.auth.updateUser({ password });
    if (error) { setError(error.message); setLoading(false); return; }
    // Clear the recovery hash so a refresh doesn't reopen this screen
    window.history.replaceState(null, "", window.location.pathname);
    setLoading(false);
    onDone();
  };

  return (
    <div style={{ width:380, background:T.surface, border:`1px solid ${T.border}`,
      borderRadius:12, padding:40, boxShadow:"0 8px 40px #00000066" }}>
      <div style={{ fontFamily:T.fontSerif, fontSize:22, fontWeight:700, color:T.text, marginBottom:6 }}>
        Set your password
      </div>
      <div style={{ fontSize:11, color:T.muted, fontFamily:T.fontMono, letterSpacing:"0.08em",
        textTransform:"uppercase", marginBottom:8 }}>
        Lvl Up Partners LLP
      </div>
      {email && <div style={{ fontSize:12, color:T.mutedLt, marginBottom:24 }}>{email}</div>}

      <div style={{ marginBottom:16 }}>
        <div style={FL}>New password</div>
        <Inp type="password" value={password} onChange={e=>setPassword(e.target.value)}
          placeholder="At least 8 characters" style={{ width:"100%", boxSizing:"border-box" }}
          onKeyDown={e=>e.key==="Enter" && save()}/>
      </div>
      <div style={{ marginBottom:24 }}>
        <div style={FL}>Confirm password</div>
        <Inp type="password" value={confirm} onChange={e=>setConfirm(e.target.value)}
          placeholder="Re-enter password" style={{ width:"100%", boxSizing:"border-box" }}
          onKeyDown={e=>e.key==="Enter" && save()}/>
      </div>
      {error && <div style={{ fontSize:12, color:T.red, marginBottom:16 }}>{error}</div>}
      <Btn onClick={save} style={{ width:"100%", justifyContent:"center",
        opacity: loading ? 0.6 : 1, cursor: loading ? "wait" : "pointer" }}>
        {loading ? "Saving…" : "Set Password & Continue"}
      </Btn>
    </div>
  );
}

function LoginScreen() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [resetSent,setResetSent]= useState(false);
  const [resetMode,setResetMode]= useState(false);

  const signIn = async () => {
    if (!email.trim() || !password.trim()) return;
    setLoading(true); setError("");
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) setError(error.message);
    setLoading(false);
  };

  const sendReset = async () => {
    if (!email.trim()) { setError("Enter your email first"); return; }
    setLoading(true); setError("");
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    });
    if (error) setError(error.message);
    else setResetSent(true);
    setLoading(false);
  };

  return (
    <div style={{ width:380, background:T.surface, border:`1px solid ${T.border}`,
      borderRadius:12, padding:40, boxShadow:"0 8px 40px #00000066" }}>
      <div style={{ fontFamily:T.fontSerif, fontSize:22, fontWeight:700, color:T.text, marginBottom:6 }}>
        Firm Intelligence
      </div>
      <div style={{ fontSize:11, color:T.muted, fontFamily:T.fontMono, letterSpacing:"0.08em",
        textTransform:"uppercase", marginBottom:32 }}>
        Lvl Up Partners LLP
      </div>
      <div style={{ marginBottom:16 }}>
        <div style={FL}>Email</div>
        <Inp value={email} onChange={e=>setEmail(e.target.value)}
          placeholder="you@lvlup.partners" style={{ width:"100%", boxSizing:"border-box" }}
          onKeyDown={e=>e.key==="Enter" && !resetMode && signIn()}/>
      </div>
      {!resetMode && <>
        <div style={{ marginBottom:24 }}>
          <div style={FL}>Password</div>
          <Inp type="password" value={password} onChange={e=>setPassword(e.target.value)}
            placeholder="••••••••" style={{ width:"100%", boxSizing:"border-box" }}
            onKeyDown={e=>e.key==="Enter" && signIn()}/>
        </div>
        {error && <div style={{ fontSize:12, color:T.red, marginBottom:16 }}>{error}</div>}
        <Btn onClick={signIn} style={{ width:"100%", justifyContent:"center",
          opacity: loading ? 0.6 : 1, cursor: loading ? "wait" : "pointer" }}>
          {loading ? "Signing in…" : "Sign In"}
        </Btn>
        <div onClick={()=>{setResetMode(true);setError("");}}
          style={{ textAlign:"center", marginTop:16, fontSize:12, color:T.muted,
            cursor:"pointer", textDecoration:"underline" }}>
          Forgot password?
        </div>
        <div style={{ textAlign:"center", marginTop:20, paddingTop:16,
          borderTop:`1px solid ${T.border}`, fontSize:11, color:T.muted, lineHeight:1.6 }}>
          First time here? Use <span onClick={()=>{setResetMode(true);setError("");}}
            style={{ color:T.accentLt, cursor:"pointer", textDecoration:"underline" }}>Forgot password</span>{' '}
          to set your password.
        </div>
      </>}
      {resetMode && <>
        {resetSent
          ? <div style={{ fontSize:13, color:T.green, textAlign:"center", lineHeight:1.6 }}>
              Email sent to {email}.<br/><br/>
              Click the link in that email and you'll be taken straight to a screen
              to choose your password.<br/><br/>
              <span style={{ color:T.muted, fontSize:12 }}>
                Check spam if it hasn't arrived in a couple of minutes.
              </span>
            </div>
          : <>
              {error && <div style={{ fontSize:12, color:T.red, marginBottom:16 }}>{error}</div>}
              <Btn onClick={sendReset} style={{ width:"100%", justifyContent:"center",
                opacity: loading ? 0.6 : 1 }}>
                {loading ? "Sending…" : "Send Reset Email"}
              </Btn>
            </>}
        <div onClick={()=>{setResetMode(false);setResetSent(false);setError("");}}
          style={{ textAlign:"center", marginTop:16, fontSize:12, color:T.muted,
            cursor:"pointer", textDecoration:"underline" }}>
          Back to sign in
        </div>
      </>}
    </div>
  );
}

function Toast({ msg, onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [msg, onDismiss]);
  const isErr = msg.startsWith("⚠");
  const isOk  = msg.startsWith("✓");
  const accent = isErr ? T.red : isOk ? T.green : T.amber;
  return (
    <div style={{ position:"fixed", bottom:28, right:28, zIndex:999, background:T.raised,
      border:`1px solid ${accent}44`, borderLeft:`3px solid ${accent}`, borderRadius:8,
      padding:"14px 20px", boxShadow:"0 8px 32px #00000099",
      display:"flex", alignItems:"flex-start", gap:12, maxWidth:440 }} className="fi">
      <span style={{ fontSize:18, marginTop:1 }}>{isErr ? "⚠" : isOk ? "✓" : "📁"}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:12, fontWeight:600, color:T.text, lineHeight:1.5 }}>{msg}</div>
        {isOk && <div style={{ fontSize:11, color:T.muted, marginTop:3 }}>
          All charts and tables updated — referral sources in Standing Data can now be assigned
        </div>}
      </div>
      <button onClick={onDismiss} style={{ background:"none", border:"none", color:T.muted,
        fontSize:16, cursor:"pointer", marginLeft:4, flexShrink:0 }}>✕</button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHEETJS LOADER — loads once from CDN on first use
═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
   XERO EXPORT PARSER
   Knows the exact sheet names and layout of the Lvl Up pack:
     "Monthly P&L"         — columnar P&L, months across columns
     "Revenue transactions" — line-level invoices with Service + Client cols
     "Detailed Time"        — time report with Staff, Contact, charge cols
     "Aged Receivables"     — aged summary, sums 1M+ columns for debts>30
     "Balance Sheet"        — point-in-time balance sheet
═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
   ROOT APP
═══════════════════════════════════════════════════════════════ */
// ── XERO DATA PARSERS ─────────────────────────────────────────────────────
function findXeroRow(rows, labelFrag) {
  for (const row of rows) {
    if (!row.Cells) continue;
    const label = row.Cells[0]?.Value || '';
    if (label.toLowerCase().includes(labelFrag.toLowerCase())) return row;
  }
  return null;
}

function getCellValues(row) {
  if (!row?.Cells) return [];
  return row.Cells.slice(1).map(c => parseFloat(c.Value?.replace(/,/g,'') || 0));
}

function parseXeroPL(report, currentServiceMap) {
  const MSHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  // Extract column headers (month labels)
  const headerRow = report.Rows?.find(r => r.RowType === 'Header');
  if (!headerRow) return [];
  const periods = headerRow.Cells?.slice(1).map(c => c.Value).filter(Boolean) || [];

  // Flatten all rows
  const allRows = [];
  (report.Rows || []).forEach(section => {
    if (section.Rows) section.Rows.forEach(r => allRows.push(r));
  });

  return periods.map((period, colIdx) => {
    const getVal = (label) => {
      const row = findXeroRow(allRows, label);
      return row ? (getCellValues(row)[colIdx] || 0) : 0;
    };

    const sfRev  = getVal("Revenue - Strategic Finance");
    const lgRev  = getVal("Revenue - Legal");
    const ihfRev = getVal("Revenue - In-House Finance");
    const contFee= getVal("Contingent Fees");
    const totRev = sfRev + lgRev + ihfRev + contFee;

    const sfCos  = getVal("People Costs - Strategic Finance");
    const lgCos  = getVal("People Costs - Legal");
    const ihfCos = getVal("People Costs - In-House Finance");

    const adm    = getVal("Total Administrative Costs");
    const gp     = totRev + sfCos + lgCos + ihfCos;
    const ebitda = gp + adm;
    const pbd    = getVal("Profit on Ordinary Activities After Taxation");

    // Parse period label e.g. "Apr 2026"
    const parts = period.split(" ");
    const mIdx  = MSHORT.findIndex(m => parts[0].startsWith(m));
    const year  = parseInt(parts[1]);
    // Normalise "Sept 2026" -> "Sep 2026"; unchanged for anything already in
    // 3-letter form or not parseable.
    const normPeriod = (mIdx >= 0 && !isNaN(year)) ? `${MSHORT[mIdx]} ${parts[1]}` : period;

    return {
      period: normPeriod, year, month: mIdx + 1,
      sf_revenue: sfRev,   sf_cos: sfCos,
      legal_revenue: lgRev, legal_cos: lgCos,
      ihf_revenue: ihfRev,  ihf_cos: ihfCos,
      total_revenue: totRev, total_cos: sfCos+lgCos+ihfCos,
      ebitda, profit_before_drawings: pbd,
      debts_over_30: 0, accrued_income: 0, accumulated_profits: 0,
    };
  }).filter(r => r.year > 0).sort((a,b) => (b.year*12+b.month)-(a.year*12+a.month));
}

function parseXeroAgedReceivables(report) {
  const debtors = [];
  (report.Rows || []).forEach(section => {
    (section.Rows || []).forEach(row => {
      if (!row.Cells || row.RowType === 'SummaryRow') return;
      const name = row.Cells[0]?.Value;
      if (!name || name === 'Total') return;
      const over30 = (parseFloat(row.Cells[3]?.Value?.replace(/,/g,'') || 0) +
                      parseFloat(row.Cells[4]?.Value?.replace(/,/g,'') || 0) +
                      parseFloat(row.Cells[5]?.Value?.replace(/,/g,'') || 0) +
                      parseFloat(row.Cells[6]?.Value?.replace(/,/g,'') || 0));
      if (over30 > 0) debtors.push({ client: name, over30: Math.round(over30*100)/100 });
    });
  });
  return debtors;
}

/* ═══════════════════════════════════════════════════════════════
   WEEKLY PERF TAB — Utilisation, revenue by staff, revenue by contact
═══════════════════════════════════════════════════════════════ */
function UtilisationTab() {
  const WEEKS = 8;
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [utilRows, setUtilRows] = useState([]);
  const [revenueEmpRows, setRevenueEmpRows] = useState([]);
  const [revenueContactRows, setRevenueContactRows] = useState([]);
  const [lastSynced, setLastSynced] = useState(null);
  const [error, setError] = useState(null);

  function mondayNWeeksAgo(n) {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff - n * 7);
    return d.toISOString().slice(0, 10);
  }
  function formatWeekLabel(dateStr) {
    const d = new Date(dateStr + 'T00:00:00Z');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  }
  function fmtHrs(mins) { return mins ? (mins / 60).toFixed(1) + 'h' : '—'; }
  function fmtRev(amt) { return amt ? '£' + Math.round(amt).toLocaleString('en-GB') : '—'; }
  function fmtSync(iso) {
    if (!iso) return 'never';
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
  // Partners carry BD and management load, so target a lower billable range -
  // and over 90% is a warning sign rather than good news.
  const PARTNER_NAMES = ['Steve Tinkler', 'Felix Wong'];

  function pctColor(pct, employeeName) {
    if (pct === null || pct === undefined) return T.muted;
    const isPartner = PARTNER_NAMES.some(p => (employeeName || '').startsWith(p));
    if (isPartner) {
      if (pct > 90) return T.red;
      if (pct >= 80) return T.amber;
      if (pct >= 60) return T.green;
      return T.amber;
    }
    if (pct >= 70) return T.green;
    if (pct >= 60) return T.amber;
    return T.red;
  }

  const weekKeys = Array.from({ length: WEEKS }, (_, i) => mondayNWeeksAgo(WEEKS - 1 - i));

  async function loadData() {
    setLoading(true);
    const cutoff = weekKeys[0];
    const [{ data: u }, { data: re }, { data: rc }] = await Promise.all([
      supabase.from('utilisation_weekly').select('*').gte('week_start', cutoff),
      supabase.from('revenue_weekly').select('*').gte('week_start', cutoff),
      fetchAllRows(() => supabase.from('revenue_contact_weekly').select('*').gte('week_start', cutoff).order('week_start').order('contact_name')),
    ]);
    setUtilRows(u || []);
    setRevenueEmpRows(re || []);
    setRevenueContactRows(rc || []);
    const allSynced = [...(u || []), ...(re || [])];
    if (allSynced.length > 0) {
      setLastSynced(allSynced.reduce((max, r) => r.last_synced_at > max ? r.last_synced_at : max, ''));
    }
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  async function syncNow() {
    setSyncing(true);
    setError(null);
    try {
      const res = await fetch('/api/xero/sync-utilisation');
      const data = await res.json();
      if (!res.ok || data.error) {
        // Show which projects failed, not just the count
        const detail = data.failures?.length ? `\n\nFailed: ${data.failures.join(' · ')}` : '';
        setError(`${data.error}${detail}`);
        setSyncing(false); return;
      }
      await loadData();
    } catch (err) { setError(err.message); }
    setSyncing(false);
  }

  // Build lookup maps
  const utilMap = {};
  for (const r of utilRows) {
    if (!utilMap[r.employee_name]) utilMap[r.employee_name] = {};
    utilMap[r.employee_name][r.week_start] = r;
  }
  const revEmpMap = {};
  for (const r of revenueEmpRows) {
    if (!revEmpMap[r.employee_name]) revEmpMap[r.employee_name] = {};
    revEmpMap[r.employee_name][r.week_start] = r.billable_amount;
  }
  const revContactMap = {};
  for (const r of revenueContactRows) {
    if (!revContactMap[r.contact_name]) revContactMap[r.contact_name] = {};
    revContactMap[r.contact_name][r.week_start] = r.billable_amount;
  }

  // Ordered employee list - core team first
  const CORE = ['Steve Tinkler','Felix Wong','Neil Bancroft-Jones','Daniel O\'Toole','Carter Rich','Victoria Harrison'];
  const empNames = [...new Set(utilRows.map(r => r.employee_name))];
  const orderedEmps = [...CORE.filter(e => empNames.includes(e)), ...empNames.filter(e => !CORE.includes(e))];

  // Most recent complete week = second-to-last key (last key = current week, may be partial)
  const mostRecentComplete = weekKeys[weekKeys.length - 2] || weekKeys[weekKeys.length - 1];

  // Contacts sorted by most recent complete week revenue, highest first
  const contactNames = [...new Set(revenueContactRows.map(r => r.contact_name))]
    .filter(c => c !== 'Internal')
    .sort((a, b) => (revContactMap[b]?.[mostRecentComplete] || 0) - (revContactMap[a]?.[mostRecentComplete] || 0));

  const cellW = 82;
  const thStyle = { minWidth: cellW };

  return (
    <div className="fi" style={{ padding: '24px 28px' }}>
      <SH label="Weekly Perf" accent={T.accent} action={
        <button onClick={syncNow} disabled={syncing || loading}
          style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            background: syncing ? T.raised : T.accent, color: syncing ? T.muted : '#fff',
            border: 'none', borderRadius: 6, cursor: syncing ? 'default' : 'pointer', fontFamily: T.fontSans }}>
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      } />
      <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginBottom: 20 }}>
        Last synced: {fmtSync(lastSynced)}
      </div>

      {error && <Card style={{ borderColor: T.red, marginBottom: 16 }}>
        <div style={{ color: T.red, fontSize: 12, fontFamily: T.fontMono }}>{error}</div>
      </Card>}

      {loading && <Card><div style={{ color: T.muted, fontSize: 12 }}>Loading...</div></Card>}

      {!loading && (
        <>
          {/* Utilisation grid */}
          <SH label="Utilisation by Staff — % of 40h" accent={T.accent} />
          <div style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono, marginBottom: 8, lineHeight: 1.7 }}>
            <strong style={{ color: T.mutedLt }}>Partners:</strong>{' '}
            <span style={{ color: T.green }}>●</span> 60-80% target{'  '}
            <span style={{ color: T.amber }}>●</span> &lt;60% or 80-90%{'  '}
            <span style={{ color: T.red }}>●</span> &gt;90%
            <br/>
            <strong style={{ color: T.mutedLt }}>Team:</strong>{' '}
            <span style={{ color: T.green }}>●</span> ≥70%{'  '}
            <span style={{ color: T.amber }}>●</span> 60-70%{'  '}
            <span style={{ color: T.red }}>●</span> &lt;60%
          </div>
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: T.raised }}>
                  <TH>Employee</TH>
                  {weekKeys.map(w => <TH key={w} right style={thStyle}>{formatWeekLabel(w)}</TH>)}
                  <TH right>Avg</TH>
                </tr></thead>
                <tbody>
                  {orderedEmps.map(emp => {
                    const pcts = weekKeys.map(w => {
                      const r = utilMap[emp]?.[w];
                      return r ? Math.round((r.billable_minutes / Math.max(1, (40 * 60) - (r.leave_minutes || 0))) * 100) : null;
                    });
                    const validPcts = pcts.filter(p => p !== null);
                    const avg = validPcts.length ? Math.round(validPcts.reduce((s, p) => s + p, 0) / validPcts.length) : null;
                    return (
                      <tr key={emp} className="rh">
                        <TD bold>{emp}</TD>
                        {weekKeys.map((w, wi) => {
                          const r = utilMap[emp]?.[w];
                          const pct = r ? Math.round((r.billable_minutes / Math.max(1, (40 * 60) - (r.leave_minutes || 0))) * 100) : null;
                          return (
                            <TD key={w} right color={pctColor(pct, emp)}>
                              {pct !== null ? (
                                <div>
                                  <div style={{ fontWeight: 700, fontFamily: T.fontMono, fontSize: 12 }}>{pct}%</div>
                                  <div style={{ fontSize: 9, color: T.muted }}>{fmtHrs(r.billable_minutes)}</div>
                                </div>
                              ) : <span style={{ color: T.muted }}>—</span>}
                            </TD>
                          );
                        })}
                        <TD right bold color={pctColor(avg, emp)}>{avg !== null ? `${avg}%` : '—'}</TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Revenue by employee */}
          <SH label="Billable Revenue by Staff" accent={T.sf} />
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: T.raised }}>
                  <TH>Employee</TH>
                  {weekKeys.map(w => <TH key={w} right style={thStyle}>{formatWeekLabel(w)}</TH>)}
                  <TH right>Total</TH>
                </tr></thead>
                <tbody>
                  {orderedEmps.map(emp => {
                    const total = weekKeys.reduce((s, w) => s + (revEmpMap[emp]?.[w] || 0), 0);
                    return (
                      <tr key={emp} className="rh">
                        <TD bold>{emp}</TD>
                        {weekKeys.map(w => {
                          const amt = revEmpMap[emp]?.[w] || 0;
                          return <TD key={w} right color={amt > 0 ? T.text : T.muted}>{fmtRev(amt)}</TD>;
                        })}
                        <TD right bold color={T.sf}>{fmtRev(total)}</TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Revenue by contact - sorted by most recent complete week */}
          <SH label={`Billable Revenue by Contact — sorted by w/c ${formatWeekLabel(mostRecentComplete)}`} accent={T.accent} />
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: T.raised }}>
                  <TH>#</TH>
                  <TH>Contact</TH>
                  {weekKeys.map(w => <TH key={w} right style={thStyle}>{formatWeekLabel(w)}</TH>)}
                  <TH right>Total</TH>
                </tr></thead>
                <tbody>
                  {contactNames.map((contact, i) => {
                    const total = weekKeys.reduce((s, w) => s + (revContactMap[contact]?.[w] || 0), 0);
                    return (
                      <tr key={contact} className="rh">
                        <TD color={T.muted}>{i + 1}</TD>
                        <TD bold>{contact}</TD>
                        {weekKeys.map(w => {
                          const amt = revContactMap[contact]?.[w] || 0;
                          return <TD key={w} right color={amt > 0 ? T.text : T.muted}>{fmtRev(amt)}</TD>;
                        })}
                        <TD right bold color={T.accent}>{fmtRev(total)}</TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   BILLING TAB — Unbilled Xero Projects time, by Business Area > Contact > Task+Employee
   Reads from Supabase cache by default (fast, no Xero calls). "Sync Now"
   triggers a full live pull from Xero, saving progress after every page.
   Business Area is assigned per TASK (in Standing Data), not per contact,
   since one contact can span multiple service lines.
═══════════════════════════════════════════════════════════════ */
function fmtGBPExact(amount) {
  if (!amount) return "—";
  return "£" + amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function currentMonthKey() {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
}


/* ═══════════════════════════════════════════════════════════════
   BILLING TAB — Flat list sorted by contact, filterable by BA
═══════════════════════════════════════════════════════════════ */

function BillingToggle({ value, onChange }) {
  const cycle = [null, 'bill', 'accrue', 'write_off'];
  const cfg = {
    bill:      { label: 'Bill',      bg: T.green, fg: '#fff' },
    accrue:    { label: 'Accrue',    bg: T.amber, fg: '#fff' },
    write_off: { label: 'Write Off', bg: T.red,   fg: '#fff' },
  };
  function handleClick() {
    const next = cycle[(cycle.indexOf(value) + 1) % cycle.length];
    onChange(next);
  }
  const c = cfg[value];
  return (
    <button onClick={handleClick}
      style={{ fontSize: 10, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${c ? 'transparent' : T.border}`,
        background: c ? c.bg : T.raised, color: c ? c.fg : T.muted,
        minWidth: 74, fontWeight: c ? 700 : 400 }}>
      {c ? c.label : '— click'}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ONBOARDING TAB
   Four independent sections - each can be run on its own, so a
   failure or a client that only needs part of the process doesn't
   block the rest. Visible to everyone.
═══════════════════════════════════════════════════════════════ */

// Shared between the per-client Company Info modal and the new All Company
// Info browser view - one definition, both use it.
const COMPANY_INFO_FIELDS = [
  ['registered_office_address', 'Registered Office Address', 'textarea'],
  ['director_id_no', 'Director ID No.', 'text'],
  ['company_registered_no', 'Company Registered No.', 'text'],
  ['incorporation_date', 'Incorporation Date', 'date'],
  ['financial_year_end', 'Financial Year-End', 'text'],
  ['next_year_end_filing', 'Next Year-End Filing', 'date'],
  ['confirmation_statement_date', 'Confirmation Statement Date', 'date'],
  ['confirmation_statement_filing', 'Confirmation Statement Filing', 'date'],
  ['corporation_tax_payment_deadline', 'Corporation Tax Payment Deadline', 'date'],
  ['corporation_tax_return_filing', 'Corporation Tax Return Filing', 'date'],
  ['emi', 'EMI', 'checkbox'],
  ['utr', 'UTR', 'text'],
  ['vat', 'VAT', 'text'],
  ['vat_periods', 'VAT Periods', 'text'],
  ['paye', 'PAYE', 'text'],
  ['payday_timing', 'Payday Timing', 'text'],
  ['number_of_employees', 'Number of Employees', 'number'],
  ['payment_run_timing', 'Payment Run Timing', 'text'],
  ['accounts_office', 'Accounts Office', 'text'],
  ['authentication_code', 'Authentication Code', 'text'],
  ['ereminders', 'E-reminders', 'checkbox'],
  ['email_address', 'Email Address', 'text'],
  ['notes', 'Notes', 'textarea'],
  ['payroll', 'Payroll', 'text'],
  ['bookkeeper', 'Bookkeeper', 'text'],
  ['compliance', 'Compliance', 'text'],
  ['accounting_systems', 'Accounting Systems', 'text'],
];

const IHF_SCOPE_ITEMS = [
  'Finance Mailbox Monitoring',
  'Transaction Posting',
  'Bank Reconciliations',
  'Sales Invoicing',
  'Quarterly VAT Returns',
  'Month-End Close',
  'Monthly Reporting Pack',
  'Payroll Processing',
  'Payroll Posting',
  // Not in Steve's specified order - kept available, sorted to the end
  'Payment Runs - fortnightly',
  'Monthly Budget Tracking',
  'Quarterly Tax Payments on Account',
  'Royalty Statements',
];

// Defined outside OnboardingTab deliberately: components declared inside a
// component are recreated on every render, which remounts their children and
// makes text inputs lose focus after each keystroke.
function OnbSection({ num, title, subtitle, children, accent }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div style={{ width: 22, height: 22, borderRadius: 11, background: accent || T.accent,
          color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{num}</div>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>{title}</span>
      </div>
      {subtitle && <div style={{ fontSize: 11, color: T.muted, marginBottom: 10, marginLeft: 32 }}>{subtitle}</div>}
      <div style={{ marginLeft: 32 }}>{children}</div>
    </div>
  );
}

const ONB_INP = { background: T.raised, color: T.text, border: `1px solid ${T.border}`,
  borderRadius: 5, padding: '7px 10px', fontSize: 12, outline: 'none' };
const ONB_LBL = { fontSize: 10, color: T.muted, fontFamily: T.fontMono,
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' };
const onbBtn = (enabled, colour) => ({
  padding: '8px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
  textTransform: 'uppercase', border: 'none', borderRadius: 6,
  background: enabled ? (colour || T.accent) : T.raised,
  color: enabled ? '#fff' : T.muted,
  cursor: enabled ? 'pointer' : 'not-allowed',
});

// Money inputs need type="text" - a number input silently rejects commas.
// Display formatted, store plain.
const fmtMoneyInput = v => {
  if (v === '' || v == null) return '';
  const n = Number(String(v).replace(/,/g, ''));
  return isFinite(n) ? n.toLocaleString('en-GB', { maximumFractionDigits: 2 }) : String(v);
};
const parseMoneyInput = v => String(v ?? '').replace(/[^0-9.]/g, '');

// Supabase returns at most 1,000 rows per request and says nothing when it cuts
// a bigger result short. This pages through until every row has been read.
async function fetchAllRows(build, pageSize = 1000) {
  const out = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await build().range(from, from + pageSize - 1);
    if (error || !data) return { data: out.length ? out : null, error };
    out.push(...data);
    if (data.length < pageSize) break;
  }
  return { data: out, error: null };
}

// Loads EVERY row of a table. A plain read is silently capped at 1,000 rows, and these tables are read
// oldest-first - so once they passed 1,000 rows the NEWEST month was the part that vanished (time_rows
// holds ~240 rows a month, so it passes the cap after about 4 months). Orders by created_at then id so page
// boundaries are stable; falls back to created_at alone if the table has no id column.
async function loadWholeTable(table) {
  for (const orders of [['created_at', 'id'], ['created_at']]) {
    const r = await fetchAllRows(() => orders.reduce((q, c) => q.order(c, { ascending: true }), supabase.from(table).select('*')));
    if (!r.error) return r;
  }
  return { data: null, error: true };
}

// ── Recharge import: the month-end skill produces a CSV, pasted into the Recharges panel ───────────────
// Columns (header required): incurred_date, client_name, description, amount, vat_treatment, email_id,
// email_subject, notes. Only description and amount are essential. Comma or tab separated.
function parseRechargeCsv(text) {
  const src = String(text || '').replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').trim();
  if (!src) return { rows: [], errors: ['Nothing pasted'] };
  const delim = (src.split('\n')[0].match(/\t/g) || []).length > (src.split('\n')[0].match(/,/g) || []).length ? '\t' : ',';
  const records = []; let field = '', rec = [], inQ = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQ) { if (c === '"') { if (src[i + 1] === '"') { field += '"'; i++; } else inQ = false; } else field += c; }
    else if (c === '"') inQ = true;
    else if (c === delim) { rec.push(field); field = ''; }
    else if (c === '\n') { rec.push(field); records.push(rec); rec = []; field = ''; }
    else field += c;
  }
  rec.push(field); records.push(rec);
  const header = records[0].map(h => h.trim().toLowerCase());
  const col = n => header.indexOf(n);
  const errors = [];
  if (col('description') < 0 || col('amount') < 0) return { rows: [], errors: ['The first line must be a header that includes at least "description" and "amount"'] };
  const get = (r, n) => (col(n) >= 0 && r[col(n)] != null ? String(r[col(n)]).trim() : '');
  const rows = [];
  records.slice(1).forEach((r, i) => {
    if (r.every(f => !String(f).trim())) return;
    const line = i + 2;
    const description = get(r, 'description');
    const amountRaw = get(r, 'amount');
    const amount = amountRaw === '' ? null : parseFloat(amountRaw.replace(/[£,\s]/g, ''));
    if (!description) { errors.push(`Line ${line}: no description - skipped`); return; }
    if (amountRaw !== '' && !isFinite(amount)) errors.push(`Line ${line}: amount "${amountRaw}" is not a number - left blank`);
    let date = get(r, 'incurred_date');
    const dmy = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmy) date = `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) date = '';
    rows.push({
      incurred_date: date, client_name: get(r, 'client_name'), description,
      amount: isFinite(amount) && amount > 0 ? Math.round(amount * 100) / 100 : null,
      vat_treatment: get(r, 'vat_treatment').toLowerCase() === 'client' ? 'client' : 'outside_scope',
      email_id: get(r, 'email_id'), email_subject: get(r, 'email_subject'), notes: get(r, 'notes'),
    });
  });
  return { rows, errors };
}

// Parsed rows -> rows for client_recharges. Every row gets a stable email_id (the real one, or one derived from its
// content) so pasting the same CSV twice can never create duplicates.
function buildRechargeImport(rows, contactNames) {
  const hash = str => { let h = 5381; for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0; return (h >>> 0).toString(36); };
  return rows.map(r => {
    const known = r.client_name ? contactNames.find(c => normaliseClientName(c) === normaliseClientName(r.client_name)) : null;
    return {
      source: 'email', status: 'pending',
      email_id: r.email_id || `import:${hash([r.client_name, r.incurred_date, r.amount, r.description].join('|').toLowerCase())}`,
      email_subject: r.email_subject || null, incurred_date: r.incurred_date || null,
      client_name: known || r.client_name || null, description: r.description, amount: r.amount, vat_treatment: r.vat_treatment,
      notes: [r.notes, r.client_name && !known ? `Client "${r.client_name}" not matched to a known client - pick one` : ''].filter(Boolean).join('. ') || null,
    };
  });
}

// ── Recharges: filing fees and other costs paid for clients, forwarded to the finance mailbox ─────────────
// Reviewed at month-end; items marked "Include" go onto that client's invoice as a line, and are marked
// Billed (with the invoice number) once the invoice is raised.
const RECHARGE_INPUT = { background: T.raised, color: T.text, fontSize: 11, fontFamily: 'inherit', padding: '5px 8px', border: `1px solid ${T.border}`, borderRadius: 4, outline: 'none' };

function RechargeRow({ r, contactNames, onUpdate }) {
  const [client, setClient] = useState(r.client_name || '');
  const [desc, setDesc] = useState(r.description || '');
  const [amount, setAmount] = useState(r.amount ?? '');
  useEffect(() => { setClient(r.client_name || ''); setDesc(r.description || ''); setAmount(r.amount ?? ''); }, [r.client_name, r.description, r.amount]);
  const known = contactNames.find(c => normaliseClientName(c) === normaliseClientName(client));
  const amt = Number(amount);
  const ready = !!known && amt > 0;
  const billed = r.status === 'billed', included = r.status === 'include';
  const flag = !billed && (!known || !(amt > 0));
  return (
    <tr style={{ borderBottom: `1px solid ${T.border}`, opacity: billed ? 0.55 : 1, background: included ? `${T.green}10` : 'transparent' }}>
      <td style={{ padding: '6px 10px', fontSize: 11, color: T.muted, whiteSpace: 'nowrap' }}>{r.incurred_date || '—'}</td>
      <td style={{ padding: '6px 6px' }}>
        <input list="recharge-clients" value={client} disabled={billed} placeholder="Client…"
          onChange={e => setClient(e.target.value)}
          onBlur={() => { const v = client.trim(); const k = contactNames.find(c => normaliseClientName(c) === normaliseClientName(v)); if ((k || v) !== (r.client_name || '')) onUpdate(r.id, { client_name: k || v || null }); }}
          title={known ? '' : 'Not a known client - pick one from the list'}
          style={{ ...RECHARGE_INPUT, width: 170, borderColor: !billed && !known ? T.red : T.border }} />
      </td>
      <td style={{ padding: '6px 6px' }}>
        <input value={desc} disabled={billed} onChange={e => setDesc(e.target.value)}
          onBlur={() => { if (desc.trim() !== (r.description || '').trim()) onUpdate(r.id, { description: desc.trim() }); }}
          style={{ ...RECHARGE_INPUT, width: 280 }} />
        {r.notes && <div style={{ fontSize: 9, color: T.amber, marginTop: 2, maxWidth: 280 }}>{r.notes}</div>}
      </td>
      <td style={{ padding: '6px 6px' }}>
        <input type="number" step="0.01" min="0" value={amount} disabled={billed} onChange={e => setAmount(e.target.value)}
          onBlur={() => { const v = amount === '' ? null : Math.round(Number(amount) * 100) / 100; if (v !== (r.amount == null ? null : Number(r.amount))) onUpdate(r.id, { amount: v }); }}
          style={{ ...RECHARGE_INPUT, width: 84, textAlign: 'right', borderColor: !billed && !(amt > 0) ? T.red : T.border }} />
      </td>
      <td style={{ padding: '6px 6px' }}>
        <select value={r.vat_treatment || 'outside_scope'} disabled={billed} onChange={e => onUpdate(r.id, { vat_treatment: e.target.value })} style={{ ...RECHARGE_INPUT, width: 128 }}>
          <option value="outside_scope">No VAT (at cost)</option>
          <option value="client">Client's VAT</option>
        </select>
      </td>
      <td style={{ padding: '6px 10px', fontSize: 10, color: T.muted, maxWidth: 190 }}>
        {r.source === 'email'
          ? <span title={`${r.email_subject || ''}\n${r.email_from || ''}`} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✉ {r.email_subject}</span>
          : 'added by hand'}
      </td>
      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', textAlign: 'right' }}>
        {billed ? <span style={{ fontSize: 10, color: T.green, fontFamily: T.fontMono }}>✓ Billed {r.billed_invoice || ''}</span> : (
          <>
            <button onClick={() => onUpdate(r.id, { status: included ? 'pending' : 'include' })} disabled={!included && !ready}
              title={!included && !ready ? 'Needs a known client and an amount first' : ''}
              style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: (!included && !ready) ? 'not-allowed' : 'pointer', marginRight: 6,
                background: included ? T.green : 'none', color: included ? '#fff' : (ready ? T.green : T.muted), border: `1px solid ${included ? T.green : (ready ? T.green : T.border)}` }}>
              {included ? '✓ Included' : 'Include'}
            </button>
            <button onClick={() => { if (window.confirm('Skip this item? It will not be recharged.')) onUpdate(r.id, { status: 'dismissed' }); }}
              style={{ padding: '4px 10px', fontSize: 10, color: T.muted, background: 'none', border: `1px solid ${T.border}`, borderRadius: 4, cursor: 'pointer' }}>Skip</button>
          </>
        )}
        {flag && <div style={{ fontSize: 9, color: T.amber, marginTop: 2 }}>needs {!known ? 'a client' : 'an amount'}</div>}
      </td>
    </tr>
  );
}

function RechargePanel({ recharges, contactNames, rechargeAccount, onSaveAccount, onReload, tableMissing }) {
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [acct, setAcct] = useState(rechargeAccount || '');
  const [add, setAdd] = useState({ client: '', description: '', amount: '', date: new Date().toISOString().slice(0, 10) });
  useEffect(() => { setAcct(rechargeAccount || ''); }, [rechargeAccount]);

  const open = recharges.filter(r => r.status === 'pending' || r.status === 'include');
  const cutoff = Date.now() - 60 * 86400000;
  const recentBilled = recharges.filter(r => r.status === 'billed' && (!r.billed_at || Date.parse(r.billed_at) > cutoff));
  const sum = rs => rs.reduce((n, r) => n + (Number(r.amount) || 0), 0);
  const pending = open.filter(r => r.status === 'pending'), included = open.filter(r => r.status === 'include');
  const gbp = n => '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  async function onUpdate(id, patch) {
    const { error } = await supabase.from('client_recharges').update(patch).eq('id', id);
    if (error) { alert(`Could not save: ${error.message}`); return; }
    await onReload();
  }
  async function runImport() {
    setMsg(null);
    const { rows, errors } = parseRechargeCsv(importText);
    if (!rows.length) { setMsg({ error: errors[0] || 'Nothing to import' }); return; }
    setImportBusy(true);
    try {
      const payload = buildRechargeImport(rows, contactNames);
      // skip anything already in the table - whatever its status, including ones you skipped or already billed
      const have = new Set();
      for (let i = 0; i < payload.length; i += 100) {
        const { data, error } = await supabase.from('client_recharges').select('email_id').in('email_id', payload.slice(i, i + 100).map(p => p.email_id));
        if (error) throw new Error(error.message);
        (data || []).forEach(d => have.add(d.email_id));
      }
      const fresh = payload.filter(p => !have.has(p.email_id));
      if (fresh.length) {
        const { error } = await supabase.from('client_recharges').upsert(fresh, { onConflict: 'email_id', ignoreDuplicates: true });
        if (error) throw new Error(error.message);
      }
      const needs = fresh.filter(f => !f.amount || !contactNames.some(c => normaliseClientName(c) === normaliseClientName(f.client_name))).length;
      setMsg({ ok: `Imported ${fresh.length} new item(s)${have.size ? `, ${have.size} already in the dashboard (skipped)` : ''}${needs ? `, ${needs} need a client or amount` : ''}.${errors.length ? ` Notes: ${errors.join('; ')}` : ''}` });
      if (fresh.length) { setImportText(''); setImportOpen(false); }
      await onReload();
    } catch (e) { setMsg({ error: e.message }); }
    setImportBusy(false);
  }
  async function addManual() {
    const k = contactNames.find(c => normaliseClientName(c) === normaliseClientName(add.client));
    const amt = Math.round(Number(add.amount) * 100) / 100;
    if (!add.description.trim() || !(amt > 0)) { alert('Add a description and an amount.'); return; }
    const { error } = await supabase.from('client_recharges').insert({
      source: 'manual', status: 'pending', client_name: k || add.client.trim() || null, description: add.description.trim(),
      amount: amt, incurred_date: add.date || null, vat_treatment: 'outside_scope' });
    if (error) { alert(`Could not add: ${error.message}`); return; }
    setAdd({ client: '', description: '', amount: '', date: new Date().toISOString().slice(0, 10) });
    await onReload();
  }

  const btn = { padding: '6px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 5, cursor: 'pointer' };
  return (
    <div style={{ marginBottom: 24 }}>
      <datalist id="recharge-clients">{contactNames.map(c => <option key={c} value={c} />)}</datalist>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 3, height: 16, background: T.amber, borderRadius: 2 }} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
            Recharges — filing fees &amp; costs to pass on
          </span>
          <span style={{ fontSize: 10, fontFamily: T.fontMono, color: pending.length ? T.amber : T.muted }}>
            {pending.length} to review ({gbp(sum(pending))}) · {included.length} included ({gbp(sum(included))})
          </span>
        </div>
        <button onClick={() => setImportOpen(o => !o)} disabled={tableMissing} style={{ ...btn, background: T.accent, color: '#fff', border: 'none' }}>
          {importOpen ? 'Close import' : 'Import month-end list'}
        </button>
      </div>

      {tableMissing && <div style={{ fontSize: 11, color: T.amber, marginBottom: 10 }}>⚠ The recharges table doesn't exist yet - run the client_recharges SQL in Supabase first.</div>}
      {msg?.ok && <div style={{ fontSize: 11, color: T.green, marginBottom: 10 }}>✓ {msg.ok}</div>}
      {msg?.error && <div style={{ fontSize: 11, color: T.red, marginBottom: 10 }}>⚠ {msg.error}</div>}
      {importOpen && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, marginBottom: 12, background: `${T.raised}66` }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 6 }}>
            Paste the CSV from the month-end recharges skill (first line is the header). Items arrive as "to review" - nothing goes on an invoice until you click Include. Pasting the same list twice is safe.
          </div>
          <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={6} placeholder="incurred_date,client_name,description,amount,vat_treatment,email_id,email_subject,notes"
            style={{ ...RECHARGE_INPUT, width: '100%', boxSizing: 'border-box', fontFamily: T.fontMono, fontSize: 10 }} />
          <div style={{ marginTop: 8 }}>
            <button onClick={runImport} disabled={importBusy || !importText.trim()} style={{ ...btn, background: T.green, color: '#fff', border: 'none' }}>{importBusy ? 'Importing…' : 'Import'}</button>
          </div>
        </div>
      )}
      {included.length > 0 && !rechargeAccount && (
        <div style={{ fontSize: 11, color: T.amber, marginBottom: 10 }}>⚠ Set the recharge account code below - invoices can't be raised with recharge lines until it's set.</div>
      )}

      <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden', background: T.card || 'transparent' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1050 }}>
            <thead><tr style={{ background: T.raised }}>
              {['Date', 'Client', 'Description (goes on the invoice)', 'Amount £', 'VAT', 'Source', ''].map((h, i) => (
                <th key={i} style={{ textAlign: i === 6 ? 'right' : 'left', padding: '7px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted, fontFamily: T.fontMono }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {[...open, ...recentBilled].map(r => <RechargeRow key={r.id} r={r} contactNames={contactNames} onUpdate={onUpdate} />)}
              {open.length + recentBilled.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '14px 16px', fontSize: 12, color: T.muted, fontStyle: 'italic' }}>
                  Nothing to review. At month-end run the recharges skill and paste its list in with "Import month-end list", or add one below.
                </td></tr>
              )}
              <tr style={{ background: `${T.raised}66` }}>
                <td style={{ padding: '6px 10px' }}><input type="date" value={add.date} onChange={e => setAdd(a => ({ ...a, date: e.target.value }))} style={{ ...RECHARGE_INPUT, width: 120 }} /></td>
                <td style={{ padding: '6px 6px' }}><input list="recharge-clients" value={add.client} placeholder="Client…" onChange={e => setAdd(a => ({ ...a, client: e.target.value }))} style={{ ...RECHARGE_INPUT, width: 170 }} /></td>
                <td style={{ padding: '6px 6px' }}><input value={add.description} placeholder="Add one by hand: e.g. Companies House filing fee - CS01" onChange={e => setAdd(a => ({ ...a, description: e.target.value }))} style={{ ...RECHARGE_INPUT, width: 280 }} /></td>
                <td style={{ padding: '6px 6px' }}><input type="number" step="0.01" min="0" value={add.amount} placeholder="0.00" onChange={e => setAdd(a => ({ ...a, amount: e.target.value }))} style={{ ...RECHARGE_INPUT, width: 84, textAlign: 'right' }} /></td>
                <td colSpan={3} style={{ padding: '6px 10px', textAlign: 'right' }}><button onClick={addManual} style={{ ...btn, background: 'none', color: T.mutedLt, border: `1px solid ${T.border}` }}>+ Add</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 10, color: T.muted, flexWrap: 'wrap' }}>
        <span>Recharge income account (Xero code):</span>
        <input value={acct} onChange={e => setAcct(e.target.value)} onBlur={() => { if (acct.trim() !== (rechargeAccount || '')) onSaveAccount(acct.trim()); }}
          placeholder="e.g. 205" style={{ ...RECHARGE_INPUT, width: 80, borderColor: rechargeAccount ? T.border : T.amber }} />
        <span>Included items become a line on that client's invoice, at cost with no VAT unless you choose "Client's VAT".</span>
      </div>
    </div>
  );
}

// Format a date as YYYY-MM-DD from its LOCAL parts. toISOString() converts through UTC, which shifts a
// local-midnight date back a day while the UK is on British Summer Time.
function fmtLocalDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Free-text billing note for a client ("Bill in USD", "Insert PO number"...). Saves when you click away,
// not on every keystroke. Defined at module level so typing never loses focus.
function BillingNotesCell({ value, disabled, onSave }) {
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  return (
    <textarea value={text} rows={2} disabled={disabled} placeholder="e.g. Bill in USD · Insert PO number"
      onChange={e => setText(e.target.value)}
      onBlur={() => { const v = text.trim(); if (v !== (value || '').trim()) onSave(v || null); }}
      style={{ width: 230, resize: 'vertical', background: T.raised, color: T.text, fontSize: 11, fontFamily: 'inherit',
        padding: '5px 8px', border: `1px solid ${text.trim() ? T.amber : T.border}`, borderRadius: 4, outline: 'none' }} />
  );
}

// Legal-suffix helpers for adopting Companies House's "Limited"/"Ltd" wording.
// Companies House returns names in CAPITALS, so we take only its legal suffix,
// never its casing or wording.
const LEGAL_SUFFIX_RE = /\s*\b(limited|ltd|llp|llc|inc|plc|gmbh|corp|co)\b\.?\s*$/i;
function splitLegalSuffix(name) {
  const n = (name || '').trim();
  const m = n.match(LEGAL_SUFFIX_RE);
  return m ? { base: n.slice(0, m.index).trim(), suffix: m[1] } : { base: n, suffix: '' };
}
function canonicalLegalSuffix(raw) {
  return ({ limited: 'Limited', ltd: 'Ltd', llp: 'LLP', llc: 'LLC', inc: 'Inc', plc: 'PLC', gmbh: 'GmbH', corp: 'Corp', co: 'Co' })[(raw || '').toLowerCase()] || '';
}

// A client's saved onboarding scope is stored under whatever spelling was used
// the first time ("Flix Interactive"), but the name picked now may be spelled
// differently ("Flix Interactive Ltd"). Exact-name lookups then missed the saved
// scope and a save created a second row. This returns the name the scope is
// ACTUALLY stored under (exact match first, then Ltd/Limited-insensitive, most
// recently updated if several), or the typed name if there is none yet.
async function resolveScopeName(name) {
  const typed = (name || '').trim();
  if (!typed) return typed;
  const { data: all } = await supabase.from('onboarding_scope').select('client_name, updated_at');
  const rows = all || [];
  const exact = rows.find(r => r.client_name === typed);
  if (exact) return exact.client_name;
  const k = normaliseClientName(typed);
  const hits = rows.filter(r => normaliseClientName(r.client_name) === k)
    .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
  return hits[0]?.client_name || typed;
}

// Full client list on the left, all Company Info fields for the selected
// client on the right. Editable, but text can't be selected/copied outside
// the actual input fields - deliberately, so the client list itself can't
// be lifted out wholesale.
function CompanyInfoBrowser({ onClose }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [info, setInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('');
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('client_company_info')
        .select('client_name, el_signed, aml_completed').order('client_name');
      setClients(data || []);
      setLoading(false);
      if (data?.length) setSelected(data[0].client_name);
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      const { data } = await supabase.from('client_company_info')
        .select('*').eq('client_name', selected).maybeSingle();
      setInfo(data || {});
    })();
  }, [selected]);

  function updateField(field, value) {
    setInfo(prev => {
      const next = { ...prev, [field]: value };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        setSaveState('saving');
        const { error } = await supabase.from('client_company_info').upsert({
          client_name: selected, ...next, updated_at: new Date().toISOString(),
        }, { onConflict: 'client_name' });
        setSaveState(error ? 'error' : 'saved');
        if (!error) {
          setClients(cs => cs.map(c => c.client_name === selected
            ? { ...c, el_signed: next.el_signed, aml_completed: next.aml_completed } : c));
        }
      }, 600);
      return next;
    });
  }

  // Blocks copying text out of the page - applied at the container level so
  // it covers labels, values, everything except the inputs' own normal
  // editing behaviour (typing, select-all-then-retype still works fine,
  // it's specifically the clipboard copy that's blocked).
  const blockCopy = e => e.preventDefault();

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: T.bg, zIndex: 2000, display: 'flex', flexDirection: 'column' }}
      onCopy={blockCopy} onCut={blockCopy} onContextMenu={blockCopy}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>All Company Info</div>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', color: T.muted, fontSize: 20, cursor: 'pointer' }}>✕</button>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ width: 260, borderRight: `1px solid ${T.border}`, overflowY: 'auto', flexShrink: 0,
          userSelect: 'none', WebkitUserSelect: 'none' }}>
          {loading && <div style={{ padding: 16, fontSize: 11, color: T.muted }}>Loading…</div>}
          {clients.map(c => (
            <div key={c.client_name} onClick={() => setSelected(c.client_name)}
              style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 12,
                background: selected === c.client_name ? T.raised : 'transparent',
                borderLeft: selected === c.client_name ? `3px solid ${T.accent}` : '3px solid transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: T.text }}>{c.client_name}</span>
              <span style={{ display: 'flex', gap: 4 }}>
                {!c.el_signed && <span title="EL not signed" style={{ width: 6, height: 6, borderRadius: 3, background: T.red }} />}
                {!c.aml_completed && <span title="AML not completed" style={{ width: 6, height: 6, borderRadius: 3, background: T.amber }} />}
              </span>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24, userSelect: 'none', WebkitUserSelect: 'none' }}>
          {!selected ? (
            <div style={{ color: T.muted, fontSize: 12 }}>Select a client on the left.</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{selected}</div>
                <span style={{ fontSize: 10, color: saveState === 'saved' ? T.green : saveState === 'saving' ? T.muted : T.amber, fontFamily: T.fontMono }}>
                  {saveState === 'saved' ? '✓ Saved' : saveState === 'saving' ? 'Saving…' : saveState === 'error' ? '⚠ Save failed' : ''}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 20, marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={!!info.el_signed} onChange={e => updateField('el_signed', e.target.checked)}
                    style={{ accentColor: T.green, width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>EL Signed</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={!!info.aml_completed} onChange={e => updateField('aml_completed', e.target.checked)}
                    style={{ accentColor: T.green, width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>AML Completed</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={!!info.simple_client_info} onChange={e => updateField('simple_client_info', e.target.checked)}
                    style={{ accentColor: T.green, width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Simple Client Info</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, userSelect: 'none' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Owner</span>
                  <select value={info.owner || ''} onChange={e => updateField('owner', e.target.value || null)}
                    style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                    <option value="">—</option>
                    <option value="Victoria">Victoria</option>
                    <option value="Ruth">Ruth</option>
                    <option value="Aivan">Aivan</option>
                    <option value="Lara">Lara</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {COMPANY_INFO_FIELDS.map(([field, label, type]) => (
                  <div key={field} style={type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}>
                    <label style={{ ...ONB_LBL, userSelect: 'none' }}>{label}</label>
                    {type === 'checkbox' ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={!!info[field]} onChange={e => updateField(field, e.target.checked)}
                          style={{ accentColor: T.accent, width: 15, height: 15, cursor: 'pointer' }} />
                        <span style={{ fontSize: 11, color: T.textSub }}>{info[field] ? 'Yes' : 'No'}</span>
                      </label>
                    ) : type === 'textarea' ? (
                      <textarea value={info[field] || ''} rows={2} onCopy={blockCopy} onCut={blockCopy}
                        onChange={e => updateField(field, e.target.value)}
                        style={{ ...ONB_INP, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: T.fontSans }} />
                    ) : (
                      <input type={type} value={info[field] || ''} onCopy={blockCopy} onCut={blockCopy}
                        onChange={e => updateField(field, e.target.value)}
                        style={{ ...ONB_INP, width: '100%', boxSizing: 'border-box' }} />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function OnboardingTab({ referrals = [] }) {
  // Shared across all sections - the one thing everything keys off
  const [clientName, setClientName] = useState('');

  // Section 1: project scope
  const [quoteInputs, setQuoteInputs] = useState({
    transactions: '', supplierInvoices: '', salesInvoices: '', employees: '',
  });
  const [scopeTicks, setScopeTicks] = useState({});
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // All scope decisions and fees, persisted to Supabase as they change
  const DEFAULT_SCOPE = {
    stat_accounts: false, stat_accounts_fee: '',
    vgec: false, vgec_fee: '', vgec_bfi_included: false,
    corp_tax: false, corp_tax_fee: '',
    emi_valuation: false, emi_valuation_fee: '',
    emi_notifications: false, emi_notifications_fee: '',
    rate_partner: 295, rate_director: 180, rate_manager: 95, rate_senior: 30,
    ihf_proposed_fee: '', ihf_proposed_payroll_fee: '',
  };
  const [scopeData, setScopeData] = useState(DEFAULT_SCOPE);
  const [saveState, setSaveState] = useState('');   // '' | saving | saved | error
  const [billingSendState, setBillingSendState] = useState('');
  const [billingSendError, setBillingSendError] = useState('');
  const saveTimer = useRef(null);

  const num = v => (v === '' || v == null ? null : Number(v));

  // Writes the whole scope row. Called on blur for text inputs and immediately
  // for tickboxes - debounced so rapid changes don't fire a request each time.
  const saveScope = useCallback(async () => {
    const name = clientName.trim();
    if (!name) return;
    setSaveState('saving');
    const row = {
      client_name: name,
      ihf_scope_items: Object.keys(scopeTicks).filter(k => scopeTicks[k]),
      ihf_transactions: num(quoteInputs.transactions),
      ihf_supplier_inv: num(quoteInputs.supplierInvoices),
      ihf_sales_inv: num(quoteInputs.salesInvoices),
      ihf_employees: num(quoteInputs.employees),
      ihf_proposed_fee: num(scopeData.ihf_proposed_fee),
      ihf_proposed_payroll_fee: num(scopeData.ihf_proposed_payroll_fee),
      stat_accounts: !!scopeData.stat_accounts,
      stat_accounts_fee: num(scopeData.stat_accounts_fee),
      vgec: !!scopeData.vgec,
      vgec_fee: num(scopeData.vgec_fee),
      vgec_bfi_included: !!scopeData.vgec_bfi_included,
      corp_tax: !!scopeData.corp_tax,
      corp_tax_fee: num(scopeData.corp_tax_fee),
      emi_valuation: !!scopeData.emi_valuation,
      emi_valuation_fee: num(scopeData.emi_valuation_fee),
      emi_notifications: !!scopeData.emi_notifications,
      emi_notifications_fee: num(scopeData.emi_notifications_fee),
      rate_partner: num(scopeData.rate_partner),
      rate_director: num(scopeData.rate_director),
      rate_manager: num(scopeData.rate_manager),
      rate_senior: num(scopeData.rate_senior),
      updated_at: new Date().toISOString(),
    };
    row.client_name = await resolveScopeName(name);
    const { error } = await supabase.from('onboarding_scope')
      .upsert(row, { onConflict: 'client_name' });
    setSaveState(error ? 'error' : 'saved');
    if (error) console.error('[onboarding] scope save failed:', error);
  }, [clientName, scopeTicks, quoteInputs, scopeData]);

  // Tickboxes save immediately, but debounced to coalesce rapid clicking
  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveScope(), 600);
  }, [saveScope]);

  // Pull any previously saved scope for this client
  const loadExistingScope = useCallback(async (name) => {
    if (!name?.trim()) return null;
    const scopeKey = await resolveScopeName(name);
    const { data } = await supabase.from('onboarding_scope')
      .select('*').eq('client_name', scopeKey).maybeSingle();
    if (!data) return null;

    setScopeData({
      stat_accounts: data.stat_accounts, stat_accounts_fee: data.stat_accounts_fee ?? '',
      vgec: data.vgec, vgec_fee: data.vgec_fee ?? '', vgec_bfi_included: data.vgec_bfi_included,
      corp_tax: data.corp_tax, corp_tax_fee: data.corp_tax_fee ?? '',
      emi_valuation: data.emi_valuation, emi_valuation_fee: data.emi_valuation_fee ?? '',
      emi_notifications: data.emi_notifications, emi_notifications_fee: data.emi_notifications_fee ?? '',
      rate_partner: data.rate_partner ?? 295, rate_director: data.rate_director ?? 180,
      rate_manager: data.rate_manager ?? 95, rate_senior: data.rate_senior ?? 30,
      ihf_proposed_fee: data.ihf_proposed_fee ?? '',
      ihf_proposed_payroll_fee: data.ihf_proposed_payroll_fee ?? '',
    });
    setQuoteInputs({
      transactions: data.ihf_transactions ?? '',
      supplierInvoices: data.ihf_supplier_inv ?? '',
      salesInvoices: data.ihf_sales_inv ?? '',
      employees: data.ihf_employees ?? '',
    });
    const ticks = {};
    (data.ihf_scope_items || []).forEach(i => { ticks[i] = true; });
    setScopeTicks(ticks);
    return data;
  }, []);

  // Pushes any fixed fees entered here into the billing tab's fixed fee schedule,
  // so they recur monthly like the existing IHF fees
  async function sendFeesToBilling() {
    const name = clientName.trim();
    if (!name) return;
    setBillingSendState('sending'); setBillingSendError('');

    const fees = [];
    if (scopeData.ihf_proposed_fee)
      fees.push({ description: 'In-House Finance Team', monthly_amount: Number(scopeData.ihf_proposed_fee),
                  service_line: 'In-House Finance', nominal_code: '203', task_name: 'IHF' });
    if (scopeData.ihf_proposed_payroll_fee)
      fees.push({ description: 'In-House Finance Team - Payroll', monthly_amount: Number(scopeData.ihf_proposed_payroll_fee),
                  service_line: 'In-House Finance (payroll)', nominal_code: '203', task_name: 'IHF Payroll' });
    // Annual fees are stored as fixed fees too - billed when they fall due
    if (scopeData.stat_accounts && scopeData.stat_accounts_fee)
      fees.push({ description: 'Statutory Accounts', monthly_amount: Number(scopeData.stat_accounts_fee),
                  service_line: 'Statutory Accounts', nominal_code: '204', task_name: 'Statutory Accounts', annual: true });
    if (scopeData.vgec && scopeData.vgec_fee)
      fees.push({ description: 'VGEC', monthly_amount: Number(scopeData.vgec_fee),
                  service_line: 'Tax Compliance - VGEC', nominal_code: '204', task_name: 'Tax Compliance - VGEC', annual: true });
    if (scopeData.corp_tax && scopeData.corp_tax_fee)
      fees.push({ description: 'Corporation Tax Services', monthly_amount: Number(scopeData.corp_tax_fee),
                  service_line: 'Tax Compliance - Corporate Tax', nominal_code: '204', task_name: 'Tax Compliance - Corporate Tax', annual: true });
    if (scopeData.emi_valuation && scopeData.emi_valuation_fee)
      fees.push({ description: 'EMI Valuation', monthly_amount: Number(scopeData.emi_valuation_fee),
                  service_line: 'Tax Compliance - EMI', nominal_code: '204', task_name: 'Tax Compliance - EMI Valuation', annual: true });
    if (scopeData.emi_notifications && scopeData.emi_notifications_fee)
      fees.push({ description: 'EMI Notifications', monthly_amount: Number(scopeData.emi_notifications_fee),
                  service_line: 'Tax Compliance - EMI', nominal_code: '204', task_name: 'Tax Compliance - EMI Notifications', annual: true });
    if (scopeData.seis_eis && scopeData.seis_eis_fee)
      fees.push({ description: 'SEIS/EIS Applications', monthly_amount: Number(scopeData.seis_eis_fee),
                  service_line: 'Tax Compliance - SEIS/EIS', nominal_code: '204', task_name: 'Tax Compliance - SEIS/EIS', annual: true });

    if (!fees.length) {
      setBillingSendError('No fees entered to send');
      setBillingSendState('');
      return;
    }

    try {
      const failures = [];
      for (const f of fees) {
        const { annual, ...row } = f;
        const payload = {
          contact_name: name, ...row,
          vat_group: '20_standard', currency: 'GBP',
          effective_from: new Date().toISOString().slice(0, 10), active: true,
        };

        // The table's unique constraint is (contact_name, task_name,
        // effective_from) - a two-column onConflict is rejected. Check for an
        // existing active fee for this task and update it, otherwise insert.
        const { data: existing } = await supabase.from('contact_fixed_fees')
          .select('id').eq('contact_name', name).eq('task_name', row.task_name)
          .eq('active', true).maybeSingle();

        const { error } = existing
          ? await supabase.from('contact_fixed_fees').update(payload).eq('id', existing.id)
          : await supabase.from('contact_fixed_fees').insert(payload);

        if (error) failures.push(`${row.description}: ${error.message}`);
      }

      if (failures.length) {
        setBillingSendError(failures.join(' · '));
        setBillingSendState('');
        return;
      }
      await supabase.from('onboarding_scope')
        .update({ sent_to_billing: true }).eq('client_name', await resolveScopeName(name));
      setBillingSendState('sent');
    } catch (err) {
      setBillingSendError(err.message);
      setBillingSendState('');
    }
  }

  // Section 2: Xero + Drive
  const [xeroAddress, setXeroAddress] = useState('');
  const [xeroEmail, setXeroEmail] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [originalBA, setOriginalBA] = useState('');
  const [ukRegistered, setUkRegistered] = useState(true);
  const [xeroResult, setXeroResult] = useState(null);
  const [xeroLoading, setXeroLoading] = useState(false);

  // Section 3: engagement letter
  const [letterFields, setLetterFields] = useState({
    signatory: 'Steve Tinkler',
    clientContact: '',
    clientEmail: '',
    clientAddress: '',
    includeIhfScope: false,
    includeTaxSchedule: false,
    includeAdvisory: false,
    scopeText: '',
    feeText: '',
  });
  const [letterResult, setLetterResult] = useState(null);
  const [letterLoading, setLetterLoading] = useState(false);

  // Section 4 (send for signature) has no state yet - Adobe Sign not connected

  // Research agent - reads Slack/Gmail/Companies House and pre-fills the rest
  const [research, setResearch] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);

  // Company Info - statutory/compliance detail captured once per client,
  // referred back to at various points
  const [companyInfo, setCompanyInfo] = useState({});
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [showAllCompanyInfo, setShowAllCompanyInfo] = useState(false);
  const [companyInfoSaveState, setCompanyInfoSaveState] = useState('');
  const companyInfoSaveTimer = useRef(null);

  // Legal name suffixes vary between tables (client_mapping might say "Ltd"
  // where client_company_info says "Limited") - normalise both sides so a
  // real match isn't missed on a suffix difference alone.
  function normaliseCompanyName(s) {
    return (s || '').toLowerCase()
      .replace(/\b(limited|ltd|llp|llc|inc|plc|gmbh|corp|co)\b\.?/g, '')
      .replace(/[^a-z0-9]/g, '')
      .trim();
  }

  async function loadCompanyInfo(name) {
    if (!name?.trim()) { setCompanyInfo({}); return; }
    const target = name.trim();

    // Exact match first - cheapest and correct for the common case
    const { data: exact } = await supabase.from('client_company_info')
      .select('*').eq('client_name', target).maybeSingle();
    if (exact) { setCompanyInfo(exact); return; }

    // Fall back to a normalised match across all rows - catches "Simbio Ltd"
    // vs the stored "Simbio Limited"
    const { data: all } = await supabase.from('client_company_info').select('*');
    const targetNorm = normaliseCompanyName(target);
    const fuzzy = (all || []).find(r => normaliseCompanyName(r.client_name) === targetNorm);
    setCompanyInfo(fuzzy || {});
  }

  const saveCompanyInfo = useCallback(async (nextInfo) => {
    const name = clientName.trim();
    if (!name) return;
    setCompanyInfoSaveState('saving');
    const { error } = await supabase.from('client_company_info').upsert({
      client_name: name, ...nextInfo, updated_at: new Date().toISOString(),
    }, { onConflict: 'client_name' });
    setCompanyInfoSaveState(error ? 'error' : 'saved');
    if (error) console.error('[company-info] save failed:', error);
  }, [clientName]);

  function updateCompanyInfoField(field, value) {
    setCompanyInfo(prev => {
      const next = { ...prev, [field]: value };
      if (companyInfoSaveTimer.current) clearTimeout(companyInfoSaveTimer.current);
      companyInfoSaveTimer.current = setTimeout(() => saveCompanyInfo(next), 600);
      return next;
    });
  }

  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [hiddenSuggestions, setHiddenSuggestions] = useState([]); // alias matches not shown by default
  const [hiddenClientNames, setHiddenClientNames] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [includeAdditionalCompanies, setIncludeAdditionalCompanies] = useState(false);
  const [additionalCompaniesText, setAdditionalCompaniesText] = useState('');
  const [profClearanceLoading, setProfClearanceLoading] = useState(false);
  const [profClearanceResult, setProfClearanceResult] = useState(null);

  async function createProfClearance() {
    if (!nameSet) return;
    setProfClearanceLoading(true); setProfClearanceResult(null);
    try {
      const additionalCompanies = includeAdditionalCompanies
        ? additionalCompaniesText.split('\n').map(s => s.trim()).filter(Boolean)
        : [];
      const res = await fetch('/api/onboarding/create-prof-clearance', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName: clientName.trim(), additionalCompanies }),
      });
      const data = await res.json();
      setProfClearanceResult(res.ok && !data.error ? data : { error: data.error || 'Failed' });
    } catch (err) {
      setProfClearanceResult({ error: err.message });
    }
    setProfClearanceLoading(false);
  }

  const nameSet = clientName.trim().length > 0;

  // Missing engagement letters scan - on demand via button
  const [missingLettersScanning, setMissingLettersScanning] = useState(false);
  const [missingLettersResults, setMissingLettersResults] = useState(null);

  async function runMissingLettersScan() {
    setMissingLettersScanning(true); setMissingLettersResults(null);
    try {
      const res = await fetch('/api/onboarding/scan-missing-letters?fromDashboard=true');
      const data = await res.json();
      if (res.ok && !data.error) setMissingLettersResults(data.results);
      else setMissingLettersResults([{ client: 'Error', status: 'error', note: data.error || 'Scan failed' }]);
    } catch (err) {
      setMissingLettersResults([{ client: 'Error', status: 'error', note: err.message }]);
    }
    setMissingLettersScanning(false);
  }

  // Every client name we already know about, loaded once and matched locally
  // as the user types - deliberately no Companies House lookup here, that
  // would add real latency per keystroke and timeliness matters more than
  // completeness for a suggestion box.
  const [knownClientNames, setKnownClientNames] = useState([]);

  useEffect(() => {
    (async () => {
      const [{ data: b }, { data: c }, { data: d }] = await Promise.all([
        supabase.from('client_mapping').select('revenue_client'),
        supabase.from('onboarding_scope').select('client_name'),
        supabase.from('client_company_info').select('client_name, company_registered_no'),
      ]);
      // ONE suggestion per company. A Set only collapsed identical text, so
      // "Flix Interactive" (from one table) and "Flix Interactive Ltd" (from
      // another) both showed. Group by the Ltd/Limited-insensitive name and show
      // the best spelling: Company Info's (the maintained, Companies House-aligned
      // one) first, then saved scopes, then Xero revenue names; within that,
      // prefer a name that has its legal suffix and sensible capitalisation.
      const candidates = [
        ...(d || []).map(r => ({ name: r.client_name, rank: 0 })),
        ...(c || []).map(r => ({ name: r.client_name, rank: 1 })),
        ...(b || []).map(r => ({ name: r.revenue_client, rank: 2 })),
      ].filter(x => x.name);
      const score = x => [
        x.rank,
        splitLegalSuffix(x.name).suffix ? 0 : 1,
        (/[a-z]/.test(x.name) && /[A-Z]/.test(x.name)) ? 0 : 1,
      ];
      const better = (x, y) => {
        const a = score(x), z = score(y);
        for (let i = 0; i < a.length; i++) if (a[i] !== z[i]) return a[i] < z[i];
        return false;
      };
      const best = {};
      for (const x of candidates) {
        const k = normaliseCompanyName(x.name);
        if (k && (!best[k] || better(x, best[k]))) best[k] = x;
      }
      // Only suggest names that match a registered company. A name is hidden as an
      // ALIAS (e.g. "Pantaloon" next to "Pantaloon Interactive Ltd") only if ALL of:
      //  - it has no Company Info record of its own,
      //  - it is the start of a company that IS confirmed on Companies House
      //    (that record holds a Companies House number),
      // so clients that aren't on Companies House (overseas), or simply haven't
      // been looked up yet, can never be hidden. Hidden names are not deleted -
      // they are still offered via "show" in the dropdown.
      const ownRowKeys = new Set((d || []).map(r => normaliseCompanyName(r.client_name)));
      const verifiedKeys = (d || []).filter(r => String(r.company_registered_no || '').trim())
        .map(r => normaliseCompanyName(r.client_name));
      const isAlias = k => k.length >= 4 && !ownRowKeys.has(k) && verifiedKeys.some(v => v.length > k.length && v.startsWith(k));
      const visible = [], hidden = [];
      for (const [k, x] of Object.entries(best)) (isAlias(k) ? hidden : visible).push(x.name);
      setKnownClientNames(visible.sort());
      setHiddenClientNames(hidden.sort());
    })();
  }, []);

  // Suffix-agnostic matching: "Nova" should suggest "Nova Studios Limited"
  // even without knowing whether it ends Ltd, Limited, LLP etc. Matches on
  // the typed text appearing anywhere in the name, case-insensitive.
  function updateNameSuggestions(value) {
    setClientName(value);
    const q = value.trim().toLowerCase();
    if (q.length < 2) { setNameSuggestions([]); setHiddenSuggestions([]); setShowSuggestions(false); return; }
    const rank = (a, b) => {
      // Names starting with the query rank above names merely containing it
      const aStarts = a.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.toLowerCase().startsWith(q) ? 0 : 1;
      return aStarts - bStarts || a.length - b.length;
    };
    const matches = knownClientNames.filter(n => n.toLowerCase().includes(q)).sort(rank).slice(0, 6);
    const hidden = hiddenClientNames.filter(n => n.toLowerCase().includes(q)).sort(rank);
    setNameSuggestions(matches);
    setHiddenSuggestions(hidden);
    setShowSuggestions(matches.length > 0 || hidden.length > 0);
  }

  // Keep the engagement letter tickboxes in step with what's actually scoped -
  // no point asking twice
  const anyTaxService = !!(scopeData.vgec || scopeData.corp_tax
    || scopeData.emi_valuation || scopeData.emi_notifications);
  const anyIhfScope = Object.values(scopeTicks).some(Boolean);

  useEffect(() => {
    setLetterFields(f => ({
      ...f,
      includeTaxSchedule: anyTaxService,
      includeIhfScope: anyIhfScope,
    }));
  }, [anyTaxService, anyIhfScope]);

  async function runResearch() {
    if (!nameSet) return;
    setResearchLoading(true); setResearch(null);
    try {
      // Start from a clean slate - a new client should never inherit the
      // previous one's fees or ticks
      setScopeData(DEFAULT_SCOPE);
      setScopeTicks({});
      setQuoteInputs({ transactions: '', supplierInvoices: '', salesInvoices: '', employees: '' });
      setQuoteResult(null);

      // If we've scoped this client before, restore what was entered and
      // STOP there - don't run the live Slack/email/Companies House lookup
      // on top. Continuing on was the actual bug being fixed here: when the
      // live agent found nothing, its response still got merged into the
      // form and could silently wipe restored values (the agent sometimes
      // returns false/0 rather than genuine null for "don't know", and that
      // isn't caught by a null-only fallback).
      const existing = await loadExistingScope(clientName);
      await loadCompanyInfo(clientName);
      if (existing) {
        setResearch({
          restoredFromSaved: true,
          savedAt: existing.updated_at,
          proposal: {
            legalEntityName: existing.client_name,
            registeredAddress: existing.client_address,
            clientContact: existing.client_contact,
            clientEmail: existing.client_email,
            suggestedSignatory: existing.signatory,
          },
          sources: {},
        });
        if (existing.client_address) setXeroAddress(existing.client_address);
        if (existing.client_email) setXeroEmail(existing.client_email);
        if (existing.referral_source) setReferralSource(existing.referral_source);
        if (existing.original_ba) setOriginalBA(existing.original_ba);
        setResearchLoading(false);
        return;
      }

      const res = await fetch(
        `/api/onboarding/agent-research?client=${encodeURIComponent(clientName.trim())}`
      );
      const data = await res.json();
      if (!res.ok || data.error) {
        setResearch({ error: data.error || 'Research failed' });
        setResearchLoading(false);
        return;
      }
      setResearch(data);

      // Pre-fill everything the agent found. Blank fields are left alone so
      // nothing already typed gets overwritten.
      const p = data.proposal || {};

      // Volumes and fees the agent inferred from the conversation
      if (p.ihfVolumes) {
        setQuoteInputs(v => ({
          transactions: p.ihfVolumes.transactions ?? v.transactions,
          supplierInvoices: p.ihfVolumes.supplierInvoices ?? v.supplierInvoices,
          salesInvoices: p.ihfVolumes.salesInvoices ?? v.salesInvoices,
          employees: p.ihfVolumes.employees ?? v.employees,
        }));
      }
      if (p.proposedFees || p.servicesToTick) {
        const pf = p.proposedFees || {};
        const st = p.servicesToTick || {};
        setScopeData(d => ({
          ...d,
          ihf_proposed_fee: pf.ihfMonthly ?? d.ihf_proposed_fee,
          ihf_proposed_payroll_fee: pf.payrollMonthly ?? d.ihf_proposed_payroll_fee,
          stat_accounts: st.statAccounts ?? d.stat_accounts,
          stat_accounts_fee: pf.statAccounts ?? d.stat_accounts_fee,
          vgec: st.vgec ?? d.vgec,
          vgec_fee: pf.vgec ?? d.vgec_fee,
          vgec_bfi_included: st.bfiIncluded ?? d.vgec_bfi_included,
          corp_tax: st.corpTax ?? d.corp_tax,
          corp_tax_fee: pf.corpTax ?? d.corp_tax_fee,
          emi_valuation: st.emiValuation ?? d.emi_valuation,
          emi_valuation_fee: pf.emiValuation ?? d.emi_valuation_fee,
          emi_notifications: st.emiNotifications ?? d.emi_notifications,
          emi_notifications_fee: pf.emiNotifications ?? d.emi_notifications_fee,
          seis_eis: st.seisEis ?? d.seis_eis,
          seis_eis_fee: pf.seisEis ?? d.seis_eis_fee,
        }));
        setTimeout(() => saveScope(), 800);
      }
      // Prefer the deterministic Companies House profile address over the
      // LLM's own transcription of it - same reliability reasoning already
      // applied to Company Info below. Falls back to the LLM's proposal
      // only if CH data wasn't available at all.
      const chProfileAddress = data?.sources?.companiesHouse?.profile?.registeredAddress;
      if (chProfileAddress) setXeroAddress(chProfileAddress);
      else if (p.registeredAddress) setXeroAddress(p.registeredAddress);
      if (p.clientEmail) setXeroEmail(p.clientEmail);
      if (p.companiesHouseNumber) setUkRegistered(true);

      // Populate Company Info from Companies House - read the deterministic
      // profile data directly (not the LLM's own transcription of it, which
      // isn't guaranteed to carry every field correctly). Only fills genuine
      // gaps - never overwrites something already entered, whether the row
      // is brand new or already existed.
      const chProfile = data?.sources?.companiesHouse?.profile;
      const chMatch = data?.sources?.companiesHouse?.bestMatch;
      const chNumber = chMatch?.number;
      const chConfidence = data?.sources?.companiesHouse?.matchConfidence;

      // Low-confidence matches (just the top search result, no name overlap) are
      // not written to Company Info - that would put another company's number and
      // address on this client's record.
      if ((chProfile || chNumber) && chConfidence !== 'low') {
        // Find the existing row by NORMALISED name (Ltd = Limited, case/spacing
        // ignored). An exact-string lookup missed "Paris Media Ltd" when working
        // on "Paris Media Limited" and created a second row for the same company.
        const { data: allInfo } = await supabase.from('client_company_info').select('*');
        const typedNorm = normaliseCompanyName(clientName);
        const current = (allInfo || []).find(r => normaliseCompanyName(r.client_name) === typedNorm) || null;

        // Companies House is authoritative for the legal SUFFIX only (Limited /
        // Ltd / LLP...), and only for an exact match - never its capitalised
        // wording, and never on a partial/low-confidence match, where renaming
        // to a different company's name would be wrong. The existing row's own
        // wording and casing is kept; only the suffix is aligned.
        let rowName = current?.client_name || clientName.trim();
        let targetName = rowName;
        const chSuffix = chConfidence === 'high' ? canonicalLegalSuffix(splitLegalSuffix(chMatch?.name).suffix) : '';
        if (chSuffix) targetName = `${splitLegalSuffix(rowName).base} ${chSuffix}`;
        if (targetName !== clientName.trim()) setClientName(targetName);

        if (current && targetName !== current.client_name) {
          const { error: renameErr } = await supabase.from('client_company_info')
            .update({ client_name: targetName }).eq('client_name', current.client_name);
          if (!renameErr) rowName = targetName;
        } else if (!current) {
          rowName = targetName;
        }

        // Only fills genuine gaps - never overwrites something already entered.
        const fill = {};
        if (!current?.company_registered_no && chNumber) fill.company_registered_no = chNumber;
        if (!current?.registered_office_address && chProfile?.registeredAddress) fill.registered_office_address = chProfile.registeredAddress;
        if (!current?.incorporation_date && chProfile?.incorporationDate) fill.incorporation_date = chProfile.incorporationDate;
        if (!current?.confirmation_statement_date && chProfile?.confirmationStatementNextDue) fill.confirmation_statement_date = chProfile.confirmationStatementNextDue;
        if (!current?.next_year_end_filing && chProfile?.accountsNextDue) fill.next_year_end_filing = chProfile.accountsNextDue;
        if (!current?.email_address && p.clientEmail) fill.email_address = p.clientEmail;

        if (Object.keys(fill).length) {
          await supabase.from('client_company_info').upsert({
            client_name: rowName, ...fill, updated_at: new Date().toISOString(),
          }, { onConflict: 'client_name' });
        }
        await loadCompanyInfo(rowName);
      }
      setLetterFields(f => ({
        ...f,
        clientContact: p.clientContact || f.clientContact,
        clientEmail: p.clientEmail || f.clientEmail,
        clientAddress: p.registeredAddress || f.clientAddress,
        signatory: p.suggestedSignatory
          || (p.servicesDiscussed?.some(s => /legal/i.test(s)) ? 'Felix Wong' : 'Steve Tinkler')
          || f.signatory,
        includeIhfScope: p.suggestedPattern === 'ihf' ? true : f.includeIhfScope,
        scopeText: p.proposedScope || f.scopeText,
        feeText: p.proposedFee || f.feeText,
      }));
      if (p.ihfScopeItems?.length) {
        const ticks = {};
        p.ihfScopeItems.forEach(item => { ticks[item] = true; });
        setScopeTicks(t => ({ ...t, ...ticks }));
      }
    } catch (err) {
      setResearch({ error: err.message });
    }
    setResearchLoading(false);
  }

  async function generateQuote() {
    if (!nameSet) return;
    setQuoteLoading(true); setQuoteResult(null);
    try {
      const ticked = Object.keys(scopeTicks).filter(k => scopeTicks[k])
        .sort((a, b) => IHF_SCOPE_ITEMS.indexOf(a) - IHF_SCOPE_ITEMS.indexOf(b));
      const params = new URLSearchParams({
        clientName: clientName.trim(),
        employees: quoteInputs.employees || '0',
        transactions: quoteInputs.transactions || '0',
        salesInvoices: quoteInputs.salesInvoices || '0',
        payments: quoteInputs.supplierInvoices || '0',
        scope: ticked.join(','),
      });
      const res = await fetch(`/api/quotes/generate-ihf-quote?${params}`);
      const data = await res.json();
      setQuoteResult(res.ok && !data.error ? data : { error: data.error || 'Failed' });

      // Seed the proposed fees from what the sheet calculated, but only where
      // nothing has been entered yet - never overwrite a figure already agreed
      if (data.calculated) {
        setScopeData(d => ({
          ...d,
          ihf_proposed_fee: d.ihf_proposed_fee || (data.calculated.ihfFee ?? ''),
          ihf_proposed_payroll_fee: d.ihf_proposed_payroll_fee || (data.calculated.payrollFee ?? ''),
        }));
        // Persist the calculated figures alongside the proposed ones
        const name = clientName.trim();
        if (name) {
          await supabase.from('onboarding_scope').upsert({
            client_name: await resolveScopeName(name),
            ihf_calculated_fee: data.calculated.ihfFee,
            ihf_payroll_fee: data.calculated.payrollFee,
            quote_sheet_id: data.spreadsheetId,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'client_name' });
        }
      }
    } catch (err) { setQuoteResult({ error: err.message }); }
    setQuoteLoading(false);
  }

  async function runXeroSetup() {
    if (!nameSet) return;
    setXeroLoading(true); setXeroResult(null);
    try {
      const params = new URLSearchParams({ clientName: clientName.trim() });
      if (xeroAddress) params.set('address', xeroAddress.replace(/\n/g, '|'));
      if (xeroEmail) params.set('email', xeroEmail);
      if (referralSource) params.set('referralSource', referralSource);
      if (originalBA) params.set('originalBA', originalBA);
      params.set('ukRegistered', String(ukRegistered));
      if (scopeData.rate_partner) params.set('partnerRate', String(scopeData.rate_partner));
      if (research?.proposal?.companiesHouseNumber) {
        params.set('companiesHouseNumber', research.proposal.companiesHouseNumber);
      }
      const res = await fetch(`/api/onboarding/xero-setup?${params}`);
      const data = await res.json();
      setXeroResult(data);
    } catch (err) { setXeroResult({ error: err.message }); }
    setXeroLoading(false);
  }

  async function createLetter() {
    if (!nameSet) return;
    setLetterLoading(true); setLetterResult(null);
    try {
      const ticked = Object.keys(scopeTicks).filter(k => scopeTicks[k])
        .sort((a, b) => IHF_SCOPE_ITEMS.indexOf(a) - IHF_SCOPE_ITEMS.indexOf(b));
      const body = {
        clientName: clientName.trim(),
        clientAddress: letterFields.clientAddress || xeroAddress,
        clientContact: letterFields.clientContact,
        signatory: letterFields.signatory,
        pattern: letterFields.includeIhfScope ? 'ihf' : 'fixed_fee',
        // scopeItems drive the IHF block; advisory text is separate
        scopeItems: letterFields.includeIhfScope ? ticked : undefined,
        scopeText: letterFields.scopeText,
        feeText: letterFields.feeText,
        includeTaxSchedule: letterFields.includeTaxSchedule,
        includeAdvisory: letterFields.includeAdvisory,
        ihfFee: scopeData.ihf_proposed_fee || undefined,
        payrollFee: scopeData.ihf_proposed_payroll_fee || undefined,
        rateCard: {
          Partner: scopeData.rate_partner, Director: scopeData.rate_director,
          Manager: scopeData.rate_manager, Senior: scopeData.rate_senior,
        },
        fees: {
          statAccounts: scopeData.stat_accounts ? scopeData.stat_accounts_fee : undefined,
          vgec: scopeData.vgec ? scopeData.vgec_fee : undefined,
          bfiIncluded: !!scopeData.vgec_bfi_included,
          corpTax: scopeData.corp_tax ? scopeData.corp_tax_fee : undefined,
          emiValuation: scopeData.emi_valuation ? scopeData.emi_valuation_fee : undefined,
          emiNotifications: scopeData.emi_notifications ? scopeData.emi_notifications_fee : undefined,
          seisEis: scopeData.seis_eis ? scopeData.seis_eis_fee : undefined,
        },
        metrics: letterFields.includeIhfScope ? {
          payments: quoteInputs.supplierInvoices,
          transactions: quoteInputs.transactions,
          salesInvoices: quoteInputs.salesInvoices,
          employees: quoteInputs.employees,
        } : undefined,
      };
      const res = await fetch('/api/onboarding/create-client', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setLetterResult(res.ok && !data.error ? data : { error: data.error || 'Failed' });

      // Notify the team - best-effort, never blocks the letter creation itself
      if (res.ok && !data.error) {
        const services = [];
        if (letterFields.includeIhfScope) services.push('In-House Finance');
        if (letterFields.includeTaxSchedule) services.push('Tax Services');
        if (letterFields.includeAdvisory) services.push('Legal / Strat Finance');

        const slackText = [
          `*New Client Engagement Letter sent - ${clientName.trim()}*`,
          ...services,
        ].join('\n');

        fetch('/api/slack/post-message', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ channel: 'general-uk', text: slackText }),
        }).catch(() => { /* best-effort, don't surface Slack failures here */ });
      }
    } catch (err) { setLetterResult({ error: err.message }); }
    setLetterLoading(false);
  }

  return (
    <div className="fi" style={{ padding: '24px 28px' }}>
      <SH label="Client Onboarding" accent={T.accent} />
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 20 }}>
        Each step runs independently — use whichever apply to this client.
      </div>

      {/* Client name - everything keys off this */}
      <Card style={{ padding: 16, marginBottom: 24, borderColor: nameSet ? T.green : T.amber }}>
        <label style={ONB_LBL}>Client legal entity name</label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', position: 'relative' }}>
          <div style={{ position: 'relative', flex: '1 1 340px', maxWidth: 420 }}>
            <input value={clientName} onChange={e => updateNameSuggestions(e.target.value)}
              placeholder="e.g. Nova Studios Limited"
              onKeyDown={e => { if (e.key === 'Enter') { setShowSuggestions(false); runResearch(); } }}
              onFocus={() => (nameSuggestions.length > 0 || hiddenSuggestions.length > 0) && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              style={{ ...ONB_INP, width: '100%', fontSize: 14, boxSizing: 'border-box' }} />
            {showSuggestions && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
                boxShadow: '0 8px 24px #00000066', zIndex: 20, overflow: 'hidden' }}>
                {nameSuggestions.map(name => (
                  <div key={name}
                    onClick={() => { setClientName(name); setShowSuggestions(false); }}
                    style={{ padding: '8px 12px', fontSize: 13, color: T.textSub, cursor: 'pointer',
                      borderBottom: `1px solid ${T.border}` }}
                    onMouseDown={e => e.preventDefault()}>
                    {name}
                  </div>
                ))}
                {hiddenSuggestions.length > 0 && (
                  <div onMouseDown={e => e.preventDefault()}
                    onClick={() => { setNameSuggestions(prev => [...prev, ...hiddenSuggestions]); setHiddenSuggestions([]); }}
                    style={{ padding: '6px 12px', fontSize: 11, color: T.muted, cursor: 'pointer' }}>
                    {hiddenSuggestions.length} other match{hiddenSuggestions.length > 1 ? 'es' : ''} hidden (not a Companies House name) —{' '}
                    <span style={{ color: T.accentLt }}>show</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <button onClick={runResearch} disabled={!nameSet || researchLoading}
            style={onbBtn(nameSet && !researchLoading)}>
            {researchLoading ? 'Researching…' : 'Find Details →'}
          </button>
          <button onClick={() => { if (nameSet) { loadCompanyInfo(clientName); setShowCompanyInfo(true); } }}
            disabled={!nameSet}
            style={onbBtn(nameSet, T.mutedLt)}>
            Company Info
          </button>
          <button onClick={() => setShowAllCompanyInfo(true)}
            style={onbBtn(true, T.mutedLt)}>
            All Company Info
          </button>
        </div>

        {showAllCompanyInfo && <CompanyInfoBrowser onClose={() => setShowAllCompanyInfo(false)} />}
        {showCompanyInfo && createPortal(
          <div style={{ position: 'fixed', inset: 0, background: '#00000088', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={e => e.target === e.currentTarget && setShowCompanyInfo(false)}>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
              padding: 24, width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Company Info — {clientName}</div>
                  <div style={{ fontSize: 10, color: companyInfoSaveState === 'saved' ? T.green : companyInfoSaveState === 'saving' ? T.muted : T.amber,
                    fontFamily: T.fontMono, marginTop: 2 }}>
                    {companyInfoSaveState === 'saved' ? '✓ Saved' : companyInfoSaveState === 'saving' ? 'Saving…' : companyInfoSaveState === 'error' ? '⚠ Save failed' : ''}
                  </div>
                </div>
                <button onClick={() => setShowCompanyInfo(false)}
                  style={{ background: 'none', border: 'none', color: T.muted, fontSize: 18, cursor: 'pointer' }}>✕</button>
              </div>

              <div style={{ display: 'flex', gap: 20, marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!companyInfo.el_signed}
                    onChange={e => updateCompanyInfoField('el_signed', e.target.checked)}
                    style={{ accentColor: T.green, width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>EL Signed</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!companyInfo.aml_completed}
                    onChange={e => updateCompanyInfoField('aml_completed', e.target.checked)}
                    style={{ accentColor: T.green, width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>AML Completed</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!companyInfo.simple_client_info}
                    onChange={e => updateCompanyInfoField('simple_client_info', e.target.checked)}
                    style={{ accentColor: T.green, width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Simple Client Info</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Owner</span>
                  <select value={companyInfo.owner || ''} onChange={e => updateCompanyInfoField('owner', e.target.value || null)}
                    style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                    <option value="">—</option>
                    <option value="Victoria">Victoria</option>
                    <option value="Ruth">Ruth</option>
                    <option value="Aivan">Aivan</option>
                    <option value="Lara">Lara</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {COMPANY_INFO_FIELDS.map(([field, label, type]) => (
                  <div key={field} style={type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}>
                    <label style={ONB_LBL}>{label}</label>
                    {type === 'checkbox' ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!companyInfo[field]}
                          onChange={e => updateCompanyInfoField(field, e.target.checked)}
                          style={{ accentColor: T.accent, width: 15, height: 15, cursor: 'pointer' }} />
                        <span style={{ fontSize: 11, color: T.textSub }}>{companyInfo[field] ? 'Yes' : 'No'}</span>
                      </label>
                    ) : type === 'textarea' ? (
                      <textarea value={companyInfo[field] || ''} rows={2}
                        onChange={e => updateCompanyInfoField(field, e.target.value)}
                        style={{ ...ONB_INP, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: T.fontSans }} />
                    ) : (
                      <input type={type} value={companyInfo[field] || ''}
                        onChange={e => updateCompanyInfoField(field, e.target.value)}
                        style={{ ...ONB_INP, width: '100%', boxSizing: 'border-box' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
        {!nameSet && (
          <div style={{ fontSize: 10, color: T.amber, marginTop: 6 }}>
            Required before any step below can run
          </div>
        )}
        {nameSet && !research && !researchLoading && (
          <div style={{ fontSize: 10, color: T.muted, marginTop: 6 }}>
            Optional — searches Slack, email and Companies House, then pre-fills the steps below
          </div>
        )}

        {research?.error && (
          <div style={{ fontSize: 11, color: T.red, marginTop: 12 }}>⚠ {research.error}</div>
        )}

        {research?.proposal && (
          <div style={{ marginTop: 14, padding: 12, background: `${T.accent}0e`,
            border: `1px solid ${T.accent}44`, borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: research.restoredFromSaved ? T.green : T.muted,
              fontFamily: T.fontMono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              {research.restoredFromSaved
                ? `Restored previously saved scope${research.savedAt ? ` (${new Date(research.savedAt).toLocaleDateString('en-GB')})` : ''}`
                : 'Found — fields below pre-filled, please check'}
            </div>

            <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.8 }}>
              {research.proposal.legalEntityName && (
                <div>
                  <strong style={{ color: T.text }}>{research.proposal.legalEntityName}</strong>
                  {research.proposal.companiesHouseNumber && (
                    <span style={{ color: T.muted }}> · {research.proposal.companiesHouseNumber}</span>
                  )}
                  {research.proposal.legalEntityConfidence && (
                    <span style={{
                      marginLeft: 8, fontSize: 9, padding: '1px 6px', borderRadius: 3,
                      background: research.proposal.legalEntityConfidence === 'high' ? `${T.green}33`
                        : research.proposal.legalEntityConfidence === 'medium' ? `${T.amber}33` : `${T.red}33`,
                      color: research.proposal.legalEntityConfidence === 'high' ? T.green
                        : research.proposal.legalEntityConfidence === 'medium' ? T.amber : T.red,
                    }}>{research.proposal.legalEntityConfidence} confidence</span>
                  )}
                </div>
              )}
              {research.proposal.registeredAddress && (
                <div style={{ color: T.muted }}>{research.proposal.registeredAddress}</div>
              )}
              {research.proposal.servicesDiscussed?.length > 0 && (
                <div>Services: {research.proposal.servicesDiscussed.join(', ')}</div>
              )}
              {research.proposal.proposedFee && (
                <div>Fee discussed: <strong style={{ color: T.text }}>{research.proposal.proposedFee}</strong></div>
              )}
            </div>

            {research.proposal.feeReasoning?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, color: T.muted, marginBottom: 4 }}>
                  Proposed fees — check these before using
                </div>
                {research.proposal.feeReasoning.map((o, i) => (
                  <div key={i} style={{ fontSize: 11, color: T.textSub }}>· {o}</div>
                ))}
              </div>
            )}
            {research.proposal.taxComplianceOpportunities?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, color: T.muted, marginBottom: 4 }}>Tax / compliance opportunities</div>
                {research.proposal.taxComplianceOpportunities.map((o, i) => (
                  <div key={i} style={{ fontSize: 11, color: T.textSub }}>· {o}</div>
                ))}
              </div>
            )}

            {research.proposal.uncertainties?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, color: T.amber, marginBottom: 4 }}>Couldn't determine</div>
                {research.proposal.uncertainties.map((u, i) => (
                  <div key={i} style={{ fontSize: 11, color: T.muted }}>· {u}</div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 10, fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>
              Sources: {research.sources?.slack?.messagesFound ?? 0} Slack ·{' '}
              {research.sources?.gmail?.messagesFound ?? 0} email ·{' '}
              {research.sources?.companiesHouse?.matches?.length ?? 0} Companies House
              {research.sources?.slack?.historyScan?.channelsSearched?.length > 0 && (
                <span style={{ color: T.muted }}>
                  {' '}· scanned {research.sources.slack.historyScan.channelsSearched.join(', ')}
                </span>
              )}
              {research.sources?.slack?.authError && (
                <span style={{ color: T.red }}> · Slack auth failed: {research.sources.slack.authError}</span>
              )}
              {research.proposal?._usage?.estimatedCostGBP != null && (
                <span style={{ color: T.muted }}> · ~£{research.proposal._usage.estimatedCostGBP}</span>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* 1. IHF QUOTE */}
      <OnbSection num="1" title="Project Scope"
        subtitle="Scope and fees for this client. Saves automatically as you type.">

        {/* --- In-House Finance --- */}
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 12 }}>
            In-House Finance
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {[
              ['transactions', 'Transactions / month'],
              ['supplierInvoices', 'Supplier invoices / month'],
              ['salesInvoices', 'Sales invoices / month'],
              ['employees', 'Employees'],
            ].map(([key, label]) => (
              <div key={key}>
                <label style={ONB_LBL}>{label}</label>
                <input type="text" inputMode="numeric"
                  value={fmtMoneyInput(quoteInputs[key])}
                  onChange={e => setQuoteInputs(v => ({ ...v, [key]: parseMoneyInput(e.target.value) }))}
                  onBlur={saveScope}
                  style={{ ...ONB_INP, width: 110 }} />
              </div>
            ))}
          </div>

          <label style={ONB_LBL}>Scope included</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
            gap: '6px 14px', marginBottom: 16 }}>
            {IHF_SCOPE_ITEMS.map(item => (
              <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 7,
                fontSize: 11, color: T.textSub, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!scopeTicks[item]}
                  onChange={e => { setScopeTicks(t => ({ ...t, [item]: e.target.checked })); scheduleSave(); }}
                  style={{ accentColor: T.accent, width: 14, height: 14, cursor: 'pointer' }} />
                {item}
              </label>
            ))}
          </div>

          <button onClick={generateQuote} disabled={!nameSet || quoteLoading}
            style={onbBtn(nameSet && !quoteLoading)}>
            {quoteLoading ? 'Generating…' : 'Generate Quote →'}
          </button>

          {quoteResult?.error && (
            <div style={{ fontSize: 11, color: T.red, marginTop: 12 }}>⚠ {quoteResult.error}</div>
          )}
          {quoteResult?.quote && (
            <div style={{ marginTop: 14, padding: 12, background: `${T.green}12`,
              border: `1px solid ${T.green}44`, borderRadius: 6, fontSize: 12, color: T.text }}>
              <div>
                Calculated: <strong>{fmtGBPExact(quoteResult.quote.ihfFee ?? quoteResult.quote.monthlyTotal)}</strong> In-House Finance
                {quoteResult.quote.payrollFee != null && (
                  <> · <strong>{fmtGBPExact(quoteResult.quote.payrollFee)}</strong> Payroll</>
                )}
                <span style={{ color: T.muted, fontSize: 11 }}> / month ({quoteResult.quote.size} tier)</span>
              </div>
              <a href={quoteResult.spreadsheetUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: T.accentLt, marginTop: 6, display: 'inline-block' }}>
                Open quote sheet →
              </a>
            </div>
          )}

          {/* Proposed fees - what we actually quote, which may differ from calculated */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16,
            paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <div>
              <label style={ONB_LBL}>Proposed IHF fee / month</label>
              <input type="text" inputMode="decimal"
                value={fmtMoneyInput(scopeData.ihf_proposed_fee)}
                onChange={e => setScopeData(d => ({ ...d, ihf_proposed_fee: parseMoneyInput(e.target.value) }))}
                onBlur={saveScope} placeholder="0"
                style={{ ...ONB_INP, width: 140 }} />
            </div>
            <div>
              <label style={ONB_LBL}>Proposed payroll fee / month</label>
              <input type="text" inputMode="decimal"
                value={fmtMoneyInput(scopeData.ihf_proposed_payroll_fee)}
                onChange={e => setScopeData(d => ({ ...d, ihf_proposed_payroll_fee: parseMoneyInput(e.target.value) }))}
                onBlur={saveScope} placeholder="0"
                style={{ ...ONB_INP, width: 150 }} />
            </div>
          </div>
        </Card>

        {/* --- Annual fixed fees --- */}
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 12 }}>
            Annual Fixed Fees
          </div>

          {[
            ['stat_accounts', 'stat_accounts_fee', 'Statutory Accounts'],
            ['vgec', 'vgec_fee', 'VGEC'],
            ['corp_tax', 'corp_tax_fee', 'Corporation Tax Services'],
            ['emi_valuation', 'emi_valuation_fee', 'EMI Valuation'],
            ['emi_notifications', 'emi_notifications_fee', 'EMI Notifications'],
            ['seis_eis', 'seis_eis_fee', 'SEIS/EIS Applications'],
          ].map(([tickKey, feeKey, label]) => (
            <div key={tickKey} style={{ display: 'flex', alignItems: 'center', gap: 12,
              marginBottom: 10, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                color: T.text, cursor: 'pointer', minWidth: 210 }}>
                <input type="checkbox" checked={!!scopeData[tickKey]}
                  onChange={e => { setScopeData(d => ({ ...d, [tickKey]: e.target.checked })); scheduleSave(); }}
                  style={{ accentColor: T.accent, width: 15, height: 15, cursor: 'pointer' }} />
                {label}
              </label>

              {scopeData[tickKey] && (
                <>
                  <input type="text" inputMode="decimal"
                    value={fmtMoneyInput(scopeData[feeKey])}
                    onChange={e => setScopeData(d => ({ ...d, [feeKey]: parseMoneyInput(e.target.value) }))}
                    onBlur={saveScope} placeholder="Fee net of VAT"
                    style={{ ...ONB_INP, width: 140 }} />
                  {tickKey === 'vgec' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11,
                      color: T.textSub, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!scopeData.vgec_bfi_included}
                        onChange={e => { setScopeData(d => ({ ...d, vgec_bfi_included: e.target.checked })); scheduleSave(); }}
                        style={{ accentColor: T.accent, width: 14, height: 14, cursor: 'pointer' }} />
                      BFI support included
                    </label>
                  )}
                </>
              )}
            </div>
          ))}
        </Card>

        {/* --- Hourly rate card --- */}
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 4 }}>
            Hourly Rate Card
          </div>
          <div style={{ fontSize: 10, color: T.muted, marginBottom: 12 }}>
            Standard rates — edit if discounted for this client
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              ['rate_partner', 'Partner'],
              ['rate_director', 'Director'],
              ['rate_manager', 'Manager'],
              ['rate_senior', 'Senior'],
            ].map(([key, label]) => (
              <div key={key}>
                <label style={ONB_LBL}>{label}</label>
                <input type="text" inputMode="decimal"
                  value={fmtMoneyInput(scopeData[key])}
                  onChange={e => setScopeData(d => ({ ...d, [key]: parseMoneyInput(e.target.value) }))}
                  onBlur={saveScope}
                  style={{ ...ONB_INP, width: 100 }} />
              </div>
            ))}
          </div>
        </Card>

        {/* Save state + send to billing */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: saveState === 'saved' ? T.green : saveState === 'saving' ? T.muted : T.amber,
            fontFamily: T.fontMono }}>
            {saveState === 'saved' ? '✓ Saved' : saveState === 'saving' ? 'Saving…' : saveState === 'error' ? '⚠ Save failed' : ''}
          </span>
          <button onClick={sendFeesToBilling} disabled={!nameSet || billingSendState === 'sending'}
            style={onbBtn(nameSet && billingSendState !== 'sending', T.accentLt)}>
            {billingSendState === 'sending' ? 'Sending…'
              : billingSendState === 'sent' ? '✓ Sent to Billing'
              : 'Send Fixed Fees to Billing →'}
          </button>
          {billingSendError && (
            <span style={{ fontSize: 11, color: T.red }}>⚠ {billingSendError}</span>
          )}
        </div>
      </OnbSection>

      {/* 2. XERO + DRIVE */}
      <OnbSection num="2" title="Create in Xero + Drive" accent={T.green}
        subtitle="Contact, Client tracking dimension, project from template, and the Drive client folder.">
        <Card style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ flex: '1 1 280px' }}>
              <label style={ONB_LBL}>Registered address</label>
              <textarea value={xeroAddress} onChange={e => setXeroAddress(e.target.value)}
                placeholder={'14 Example Street\nLondon\nEC1V 9AA'} rows={3}
                style={{ ...ONB_INP, width: '100%', boxSizing: 'border-box', resize: 'vertical',
                  fontFamily: T.fontSans }} />
            </div>
            <div style={{ flex: '0 1 240px' }}>
              <label style={ONB_LBL}>Contact email (optional)</label>
              <input value={xeroEmail} onChange={e => setXeroEmail(e.target.value)}
                placeholder="finance@client.com" style={{ ...ONB_INP, width: '100%', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div>
              <label style={ONB_LBL}>Referral source</label>
              <select value={referralSource} onChange={e => setReferralSource(e.target.value)}
                style={{ ...ONB_INP, width: 180 }}>
                <option value="">— none —</option>
                {(referrals || []).map(r => (
                  <option key={r.name || r} value={r.name || r}>{r.name || r}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={ONB_LBL}>Original business area</label>
              <select value={originalBA} onChange={e => setOriginalBA(e.target.value)}
                style={{ ...ONB_INP, width: 180 }}>
                <option value="">— none —</option>
                <option>Strategic Finance</option>
                <option>Legal</option>
                <option>In-House Finance</option>
                <option>Tax</option>
              </select>
            </div>
            <div>
              <label style={ONB_LBL}>VAT treatment</label>
              <select value={ukRegistered ? 'uk' : 'non_uk'}
                onChange={e => setUkRegistered(e.target.value === 'uk')}
                style={{ ...ONB_INP, width: 190 }}>
                <option value="uk">UK — 20% standard</option>
                <option value="non_uk">Non-UK — zero rated</option>
              </select>
            </div>
          </div>

          <button onClick={runXeroSetup} disabled={!nameSet || xeroLoading}
            style={onbBtn(nameSet && !xeroLoading, T.green)}>
            {xeroLoading ? 'Creating…' : 'Create in Xero + Drive →'}
          </button>

          {xeroResult && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {xeroResult.error && <div style={{ fontSize: 11, color: T.red, wordBreak: 'break-word' }}>⚠ {xeroResult.error}</div>}
              {['contact', 'trackingOption', 'project', 'driveFolder', 'billingConfig', 'companyInfo', 'clientMapping'].map(k => {
                const r = xeroResult[k];
                if (!r) return null;
                const label = { contact: 'Xero contact', trackingOption: 'Client dimension', project: 'Xero project', driveFolder: 'Drive folder', billingConfig: 'Billing config', companyInfo: 'Company info', clientMapping: 'Client mapping' }[k];
                return (
                  <div key={k} style={{ fontSize: 11,
                    color: r.error ? T.red : r.created ? T.green : T.muted }}>
                    {r.error ? '⚠' : r.created ? '✓' : '•'} {label}:{' '}
                    {r.error ? r.error
                      : r.skipped ? `skipped (${r.skipped})`
                      : r.created ? 'created' : 'already existed'}
                    {k === 'project' && r.tasksCopied !== undefined &&
                      ` — ${r.tasksCopied}/${r.tasksAvailable} tasks copied`}
                    {k === 'project' && r.rateOverrides?.length > 0 && (
                      <div style={{ fontSize: 10, color: T.amber, marginLeft: 14 }}>
                        Partner rate applied: {r.rateOverrides.join('; ')}
                      </div>
                    )}
                    {k === 'driveFolder' && r.url && (
                      <a href={r.url} target="_blank" rel="noopener noreferrer"
                        style={{ color: T.accentLt, marginLeft: 8 }}>open →</a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </OnbSection>

      {/* 3. ENGAGEMENT LETTER */}
      <OnbSection num="3" title="Create Engagement Letter" accent={T.amber}
        subtitle="Fills the master template and saves it to the client's 1. Client Setup folder.">
        <Card style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div>
              <label style={ONB_LBL}>Signatory</label>
              <select value={letterFields.signatory}
                onChange={e => setLetterFields(f => ({ ...f, signatory: e.target.value }))}
                style={{ ...ONB_INP, width: 160 }}>
                <option>Steve Tinkler</option>
                <option>Felix Wong</option>
              </select>
            </div>
            <div>
              <label style={ONB_LBL}>Client contact (first name)</label>
              <input value={letterFields.clientContact}
                onChange={e => setLetterFields(f => ({ ...f, clientContact: e.target.value }))}
                placeholder="Jamie" style={{ ...ONB_INP, width: 150 }} />
            </div>
            <div>
              <label style={ONB_LBL}>Client email</label>
              <input value={letterFields.clientEmail}
                onChange={e => setLetterFields(f => ({ ...f, clientEmail: e.target.value }))}
                placeholder="jamie@client.com" style={{ ...ONB_INP, width: 200 }} />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
            color: T.text, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={letterFields.includeIhfScope}
              onChange={e => setLetterFields(f => ({ ...f, includeIhfScope: e.target.checked }))}
              style={{ accentColor: T.accent, width: 15, height: 15, cursor: 'pointer' }} />
            Include In-House Finance scope
            <span style={{ fontSize: 10, color: T.muted }}>
              (uses the boxes ticked in step 1)
            </span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
            color: T.text, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={letterFields.includeTaxSchedule}
              onChange={e => setLetterFields(f => ({ ...f, includeTaxSchedule: e.target.checked }))}
              style={{ accentColor: T.accent, width: 15, height: 15, cursor: 'pointer' }} />
            Include Tax Services schedules
            <span style={{ fontSize: 10, color: T.muted }}>
              (set automatically from the tax services ticked above)
            </span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
            color: T.text, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={letterFields.includeAdvisory}
              onChange={e => setLetterFields(f => ({ ...f, includeAdvisory: e.target.checked }))}
              style={{ accentColor: T.accent, width: 15, height: 15, cursor: 'pointer' }} />
            Include Advisory Scope
            <span style={{ fontSize: 10, color: T.muted }}>
              (T&amp;M or fixed-fee advisory work, separate from the IHF retainer)
            </span>
          </label>

          {letterFields.includeAdvisory && (
            <div style={{ marginBottom: 14 }}>
              <label style={ONB_LBL}>Advisory scope of work</label>
              <textarea value={letterFields.scopeText}
                onChange={e => setLetterFields(f => ({ ...f, scopeText: e.target.value }))}
                placeholder="e.g. Financial model build presenting a profit and loss, balance sheet and cash flow for a five year period."
                rows={3}
                style={{ ...ONB_INP, width: '100%', maxWidth: 620, boxSizing: 'border-box',
                  resize: 'vertical', fontFamily: T.fontSans }} />
            </div>
          )}

          {letterFields.includeAdvisory && (
          <div style={{ marginBottom: 14 }}>
            <label style={ONB_LBL}>Advisory fees</label>
            <textarea value={letterFields.feeText}
              onChange={e => setLetterFields(f => ({ ...f, feeText: e.target.value }))}
              onBlur={e => {
                // Add thousands separators to any bare number over 999 - £6000 becomes £6,000
                const withCommas = e.target.value.replace(
                  /£\s?(\d{4,})(?![\d,])/g,
                  (_, n) => '£' + Number(n).toLocaleString('en-GB')
                );
                if (withCommas !== e.target.value) {
                  setLetterFields(f => ({ ...f, feeText: withCommas }));
                }
              }}
              placeholder="e.g. £6,000 + VAT&#10;or: A monthly cost of £4,125 (plus VAT), reviewed quarterly."
              rows={2}
              style={{ ...ONB_INP, width: '100%', maxWidth: 620, boxSizing: 'border-box',
                resize: 'vertical', fontFamily: T.fontSans }} />
          </div>
          )}

          <button onClick={createLetter} disabled={!nameSet || letterLoading}
            style={onbBtn(nameSet && !letterLoading, T.amber)}>
            {letterLoading ? 'Creating…' : 'Create Engagement Letter →'}
          </button>

          {letterResult?.error && (
            <div style={{ fontSize: 11, color: T.red, marginTop: 12 }}>⚠ {letterResult.error}</div>
          )}
          {letterResult?.documentId && (
            <div style={{ marginTop: 14, padding: 12, background: `${T.amber}12`,
              border: `1px solid ${T.amber}44`, borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: T.text, marginBottom: 6 }}>
                ✓ Letter created in <strong>1. Client Setup</strong>
              </div>
              {letterResult.warning && (
                <div style={{ fontSize: 10, color: T.amber, marginBottom: 6 }}>
                  ⚠ {letterResult.warning}
                </div>
              )}
              <a href={letterResult.documentUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: T.accentLt, marginRight: 12 }}>Open letter →</a>
              <a href={letterResult.folderUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: T.accentLt }}>Open folder →</a>

              {letterResult.review && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.amber}33` }}>
                  {letterResult.review.clean === true && (
                    <div style={{ fontSize: 11, color: T.green }}>
                      ✓ AI review found no formatting/sequencing issues
                      {letterResult.review.estimatedCostGBP != null && (
                        <span style={{ color: T.muted }}> (~£{letterResult.review.estimatedCostGBP})</span>
                      )}
                    </div>
                  )}
                  {letterResult.review.clean === false && (
                    <div>
                      <div style={{ fontSize: 11, color: T.red, fontWeight: 700, marginBottom: 4 }}>
                        ⚠ AI review flagged {letterResult.review.issues?.length || 0} issue(s) - check before sending:
                      </div>
                      {(letterResult.review.issues || []).map((issue, i) => (
                        <div key={i} style={{ fontSize: 11, color: T.textSub, marginLeft: 8 }}>• {issue}</div>
                      ))}
                    </div>
                  )}
                  {letterResult.review.clean === null && (
                    <div style={{ fontSize: 10, color: T.muted }}>AI review could not be parsed - check the letter manually.</div>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
            <label style={ONB_LBL}>Professional Clearance Letter</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
              color: T.text, cursor: 'pointer', margin: '8px 0' }}>
              <input type="checkbox" checked={includeAdditionalCompanies}
                onChange={e => setIncludeAdditionalCompanies(e.target.checked)}
                style={{ accentColor: T.accent, width: 15, height: 15, cursor: 'pointer' }} />
              Include additional companies
            </label>
            {includeAdditionalCompanies && (
              <textarea value={additionalCompaniesText}
                onChange={e => setAdditionalCompaniesText(e.target.value)}
                placeholder={'One company name per line, e.g.\nSleepy Cat Limited'}
                rows={2}
                style={{ ...ONB_INP, width: '100%', maxWidth: 400, boxSizing: 'border-box',
                  resize: 'vertical', fontFamily: T.fontSans, marginBottom: 10 }} />
            )}
            <div>
              <button onClick={createProfClearance} disabled={!nameSet || profClearanceLoading}
                style={onbBtn(nameSet && !profClearanceLoading, T.mutedLt)}>
                {profClearanceLoading ? 'Creating…' : 'Create Prof Clearance Letter →'}
              </button>
            </div>
            {profClearanceResult?.error && (
              <div style={{ fontSize: 11, color: T.red, marginTop: 10 }}>⚠ {profClearanceResult.error}</div>
            )}
            {profClearanceResult?.documentId && (
              <div style={{ marginTop: 10, fontSize: 11 }}>
                ✓ Created for: {profClearanceResult.allEntities?.join(', ')}
                {' — '}
                <a href={profClearanceResult.documentUrl} target="_blank" rel="noopener noreferrer"
                  style={{ color: T.accentLt }}>Open →</a>
              </div>
            )}
          </div>
        </Card>
      </OnbSection>

      {/* 4. SEND FOR SIGNATURE */}
      <OnbSection num="4" title="Send for Signature" accent={T.muted}
        subtitle="Sends via Adobe Sign; the signed copy files itself back to the client folder.">
        <Card style={{ padding: 16, opacity: 0.65 }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 12 }}>
            Not yet connected — needs Adobe Sign API credentials before this can work.
            Until then, download the letter from step 3 and send it from Adobe directly.
          </div>
          <button disabled style={onbBtn(false)}>Send for Signature →</button>
        </Card>
      </OnbSection>

      {/* 5. AML */}
      <OnbSection num="5" title="Collate AML Information" accent={T.muted}
        subtitle="ICAEW requirement for bookkeeping and tax clients: proof of ID and recent utility bill for each director / owner.">
        <Card style={{ padding: 16, opacity: 0.65 }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 12 }}>
            Not yet built. Intended to pull the officer list from Companies House,
            work out who needs to provide what, and draft the request — collection
            and verification stay manual, which is appropriate for AML.
          </div>
          <button disabled style={onbBtn(false)}>Collate AML Info →</button>
        </Card>
      </OnbSection>

      <OnbSection num="6" title="Missing Engagement Letters" accent={T.amber}
        subtitle="Checks every revenue-earning client for a signed letter on file. Runs automatically Sunday night, or on demand here.">
        <Card style={{ padding: 16 }}>
          <button onClick={runMissingLettersScan} disabled={missingLettersScanning}
            style={onbBtn(!missingLettersScanning, T.amber)}>
            {missingLettersScanning ? 'Scanning…' : 'Scan for Missing Letters →'}
          </button>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 8 }}>
            Checks the last 90 days of revenue-earning clients against their Drive folder.
            Detects a genuinely signed letter by finding a real name filled into the
            Print Name field, not just the word "signed" appearing anywhere.
          </div>

          {missingLettersResults && (
            <div style={{ marginTop: 14 }}>
              {(() => {
                const problems = missingLettersResults.filter(r => r.status !== 'signed');
                if (!problems.length) {
                  return <div style={{ fontSize: 12, color: T.green }}>✓ Every revenue-earning client has a signed letter on file.</div>;
                }
                const labels = {
                  no_folder: 'No Drive folder found',
                  no_setup_folder: 'No "1. Client Setup" subfolder',
                  no_letter: 'No engagement letter found',
                  unsigned: 'Letter found, signature looks blank',
                  unverified: 'PDF/Word - could not verify, check manually',
                  error: 'Error checking this client',
                };
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, color: T.amber, marginBottom: 4 }}>
                      {problems.length} of {missingLettersResults.length} clients need a look
                    </div>
                    {problems.map((r, i) => (
                      <div key={i} style={{ fontSize: 12, padding: '8px 10px', background: T.raised, borderRadius: 5 }}>
                        <div style={{ color: T.text, fontWeight: 600 }}>{r.client}</div>
                        <div style={{ color: r.status === 'unverified' ? T.amber : T.red, fontSize: 11 }}>
                          {labels[r.status] || r.status}
                        </div>
                        {r.evidence && <div style={{ color: T.muted, fontSize: 10 }}>{r.evidence}</div>}
                        {r.folderUrl && (
                          <a href={r.folderUrl} target="_blank" rel="noopener noreferrer"
                            style={{ color: T.accentLt, fontSize: 10 }}>Open folder →</a>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </Card>
      </OnbSection>
    </div>
  );
}

// Defined at module level deliberately - a component declared inside another
// component's render function is recreated on every render, which forces
// React to unmount and remount everything inside it (including any input),
// losing focus after every single keystroke. Same class of bug fixed earlier
// on the Onboarding tab's Section/inputs.
function TD2({ children, right, center, bold, color, style = {}, colSpan }) {
  return (
    <td colSpan={colSpan} style={{ textAlign: right ? 'right' : center ? 'center' : 'left', padding: '6px 10px',
      color: color || T.text, fontWeight: bold ? 700 : 400,
      fontFamily: right ? T.fontMono : T.fontSans, fontSize: 11,
      borderBottom: `1px solid ${T.border}22`, ...style }}>
      {children}
    </td>
  );
}

function TH2({ children, right, center, style = {} }) {
  return (
    <th style={{ textAlign: right ? 'right' : center ? 'center' : 'left', padding: '8px 10px',
      color: T.muted, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em',
      fontWeight: 600, fontFamily: T.fontMono, whiteSpace: 'nowrap', background: T.raised,
      borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, ...style }}>
      {children}
    </th>
  );
}

function isPayrollFee(f) {
  const s = `${f.task_name || ''} ${f.service_line || ''} ${f.description || ''}`.toLowerCase();
  return s.includes('payroll');
}
// Tax/Accounts fees are annual, billed on a completely different cadence to
// the monthly IHF retainer - they must never share the same bill decision.
function isTaxAccountsFee(f) {
  const s = `${f.task_name || ''} ${f.service_line || ''} ${f.description || ''}`.toLowerCase();
  return s.includes('tax compliance') || s.includes('vgec') || s.includes('corporation tax')
    || s.includes('emi') || s.includes('statutory accounts') || s.includes('annual tax')
    || s.includes('seis') || s.includes('eis');
}
function fixedFeeDescription(f) {
  if (isPayrollFee(f) && f.headcount) return `${f.description} (${f.headcount} people)`;
  return f.description;
}
// Same suffix-agnostic matching used everywhere else tonight, for grouping
// "CodeDev Group Ltd" and "CodeDev Group Limited" as one client rather than
// two separate rows.
function normaliseClientName(s) {
  return (s || '').toLowerCase()
    .replace(/\b(limited|ltd|llp|llc|inc|plc|gmbh|corp|co)\b\.?/g, '')
    .replace(/[^a-z0-9]/g, '').trim();
}
// Groups a {contact: [...]} map by normalised name (merging Ltd/Limited
// variants into one entry, combining their arrays) and returns entries
// sorted alphabetically by display name.
function groupAndSortByClient(feeMap) {
  const grouped = {};
  for (const [contact, items] of Object.entries(feeMap)) {
    const key = normaliseClientName(contact);
    if (!grouped[key]) grouped[key] = { displayName: contact, contacts: [], items: [] };
    grouped[key].contacts.push(contact);
    grouped[key].items.push(...items);
  }
  return Object.values(grouped).sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function BillingTab() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncCheck, setSyncCheck] = useState(null); // {expectedMinutes, savedMinutes, stoppedEarly, at}
  const [recharges, setRecharges] = useState([]);          // filing fees etc. to pass on to clients
  const [rechargeAccount, setRechargeAccount] = useState('');
  const [rechargesMissing, setRechargesMissing] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [taskBaMapping, setTaskBaMapping] = useState({});
  const [contactBillingMap, setContactBillingMap] = useState({});
  const [serviceOptions, setServiceOptions] = useState([]);
  const [trackingCategories, setTrackingCategories] = useState({}); // eslint-disable-line no-unused-vars
  const [fixedFeeMap, setFixedFeeMap] = useState({});
  // Contacts start collapsed - after a full data import this table can have
  // many hundreds of task rows across the whole firm, and rendering all of
  // them into the DOM at once (each row genuinely heavy: multiple cells,
  // conditional buttons, an input that forces a remount on certain changes)
  // is what was grinding the browser to a halt, not the data computation
  // itself. Only expanded contacts' rows actually exist in the DOM.
  const [expandedContacts, setExpandedContacts] = useState(() => new Set());
  const [allContacts, setAllContacts] = useState([]);
  const [selections, setSelections] = useState({});
  const [invoiceResults, setInvoiceResults] = useState({});
  const [fixedFeeDecisions, setFixedFeeDecisions] = useState({}); // contact -> 'bill'|'accrue'|'write_off'|null - In-House Finance / Payroll only
  const [taxFeeDecisions, setTaxFeeDecisions] = useState({}); // contact -> 'bill'|'accrue'|'write_off'|null - Tax / Accounts (annual) only

  // Memoized - grouping/sorting fixedFeeMap is real work (merging Ltd/Limited
  // variants, sorting alphabetically) and was previously recomputed on every
  // single render, including every decision toggle click. Only recompute
  // when the fee data itself changes.
  const groupedFixedFeeClients = useMemo(() => groupAndSortByClient(fixedFeeMap), [fixedFeeMap]);


  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [syncProgress, setSyncProgress] = useState({ done: 0, total: null });
  const [baFilter, setBaFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All'); // All | bill | accrue | write_off | blank
  const [newFee, setNewFee] = useState({ contact_name:'', description:'', monthly_amount:'', service_line:'', nominal_code:'203', vat_group:'20_standard' });
  const [addingFee, setAddingFee] = useState(false);

  // "Bill up until" — if within first 7 days of month, default to prior month
  // (still in the billing window for last month), otherwise current month
  const defaultBillingMonth = (() => {
    const now = new Date();
    if (now.getDate() <= 7) {
      // First 7 days — default to previous month
      return new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 7);
    }
    return currentMonthKey();
  })();
  const [billingUntil, setBillingUntil] = useState(defaultBillingMonth);
  const billingMonth = billingUntil || defaultBillingMonth;

  // Selection key: consolidated per contact+task (no per-employee split in this view)
  function selKey(contact, task) { return `${contact}::__all__::${task}`; }

  function getSel(contact, task) {
    return selections[selKey(contact, task)] || {};
  }

  async function saveSel(contact, task, patch) {
    const key = selKey(contact, task);
    const existing = selections[key] || {};
    // When decision changes, auto-set mark_all_as_billed accordingly (user can still override)
    if ('billing_decision' in patch) {
      const d = patch.billing_decision;
      patch.mark_all_as_billed = (d === 'bill' || d === 'write_off');
    }
    const updated = { ...existing, ...patch };
    setSelections(s => ({ ...s, [key]: updated }));
    const { error } = await supabase.from('billing_selections').upsert({
      contact_name: contact, employee_name: '__all__', task_name: task,
      billing_decision: updated.billing_decision ?? null,
      amount_basis: updated.amount_basis ?? 'full',
      billable_amount: updated.billable_amount ?? null,
      billable_hours: updated.billable_hours ?? null,
      mark_all_as_billed: updated.mark_all_as_billed ?? false,
      line_description: updated.line_description ?? null,
      nominal_code: updated.nominal_code ?? null,
      service_line: updated.service_line ?? null,
      vat_group: updated.vat_group ?? null,
      client_dimension: updated.client_dimension ?? null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'contact_name,employee_name,task_name' });
    if (error) {
      console.error('[saveSel] Supabase write failed:', error);
      setError(`Save failed: ${error.message}${error.message?.includes('column') ? ' — run the amount_basis migration in Supabase' : ''}`);
    }
  }

  async function loadSelections() {
    const { data } = await supabase.from('billing_selections').select('*');
    const map = {};
    (data || []).forEach(s => {
      map[`${s.contact_name}::${s.employee_name}::${s.task_name}`] = s;
    });
    setSelections(map);
  }

  async function loadFromCache() {
    setLoading(true); setError(null);
    try {
      const res = await fetch('/api/billing/get-cache');
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Failed'); setLoading(false); return; }
      setRows(data.rows || []);
      setLastSyncedAt(data.lastSyncedAt);
      const tm = {}; (data.taskBusinessAreaMapping || []).forEach(m => { tm[m.task_name] = m; }); setTaskBaMapping(tm);
      const cm = {}; (data.contactBillingConfig || []).forEach(c => { cm[c.contact_name] = c; }); setContactBillingMap(cm);
      const ffm = {}; (data.fixedFees || []).forEach(f => { if (!ffm[f.contact_name]) ffm[f.contact_name] = []; ffm[f.contact_name].push(f); }); setFixedFeeMap(ffm);
      // Service options for Add Fixed Fee form
      const svcOpts = (data.trackingCategories?.['Service']?.options || []);
      setServiceOptions(svcOpts);
      setTrackingCategories(data.trackingCategories || {});
      // All contacts for dropdown
      const names = [...new Set((data.rows || []).map(r => r.contact_name))].sort();
      setAllContacts(names);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadFromCache(); loadSelections(); loadFixedFeeDecisions(); loadRecharges(); }, []);

  async function loadRecharges() {
    const { data, error } = await supabase.from('client_recharges').select('*')
      .in('status', ['pending', 'include', 'billed']).order('incurred_date', { ascending: false });
    if (error) {
      // 42P01 = table not created yet
      setRechargesMissing(error.code === '42P01' || /does not exist|schema cache/i.test(error.message || ''));
      if (!/does not exist|schema cache/i.test(error.message || '')) console.error('[recharges] load failed:', error);
      setRecharges([]);
    } else { setRechargesMissing(false); setRecharges(data || []); }
    const { data: setting } = await supabase.from('firm_settings').select('*').eq('key', 'recharge_account_code').maybeSingle();
    setRechargeAccount((setting?.value || '').trim());
  }
  async function saveRechargeAccount(v) {
    setRechargeAccount(v);
    await supabase.from('firm_settings').upsert({ key: 'recharge_account_code', value: v }, { onConflict: 'key' });
  }

  async function loadFixedFeeDecisions() {
    const { data } = await supabase.from('fixed_fee_decisions').select('*');
    const ihf = {}, tax = {};
    (data || []).forEach(d => {
      if (d.fee_type === 'ihf') ihf[d.contact_name] = d.decision;
      else tax[d.contact_name] = d.decision;
    });
    setFixedFeeDecisions(ihf);
    setTaxFeeDecisions(tax);
  }

  async function saveFixedFeeDecision(feeType, contact, decision) {
    const { error } = await supabase.from('fixed_fee_decisions').upsert({
      contact_name: contact, fee_type: feeType, decision, updated_at: new Date().toISOString(),
    }, { onConflict: 'contact_name,fee_type' });
    if (error) console.error('[fixed_fee_decisions] save failed:', error);
  }

  // ONE sync, and it is always the full rebuild. The old primary "Sync Now" only
  // re-read the last 5 days and ADDED them to whatever was already cached, so after
  // a full resync early in the month it left the current month almost empty while
  // the previous month (never cleared) stayed large. A rebuild is now cheap enough
  // to do every time (the fetch asks Xero for uninvoiced time only), and the result
  // is checked against the hours Xero itself says are waiting to be invoiced.
  async function syncNow() {
    setSyncing(true); setError(null); setSyncCheck(null);
    let offset = 0; const limit = 50; let done = 0; let total = null;
    let expectedMinutes = null, savedMinutes = 0, stoppedEarly = false;
    const allFailures = [];
    const allUnresolved = [];
    try {
      while (true) {
        const res = await fetch(`/api/xero/test-unbilled-time?offset=${offset}&limit=${limit}&forceRefresh=true`);
        let data;
        try { data = await res.json(); }
        catch {
          setError(`The sync stopped part-way: the server didn't answer in time (after ${done} of ${total ?? '?'} projects). The saved time is incomplete - click Sync Now again.`);
          stoppedEarly = true; break;
        }
        if (!res.ok || data.error) { setError(data.error || 'Sync failed'); stoppedEarly = true; break; }
        done += data.projectsProcessed || 0;
        if (total === null) total = data.projectsWithUnbilledTime;
        if (expectedMinutes === null) expectedMinutes = data.totalMinutesExpected ?? null;
        setSyncProgress({ done, total });
        if (data.failureDetail?.length) {
          allFailures.push(...data.failureDetail.map(f => f.contact || f.project));
        }
        if (data.unresolvedTaskDetail?.length) allUnresolved.push(...data.unresolvedTaskDetail);
        const saveRows = buildSaveRows(data.projects || []);
        savedMinutes += saveRows.reduce((n, r) => n + (r.totalDurationMinutes || 0), 0);
        const saveRes = await fetch('/api/billing/save-cache', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows: saveRows, reset: offset === 0 }),
        });
        if (!saveRes.ok) {
          let sd = {}; try { sd = await saveRes.json(); } catch { /* not JSON */ }
          setError(sd.error || 'Saving to the cache failed'); stoppedEarly = true; break;
        }
        if (!data.hasMore) break;
        offset = data.nextOffset || offset + limit;
      }
    } catch (err) {
      setError(err.message); stoppedEarly = true;
    }
    if (allFailures.length) {
      setError(`Sync completed, but these clients failed to fetch and may show as having no unbilled time: ${[...new Set(allFailures)].join(', ')}. Try Sync Now again - if it keeps failing for the same client, it's worth investigating that one specifically.`);
    }
    if (expectedMinutes != null) setSyncCheck({ expectedMinutes, savedMinutes, stoppedEarly, unresolved: allUnresolved, at: new Date() });
    await loadFromCache(); setSyncing(false); setSyncProgress({ done: 0, total: null });
  }

  function buildSaveRows(projects) {
    const out = [];
    for (const p of projects) {
      for (const g of p.unbilledByEmployeeTask || []) {
        if (!g.totalDuration && !g.amount) continue;
        out.push({ contactName: p.contactName, employeeName: g.employeeName, taskName: g.taskName,
          month: g.month || 'unknown', totalDurationMinutes: g.totalDuration, uninvoicedAmount: Math.round((g.amount||0)*100)/100 });
      }
    }
    return out;
  }

  function businessAreaForTask(taskName) {
    return taskBaMapping[taskName]?.business_area || 'Needs Mapping';
  }

  // Build flat rows: one per contact+task, summing across employees and months
  const EXCLUDED = ['networking and pre-sales'];
  const flatRows = useMemo(() => {
    const map = {};
    for (const r of rows) {
      if (EXCLUDED.includes((r.contact_name || '').trim().toLowerCase())) continue;
      if (r.month > billingMonth) continue; // exclude months after billing cutoff
      const key = `${r.contact_name}::${r.task_name}`;
      if (!map[key]) map[key] = {
        contact: r.contact_name, task: r.task_name,
        ba: businessAreaForTask(r.task_name),
        prevAmount: 0, currentAmount: 0, prevMins: 0, currentMins: 0,
      };
      const isCurrent = r.month === billingMonth;
      if (isCurrent) { map[key].currentAmount += Number(r.uninvoiced_amount); map[key].currentMins += r.total_duration_minutes || 0; }
      else           { map[key].prevAmount    += Number(r.uninvoiced_amount); map[key].prevMins    += r.total_duration_minutes || 0; }
    }
    return Object.values(map).sort((a, b) => a.contact.localeCompare(b.contact) || a.task.localeCompare(b.task));
  }, [rows, billingMonth]);

  const baOptions = ['All', ...new Set(flatRows.map(r => r.ba))].filter(Boolean).sort();
  const displayRows = useMemo(() => flatRows.filter(r => {
    if (baFilter !== 'All' && r.ba !== baFilter) return false;
    if (statusFilter !== 'All') {
      const d = getSel(r.contact, r.task).billing_decision ?? null;
      if (statusFilter === 'blank' ? d !== null : d !== statusFilter) return false;
    }
    return true;
  }), [flatRows, baFilter, statusFilter, selections]);

  // Group by contact for subtotals
  const byContact = useMemo(() => {
    const map = {};
    for (const r of displayRows) {
      if (!map[r.contact]) map[r.contact] = [];
      map[r.contact].push(r);
    }
    return map;
  }, [displayRows]);

  // Combined totals across BOTH fixed fees and T&M time - uses flatRows
  // (every client) rather than the filtered displayRows, since these totals
  // should reflect the whole picture regardless of whatever filter happens
  // to be applied on screen.
  const fixedFeeTotals = useMemo(() => {
    let total = 0, toBill = 0, toAccrue = 0;

    for (const { contacts, items } of groupedFixedFeeClients) {
      const contact = contacts[0];
      for (const f of items) {
        const amt = f.monthly_amount || 0;
        total += amt;
        const decision = isTaxAccountsFee(f) ? taxFeeDecisions[contact] : fixedFeeDecisions[contact];
        if (decision === 'bill') toBill += amt;
        else if (decision === 'accrue') toAccrue += amt;
      }
    }

    for (const row of flatRows) {
      const sel = getSel(row.contact, row.task);
      if (!sel.billing_decision) continue;
      const amt = computeAmount(sel, row) || 0;
      total += amt;
      if (sel.billing_decision === 'bill') toBill += amt;
      else if (sel.billing_decision === 'accrue') toAccrue += amt;
    }

    return { total, toBill, toAccrue };
  }, [groupedFixedFeeClients, fixedFeeDecisions, taxFeeDecisions, flatRows, selections]);

  // ── Recharges: included items become a line on the client's invoice ──
  const knownContactNames = useMemo(() => [...new Set([
    ...Object.keys(contactBillingMap), ...Object.keys(fixedFeeMap), ...flatRows.map(r => r.contact),
  ])].filter(Boolean).sort(), [contactBillingMap, fixedFeeMap, flatRows]);

  const includedRechargesFor = (contact) => recharges.filter(r =>
    r.status === 'include' && r.client_name && Number(r.amount) > 0 &&
    normaliseClientName(r.client_name) === normaliseClientName(contact));

  async function markRechargesBilled(contact, invoiceNumber) {
    const ids = includedRechargesFor(contact).map(r => r.id);
    if (!ids.length) return;
    const patch = { status: 'billed', billed_invoice: invoiceNumber || null, billed_at: new Date().toISOString() };
    const { error } = await supabase.from('client_recharges').update(patch).in('id', ids);
    if (error) console.error('[recharges] could not mark billed:', error);
    else setRecharges(rs => rs.map(r => ids.includes(r.id) ? { ...r, ...patch } : r));
  }

  function computeRate(row) {
    const totalMins = row.prevMins + row.currentMins;
    const totalAmt = row.prevAmount + row.currentAmount;
    return totalMins > 0 ? totalAmt / (totalMins / 60) : 0;
  }

  function amountBasis(sel) {
    // New field with backward-compat fallback from old boolean
    if (sel.amount_basis) return sel.amount_basis;
    return sel.bill_full_amount === false ? 'manual' : 'full';
  }

  function computeAmount(sel, row) {
    if (sel.billing_decision === 'write_off') return 0;
    if (sel.billable_amount != null) return sel.billable_amount;
    const basis = amountBasis(sel);
    if (basis === 'current_month') return row.currentAmount;
    return row.prevAmount + row.currentAmount; // full, or manual with no value entered yet
  }

  function computeHours(sel, row) {
    if (sel.billable_hours != null) return sel.billable_hours;
    const rate = computeRate(row);
    const amt = computeAmount(sel, row);
    return rate > 0 ? amt / rate : (row.prevMins + row.currentMins) / 60;
  }

  // Helper: build invoice dates
  function invoiceDates(contactCfg) {
    // Use the actual billing period being processed (e.g. "2026-08"), not
    // today's real date - the previous version always computed "end of the
    // CURRENT calendar month as of right now", which is wrong whenever
    // month-end processing happens on a different day than the last day of
    // the month actually being billed (running it a few days late, or a
    // long batch spanning midnight into a new month).
    const [y, m] = billingMonth.split('-').map(Number);
    const lastDay = new Date(y, m, 0); // day 0 of next month = last day of billingMonth
    // Format from LOCAL date parts directly - toISOString() converts
    // through UTC, which shifts this back a day whenever the browser is in
    // a timezone ahead of UTC (BST, UTC+1, in effect in the UK for most of
    // the year). This date represents a calendar day, not a UTC instant,
    // so it must never round-trip through UTC.
    const fmtLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const issueDate = fmtLocalDate(lastDay);
    const dueDateObj = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate() + (contactCfg.payment_terms_days ?? 7));
    const dueDate = fmtLocalDate(dueDateObj);
    return { issueDate, dueDate };
  }

  // Helper: periods from rows being billed for a contact
  function billedPeriods(contact) {
    const months = new Set(rows.filter(r => r.contact_name === contact && r.month <= billingMonth).map(r => r.month));
    return [...months];
  }

  const [draftSyncResults, setDraftSyncResults] = useState({});
  const [accrualResult, setAccrualResult] = useState(null);
  const [unsentInvoices, setUnsentInvoices] = useState(null);
  const [loadingUnsent, setLoadingUnsent] = useState(false);
  const [draftResults, setDraftResults] = useState(null);
  const [creatingDrafts, setCreatingDrafts] = useState(false);

  async function findUnsentInvoices() {
    setLoadingUnsent(true);
    setDraftResults(null);
    try {
      const res = await fetch(`/api/billing/find-unsent-invoices?month=${billingMonth}`);
      const data = await res.json();
      if (!res.ok || data.error) {
        setUnsentInvoices({ error: typeof data.error === 'string' ? data.error : JSON.stringify(data.error) });
      } else {
        setUnsentInvoices(data);
      }
    } catch (err) {
      setUnsentInvoices({ error: err.message });
    }
    setLoadingUnsent(false);
  }

  async function createEmailDrafts() {
    const ready = (unsentInvoices?.invoices || []).filter(i => i.hasEmails);
    if (!ready.length) return;
    setCreatingDrafts(true);
    setDraftResults(null);
    try {
      const res = await fetch('/api/billing/create-invoice-drafts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoices: ready }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setDraftResults({ error: typeof data.error === 'string' ? data.error : JSON.stringify(data.error) });
      } else {
        setDraftResults(data);
      }
    } catch (err) {
      setDraftResults({ error: err.message });
    }
    setCreatingDrafts(false);
  }

  const [postingAccrual, setPostingAccrual] = useState(false);

  // Collects every task/fixed-fee tagged "Accrue" into journal lines
  function buildAccrualLines() {
    const lines = [];
    // T&M tasks marked Accrue
    for (const row of flatRows) {
      const sel = getSel(row.contact, row.task);
      if (sel.billing_decision !== 'accrue') continue;
      const amount = computeAmount(sel, row);
      if (!amount) continue;
      const tc = taskBaMapping[row.task] || {};
      const cfg = contactBillingMap[row.contact] || {};
      lines.push({
        contactName: row.contact,
        description: row.task,
        amount,
        revenueAccount: tc.nominal_code || BA_NOMINAL_DEFAULTS[row.ba] || '201',
        serviceLine: sel.service_line || tc.service_line || '',
        clientDimension: cfg.client_dimension || '',
      });
    }
    // Fixed fee clients marked Accrue
    for (const [contact, decision] of Object.entries(fixedFeeDecisions)) {
      if (decision !== 'accrue') continue;
      const cfg = contactBillingMap[contact] || {};
      for (const f of (fixedFeeMap[contact] || [])) {
        if (isTaxAccountsFee(f)) continue; // these follow taxFeeDecisions, not fixedFeeDecisions
        if (!f.monthly_amount) continue;
        lines.push({
          contactName: contact,
          description: fixedFeeDescription(f),
          amount: f.monthly_amount,
          revenueAccount: f.nominal_code || '203',
          serviceLine: f.service_line || '',
          clientDimension: cfg.client_dimension || '',
        });
      }
    }
    // Tax/Accounts fees marked Accrue - separate decision, annual not monthly
    for (const [contact, decision] of Object.entries(taxFeeDecisions)) {
      if (decision !== 'accrue') continue;
      const cfg = contactBillingMap[contact] || {};
      for (const f of (fixedFeeMap[contact] || [])) {
        if (!isTaxAccountsFee(f)) continue;
        if (!f.monthly_amount) continue;
        lines.push({
          contactName: contact,
          description: fixedFeeDescription(f),
          amount: f.monthly_amount,
          revenueAccount: f.nominal_code || '203',
          serviceLine: f.service_line || '',
          clientDimension: cfg.client_dimension || '',
        });
      }
    }
    return lines;
  }

  async function postAccrualJournal() {
    const lines = buildAccrualLines();
    if (!lines.length) {
      setAccrualResult({ error: 'Nothing tagged as Accrue' });
      return;
    }
    setPostingAccrual(true);
    setAccrualResult(null);
    try {
      const res = await fetch('/api/billing/raise-accrual-journal', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: billingMonth, lines }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setAccrualResult({ error: typeof data.error === 'string' ? data.error : JSON.stringify(data.error) });
      } else {
        setAccrualResult(data);
      }
    } catch (err) {
      setAccrualResult({ error: err.message });
    }
    setPostingAccrual(false);
  }


  // Vat group -> Xero TaxType (matches raise-invoice.js)
  function xeroTaxTypeFor(vatGroup) {
    const map = { '20_standard': 'OUTPUT2', 'exempt': 'EXEMPTOUTPUT', 'zero_rated': 'ZERORATEDOUTPUT', 'outside_scope': 'NONE', 'no_vat': 'NONE' };
    return map[vatGroup] || 'OUTPUT2';
  }

  // Build the target line items we WANT for a contact, from our own billing
  // decisions - independent of whatever currently exists in the Xero draft.
  function buildTargetLines(contact) {
    const contactCfg = contactBillingMap[contact] || {};
    const targets = [];

    // Fixed fee lines (IHF) - only if marked Bill
    if (fixedFeeDecisions[contact] === 'bill') {
      for (const f of fixedFeeMap[contact] || []) {
        if (isTaxAccountsFee(f)) continue; // these follow taxFeeDecisions, not fixedFeeDecisions
        targets.push({
          description: fixedFeeDescription(f),
          quantity: 1,
          unitAmount: f.monthly_amount,
          amount: f.monthly_amount,
          accountCode: f.nominal_code || '203',
          taxType: xeroTaxTypeFor(f.vat_group || contactCfg.vat_group),
          serviceLine: f.service_line || '',
          clientDimension: contactCfg.client_dimension || '',
        });
      }
    }

    // Tax/Accounts fee lines (VGEC, Corp Tax, Stat Accounts, EMI) - annual,
    // separate decision entirely from the monthly IHF retainer above
    if (taxFeeDecisions[contact] === 'bill') {
      for (const f of fixedFeeMap[contact] || []) {
        if (!isTaxAccountsFee(f)) continue;
        targets.push({
          description: fixedFeeDescription(f),
          quantity: 1,
          unitAmount: f.monthly_amount,
          amount: f.monthly_amount,
          accountCode: f.nominal_code || '203',
          taxType: xeroTaxTypeFor(f.vat_group || contactCfg.vat_group),
          serviceLine: f.service_line || '',
          clientDimension: contactCfg.client_dimension || '',
        });
      }
    }

    // T&M lines - one per task marked Bill for this contact
    const contactRows = (byContact[contact] || []).filter(r => getSel(contact, r.task).billing_decision === 'bill');
    for (const row of contactRows) {
      const sel = getSel(contact, row.task);
      const amount = computeAmount(sel, row);
      const hours = computeHours(sel, row);
      const tc = taskBaMapping[row.task] || {};
      const rate = hours > 0 ? Math.round((amount / hours) * 100) / 100 : amount;
      targets.push({
        description: sel.line_description || row.task,
        quantity: Math.round(hours * 100000) / 100000 || 1,
        unitAmount: hours > 0 ? rate : amount,
        amount,
        accountCode: tc.nominal_code || BA_NOMINAL_DEFAULTS[row.ba] || '201',
        taxType: xeroTaxTypeFor(contactCfg.vat_group),
        serviceLine: sel.service_line || tc.service_line || '',
        clientDimension: contactCfg.client_dimension || '',
      });
    }

    // Recharges (filing fees etc.) - appended last, so they never displace a native time line
    for (const r of includedRechargesFor(contact)) {
      targets.push({
        description: r.description || 'Recharge', quantity: 1, unitAmount: Number(r.amount), amount: Number(r.amount),
        accountCode: rechargeAccount,
        taxType: r.vat_treatment === 'client' ? xeroTaxTypeFor(contactCfg.vat_group) : 'NONE',
        serviceLine: '', clientDimension: contactCfg.client_dimension || '',
      });
    }

    return targets;
  }


  // Creates drafts for every billed contact directly via the Xero API - no
  // manual step in Xero required. This is the actual zero-manual-access
  // workflow; raiseInvoice() already did all the work per contact, it just
  // never had a bulk button wired to it.
  const [exportingReview, setExportingReview] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  // Builds a permanent record of the current review (all T&M lines and
  // fixed fees, with whatever decisions have been made so far) and exports
  // it to a fresh Google Sheet. Intended to run the day before month-end,
  // so there's a durable snapshot independent of whatever changes later.
  async function exportReviewToSheet() {
    setExportingReview(true); setExportResult(null);
    try {
      const header = ['Contact', 'Type', 'Task/Fee', 'Decision', 'Amount', 'Hours', 'Service Line', 'Description'];
      const rows = [header];

      // T&M lines
      for (const [contact, contactRows] of Object.entries(byContact)) {
        for (const row of contactRows) {
          const sel = getSel(contact, row.task);
          if (!sel.billing_decision) continue; // only export lines someone has actually looked at
          const amount = computeAmount(sel, row);
          const hours = computeHours(sel, row);
          rows.push([
            contact, 'T&M', row.task, sel.billing_decision,
            amount || 0, Math.round((hours || 0) * 100) / 100,
            sel.service_line || '', sel.line_description || '',
          ]);
        }
      }

      // Fixed fees
      for (const [contact, fees] of Object.entries(fixedFeeMap)) {
        for (const f of fees) {
          rows.push([
            contact, 'Fixed Fee', f.description, fixedFeeDecisions[contact] || '(not set)',
            f.monthly_amount || 0, '', f.service_line || '', f.description,
          ]);
        }
      }

      if (rows.length <= 1) {
        setExportResult({ error: 'Nothing to export yet - make some Bill/Accrue/Write Off decisions first.' });
        setExportingReview(false);
        return;
      }

      const res = await fetch('/api/billing/export-review', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: billingMonth, rows }),
      });
      const data = await res.json();
      setExportResult(res.ok && !data.error ? data : { error: data.error || 'Export failed' });
    } catch (err) {
      setExportResult({ error: err.message });
    }
    setExportingReview(false);
  }

  // Clears every bill/accrue/write-off decision so the review starts clean
  // for a new month, rather than carrying over last month's choices. This
  // is deliberately destructive and asks for confirmation - the exported
  // Sheet is the permanent record of what was decided, not this table.
  async function resetForNewMonth() {
    const confirmed = window.confirm(
      'This clears every Bill/Accrue/Write Off decision for every contact, so next month starts blank. ' +
      'Make sure you have exported this month\'s review to a Sheet first if you want a record of it. Continue?'
    );
    if (!confirmed) return;
    const { error } = await supabase.from('billing_selections').delete().neq('contact_name', '__never_matches__');
    if (error) { alert(`Reset failed: ${error.message}`); return; }
    await loadSelections();
    alert('Billing selections cleared - ready for the new month.');
  }

  async function raiseAllInvoices() {
    const ihfBillContacts = Object.entries(fixedFeeDecisions).filter(([, d]) => d === 'bill').map(([c]) => c);
    const taxBillContacts = Object.entries(taxFeeDecisions).filter(([, d]) => d === 'bill').map(([c]) => c);
    const tmBillContacts = [...new Set(flatRows.filter(r => getSel(r.contact, r.task).billing_decision === 'bill').map(r => r.contact))];
    const rechargeContacts = [...new Set(recharges.filter(r => r.status === 'include' && r.client_name && Number(r.amount) > 0)
      .map(r => knownContactNames.find(c => normaliseClientName(c) === normaliseClientName(r.client_name)) || r.client_name))];
    const allContactsToRaise = [...new Set([...ihfBillContacts, ...taxBillContacts, ...tmBillContacts, ...rechargeContacts])];

    if (!allContactsToRaise.length) {
      alert('No contacts have anything marked as "Bill" yet.');
      return;
    }

    // Skip anyone already successfully raised in this session - makes
    // re-running after a partial failure safe rather than risking a
    // duplicate invoice for contacts that already went through fine.
    const remaining = allContactsToRaise.filter(c => !invoiceResults[c]?.invoiceNumber);

    // Recharges are easy to forget: say plainly if some are still awaiting a decision (they go on NO invoice), and refuse
    // to send recharge lines without an account code.
    if (rechargeContacts.length && !rechargeAccount) { alert('Set the recharge account code in the Recharges panel first - there are recharges marked Include.'); return; }
    const unreviewed = recharges.filter(r => r.status === 'pending');
    if (unreviewed.length && !window.confirm(
      `${unreviewed.length} recharge item(s) totalling £${unreviewed.reduce((n, r) => n + (Number(r.amount) || 0), 0).toFixed(2)} are still waiting for your decision (Include or Skip). ` +
      `They will NOT be on any invoice.\n\nRaise the invoices anyway?`
    )) return;

    // Billing notes ("Bill in USD", "Insert PO number"...) were being missed each month - put them in front of you
    // right before anything is sent.
    const withNotes = remaining.filter(c => (contactBillingMap[c]?.billing_notes || '').trim());
    if (withNotes.length && !window.confirm(
      `These clients have billing notes - check each one is handled before sending:\n\n` +
      withNotes.map(c => `• ${c}: ${contactBillingMap[c].billing_notes.trim()}`).join('\n') +
      `\n\nRaise the invoices now?`
    )) return;

    for (const contact of remaining) {
      let result = await raiseInvoice(contact);
      // Automatic retry for the specific failure mode we know is transient
      // and safe to retry (rate limit / timeout cutting the response short).
      // Safe because of the idempotency check in raise-invoice.js - a retry
      // can never create a duplicate even if the first attempt actually
      // succeeded in Xero.
      if (result === false) {
        await new Promise(r => setTimeout(r, 2000));
        result = await raiseInvoice(contact);
      }
      // Much longer pacing than before - the shorter gap still wasn't
      // enough to reliably avoid Xero's rate limit across a full batch.
      await new Promise(r => setTimeout(r, 1500));
    }
  }

  // The main bulk action: scan every contact with ANY "Bill" decision
  // (T&M row or fixed fee), find their Xero draft, match by amount,
  // update matched lines, add missing ones, drop extras, set date to
  // month-end. One click, no per-contact selection.
  // NOTE: this only UPDATES an existing draft - it does not create one.
  // For creating drafts from scratch via the API, use raiseAllInvoices above.
  async function syncAllBilledDrafts() {
    const ihfBillContacts = Object.entries(fixedFeeDecisions).filter(([, d]) => d === 'bill').map(([c]) => c);
    const taxBillContacts = Object.entries(taxFeeDecisions).filter(([, d]) => d === 'bill').map(([c]) => c);
    const tmBillContacts = [...new Set(flatRows.filter(r => getSel(r.contact, r.task).billing_decision === 'bill').map(r => r.contact))];
    const rechargeContacts = [...new Set(recharges.filter(r => r.status === 'include' && r.client_name && Number(r.amount) > 0)
      .map(r => knownContactNames.find(c => normaliseClientName(c) === normaliseClientName(r.client_name)) || r.client_name))];
    if (rechargeContacts.length && !rechargeAccount) { alert('Set the recharge account code in the Recharges panel before syncing - there are recharges to go on invoices.'); return; }
    const allContactsToSync = [...new Set([...ihfBillContacts, ...taxBillContacts, ...tmBillContacts, ...rechargeContacts])];

    if (!allContactsToSync.length) {
      alert('No contacts have anything marked as "Bill" yet.');
      return;
    }

    // Invoice date = last day of the BILLING month (from "Bill up until"), not today
    const [byY, byM] = billingMonth.split('-').map(Number);
    const lastDayDate = new Date(byY, byM, 0);
    const lastDay = fmtLocalDate(lastDayDate);

    for (const contact of allContactsToSync) {
      setDraftSyncResults(r => ({ ...r, [contact]: { loading: true } }));
      try {
        const findRes = await fetch(`/api/billing/find-draft-invoice?contactName=${encodeURIComponent(contact)}`);
        const draft = await findRes.json();
        if (!findRes.ok || draft.error) {
          const hint = draft.suggestions?.length
            ? ` — did you mean: ${draft.suggestions.join(', ')}?`
            : '';
          const diag = draft.totalDraftsFetched !== undefined
            ? ` [${draft.totalDraftsFetched} ACCREC drafts / ${draft.rawInvoicesFetchedBeforeTypeFilter} raw invoices fetched, tenant ${draft.tenantIdUsed}]`
            : '';
          setDraftSyncResults(r => ({ ...r, [contact]: { error: (draft.error || 'Draft not found') + hint + diag } }));
          continue;
        }

        const targets = buildTargetLines(contact);
        const existingLines = draft.lineItems || [];

        // Due date = invoice date + this contact's payment terms (not today + terms)
        const cfgForDue = contactBillingMap[contact] || {};
        const termsDays = cfgForDue.payment_terms_days ?? 7;
        const contactDueDate = fmtLocalDate(new Date(lastDayDate.getFullYear(), lastDayDate.getMonth(), lastDayDate.getDate() + termsDays));

        // NEVER delete existing lines - they carry the Xero time-entry linkage.
        // Update each existing line in place with our corrected values (matched
        // by position), then append any extra target lines as new ones.
        const desiredLines = existingLines.map((el, i) => {
          const t = targets[i]; // corresponding target, if we have one
          return {
            matchLineItemId: el.lineItemId,
            description: t?.description ?? el.description,
            quantity: t?.quantity ?? el.quantity,
            unitAmount: t?.unitAmount ?? el.unitAmount,
            accountCode: t?.accountCode ?? el.accountCode,
            taxType: t?.taxType ?? el.taxType,
            serviceLine: t?.serviceLine || '',
            clientDimension: t?.clientDimension || '',
          };
        });

        // Append any target lines beyond the count of existing draft lines
        for (let i = existingLines.length; i < targets.length; i++) {
          const t = targets[i];
          desiredLines.push({
            description: t.description, quantity: t.quantity, unitAmount: t.unitAmount,
            accountCode: t.accountCode, taxType: t.taxType,
            serviceLine: t.serviceLine || '', clientDimension: t.clientDimension || '',
          });
        }

        const addedCount = Math.max(0, targets.length - existingLines.length);

        const syncRes = await fetch('/api/billing/sync-draft-invoice', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoiceId: draft.invoiceId, desiredLines, issueDate: lastDay, dueDate: contactDueDate }),
        });
        const syncData = await syncRes.json();
        if (!syncRes.ok || syncData.error) {
          setDraftSyncResults(r => ({ ...r, [contact]: { error: typeof syncData.error === 'string' ? syncData.error : JSON.stringify(syncData.error) } }));
          continue;
        }
        setDraftSyncResults(r => ({ ...r, [contact]: { success: true, invoiceNumber: syncData.invoiceNumber, xeroUrl: syncData.xeroUrl, matchedCount: desiredLines.length, addedCount } }));
        if (includedRechargesFor(contact).length) await markRechargesBilled(contact, syncData.invoiceNumber);
      } catch (err) {
        setDraftSyncResults(r => ({ ...r, [contact]: { error: err.message } }));
      }
      await new Promise(res => setTimeout(res, 400)); // pace requests
    }

    // Handle any write-offs (marks their time as billed in Xero, no invoice)
    try {
      const n = await processWriteOffs();
      if (n > 0) setDraftSyncResults(r => ({ ...r, _writeoffs: { success: true, invoiceNumber: `${n} write-off contact(s) processed` } }));
    } catch { /* best-effort */ }
  }

  async function raiseInvoice(contact) {
    const contactCfg = contactBillingMap[contact] || {};
    const { issueDate, dueDate } = invoiceDates(contactCfg);
    const hasTmBill = (byContact[contact] || []).some(r => getSel(contact, r.task).billing_decision === 'bill');
    const hasIhfBill = fixedFeeDecisions[contact] === 'bill';
    const hasTaxBill = taxFeeDecisions[contact] === 'bill';

    const rechargeRows = includedRechargesFor(contact);
    if (!hasTmBill && !hasIhfBill && !hasTaxBill && !rechargeRows.length) {
      setInvoiceResults(ir => ({ ...ir, [contact]: { error: 'No rows marked as Bill' } })); return;
    }
    if (rechargeRows.length && !rechargeAccount) {
      // the server would silently post an empty account to 200 - never let recharges go out without one
      setInvoiceResults(ir => ({ ...ir, [contact]: { error: 'Set the recharge account code in the Recharges panel first' } })); return false;
    }

    // Build line items — combine fixed fee + T&M into ONE invoice if both present
    const lineItems = [];

    // Fixed fee lines first
    if (hasIhfBill) {
      const fees = (fixedFeeMap[contact] || []).filter(f => !isTaxAccountsFee(f));
      fees.forEach(f => lineItems.push({
        description: fixedFeeDescription(f), quantity: 1, unitAmount: f.monthly_amount,
        nominalCode: f.nominal_code || '203', vatGroup: f.vat_group || '20_standard',
        serviceLine: f.service_line || '', clientDimension: contactCfg.client_dimension || '',
      }));
    }

    // Tax/Accounts fee lines - separate annual decision, not tied to IHF
    if (hasTaxBill) {
      const taxFees = (fixedFeeMap[contact] || []).filter(f => isTaxAccountsFee(f));
      taxFees.forEach(f => lineItems.push({
        description: fixedFeeDescription(f), quantity: 1, unitAmount: f.monthly_amount,
        nominalCode: f.nominal_code || '203', vatGroup: f.vat_group || '20_standard',
        serviceLine: f.service_line || '', clientDimension: contactCfg.client_dimension || '',
      }));
    }

    // T&M lines
    if (hasTmBill) {
      const contactRows = (byContact[contact] || []).filter(r => getSel(contact, r.task).billing_decision === 'bill');
      contactRows.forEach(row => {
        const sel = getSel(contact, row.task);
        const amount = computeAmount(sel, row);
        const hours = computeHours(sel, row);
        const tc = taskBaMapping[row.task] || {};
        const rate = hours > 0 ? Math.round((amount / hours) * 100) / 100 : amount;
        lineItems.push({
          description: sel.line_description || row.task,
          quantity: Math.round(hours * 100000) / 100000 || 1, unitAmount: rate,
          nominalCode: tc.nominal_code || BA_NOMINAL_DEFAULTS[row.ba] || '201',
          vatGroup: contactCfg.vat_group || '20_standard',
          serviceLine: sel.service_line || tc.service_line || '', clientDimension: contactCfg.client_dimension || '',
        });
      });
    }

    // Recharges (filing fees etc.) - at cost, outside the scope of VAT unless marked "Client's VAT"
    rechargeRows.forEach(r => lineItems.push({
      description: r.description || 'Recharge', quantity: 1, unitAmount: Number(r.amount),
      nominalCode: rechargeAccount,
      vatGroup: r.vat_treatment === 'client' ? (contactCfg.vat_group || '20_standard') : 'outside_scope',
      serviceLine: '', clientDimension: contactCfg.client_dimension || '',
    }));

    const resultKey = contact; // single result key regardless of type
    setInvoiceResults(ir => ({ ...ir, [resultKey]: { loading: true } }));
    // Also clear the IHF-specific result key so it shows here instead
    if (hasIhfBill) setInvoiceResults(ir => ({ ...ir, [`ihf::${contact}`]: null }));

    try {
      const res = await fetch('/api/billing/raise-invoice', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactName: contact, invoiceType: hasIhfBill ? 'mixed' : 'tm', issueDate, dueDate, currencyCode: contactCfg.currency || 'GBP', lineItems }),
      });
      const rawText = await res.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch {
        // Empty or malformed body - usually a rate limit or timeout cutting
        // the response short mid-batch, not a problem with this specific
        // client. Safe to just retry this contact.
        setInvoiceResults(ir => ({ ...ir, [resultKey]: {
          error: `Empty/malformed response from server (likely a rate limit or timeout, not a problem with this client) - safe to retry`,
        } }));
        return false;
      }
      if (!res.ok || data.error) {
        setInvoiceResults(ir => ({ ...ir, [resultKey]: { error: typeof data.error === 'string' ? data.error : JSON.stringify(data.error) } }));
        return false;
      }
      setInvoiceResults(ir => ({ ...ir, [resultKey]: { invoiceNumber: data.invoiceNumber, xeroUrl: data.xeroUrl, total: data.total } }));
      if (rechargeRows.length) {
        if (data.alreadyExisted) {
          // the server reused an invoice raised earlier and added nothing - do NOT pretend the recharges went out
          setInvoiceResults(ir => ({ ...ir, [resultKey]: { ...ir[resultKey], warning: 'invoice already existed - recharge lines NOT added, add them in Xero' } }));
        } else await markRechargesBilled(contact, data.invoiceNumber);
      }

      // Mark time as billed for any marked rows - only the SPECIFIC tasks that were billed
      const billedTaskNames = (byContact[contact] || [])
        .filter(r => getSel(contact, r.task).mark_all_as_billed)
        .map(r => r.task);
      const hasMarkBilled = hasIhfBill || billedTaskNames.length > 0;
      if (hasMarkBilled) {
        try {
          // Hard timeout - this call must never be able to silently stall the
          // whole "Raise All" loop. If it's slow, we move on rather than hang.
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);
          await fetch('/api/billing/mark-time-billed', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contactName: contact,
              periods: billedPeriods(contact),
              invoiceType: hasIhfBill ? 'fixed_fee' : 'tm',
              xeroInvoiceId: data.invoiceId,
              taskNames: billedTaskNames,
            }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
        } catch { /* best-effort - a timeout or failure here never blocks the invoice itself */ }
      }

      // Note: no post-invoice sync here — the additive upsert would double-count
      // minutes in the cache. Use Sync Now manually to refresh after invoicing.
      return true;
    } catch (err) { setInvoiceResults(ir => ({ ...ir, [resultKey]: { error: err.message } })); return false; }
  }

  // raiseIhfInvoice now just delegates to raiseInvoice (which handles combined)
  // eslint-disable-next-line no-unused-vars
  async function raiseIhfInvoice(contact) {
    return raiseInvoice(contact);
  }

  async function processWriteOffs() {
    // Mark time as billed for all write-off decisions (clears them from unbilled list)
    const writeOffContacts = [...new Set([
      ...Object.entries(fixedFeeDecisions).filter(([,d]) => d === 'write_off').map(([c]) => c),
      ...flatRows.filter(r => getSel(r.contact, r.task).billing_decision === 'write_off').map(r => r.contact),
    ])];
    for (const contact of writeOffContacts) {
      try {
        await fetch('/api/billing/mark-time-billed', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactName: contact, periods: billedPeriods(contact), invoiceType: 'tm' }),
        });
      } catch { /* best-effort */ }
    }
    return writeOffContacts.length;
  }


  function fmtSync(iso) {
    if (!iso) return 'never';
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }



  const inpStyle = { background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4,
    padding: '3px 6px', fontSize: 10, fontFamily: T.fontMono, textAlign: 'right', outline: 'none' };

  return (
    <div className="fi" style={{ padding: '24px 28px' }}>
      {/* Header */}
      <SH label="Billing — Unbilled Time" accent={T.accent} action={
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={async () => { try { await fetch('/api/xero/tracking-categories'); await loadFromCache(); } catch(e) { alert(e.message); } }}
            style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
              background: 'none', color: T.mutedLt, border: `1px solid ${T.border}`, borderRadius: 6, cursor: 'pointer' }}>
            Refresh Tracking
          </button>
          <button onClick={() => syncNow()} disabled={syncing || loading}
            title="Rebuilds all unbilled time from Xero, then checks it against the hours Xero says are waiting to be invoiced"
            style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              background: syncing ? T.raised : T.accent, color: syncing ? T.muted : '#fff', border: 'none', borderRadius: 6, cursor: syncing ? 'default' : 'pointer' }}>
            {syncing ? `Syncing ${syncProgress.done || ''}${syncProgress.total ? ` / ${syncProgress.total}` : ''}...` : 'Sync Now'}
          </button>
        </div>
      } />
      <div style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono, marginBottom: 12 }}>
        Last synced: {fmtSync(lastSyncedAt)}
      </div>
      {syncCheck?.unresolved?.length > 0 && (
        <div style={{ fontSize: 10, color: T.amber, fontFamily: T.fontMono, marginBottom: 12 }}>
          ⚠ Time logged against tasks Xero can't find (shown as "(task not found in Xero)" and valued at £0):{' '}
          {syncCheck.unresolved.map(u => `${u.contact} ${(u.minutes / 60).toFixed(1)}h`).join(', ')}.
          Check the task in Xero - it may have been deleted, or the time moved to a task that no longer exists.
        </div>
      )}
      {syncCheck && (() => {
        const hrs = m => (m / 60).toLocaleString('en-GB', { maximumFractionDigits: 1 });
        const pct = syncCheck.expectedMinutes > 0 ? (syncCheck.savedMinutes / syncCheck.expectedMinutes) * 100 : 100;
        const ok = !syncCheck.stoppedEarly && pct >= 98;
        return (
          <div style={{ fontSize: 10, color: ok ? T.green : T.amber, fontFamily: T.fontMono, marginBottom: 12 }}>
            {ok ? '✓' : '⚠'} Sync check: {hrs(syncCheck.savedMinutes)}h saved of {hrs(syncCheck.expectedMinutes)}h Xero reports as unbilled ({pct.toFixed(1)}%)
            {!ok && ' — looks incomplete. Click Sync Now again; if it stays short, the clients listed in the error above (or the gap itself) are the place to look.'}
            {ok && pct > 102 && " — more than Xero's own counter reports: the sync now reads every project directly, because that counter under-reports for some."}
          </div>
        );
      })()}

      {error && <Card style={{ borderColor: T.red, marginBottom: 12 }}>
        <div style={{ color: T.red, fontSize: 11, fontFamily: T.fontMono }}>{error}</div>
      </Card>}

      {/* Invoice result notices — always visible after raising */}
      {Object.entries(invoiceResults).filter(([,r]) => r && (r.invoiceNumber || r.error)).map(([contact, r]) => (
        <div key={contact} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', marginBottom: 6,
          background: r.invoiceNumber ? `${T.green}15` : `${T.red}15`,
          border: `1px solid ${r.invoiceNumber ? T.green : T.red}44`, borderRadius: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: r.invoiceNumber ? T.green : T.red }}>
            {r.invoiceNumber ? '✓' : '⚠'}
          </span>
          <span style={{ fontSize: 11, color: T.text, flex: 1 }}>
            <strong>{contact}</strong> — {r.invoiceNumber ? `${r.invoiceNumber} raised as DRAFT` : r.error}
          </span>
          {r.xeroUrl && (
            <a href={r.xeroUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 10, color: T.accentLt, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Open in Xero →
            </a>
          )}
          <button onClick={() => setInvoiceResults(ir => { const n = {...ir}; delete n[contact]; return n; })}
            style={{ fontSize: 10, color: T.muted, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      ))}

      {/* Controls row: date filter + BA filter */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono, whiteSpace: 'nowrap' }}>Bill up until:</span>
          <input type="month" value={billingUntil} onChange={e => setBillingUntil(e.target.value)}
            style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 5,
              padding: '5px 8px', fontSize: 11, fontFamily: T.fontMono, outline: 'none' }} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>BA:</span>
          {baOptions.map(ba => (
            <button key={ba} onClick={() => setBaFilter(ba === baFilter ? 'All' : ba)}
              style={{ fontSize: 10, padding: '4px 10px', borderRadius: 4, border: `1px solid ${baFilter === ba ? T.accent : T.border}`,
                background: baFilter === ba ? T.accent : T.raised, color: baFilter === ba ? '#fff' : T.mutedLt, cursor: 'pointer' }}>
              {ba}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>Status:</span>
          {[
            { key: 'All', label: 'All', col: T.accent },
            { key: 'bill', label: 'Bill', col: T.green },
            { key: 'accrue', label: 'Accrue', col: T.amber },
            { key: 'write_off', label: 'Write Off', col: T.red },
            { key: 'blank', label: 'Blank', col: T.muted },
          ].map(s => {
            const active = statusFilter === s.key;
            return (
              <button key={s.key} onClick={() => setStatusFilter(active && s.key !== 'All' ? 'All' : s.key)}
                style={{ fontSize: 10, padding: '4px 10px', borderRadius: 4,
                  border: `1px solid ${active ? s.col : T.border}`,
                  background: active ? s.col : T.raised,
                  color: active ? '#fff' : T.mutedLt, cursor: 'pointer', fontWeight: active ? 700 : 400 }}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <RechargePanel recharges={recharges} contactNames={knownContactNames} rechargeAccount={rechargeAccount}
        onSaveAccount={saveRechargeAccount} onReload={loadRecharges} tableMissing={rechargesMissing} />

      {/* Sync Draft Invoices — bulk action. Scans every contact with anything
          marked "Bill" (T&M or fixed fee), finds their Xero draft (created
          manually via Projects > Invoice > Tasks & Expenses - this is what
          establishes the native time-entry linkage), matches lines by amount,
          updates/adds/removes lines to match our billing decisions exactly,
          and sets the invoice date to month-end. One click, no per-contact
          selection needed. */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 16, background: T.blue, borderRadius: 2 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
              Month-End Workflow
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportReviewToSheet} disabled={exportingReview}
              style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                background: 'none', color: T.mutedLt, border: `1px solid ${T.border}`, borderRadius: 6, cursor: 'pointer' }}>
              {exportingReview ? 'Exporting…' : 'Export Review to Sheet'}
            </button>
            <button onClick={resetForNewMonth}
              style={{ padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                background: 'none', color: T.red, border: `1px solid ${T.red}66`, borderRadius: 6, cursor: 'pointer' }}>
              Reset for New Month
            </button>
          </div>
        </div>
        <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginBottom: 10 }}>
          Suggested flow: run Sync Now the day before month-end and make your Bill/Accrue/Write
          Off decisions → Export Review to Sheet for a permanent record → resync again on the last day once
          timesheets are finalised → Raise All Invoices → Reset for New Month once everything's sent.
        </div>
        {exportResult?.error && (
          <div style={{ fontSize: 11, color: T.red, marginBottom: 10 }}>⚠ {exportResult.error}</div>
        )}
        {exportResult?.spreadsheetUrl && (
          <div style={{ fontSize: 11, color: T.green, marginBottom: 10 }}>
            ✓ Exported {exportResult.rowsWritten} rows —{' '}
            <a href={exportResult.spreadsheetUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accentLt }}>Open Sheet →</a>
          </div>
        )}
      </div>

      {/* Raises drafts directly via the Xero API for every billed contact -
          the actual zero-manual-Xero workflow this whole billing tab was
          designed around. */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 16, background: T.green, borderRadius: 2 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
              Raise Invoices
            </span>
          </div>
          <button onClick={raiseAllInvoices}
            style={{ padding: '8px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              background: T.green, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Raise All Invoices →
          </button>
        </div>
        <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginBottom: 10 }}>
          Creates a DRAFT invoice in Xero for every contact marked "Bill" below, entirely via the API -
          no manual step in Xero needed. This is the main action for month-end.
        </div>

        {Object.keys(invoiceResults).length > 0 && (
          <Card style={{ padding: 12 }}>
            {Object.entries(invoiceResults)
              .filter(([contact, r]) => !contact.startsWith('ihf::') && r !== null)
              .map(([contact, r]) => (
              <div key={contact} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${T.border}33` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, minWidth: 160 }}>{contact}</span>
                {r?.loading && <span style={{ fontSize: 11, color: T.muted }}>Raising...</span>}
                {r?.invoiceNumber && (
                  <span style={{ fontSize: 11, color: T.green }}>
                    ✓ {r.invoiceNumber} created ({fmtGBPExact(r.total)})
                    {r.xeroUrl && <a href={r.xeroUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accentLt, marginLeft: 8 }}>Open →</a>}
                  </span>
                )}
                {r?.error && <span style={{ fontSize: 11, color: T.red }}>⚠ {r.error}</span>}
              </div>
            ))}
          </Card>
        )}
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 16, background: T.accent, borderRadius: 2 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
              Sync Draft Invoices <span style={{ color: T.muted, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional - only if you created a draft manually in Xero)</span>
            </span>
          </div>
          <button onClick={syncAllBilledDrafts}
            style={{ padding: '8px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              background: T.accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Sync All Billed Drafts
          </button>
        </div>
        <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginBottom: 10 }}>
          Only needed if you've manually created a draft in Xero (Projects → Invoice → Tasks &amp; Expenses)
          to preserve the native time-entry linkage. This updates that existing draft's lines and codes -
          it never creates one. For month-end, use "Raise All Invoices" above instead.
        </div>

        {Object.keys(draftSyncResults).length > 0 && (
          <Card style={{ padding: 12 }}>
            {Object.entries(draftSyncResults).map(([contact, r]) => (
              <div key={contact} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${T.border}33` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, minWidth: 160 }}>{contact}</span>
                {r?.loading && <span style={{ fontSize: 11, color: T.muted }}>Syncing...</span>}
                {r?.success && (
                  <span style={{ fontSize: 11, color: T.green }}>
                    ✓ {r.invoiceNumber} updated ({r.matchedCount} lines)
                    <a href={r.xeroUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accentLt, marginLeft: 8 }}>Open →</a>
                  </span>
                )}
                {r?.error && <span style={{ fontSize: 11, color: T.red }}>⚠ {r.error}</span>}
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* Accrued Revenue Journal — posts a DRAFT auto-reversing manual journal
          for everything tagged Accrue. Mirrors the manual month-end structure:
          CR revenue / DR 640 per task, Service + Client tracking on both sides. */}
      {(() => {
        const accrualLines = buildAccrualLines();
        const totalAccrual = accrualLines.reduce((s, l) => s + l.amount, 0);
        const [ay, am] = billingMonth.split('-').map(Number);
        const mLabels = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const jDate = new Date(Date.UTC(ay, am, 0)).toISOString().slice(0, 10);
        const rDate = new Date(Date.UTC(ay, am, 1)).toISOString().slice(0, 10);
        if (!accrualLines.length && !accrualResult) return null;
        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 3, height: 16, background: T.amber, borderRadius: 2 }} />
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
                Accrued Revenue Journal
              </span>
            </div>
            <Card style={{ padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: accrualLines.length ? 12 : 0 }}>
                <div style={{ fontSize: 11, color: T.textSub, fontFamily: T.fontMono }}>
                  <strong style={{ color: T.amber }}>{mLabels[am-1]}-{String(ay).slice(2)} Accrued Revenue</strong>
                  <span style={{ color: T.muted }}> · dated {jDate} · reverses {rDate}</span>
                  <br/>
                  <span style={{ color: T.muted }}>{accrualLines.length} line{accrualLines.length !== 1 ? 's' : ''} · </span>
                  <strong style={{ color: T.text }}>{fmtGBPExact(totalAccrual)}</strong>
                </div>
                {!accrualResult?.manualJournalId && (
                  <button onClick={postAccrualJournal} disabled={postingAccrual || !accrualLines.length}
                    style={{ padding: '8px 18px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                      background: postingAccrual ? T.raised : T.amber, color: postingAccrual ? T.muted : '#fff',
                      border: 'none', borderRadius: 6, cursor: postingAccrual || !accrualLines.length ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                    {postingAccrual ? 'Posting...' : 'Post Accrual Journal →'}
                  </button>
                )}
              </div>

              {accrualResult?.error && (
                <div style={{ fontSize: 11, color: T.red, marginTop: 8 }}>⚠ {accrualResult.error}</div>
              )}
              {accrualResult?.manualJournalId && (
                <div style={{ fontSize: 11, color: T.green, marginTop: 8 }}>
                  ✓ Draft journal posted ({accrualResult.lineCount} lines, {fmtGBPExact(accrualResult.totalAccrued)})
                  <a href={accrualResult.xeroUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accentLt, marginLeft: 8 }}>Open in Xero →</a>
                  {accrualResult.reversalId
                    ? <span style={{ color: T.muted, marginLeft: 8 }}>· reversal created for {accrualResult.reversalDate}</span>
                    : <span style={{ color: T.amber, marginLeft: 8 }}>· ⚠ reversal not created{accrualResult.reversalError ? `: ${accrualResult.reversalError}` : ''}</span>}
                </div>
              )}

              {accrualLines.length > 0 && !accrualResult?.manualJournalId && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: T.raised }}>
                      <TH2>Contact</TH2>
                      <TH2>Task / Description</TH2>
                      <TH2 right>Amount</TH2>
                      <TH2 center>CR</TH2>
                      <TH2 center>DR</TH2>
                      <TH2>Service</TH2>
                      <TH2>Client</TH2>
                    </tr></thead>
                    <tbody>
                      {accrualLines.map((l, i) => (
                        <tr key={i}>
                          <TD2 style={{ fontSize: 10 }}>{l.contactName}</TD2>
                          <TD2 style={{ fontSize: 10, color: T.textSub }}>{l.description}</TD2>
                          <TD2 right style={{ fontSize: 10 }}>{fmtGBPExact(l.amount)}</TD2>
                          <TD2 center style={{ fontSize: 10, fontFamily: T.fontMono, color: T.mutedLt }}>{l.revenueAccount}</TD2>
                          <TD2 center style={{ fontSize: 10, fontFamily: T.fontMono, color: T.mutedLt }}>640</TD2>
                          <TD2 style={{ fontSize: 9, color: l.serviceLine ? T.textSub : T.red }}>{l.serviceLine || '⚠ not set'}</TD2>
                          <TD2 style={{ fontSize: 9, color: l.clientDimension ? T.textSub : T.red }}>{l.clientDimension || '⚠ not set'}</TD2>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        );
      })()}

      {/* Invoice Emails — finds AUTHORISED invoices Xero hasn't emailed yet
          (SentToContact = false) and creates a Gmail draft per invoice with
          the PDF attached. Drafts are not sent - review and send manually. */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ width: 3, height: 16, background: T.accent, borderRadius: 2 }} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
            Invoice Emails
          </span>
        </div>
        <Card style={{ padding: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: unsentInvoices ? 12 : 0 }}>
            <button onClick={findUnsentInvoices} disabled={loadingUnsent}
              style={{ padding: '7px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                background: loadingUnsent ? T.raised : T.accent, color: loadingUnsent ? T.muted : '#fff',
                border: 'none', borderRadius: 6, cursor: loadingUnsent ? 'default' : 'pointer' }}>
              {loadingUnsent ? 'Checking...' : 'Find Unsent Invoices'}
            </button>
            {unsentInvoices?.readyToSend > 0 && !draftResults && (
              <button onClick={createEmailDrafts} disabled={creatingDrafts}
                style={{ padding: '7px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                  background: creatingDrafts ? T.raised : T.green, color: creatingDrafts ? T.muted : '#fff',
                  border: 'none', borderRadius: 6, cursor: creatingDrafts ? 'default' : 'pointer' }}>
                {creatingDrafts ? 'Creating...' : `Create ${unsentInvoices.readyToSend} Draft${unsentInvoices.readyToSend !== 1 ? 's' : ''} →`}
              </button>
            )}
            <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>
              from finance@lvlup.partners
            </span>
          </div>

          {unsentInvoices?.error && (
            <div style={{ fontSize: 11, color: T.red }}>⚠ {unsentInvoices.error}</div>
          )}

          {unsentInvoices?.invoices && (
            <>
              <div style={{ fontSize: 11, color: T.textSub, fontFamily: T.fontMono, marginBottom: 8 }}>
                {unsentInvoices.unsentCount} approved invoice{unsentInvoices.unsentCount !== 1 ? 's' : ''} not yet sent
                {unsentInvoices.missingEmails?.length > 0 && (
                  <span style={{ color: T.amber }}> · {unsentInvoices.missingEmails.length} missing email addresses</span>
                )}
              </div>
              {unsentInvoices.invoices.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: T.raised }}>
                      <TH2>Invoice</TH2>
                      <TH2>Contact</TH2>
                      <TH2 right>Amount</TH2>
                      <TH2>Recipients</TH2>
                      <TH2>Status</TH2>
                    </tr></thead>
                    <tbody>
                      {unsentInvoices.invoices.map(inv => {
                        const dr = draftResults?.results?.find(r => r.invoiceNumber === inv.invoiceNumber);
                        return (
                          <tr key={inv.invoiceId}>
                            <TD2 style={{ fontSize: 10, fontFamily: T.fontMono }}>
                              <a href={inv.xeroUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accentLt, textDecoration: 'none' }}>
                                {inv.invoiceNumber}
                              </a>
                            </TD2>
                            <TD2 style={{ fontSize: 10 }}>{inv.contactName}</TD2>
                            <TD2 right style={{ fontSize: 10 }}>{fmtGBPExact(inv.total)}</TD2>
                            <TD2 style={{ fontSize: 9, color: inv.hasEmails ? T.textSub : T.red }}>
                              {inv.hasEmails ? inv.emails.join(', ') : '⚠ no emails in Standing Data'}
                            </TD2>
                            <TD2 style={{ fontSize: 10 }}>
                              {dr?.success ? (
                                <span style={{ color: T.green }}>
                                  ✓ draft created
                                  <a href={dr.gmailUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accentLt, marginLeft: 6 }}>Open →</a>
                                </span>
                              ) : dr?.error ? (
                                <span style={{ color: T.red, fontSize: 9 }}>⚠ {dr.error}</span>
                              ) : inv.hasEmails ? (
                                <span style={{ color: T.muted }}>ready</span>
                              ) : (
                                <span style={{ color: T.amber }}>needs email</span>
                              )}
                            </TD2>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {draftResults?.error && <div style={{ fontSize: 11, color: T.red, marginTop: 8 }}>⚠ {draftResults.error}</div>}
          {draftResults?.created > 0 && (
            <div style={{ fontSize: 11, color: T.green, marginTop: 10 }}>
              ✓ {draftResults.created} draft{draftResults.created !== 1 ? 's' : ''} created in finance@lvlup.partners
              {draftResults.failed > 0 && <span style={{ color: T.amber }}> · {draftResults.failed} failed</span>}
            </div>
          )}
        </Card>
      </div>

      {Object.keys(fixedFeeMap).length > 0 && (baFilter === 'All' || baFilter === 'In-House Finance') && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: 3, height: 16, background: T.ihf, borderRadius: 2 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
              Fixed Fee Projects
            </span>
          </div>
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: T.raised }}>
                  <TH2>Contact</TH2>
                  <TH2>Fixed Fee Lines (Amount &amp; Decision Inline)</TH2>
                  <TH2></TH2>
                </tr></thead>
                <tbody>
                  {groupedFixedFeeClients.map(({ displayName, contacts, items: allFees }) => {
                    // Use the first contact name for saving decisions/results -
                    // all merged variants share the same underlying fee rows,
                    // so any one of them is a valid key
                    const contact = contacts[0];
                    const ihfFees = allFees.filter(f => !isTaxAccountsFee(f));
                    const taxFees = allFees.filter(f => isTaxAccountsFee(f));
                    const ihfTotal = ihfFees.reduce((s, f) => s + (f.monthly_amount || 0), 0);
                    const taxTotal = taxFees.reduce((s, f) => s + (f.monthly_amount || 0), 0);
                    const result = invoiceResults[contact] || invoiceResults[`ihf::${contact}`];
                    const ihfDecision = fixedFeeDecisions[contact] ?? null;
                    const taxDecision = taxFeeDecisions[contact] ?? null;

                    const feeGroup = (fees, label, total, decision, setDecision, feeType) => fees.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: T.muted }}>{label}</span>
                          <span style={{ fontSize: 11, fontFamily: T.fontMono, color: T.ihf, fontWeight: 700 }}>£{total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                          <BillingToggle value={decision} onChange={v => {
                            setDecision(d => ({ ...d, [contact]: v }));
                            saveFixedFeeDecision(feeType, contact, v);
                          }} />
                        </div>
                        {fees.map((f, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginLeft: 8 }}>
                            <span style={{ fontSize: 10, color: T.textSub }}>
                              {f.description} — <span style={{ fontFamily: T.fontMono, color: T.text }}>£{f.monthly_amount?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                              {f.service_line && <span style={{ color: T.muted }}> ({f.service_line})</span>}
                            </span>
                            {isPayrollFee(f) && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input type="number" defaultValue={f.headcount ?? ''} placeholder="headcount"
                                  onBlur={async e => {
                                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                                    const { error } = await supabase.from('contact_fixed_fees')
                                      .update({ headcount: val }).eq('id', f.id);
                                    if (error) { alert(`Failed to save headcount: ${error.message}`); return; }
                                    await loadFromCache();
                                  }}
                                  style={{ width: 56, fontSize: 9, padding: '2px 5px', background: T.raised,
                                    color: T.text, border: `1px solid ${T.border}`, borderRadius: 3 }} />
                                <span style={{ fontSize: 9, color: T.muted }}>people</span>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    );

                    return (
                      <tr key={displayName} className="rh">
                        <TD2 bold>
                          {displayName}
                          {(() => {
                            const note = contacts.map(n => contactBillingMap[n]?.billing_notes).find(x => (x || '').trim());
                            return note ? <div style={{ marginTop: 3, fontSize: 10, fontWeight: 600, color: T.amber, whiteSpace: 'normal' }}>📝 {note.trim()}</div> : null;
                          })()}
                          {contacts.length > 1 && (
                            <div style={{ fontSize: 9, color: T.muted, fontWeight: 400 }}>
                              (merged: {contacts.join(', ')})
                            </div>
                          )}
                        </TD2>
                        <TD2>
                          {feeGroup(ihfFees, 'In-House Finance / Payroll (monthly)', ihfTotal, ihfDecision, setFixedFeeDecisions, 'ihf')}
                          {feeGroup(taxFees, 'Tax / Accounts (annual)', taxTotal, taxDecision, setTaxFeeDecisions, 'tax')}
                        </TD2>
                        <TD2 right style={{ paddingRight: 14 }}>
                          {result?.invoiceNumber ? (
                            <span style={{ fontSize: 10, color: T.green, fontFamily: T.fontMono }}>
                              ✓ {result.invoiceNumber}
                              <a href={result.xeroUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accentLt, marginLeft: 6 }}>Open →</a>
                            </span>
                          ) : result?.error ? (
                            <span style={{ fontSize: 10, color: T.red }}>{result.error}</span>
                          ) : null}
                        </TD2>
                      </tr>
                    );
                  })}
                  {/* Add new fixed fee row */}
                  {addingFee ? (
                    <tr style={{ background: `${T.accent}08` }}>
                      <TD2>
                        <select value={newFee.contact_name} onChange={e => setNewFee(f => ({ ...f, contact_name: e.target.value }))}
                          style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 11 }}>
                          <option value="">Select contact...</option>
                          {allContacts.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </TD2>
                      <TD2>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <input value={newFee.description} onChange={e => setNewFee(f => ({ ...f, description: e.target.value }))} placeholder="e.g. In-House Finance Team"
                            style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 11, width: 200, outline: 'none' }} />
                          <select value={newFee.service_line} onChange={e => setNewFee(f => ({ ...f, service_line: e.target.value }))}
                            style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 11 }}>
                            <option value="">Service line...</option>
                            {serviceOptions.length > 0
                              ? serviceOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)
                              : ['In-House Finance','In-House Finance (payroll)','In-House Finance (out of scope)','Tax Compliance - Corporate Tax','Tax Compliance - EMI','Tax Compliance - VGEC'].map(s => <option key={s} value={s}>{s}</option>)
                            }
                          </select>
                          <input type="number" step="0.01" value={newFee.monthly_amount} onChange={e => setNewFee(f => ({ ...f, monthly_amount: e.target.value }))} placeholder="0.00"
                            style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '4px 8px', fontSize: 11, width: 90, outline: 'none' }} />
                        </div>
                      </TD2>
                      <TD2 right style={{ paddingRight: 14 }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={async () => {
                            if (!newFee.contact_name || !newFee.description || !newFee.monthly_amount) return;
                            const row = { contact_name: newFee.contact_name, description: newFee.description,
                              task_name: newFee.description, // required column, no separate field in this form
                              monthly_amount: parseFloat(newFee.monthly_amount), service_line: newFee.service_line || null,
                              nominal_code: '203', vat_group: '20_standard', currency: 'GBP',
                              effective_from: new Date().toISOString().slice(0,10), active: true };
                            const { error } = await supabase.from('contact_fixed_fees').insert(row);
                            if (error) { alert(`Failed to save: ${error.message}`); return; }
                            setNewFee({ contact_name:'', description:'', monthly_amount:'', service_line:'', nominal_code:'203', vat_group:'20_standard' });
                            setAddingFee(false);
                            await loadFromCache();
                          }} style={{ fontSize: 10, padding: '4px 10px', background: T.green, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                            ✓ Save
                          </button>
                          <button onClick={() => setAddingFee(false)} style={{ fontSize: 10, padding: '4px 10px', background: T.raised, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 4, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </TD2>
                    </tr>
                  ) : (
                    <tr>
                      <TD2 colSpan={3} style={{ padding: '8px 14px' }}>
                        <button onClick={() => setAddingFee(true)}
                          style={{ fontSize: 11, color: T.ihf, background: 'none', border: `1px solid ${T.ihf}44`, borderRadius: 5, padding: '4px 12px', cursor: 'pointer' }}>
                          + Add New Fixed Price Project
                        </button>
                      </TD2>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${T.border}`, background: T.raised }}>
                    <TD2 bold>Total (all clients)</TD2>
                    <TD2 bold right style={{ fontFamily: T.fontMono, color: T.text }}>
                      £{fixedFeeTotals.total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </TD2>
                    <TD2></TD2>
                  </tr>
                  <tr>
                    <TD2 style={{ color: T.textSub }}>To Bill</TD2>
                    <TD2 right style={{ fontFamily: T.fontMono, color: T.green }}>
                      £{fixedFeeTotals.toBill.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </TD2>
                    <TD2></TD2>
                  </tr>
                  <tr>
                    <TD2 style={{ color: T.textSub }}>To Accrue</TD2>
                    <TD2 right style={{ fontFamily: T.fontMono, color: T.amber }}>
                      £{fixedFeeTotals.toAccrue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </TD2>
                    <TD2></TD2>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* If no fixed fees configured yet, still show the Add button */}
      {Object.keys(fixedFeeMap).length === 0 && (baFilter === 'All' || baFilter === 'In-House Finance') && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 3, height: 16, background: T.ihf, borderRadius: 2 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.mutedLt, fontFamily: T.fontMono }}>
              Fixed Price Projects
            </span>
          </div>
          <Card>
            {!addingFee ? (
              <button onClick={() => setAddingFee(true)}
                style={{ fontSize: 11, color: T.ihf, background: 'none', border: `1px solid ${T.ihf}44`, borderRadius: 5, padding: '6px 14px', cursor: 'pointer' }}>
                + Add New Fixed Price Project
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={newFee.contact_name} onChange={e => setNewFee(f => ({ ...f, contact_name: e.target.value }))}
                  style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 11 }}>
                  <option value="">Contact...</option>
                  {allContacts.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={newFee.description} onChange={e => setNewFee(f => ({ ...f, description: e.target.value }))} placeholder="Description"
                  style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 11, width: 200, outline: 'none' }} />
                <input type="number" step="0.01" value={newFee.monthly_amount} onChange={e => setNewFee(f => ({ ...f, monthly_amount: e.target.value }))} placeholder="£ amount"
                  style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 11, width: 90, outline: 'none' }} />
                <select value={newFee.service_line} onChange={e => setNewFee(f => ({ ...f, service_line: e.target.value }))}
                  style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '5px 8px', fontSize: 11 }}>
                  <option value="">Service line...</option>
                  {(serviceOptions.length > 0 ? serviceOptions.map(o => o.name) : ['In-House Finance','In-House Finance (payroll)','In-House Finance (out of scope)','Tax Compliance - Corporate Tax','Tax Compliance - EMI','Tax Compliance - VGEC']).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={async () => {
                  if (!newFee.contact_name || !newFee.description || !newFee.monthly_amount) return;
                  const { error } = await supabase.from('contact_fixed_fees').insert({ contact_name: newFee.contact_name, description: newFee.description,
                    task_name: newFee.description, // required column, no separate field in this form
                    monthly_amount: parseFloat(newFee.monthly_amount), service_line: newFee.service_line || null,
                    nominal_code: '203', vat_group: '20_standard', currency: 'GBP', effective_from: new Date().toISOString().slice(0,10), active: true });
                  if (error) { alert(`Failed to save: ${error.message}`); return; }
                  setNewFee({ contact_name:'', description:'', monthly_amount:'', service_line:'', nominal_code:'203', vat_group:'20_standard' });
                  setAddingFee(false); await loadFromCache();
                }} style={{ padding: '5px 12px', background: T.green, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Save</button>
                <button onClick={() => setAddingFee(false)} style={{ padding: '5px 10px', background: T.raised, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Cancel</button>
              </div>
            )}
          </Card>
        </div>
      )}

      {loading && <Card><div style={{ color: T.muted, fontSize: 12 }}>Loading...</div></Card>}

      {!loading && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
            <button onClick={() => setExpandedContacts(new Set(Object.keys(byContact)))}
              style={{ fontSize: 10, padding: '4px 10px', background: 'none', color: T.mutedLt, border: `1px solid ${T.border}`, borderRadius: 4, cursor: 'pointer' }}>
              Expand All
            </button>
            <button onClick={() => setExpandedContacts(new Set())}
              style={{ fontSize: 10, padding: '4px 10px', background: 'none', color: T.mutedLt, border: `1px solid ${T.border}`, borderRadius: 4, cursor: 'pointer' }}>
              Collapse All
            </button>
          </div>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
              <thead>
                <tr>
                  <TH2>Contact / Task</TH2>
                  <TH2 right>Prev Periods</TH2>
                  <TH2 right>Current Month</TH2>
                  <TH2 right>Rate/hr</TH2>
                  <TH2 center>Decision</TH2>
                  <TH2 center>Basis</TH2>
                  <TH2 right>Amount</TH2>
                  <TH2 right>Hours</TH2>
                  <TH2>Description</TH2>
                  <TH2>Service Line</TH2>
                  <TH2></TH2>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byContact).map(([contact, contactRows]) => {
                  const totalPrev    = contactRows.reduce((s, r) => s + r.prevAmount, 0);
                  const totalCurrent = contactRows.reduce((s, r) => s + r.currentAmount, 0);
                  const totalAmt     = contactRows.reduce((s, r) => s + computeAmount(getSel(contact, r.task), r), 0);
                  const result       = invoiceResults[contact];
                  const isExpanded   = expandedContacts.has(contact);

                  return (
                    <Fragment key={contact}>
                      {/* Collapsed summary row - clicking expands to show task
                          rows. Only expanded contacts' task rows exist in the
                          DOM at all, which is the actual fix for a large
                          import grinding the browser to a halt. */}
                      <tr onClick={() => setExpandedContacts(s => {
                        const next = new Set(s);
                        if (next.has(contact)) next.delete(contact); else next.add(contact);
                        return next;
                      })} style={{ cursor: 'pointer', background: isExpanded ? `${T.accent}0c` : 'transparent', borderTop: `1px solid ${T.border}` }}>
                        <TD2 bold style={{ paddingLeft: 14 }}>
                          <span style={{ display: 'inline-block', width: 14, color: T.muted }}>{isExpanded ? '▾' : '▸'}</span>
                          {contact}
                          <span style={{ fontSize: 10, color: T.muted, fontWeight: 400, marginLeft: 8 }}>
                            ({contactRows.length} task{contactRows.length !== 1 ? 's' : ''})
                          </span>
                          {(contactBillingMap[contact]?.billing_notes || '').trim() && (
                            <div style={{ marginTop: 3, paddingLeft: 14, fontSize: 10, fontWeight: 600, color: T.amber, whiteSpace: 'normal' }}>
                              📝 {contactBillingMap[contact].billing_notes.trim()}
                            </div>
                          )}
                        </TD2>
                        <TD2 right color={totalPrev > 0 ? T.amber : T.muted}>{totalPrev > 0 ? fmtGBPExact(totalPrev) : '—'}</TD2>
                        <TD2 right color={totalCurrent > 0 ? T.text : T.muted}>{totalCurrent > 0 ? fmtGBPExact(totalCurrent) : '—'}</TD2>
                        <TD2></TD2>
                        <TD2></TD2>
                        <TD2></TD2>
                        <TD2 right bold>{totalAmt > 0 ? fmtGBPExact(totalAmt) : '—'}</TD2>
                        <TD2></TD2>
                        <TD2></TD2>
                        <TD2></TD2>
                        <TD2 style={{ paddingRight: 14 }}>
                          {result?.invoiceNumber ? (
                            <span style={{ fontSize: 10, color: T.green, fontFamily: T.fontMono }}>✓ {result.invoiceNumber}{result.warning && <span style={{ color: T.amber }}> ⚠ {result.warning}</span>}</span>
                          ) : result?.error ? (
                            <span style={{ fontSize: 10, color: T.red }}>{result.error}</span>
                          ) : null}
                        </TD2>
                      </tr>
                      {/* Task rows - only rendered when this contact is expanded */}
                      {isExpanded && contactRows.map((row, ri) => {
                        const sel = getSel(contact, row.task);
                        const rate = computeRate(row);
                        const amount = computeAmount(sel, row);
                        const hours = computeHours(sel, row);
                        const decision = sel.billing_decision ?? null;
                        const isWriteOff = decision === 'write_off';
                        const desc = sel.line_description ?? row.task;

                        return (
                          <tr key={row.task} style={{ background: ri % 2 === 0 ? 'transparent' : `${T.raised}44` }}>
                            <TD2 style={{ paddingLeft: 32, fontSize: 11 }}>
                              <span style={{ color: T.textSub, fontSize: 10 }}>{row.task}</span>
                            </TD2>
                            <TD2 right color={row.prevAmount > 0 ? T.amber : T.muted}>{row.prevAmount > 0 ? fmtGBPExact(row.prevAmount) : '—'}</TD2>
                            <TD2 right color={row.currentAmount > 0 ? T.text : T.muted}>{row.currentAmount > 0 ? fmtGBPExact(row.currentAmount) : '—'}</TD2>
                            <TD2 right color={T.mutedLt} style={{ fontSize: 10, fontFamily: T.fontMono }}>{rate > 0 ? `£${Math.round(rate)}` : '—'}</TD2>
                            <TD2 center>
                              <BillingToggle value={decision} onChange={v => saveSel(contact, row.task, { billing_decision: v })} />
                            </TD2>
                            <TD2 center>
                              {!isWriteOff && decision && (() => {
                                const basis = amountBasis(sel);
                                const btn = (key, label) => (
                                  <button key={key} onClick={() => {
                                    // Clicking a basis button sets the amount to that basis's default value.
                                    // The amount field remains editable afterward - user edits override this.
                                    const newAmount = key === 'current_month' ? row.currentAmount : row.prevAmount + row.currentAmount;
                                    saveSel(contact, row.task, { amount_basis: key, billable_amount: newAmount });
                                  }}
                                    style={{ fontSize: 9, padding: '3px 6px', borderRadius: 3, border: 'none', cursor: 'pointer',
                                      background: basis === key ? T.accent : T.raised, color: basis === key ? '#fff' : T.mutedLt, fontWeight: basis === key ? 700 : 400 }}>
                                    {label}
                                  </button>
                                );
                                return (
                                  <div style={{ display: 'flex', gap: 2 }}>
                                    {btn('full', 'Full')}
                                    {btn('current_month', 'Curr')}
                                    {btn('manual', 'Man')}
                                  </div>
                                );
                              })()}
                            </TD2>
                            <TD2 right>
                              {decision && (
                                isWriteOff ? <span style={{ color: T.muted, fontSize: 10 }}>£0</span> : (
                                  <input type="number" step="0.01"
                                    key={`${contact}-${row.task}-${amountBasis(sel)}-${sel.billable_amount}`}
                                    defaultValue={amount}
                                    onBlur={e => {
                                      const v = parseFloat(e.target.value);
                                      saveSel(contact, row.task, { billable_amount: isNaN(v) ? 0 : v });
                                    }}
                                    style={{ ...inpStyle, width: 82, border: `1px solid ${amountBasis(sel) === 'manual' ? T.accentLt : T.border}` }} />
                                )
                              )}
                            </TD2>
                            <TD2 right>
                              {decision && !isWriteOff && (
                                <input type="number" step="0.1" defaultValue={Math.round(hours * 10) / 10}
                                  onBlur={e => {
                                    const h = parseFloat(e.target.value) || null;
                                    const r = computeRate(row);
                                    const patch = { billable_hours: h };
                                    // Auto-update amount from hours × rate, unless hours = 1 (fixed fee qty)
                                    if (h !== null && h !== 1 && r > 0) {
                                      patch.billable_amount = Math.round(h * r * 100) / 100;
                                      patch.amount_basis = 'manual';
                                    }
                                    saveSel(contact, row.task, patch);
                                  }}
                                  style={{ ...inpStyle, width: 60, border: `1px solid ${T.border}` }} />
                              )}
                            </TD2>
                            <TD2>
                              {decision && !isWriteOff && (
                                <input defaultValue={desc} placeholder={row.task}
                                  onBlur={e => saveSel(contact, row.task, { line_description: e.target.value || null })}
                                  style={{ background: T.raised, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, padding: '3px 6px', fontSize: 10, fontFamily: T.fontSans, outline: 'none', width: 180 }} />
                              )}
                            </TD2>
                            <TD2>
                              {decision && !isWriteOff && (() => {
                                // Defaults to the standing-data value for this task, but can be
                                // overridden per row - the override then flows into whatever
                                // gets sent when the draft is raised or synced (see getSel/saveSel
                                // service_line usage in the line-building logic above)
                                const standingDefault = taskBaMapping[row.task]?.service_line || '';
                                const current = sel.service_line || standingDefault;
                                return serviceOptions.length > 0 ? (
                                  <select value={current}
                                    onChange={e => saveSel(contact, row.task, { service_line: e.target.value || null })}
                                    style={{ background: T.raised, color: T.text, border: `1px solid ${sel.service_line ? T.accentLt : T.border}`,
                                      borderRadius: 4, padding: '3px 6px', fontSize: 10, fontFamily: T.fontSans, outline: 'none', width: 160 }}>
                                    <option value="">— none —</option>
                                    {serviceOptions.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                                  </select>
                                ) : (
                                  <input defaultValue={current} placeholder={standingDefault}
                                    onBlur={e => saveSel(contact, row.task, { service_line: e.target.value || null })}
                                    style={{ background: T.raised, color: T.text, border: `1px solid ${sel.service_line ? T.accentLt : T.border}`,
                                      borderRadius: 4, padding: '3px 6px', fontSize: 10, fontFamily: T.fontSans, outline: 'none', width: 160 }} />
                                );
                              })()}
                            </TD2>
                            <TD2></TD2>
                          </tr>
                        );
                      })}

                      {/* Contact subtotal row — compact, fixed height */}
                      <tr style={{ background: T.raised }}>
                        <TD2 style={{ paddingLeft: 14, fontSize: 10, color: T.mutedLt, padding: '5px 10px' }}>
                          <span style={{ fontWeight: 600 }}>{contact}</span>
                        </TD2>
                        <TD2 right bold color={T.amber} style={{ fontSize: 10, padding: '5px 10px' }}>{totalPrev > 0 ? fmtGBPExact(totalPrev) : '—'}</TD2>
                        <TD2 right bold style={{ fontSize: 10, padding: '5px 10px' }}>{totalCurrent > 0 ? fmtGBPExact(totalCurrent) : '—'}</TD2>
                        <TD2 style={{ padding: '5px 10px' }}></TD2><TD2 style={{ padding: '5px 10px' }}></TD2><TD2 style={{ padding: '5px 10px' }}></TD2>
                        <TD2 right bold color={totalAmt > 0 ? T.green : T.muted} style={{ fontSize: 10, padding: '5px 10px' }}>{totalAmt > 0 ? fmtGBPExact(totalAmt) : '—'}</TD2>
                        <TD2 style={{ padding: '5px 10px' }}></TD2><TD2 style={{ padding: '5px 10px' }}></TD2><TD2 style={{ padding: '5px 10px' }}></TD2>
                        <TD2 right style={{ paddingRight: 10, padding: '5px 10px' }}>
                          {result?.invoiceNumber && (
                            <span style={{ fontSize: 10, color: T.green, fontFamily: T.fontMono }}>
                              ✓ {result.invoiceNumber}
                              <a href={result.xeroUrl} target="_blank" rel="noopener noreferrer"
                                style={{ color: T.accentLt, marginLeft: 6, textDecoration: 'none' }}>Open →</a>
                            </span>
                          )}
                          {result?.error && <span style={{ fontSize: 10, color: T.red }}>{result.error}</span>}
                        </TD2>
                      </tr>
                      {/* spacer */}
                      <tr><td colSpan={11} style={{ height: 6, background: 'transparent' }}></td></tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </Card>
        </>
      )}
    </div>
  );
}

export default function App() {
  const [session,    setSession]    = useState(null);
  const [currentUser, setCurrentUser] = useState(null); // { email, ba, isPartner }
  const [authLoading, setAuthLoading] = useState(true);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [tab, setTabRaw] = useState(() => {
    // URL is checked first - it survives a full tab reload/restore by the OS,
    // which sessionStorage does not if the browser fully discards a
    // backgrounded tab under memory pressure rather than just pausing it
    // (device-dependent, which is why this could work for one person and
    // not another on the same code).
    const fromUrl = new URLSearchParams(window.location.search).get('tab');
    return fromUrl || sessionStorage.getItem("fi_active_tab") || "crm";
  });
  const setTab = (t) => {
    setTabRaw(t);
    sessionStorage.setItem("fi_active_tab", t);
    const url = new URL(window.location);
    url.searchParams.set('tab', t);
    window.history.replaceState(null, '', url);
  };
  const [crmOpps,    setCrmOpps]    = useState([]);
  const [plRows,     setPlRows]     = useState(SEED_PL_ROWS);
  const [clientRows, setClientRows] = useState(SEED_CLIENT_ROWS);
  const [timeRows,        setTimeRows]        = useState(SEED_TIME_ROWS);
  const [mapping,    setMapping]    = useState(SEED_MAPPING);
  const [referrals,  setReferrals]  = useState(DEFAULT_REFERRALS);
  const [serviceMap, setServiceMap] = useState(DEFAULT_SERVICE_MAP);
  const [serviceRevRows, setServiceRevRows] = useState(SEED_SERVICE_REV_ROWS);
  const [employees, setEmployees]           = useState(DEFAULT_EMPLOYEES);
  const [staffTimeRows,   setStaffTimeRows]   = useState(SEED_STAFF_TIME);
  const [agedDebtors, setAgedDebtors]       = useState(SEED_AGED_DEBTORS);
  const [accruedClients,  setAccruedClients]  = useState(SEED_ACCRUED_CLIENTS);
  const [delegateThreshold, setDelegateThreshold] = useState(5000);
  const [selIdx,     setSelIdx]     = useState(0);
  const [toast,      setToast]      = useState(null);

  const [uploading,     setUploading]     = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroSyncing,   setXeroSyncing]   = useState(false);

  // Auth state management
  const loadCurrentUserRef = useRef(null);

  useEffect(() => {
    loadCurrentUserRef.current = loadCurrentUser;
  });

  useEffect(() => {
    // Supabase recovery/invite links arrive in one of two formats depending on
    // the auth flow configured for the project:
    //   - Implicit flow: #access_token=...&type=recovery  (hash fragment)
    //   - PKCE flow:     ?code=...&type=recovery            (query string)
    // PKCE is Supabase's current default for new projects. Only checking the
    // hash (as before) meant PKCE-format links were never detected at all -
    // the user would land on the normal dashboard/login with no indication
    // anything had gone wrong.
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const isRecoveryLink =
      hash.includes('type=recovery') || hash.includes('type=invite') ||
      search.includes('type=recovery') || search.includes('type=invite');

    if (isRecoveryLink) setNeedsPassword(true);

    // PKCE flow requires explicitly exchanging the code for a session -
    // supabase-js doesn't do this automatically on page load.
    const params = new URLSearchParams(search);
    const code = params.get('code');
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
        if (error) {
          console.error('[auth] code exchange failed:', error);
        } else if (isRecoveryLink) {
          setNeedsPassword(true);
        }
        // Clean the code out of the URL either way, so a refresh doesn't
        // try to re-exchange an already-used code (which errors)
        window.history.replaceState(null, '', window.location.pathname);
      });
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadCurrentUserRef.current(session.user.email);
      else setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setNeedsPassword(true);
      setSession(session);
      if (session) loadCurrentUserRef.current(session.user.email);
      else { setCurrentUser(null); setAuthLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const PARTNERS = ['steve.tinkler@lvlup.partners'];

  // Correct tab if it becomes inaccessible after currentUser loads
  useEffect(() => {
    if (!currentUser || currentUser.isPartner) return;
    const allowed = currentUser.tab_access;
    if (allowed?.length && !allowed.includes(tab)) {
      setTab(allowed[0]);
    }
  }, [currentUser]); // eslint-disable-line

  const loadCurrentUser = async (email) => {
    const isPartner = PARTNERS.includes(email.toLowerCase());
    // Try to find matching employee record for BA
    const { data: emp } = await supabase
      .from('employees')
      .select('ba,tab_access')
      .ilike('email', email)
      .maybeSingle();
    const ba = emp?.ba || null;
    // Ensure tab_access is always a proper JS array
    let tab_access = emp?.tab_access || null;
    if (typeof tab_access === 'string') {
      try { tab_access = JSON.parse(tab_access); } catch(e) { tab_access = null; }
    }
    setCurrentUser({ email, isPartner, ba, tab_access, crm_see_partners: emp?.crm_see_partners || false });
    // Only override the tab if the one already restored (from URL/session)
    // isn't actually valid for this user - previously this ran
    // unconditionally on every load, silently discarding whatever tab had
    // just been correctly restored for any non-partner, every single time.
    if (!isPartner && !(tab_access?.length && tab_access.includes(tab))) {
      if (tab_access?.length) {
        setTab(tab_access[0]);
      } else if (ba) {
        const baTabMap = { 'Strategic Finance':'sf', 'Legal':'legal', 'In-House Finance':'ihf' };
        setTab(baTabMap[ba] || 'exec');
      }
    }
    setAuthLoading(false);
  };

  // Check if Xero is connected on load
  useEffect(() => {
    supabase.from('xero_tokens').select('tenant_id').single()
      .then(({ data }) => { if (data?.tenant_id) setXeroConnected(true); });
    // Check URL for xero=connected param
    if (window.location.search.includes('xero=connected')) {
      setXeroConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Sync data from Xero
  const handleUpload = useCallback(async (file) => {
    setUploading(true);
    setToast(`Loading "${file.name}"…`);
    try {
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type:'array' });

      // Parse P&L sheet — accepts both Monthly P&L and Profit and Loss sheet names
      const plSheet = wb.Sheets['Monthly P&L'] || wb.Sheets['Profit and Loss'] || wb.Sheets[wb.SheetNames[0]];
      if (!plSheet) throw new Error('No P&L sheet found — expected Monthly P&L or Profit and Loss');
      const raw = XLSX.utils.sheet_to_json(plSheet, { header:1, defval:'' });

      // Find header row (row 5 in both formats — contains month names like May 2026)
      const MSHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

      // Normalize a cell value to a string, handling Date objects from cellDates:true
      const cellStr = (c) => {
        if (!c && c !== 0) return '';
        if (c instanceof Date) {
          // Format date as "Mon YYYY"
          return MSHORT[c.getMonth()] + ' ' + c.getFullYear();
        }
        return String(c).trim();
      };

      let headerRow = -1;
      let periods = [];
      for (let i = 0; i < Math.min(20, raw.length); i++) {
        const cells = raw[i].map(cellStr);
        const mCount = cells.filter(c => MSHORT.some(m => c.startsWith(m))).length;
        const hasAccount = cells[1] === 'Account';
        if (mCount >= 1 || hasAccount) {
          headerRow = i;
          periods = cells.slice(1).filter(p => MSHORT.some(m => p.startsWith(m)));
          if (periods.length > 0) break;
        }
      }
      if (headerRow < 0 || periods.length === 0) throw new Error('No month columns found — check the P&L sheet format');

      // Use cellStr for header cells too
      const headerCellsFn = raw[headerRow].map(cellStr);

      // Find column index for each period (skip col 0=section, col 1=label)
      const headerCells = headerCellsFn;

      const getNumericRow = (label) => {
        for (let i = headerRow+1; i < raw.length; i++) {
          const rowLabel = String(raw[i][1] || raw[i][0] || '').trim();
          if (rowLabel.toLowerCase().includes(label.toLowerCase())) {
            // Return only columns that align with period headers — skip formula strings
            return raw[i];
          }
        }
        return null;
      };

      // Extract numeric value — skip formula strings (start with =)
      const numVal = (v) => {
        const s = String(v).trim();
        if (s.startsWith('=') || s === '' || s === 'null') return 0;
        return parseFloat(s.replace(/[£, ]/g,'')) || 0;
      };

      const vals = (row) => {
        if (!row) return periods.map(() => 0);
        return periods.map(p => {
          const colIdx = headerCells.indexOf(String(p).trim());
          return colIdx >= 0 ? numVal(row[colIdx]) : 0;
        });
      };

      const sfRev  = vals(getNumericRow('Revenue - Strategic Finance'));
      const lgRev  = vals(getNumericRow('Revenue - Legal'));
      const ihfRev = vals(getNumericRow('Revenue - In-House Finance'));
      const taxRev = vals(getNumericRow('Revenue - Tax'));
      const sfCos  = vals(getNumericRow('People Costs - Strategic Finance'));
      const lgCos  = vals(getNumericRow('People Costs - Legal'));
      const ihfCos = vals(getNumericRow('People Costs - In-House Finance'));
      const taxCos = vals(getNumericRow('People Costs - Tax'));

      // Get additional admin costs for EBITDA calculation
      const allAdminRows = [];
      let inAdmin = false;
      for (let i = headerRow+1; i < raw.length; i++) {
        const sec = String(raw[i][0] || '').trim();
        const lbl = String(raw[i][1] || '').trim();
        if (sec === 'Administrative Costs') { inAdmin = true; continue; }
        if (sec && sec !== 'Administrative Costs' && inAdmin) break;
        if (inAdmin && lbl && !lbl.startsWith('Total') && !lbl.includes('%')) {
          allAdminRows.push(raw[i]);
        }
      }

      // Interest and tax rows for PBD
      const interest = vals(getNumericRow('Interest Income'));
      const taxation = vals(getNumericRow('Taxation'));
      const taxReclass = vals(getNumericRow('Tax - reclass'));

      // Calculate EBITDA and PBD from components (avoids formula strings)
      const newPL = periods.map((period, i) => {
        const parts = String(period).trim().split(' ');
        // Header detection above is prefix-based (startsWith), so this lookup
        // must be too: UK-locale Xero exports write September as "Sept", which
        // an exact indexOf("Sept") never matched - the column was found, then
        // silently discarded as an invalid month.
        const mIdx  = MSHORT.findIndex(m => parts[0].startsWith(m));
        const year  = parseInt(parts[1]) || new Date().getFullYear();
        if (mIdx < 0) return null;

        const sf  = sfRev[i] || 0;
        const lg  = lgRev[i] || 0;
        const ihf = ihfRev[i] || 0;
        const tax = taxRev[i] || 0;
        const totalRev = sf + lg + ihf + tax;
        const totalCos = (sfCos[i]||0) + (lgCos[i]||0) + (ihfCos[i]||0) + (taxCos[i]||0);
        const gp = totalRev + totalCos;

        // Sum all admin cost rows
        const adminTotal = allAdminRows.reduce((sum, row) => {
          const colIdx = headerCells.indexOf(String(period).trim());
          return sum + (colIdx >= 0 ? numVal(row[colIdx]) : 0);
        }, 0);

        const calcEbitda = gp + adminTotal;
        const calcPbd    = calcEbitda + (interest[i]||0) + (taxation[i]||0) + (taxReclass[i]||0);

        return {
          // Always store the 3-letter form ("Sep 2026", never "Sept 2026") so the
          // period key matches every other month and the sort/match helpers.
          period: `${MSHORT[mIdx]} ${year}`, year, month: mIdx + 1,
          sf_revenue: sf, sf_cos: sfCos[i]||0,
          legal_revenue: lg, legal_cos: lgCos[i]||0,
          ihf_revenue: ihf, ihf_cos: ihfCos[i]||0,
          tax_revenue: tax, tax_cos: taxCos[i]||0,
          total_revenue: totalRev,
          total_cos: totalCos,
          ebitda: calcEbitda,
          profit_before_drawings: calcPbd,
          debts_over_30: 0, accrued_income: 0, accumulated_profits: 0,
        };
      }).filter(Boolean).sort((a,b) => (b.year*12+b.month)-(a.year*12+a.month));

      if (newPL.length === 0) throw new Error('No valid months parsed from P&L sheet');

      // Save ONLY the most recent month to Supabase — delete first to prevent duplicates
      try {
        const latestRow = newPL[0]; // newPL is sorted newest first
        if (latestRow) {
          await supabase.from("pl_rows").delete().eq("period", latestRow.period);
          // Also delete and reinsert client_rows and service_rev_rows for this period
          await supabase.from("client_rows").delete().eq("period", latestRow.period);
          await supabase.from("service_rev_rows").delete().eq("period", latestRow.period);
          await supabase.from("time_rows").delete().eq("period", latestRow.period);
          await supabase.from("aged_debtors").delete().eq("period", latestRow.period);
          await supabase.from("accrued_clients").delete().eq("period", latestRow.period);
          await supabase.from("pl_rows").insert([{
            period: latestRow.period, year: latestRow.year, month: latestRow.month,
            sf_revenue: latestRow.sf_revenue, sf_cos: latestRow.sf_cos,
            legal_revenue: latestRow.legal_revenue, legal_cos: latestRow.legal_cos,
            ihf_revenue: latestRow.ihf_revenue, ihf_cos: latestRow.ihf_cos,
            tax_revenue: latestRow.tax_revenue||0, tax_cos: latestRow.tax_cos||0,
            total_revenue: latestRow.total_revenue, total_cos: latestRow.total_cos,
            ebitda: latestRow.ebitda, profit_before_drawings: latestRow.profit_before_drawings,
            debts_over_30: 0, accrued_income: 0, accumulated_profits: 0,
          }]);
        }
      } catch (saveErr) {
        console.error("Failed to save P&L to Supabase:", saveErr);
      }

      // Reload full dataset from Supabase so historical months are preserved in memory
      try {
        const { data: allPL } = await supabase.from("pl_rows").select("*")
          .order("year", { ascending:false }).order("month", { ascending:false }).limit(1000);
        if (allPL?.length > 0) {
          const allSorted = [...allPL].sort((a,b) => (b.year*12+b.month)-(a.year*12+a.month));
          setPlRows(allSorted.map(r => ({
            period: r.period, year: r.year, month: r.month,
            sf_revenue: Number(r.sf_revenue)||0, sf_cos: Number(r.sf_cos)||0,
            legal_revenue: Number(r.legal_revenue)||0, legal_cos: Number(r.legal_cos)||0,
            ihf_revenue: Number(r.ihf_revenue)||0, ihf_cos: Number(r.ihf_cos)||0,
            tax_revenue: Number(r.tax_revenue)||0, tax_cos: Number(r.tax_cos)||0,
            total_revenue: Number(r.total_revenue)||0, total_cos: Number(r.total_cos)||0,
            ebitda: Number(r.ebitda)||0,
            profit_before_drawings: Number(r.profit_before_drawings)||0,
            debts_over_30: Number(r.debts_over_30)||0,
            accrued_income: Number(r.accrued_income)||0,
            accumulated_profits: Number(r.accumulated_profits)||0,
          })));
          setSelIdx(0);
        }
      } catch(e) { console.error("Failed to reload P&L:", e); }

      // ── Parse Account Transactions (single month) ──────────────────
      const acctSheet = wb.Sheets['Account Transactions'];
      const periodLabel = newPL[0]?.period || 'Unknown';
      if (acctSheet) {
        const acctRaw = XLSX.utils.sheet_to_json(acctSheet, { header:1, defval:'', cellDates:true });
        const clientMap = {};

        // Fetch fresh service map from Supabase at upload time to avoid stale closure
        const { data: freshSvcMap } = await supabase.from('service_map').select('service,ba');
        const svcMapLookup = {};
        (freshSvcMap||[]).forEach(r => { svcMapLookup[r.service] = r.ba; });

        // BEGIN service-revenue-parse
        const BA_LABEL = { sf:'Strategic Finance', legal:'Legal', ihf:'In-House Finance', tax:'Tax' };
        // Name-based guess - now only a FALLBACK for lines outside the four revenue accounts.
        const getBa = (service) => {
          const mappedBa = svcMapLookup[service];
          if (mappedBa) {
            // Accept the long names the Standing Data screen writes ("Tax") AND the short keys the old
            // auto-save wrote ("tax"). The old exact-match check sent every short key - including Tax
            // Compliance - EMI / VGEC - to In-House Finance.
            const m = String(mappedBa).trim().toLowerCase();
            if (m === 'strategic finance' || m === 'sf') return 'sf';
            if (m === 'legal') return 'legal';
            if (m.startsWith('tax')) return 'tax';
            if (m === 'ihf' || m.startsWith('in-house')) return 'ihf';
          }
          const s = (service||'').toLowerCase();
          if (s.includes('tax')||s.includes('compliance')||s.includes('vat')||s.includes('proposition')) return 'tax';
          if (s.includes('legal')||s.includes('gc support')||s.includes('m&a - legal')) return 'legal';
          if (s.includes('in-house')||s.includes('ihf')||s.includes('payroll')) return 'ihf';
          return 'sf';
        };

        // Business Area comes from the ACCOUNT each line is booked to ("Revenue - Tax" etc.) - the same
        // basis as the P&L - so the service and client breakdowns reconcile to it. Assigning by the
        // Service tag instead let the two disagree (revenue tagged "In-House Finance" but booked to the
        // Strategic Finance account showed on the IHF tab, Statutory Accounts booked to Tax landed on
        // Strategic Finance, ...).
        const baFromAccount = (heading) => {
          const h = (heading || '').toLowerCase();
          if (!h.startsWith('revenue')) return null;
          if (h.includes('tax')) return 'tax';
          if (h.includes('legal')) return 'legal';
          if (h.includes('in-house') || h.includes('ihf')) return 'ihf';
          if (h.includes('strategic')) return 'sf';
          return null;
        };
        const hasRevenueHeadings = acctRaw.some(r => typeof r[0] === 'string' && /^revenue/i.test(r[0].trim()));
        const svcByBa = {};
        let acctSection = '';

        acctRaw.forEach(row => {
          const cell0 = row[0];
          if (typeof cell0 === 'string') {                       // account heading / total rows
            const h = cell0.trim();
            if (h && !/^total/i.test(h)) acctSection = h;
            return;
          }
          if (!cell0) return;
          const isDate = (cell0 instanceof Date && !isNaN(cell0)) || (typeof cell0==='number' && cell0>40000);
          if (!isDate) return;
          if (hasRevenueHeadings && !/^revenue/i.test(acctSection)) return;   // only revenue accounts
          // Include reversals - they are needed for correct net revenue

          const debit  = parseFloat(row[7]) || 0;
          const credit = parseFloat(row[8]) || 0;
          const net    = credit - debit;
          const service = String(row[10]||'').trim();
          const client  = String(row[11]||'').trim();
          if (net === 0) return;

          const ba = baFromAccount(acctSection) || getBa(service);
          // Client table needs a client tag to attribute a line to a client
          if (client) {
            if (!clientMap[client]) clientMap[client] = {sf:0,legal:0,ihf:0,tax:0};
            clientMap[client][ba] += net;
          }
          // Service table takes EVERY line - accrual reversals and untagged journals included (they used to
          // be skipped, which overstated the BAs they reverse out of and understated the BAs they accrue to)
          const label = service || '(no service tag)';
          const k = ba + '|||' + label;
          if (!svcByBa[k]) svcByBa[k] = { ba, service: label, amount: 0 };
          svcByBa[k].amount += net;
        });

        const newClientRows = Object.entries(clientMap)
          .map(([client,v]) => ({
            period:periodLabel, client,
            sf:Math.round((v.sf||0)*100)/100,
            legal:Math.round((v.legal||0)*100)/100,
            ihf:Math.round((v.ihf||0)*100)/100,
            tax:Math.round((v.tax||0)*100)/100,
            total:Math.round(((v.sf||0)+(v.legal||0)+(v.ihf||0)+(v.tax||0))*100)/100,
          })).filter(r=>r.total>0).sort((a,b)=>b.total-a.total);

        // The same service name can be booked to more than one BA in a month (e.g. "In-House Finance"
        // invoiced onto the Strategic Finance account). Keep names unique per month: the BA with the most
        // revenue keeps the plain name, the others are labelled with where they were booked.
        const svcByName = {};
        Object.values(svcByBa).forEach(r => { (svcByName[r.service] ||= []).push(r); });
        const newSvcRows = [];
        Object.values(svcByName).forEach(list => {
          list.sort((x, y) => Math.abs(y.amount) - Math.abs(x.amount));
          list.forEach((r, i) => newSvcRows.push({
            period: periodLabel,
            service: i === 0 ? r.service : `${r.service} (booked to ${BA_LABEL[r.ba]})`,
            ba: r.ba,
            amount: Math.round(r.amount*100)/100,
          }));
        });
        for (let i = newSvcRows.length - 1; i >= 0; i--) if (newSvcRows[i].amount === 0) newSvcRows.splice(i, 1);
        // END service-revenue-parse

        if (newClientRows.length > 0) {
          setClientRows(newClientRows);
          try {
            await supabase.from("client_rows").delete().eq("period", periodLabel);
            await supabase.from("client_rows").insert(newClientRows);
          } catch(e) { console.error("client_rows save failed:", e); }
        }
        if (newSvcRows.length > 0) {
          setServiceRevRows(newSvcRows);
          try {
            await supabase.from("service_rev_rows").delete().eq("period", periodLabel);
            await supabase.from("service_rev_rows").insert(newSvcRows);
          } catch(e) { console.error("service_rev_rows save failed:", e); }

          // Auto-add any new services to service_map if not already present
          try {
            const existingSvcs = new Set(serviceMap.map(s => s.service));
            // Long BA names (what the Standing Data screen reads and writes) - the old short keys
            // ('tax', 'ihf') are what sent services to In-House Finance. Labels like "(no service tag)" and
            // "... (booked to X)" are not real services, so they are not added to the map.
            const newMapEntries = newSvcRows
              .filter(r => !existingSvcs.has(r.service) && !r.service.startsWith('(') && !/ \(booked to /.test(r.service))
              .map(r => ({ service: r.service, ba: BA_LABEL[r.ba] }));
            if (newMapEntries.length > 0) {
              await supabase.from("service_map").insert(newMapEntries);
              setServiceMap(prev => [
                ...prev,
                ...newMapEntries.map(r => ({ service: r.service, ba: r.ba, _id: null }))
              ]);
            }
          } catch(e) { console.error("service_map auto-save failed:", e); }
        }
      }

      // ── Parse Detailed Time sheet ─────────────────────────────────────
      const timeSheet = wb.Sheets['Detailed Time'];
      if (timeSheet) {
        const tRaw = XLSX.utils.sheet_to_json(timeSheet, { header:1, defval:'', cellDates:true });
        const tHeader = tRaw.find(r => String(r[0]).trim() === 'Date');
        if (tHeader) {
          const tHIdx = tRaw.indexOf(tHeader);
          const PARTNERS = ['steve tinkler','felix wong'];
          const newTimeRows = [];
          const staffAgg = {};
          const hasCostCol = tHeader && String(tHeader[10]||'').toLowerCase().includes('cost');

          tRaw.slice(tHIdx+1).forEach(row => {
            if (!row[0] || !row[1]) return;
            const staff  = String(row[1]).trim();
            const client = String(row[2]).trim();
            const task   = String(row[4]).trim();
            const ctype  = String(row[5]).trim();
            const timeStr= String(row[7]).trim();
            const charge = parseFloat(row[9]) || 0;
            const cost   = hasCostCol ? (parseFloat(row[10])||0) : 0;
            if (ctype.toLowerCase()==='non chargeable') return;
            if (client==='Internal') return;
            const parts = timeStr.split(':');
            const hrs = parts.length===2 ? (parseInt(parts[0])+parseInt(parts[1])/60) : 0;
            const isPartner = PARTNERS.includes(staff.toLowerCase());
            newTimeRows.push({
              period:periodLabel, staff, client_name:client,
              task, charge_type:ctype,
              hours:Math.round(hrs*100)/100, charge, cost,
              non_partner_charge:isPartner?0:charge,
            });
            if (!staffAgg[staff]) staffAgg[staff]={staff,period:periodLabel,hours:0,charge:0,cost:0};
            staffAgg[staff].hours += hrs;
            staffAgg[staff].charge += charge;
            staffAgg[staff].cost += cost;
          });

          if (newTimeRows.length > 0) {
            const staffRows = Object.values(staffAgg).map(s=>({...s,hours:Math.round(s.hours*100)/100,cost:Math.round((s.cost||0)*100)/100}));
            setTimeRows(newTimeRows);
            setStaffTimeRows(staffRows);
            const clientAgg = {};
            newTimeRows.forEach(r => {
              if (!clientAgg[r.client_name]) clientAgg[r.client_name]={staff:'various',period:r.period,client:r.client_name,hours:0,charge:0,cost:0};
              clientAgg[r.client_name].hours += r.hours;
              clientAgg[r.client_name].charge += r.charge;
              clientAgg[r.client_name].cost += r.cost||0;
            });
            // Build per-person-per-client summary for BA tab person columns
            const personClientAgg = {};
            newTimeRows.forEach(r => {
              const key = r.staff + '|||' + r.client_name;
              if (!personClientAgg[key]) personClientAgg[key] = {staff:r.staff, client:r.client_name, period:r.period, charge:0, hours:0, cost:0};
              personClientAgg[key].charge += r.charge||0;
              personClientAgg[key].hours  += r.hours||0;
              personClientAgg[key].cost   += r.cost||0;
            });
            const personClientRows = Object.values(personClientAgg).map(r=>({
              period:r.period, staff:r.staff, client:r.client,
              task:'person_client', charge_type:'person_client',
              hours_decimal:Math.round(r.hours*100)/100,
              charge:Math.round(r.charge*100)/100,
              cost:Math.round((r.cost||0)*100)/100,
            }));
            const staffSummaryRows = staffRows.map(r=>({
              period:r.period,staff:r.staff,client:'__staff_summary__',task:'staff_total',charge_type:'summary',
              hours_decimal:Math.round(r.hours*100)/100,charge:Math.round(r.charge*100)/100,cost:Math.round((r.cost||0)*100)/100,
            }));
            // Also keep client totals
            const summaryRows = Object.values(clientAgg).map(r=>({
              period:r.period,staff:'various',client:r.client,task:'summary',charge_type:'summary',
              hours_decimal:Math.round(r.hours*100)/100,charge:Math.round(r.charge*100)/100,cost:Math.round((r.cost||0)*100)/100,
            }));
            try {
              await supabase.from("time_rows").delete().eq("period", periodLabel);
              await supabase.from("time_rows").insert([...summaryRows,...staffSummaryRows,...personClientRows]);
            } catch(e) { console.error("time_rows save failed:", e); }
          }
        }
      }

      // ── Parse Aged Receivables ────────────────────────────────────────
      let debtsOver30 = 0;
      const arSheet = wb.Sheets['Aged Receivables Summary'] || wb.Sheets['Aged Receivables'];
      const newDebtorRows = [];
      if (arSheet) {
        const arRaw = XLSX.utils.sheet_to_json(arSheet, { header:1, defval:0 });
        arRaw.forEach(row => {
          const label = String(row[0]||'').trim();
          if (!label||label==='Contact'||label.toLowerCase().includes('total')||
              label.toLowerCase().includes('lvl')||label.toLowerCase().includes('as at')||
              label.toLowerCase().includes('aged')||label.toLowerCase().includes('ageing')) return;
          const over30 = (parseFloat(row[3])||0)+(parseFloat(row[4])||0)+(parseFloat(row[5])||0)+(parseFloat(row[6])||0);
          debtsOver30 += over30;
          if (over30 > 0) newDebtorRows.push({ client: label, over30, period: periodLabel });
        });
        if (newDebtorRows.length > 0) {
          setAgedDebtors(newDebtorRows.map(r => ({ client: r.client, over30: r.over30 })));
          try {
            await supabase.from("aged_debtors").delete().eq("period", periodLabel);
            await supabase.from("aged_debtors").insert(newDebtorRows);
          } catch(e) { console.error("aged_debtors save failed:", e); }
        }
      }

      // ── Parse Balance Sheet ────────────────────────────────────────────
      let accruedIncome = 0, accumulatedProfits = 0;
      const bsSheet = wb.Sheets['Balance Sheet'];
      if (bsSheet) {
        const bsRaw = XLSX.utils.sheet_to_json(bsSheet, { header:1, defval:'' });
        bsRaw.forEach(row => {
          const label = String(row[1]||'').trim().toLowerCase();
          const v = parseFloat(row[2])||0;
          if (label==='accrued income') accruedIncome = v;
          if (label.includes('retained earnings')) accumulatedProfits += v;
        });
      }

      // ── Parse Accrued Income Transactions ────────────────────────────
      // Use closing balance from balance sheet (already parsed above)
      // Also build per-client accrued income for the accrued clients table
      const accrTxSheet = wb.Sheets['Accrued Income Transactions'];
      const newAccruedClients = [];
      if (accrTxSheet) {
        const acRaw = XLSX.utils.sheet_to_json(accrTxSheet, { header:1, defval:'', cellDates:true });
        const accrualMap = {};
        acRaw.forEach(row => {
          if (!row[0] || typeof row[0] === 'string') return;
          const desc    = String(row[2]||'').trim();
          const debit   = parseFloat(row[5])||0; // col 5 = Debit Source (new accrual)
          if (debit <= 0 || desc.startsWith('Reversal:')) return;
          // Extract client from "Mth-YY Accrued Income - Accrued Income - ClientName"
          const match = desc.match(/Accrued Income - Accrued Income - (.+)$/i);
          if (!match) return;
          const client = match[1].trim().replace(/\s+$/, '');
          accrualMap[client] = (accrualMap[client]||0) + debit;
        });
        Object.entries(accrualMap).forEach(([client,amount]) => {
          if (amount > 0) newAccruedClients.push({client, amount:Math.round(amount*100)/100, period:periodLabel});
        });
        if (newAccruedClients.length > 0) {
          setAccruedClients(newAccruedClients.map(r=>({client:r.client, amount:r.amount})));
          try {
            await supabase.from("accrued_clients").delete().eq("period", periodLabel);
            await supabase.from("accrued_clients").insert(newAccruedClients);
          } catch(e) { console.error("accrued_clients save failed:", e); }
        }
      }

      // ── Update pl_row with balance sheet figures ──────────────────────
      if (debtsOver30 > 0 || accruedIncome > 0 || accumulatedProfits > 0) {
        try {
          await supabase.from("pl_rows").update({
            debts_over_30: debtsOver30,
            accrued_income: accruedIncome,
            accumulated_profits: accumulatedProfits,
          }).eq("period", periodLabel);
          setPlRows(prev => prev.map(r =>
            r.period===periodLabel
              ? {...r, debts_over_30:debtsOver30, accrued_income:accruedIncome, accumulated_profits:accumulatedProfits}
              : r
          ));
        } catch(e) { console.error("Balance sheet update failed:", e); }
      }

      setToast(`✓ "${file.name}" loaded — ${periodLabel}`);
    } catch (err) {
      setToast(`⚠ Parse error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }, [serviceMap]);

  const syncFromXero = useCallback(async () => {
    setXeroSyncing(true);
    setToast('Syncing from Xero...');
    try {
      const now    = new Date();
      const year   = now.getFullYear();
      const month  = String(now.getMonth() + 1).padStart(2, '0');
      const lastDay = String(new Date(year, now.getMonth()+1, 0).getDate()).padStart(2,'0');
      const toDate   = `${year}-${month}-${lastDay}`;
      const fromDate = `${year - 1}-${month}-01`;
      // Current month window for invoices
      const monthFrom = `${year}-${month}-01`;
      const monthTo   = toDate;

      const [plRes, arRes] = await Promise.all([
        fetch(`/api/xero/data?type=profit_and_loss&fromDate=${fromDate}&toDate=${toDate}`),
        fetch(`/api/xero/data?type=aged_receivables&toDate=${toDate}`),
      ]);
      const [plData, arData] = await Promise.all([plRes.json(), arRes.json()]);

      // Parse and MERGE Xero P&L data into existing rows (don't wipe history)
      if (plData?.Reports?.[0]) {
        const parsed = parseXeroPL(plData.Reports[0], serviceMap);
        if (parsed.length > 0) {
          setPlRows(prev => {
            const xeroPeriods = new Set(parsed.map(r => r.period));
            const kept = prev.filter(r => !xeroPeriods.has(r.period));
            return [...parsed, ...kept].sort((a,b) => (b.year*12+b.month)-(a.year*12+a.month));
          });
          setSelIdx(0);
        }
      }

      // Parse aged receivables
      if (arData?.Reports?.[0]) {
        const debtors = parseXeroAgedReceivables(arData.Reports[0]);
        if (debtors.length > 0) setAgedDebtors(debtors);
      }

      // Fetch invoices for current month — paginate through all pages
      const latestPeriod = `${now.toLocaleString('en-GB',{month:'short'})} ${year}`;
      const clientMap = {};
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const invRes = await fetch(
          `/api/xero/data?type=invoices&fromDate=${monthFrom}&toDate=${monthTo}&page=${page}`
        );
        const invData = await invRes.json();
        const invoices = invData?.Invoices || [];
        invoices.forEach(inv => {
          const client = inv.Contact?.Name;
          if (!client) return;
          const amt = inv.SubTotal || 0; // ex-VAT
          if (!clientMap[client]) clientMap[client] = { total:0 };
          clientMap[client].total += amt;
        });
        const pagination = invData?.pagination;
        hasMore = pagination && page < pagination.pageCount;
        page++;
        if (page > 20) break; // safety limit
      }
      if (Object.keys(clientMap).length > 0) {
        const newClientRows = Object.entries(clientMap)
          .filter(([,v]) => v.total > 0)
          .map(([client, v]) => ({
            period: latestPeriod,
            client,
            sf: 0, legal: 0, ihf: 0, // BA split not available from invoices alone
            total: Math.round(v.total * 100) / 100,
          }));
        setClientRows(newClientRows);
      }

      setToast('✓ Xero sync complete');
    } catch (err) {
      setToast(`⚠ Xero sync failed: ${err.message}`);
    } finally {
      setXeroSyncing(false);
    }
  }, [serviceMap]);


  // ── Load standing data from Supabase on mount ──────────────────────────
  useEffect(() => {
    async function loadStandingData() {
      try {
        const [refRes, mapRes, empRes, svcRes, settRes, plRes, crRes, trRes, adRes, acRes, srvRes, crmRes] = await Promise.all([
          supabase.from("referral_sources").select("*").order("created_at"),
          supabase.from("client_mapping").select("*").order("created_at"),
          supabase.from("employees").select("*").order("created_at"),
          supabase.from("service_map").select("*").order("created_at"),
          supabase.from("firm_settings").select("*"),
          supabase.from("pl_rows").select("*").order("year", { ascending:false }).order("month", { ascending:false }).limit(1000),
          loadWholeTable("client_rows"),
          loadWholeTable("time_rows"),
          loadWholeTable("aged_debtors"),
          loadWholeTable("accrued_clients"),
          loadWholeTable("service_rev_rows"),
          supabase.from("crm_opportunities").select("*").order("date_initiated", { ascending:false }).limit(500),
        ]);

        if (refRes.data?.length > 0)
          setReferrals(refRes.data.map(r => ({ id:r.id, name:r.name })));

        if (mapRes.data?.length > 0)
          setMapping(mapRes.data.map(r => ({
            time_name:    r.time_name || "",
            revenue_client: r.revenue_client,
            tag:          r.tag || "",
            referral:     r.referral || "",
            origBA:       r.orig_ba || "",
            equityCont:   r.equity_cont || false,
            _id:          r.id,
          })));

        if (empRes.data?.length > 0)
          setEmployees(empRes.data.map(r => ({
            name:       r.name,
            email:      r.email || "",
            role:       r.role  || "Associate",
            ba:         r.ba    || "Strategic Finance",
            tab_access: r.tab_access || null,
            crm_see_partners: r.crm_see_partners || false,
            _id:        r.id,
          })));

        if (svcRes.data?.length > 0)
          setServiceMap(svcRes.data.map(r => ({
            service: r.service,
            ba:      r.ba,
            _id:     r.id,
          })));

        const thresh = settRes.data?.find(s => s.key === "delegate_threshold");
        if (thresh) setDelegateThreshold(Number(thresh.value) || 5000);

        if (plRes.data?.length > 0) {
          const sorted = [...plRes.data].sort((a,b) => (b.year*12+b.month)-(a.year*12+a.month));
          setPlRows(sorted.map(r => ({
            period: r.period, year: r.year, month: r.month,
            sf_revenue: Number(r.sf_revenue)||0, sf_cos: Number(r.sf_cos)||0,
            legal_revenue: Number(r.legal_revenue)||0, legal_cos: Number(r.legal_cos)||0,
            ihf_revenue: Number(r.ihf_revenue)||0, ihf_cos: Number(r.ihf_cos)||0,
            tax_revenue: Number(r.tax_revenue)||0, tax_cos: Number(r.tax_cos)||0,
            total_revenue: Number(r.total_revenue)||0, total_cos: Number(r.total_cos)||0,
            ebitda: Number(r.ebitda)||0,
            profit_before_drawings: Number(r.profit_before_drawings)||0,
            debts_over_30: Number(r.debts_over_30)||0,
            accrued_income: Number(r.accrued_income)||0,
            accumulated_profits: Number(r.accumulated_profits)||0,
          })));
        }

        if (crRes.data?.length > 0) {
          setClientRows(crRes.data.map(r => ({
            period: r.period, client: r.client,
            sf: Number(r.sf)||0, legal: Number(r.legal)||0,
            ihf: Number(r.ihf)||0, tax: Number(r.tax)||0, total: Number(r.total)||0,
          })));
        }

        if (trRes.data?.length > 0) {
          // Split staff summaries from client summaries
          const staffSummaries = trRes.data.filter(r => r.client === '__staff_summary__');
          // Include both client summaries and person-client rows
          setTimeRows(trRes.data.filter(r => r.client !== '__staff_summary__').map(r => ({
            period: r.period, client_name: r.client,
            staff: r.staff, task: r.task,
            charge_type: r.charge_type,
            hours: Number(r.hours_decimal)||0,
            charge: Number(r.charge)||0,
            cost: Number(r.cost)||0,
            non_partner_charge: Number(r.charge)||0,
          })));
          if (staffSummaries.length > 0) {
            setStaffTimeRows(staffSummaries.map(r => ({
              staff: r.staff, period: r.period,
              hours: Number(r.hours_decimal)||0,
              charge: Number(r.charge)||0,
            })));
          }
        }

        if (adRes.data?.length > 0) {
          setAgedDebtors(adRes.data.map(r => ({
            client: r.client, over30: Number(r.over30)||0,
          })));
        }

        if (acRes.data?.length > 0) {
          setAccruedClients(acRes.data.map(r => ({
            client: r.client, amount: Number(r.amount)||0, period: r.period,
          })));
        }

        if (crmRes.data?.length > 0) {
          setCrmOpps(crmRes.data);
        }
        if (srvRes.data?.length > 0) {
          setServiceRevRows(srvRes.data.map(r => ({
            period: r.period, service: r.service, ba: r.ba,
            amount: Number(r.amount)||0,
          })));
        }

      } catch (err) {
        console.error("Failed to load standing data:", err);
      }
    }
    loadStandingData();
  }, []);

  // ── Save helpers ───────────────────────────────────────────────────────
  const saveReferrals = useCallback(async (newRefs) => {
    setReferrals(newRefs);
    await supabase.from("referral_sources").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    if (newRefs.length > 0)
      await supabase.from("referral_sources").insert(newRefs.map(r => ({ name: r.name })));
  }, []);

  const saveMapping = useCallback(async (newMapping) => {
    setMapping(newMapping);
    await supabase.from("client_mapping").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    if (newMapping.length > 0)
      await supabase.from("client_mapping").insert(newMapping.map(m => ({
        revenue_client: m.revenue_client,
        time_name:      m.time_name,
        tag:            m.tag,
        referral:       m.referral,
        orig_ba:        m.origBA,
        equity_cont:    m.equityCont,
      })));
  }, []);

  // Saves the Employees list WITHOUT destroying what isn't shown in it. The old
  // version deleted every row and re-inserted only name/email/role/ba, which wiped
  // every person's tab access and "CRM see partners" setting each time anyone
  // added/removed an employee or changed a Business Area dropdown. Now: people are
  // matched by email, updated in place (only those four fields), new people are
  // inserted, and only people removed from the list are deleted.
  const saveEmployees = useCallback(async (newEmps) => {
    setEmployees(newEmps);
    const keyOf = e => (e.email || '').trim().toLowerCase() || `name:${(e.name || '').trim().toLowerCase()}`;
    const { data: existing, error: readErr } = await supabase.from('employees').select('id, name, email');
    if (readErr) { console.error('[employees] not saved - could not read the current rows:', readErr); return; }
    const byKey = {};
    (existing || []).forEach(r => { byKey[keyOf(r)] = r; });
    const keep = new Set(newEmps.map(keyOf));
    const removeIds = (existing || []).filter(r => !keep.has(keyOf(r))).map(r => r.id);
    if (removeIds.length) await supabase.from('employees').delete().in('id', removeIds);
    for (const e of newEmps) {
      const row = { name: e.name, email: e.email, role: e.role, ba: e.ba };
      const found = byKey[keyOf(e)];
      const { error } = found
        ? await supabase.from('employees').update(row).eq('id', found.id)
        : await supabase.from('employees').insert(row);
      if (error) console.error('[employees] save failed for', e.name, error);
    }
  }, []);

  const saveServiceMap = useCallback(async (newSvcMap) => {
    setServiceMap(newSvcMap);
    // Never delete-all - individual saves handled by updSvc/rmSvc
  }, []);

  const saveThreshold = useCallback(async (val) => {
    setDelegateThreshold(val);
    await supabase.from("firm_settings")
      .upsert({ key:"delegate_threshold", value:String(val) }, { onConflict:"key" });
  }, []);


  if (authLoading) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{GCSS}</style>
      <div style={{ color:T.muted, fontSize:14, fontFamily:T.fontSans }}>Loading…</div>
    </div>
  );

  // Arriving from an invite or reset link - must set a password before continuing
  if (needsPassword && session) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{GCSS}</style>
      <SetPasswordScreen email={session.user?.email} onDone={() => setNeedsPassword(false)}/>
    </div>
  );

  if (!session) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{GCSS}</style>
      <LoginScreen/>
    </div>
  );

  return (
    <>
      <style>{GCSS}</style>
      <div style={{ minHeight:"100vh", background:T.bg, fontFamily:T.fontSans }}>
        <TopBar plRows={plRows} selectedIdx={selIdx} setSelectedIdx={setSelIdx}
          xeroConnected={xeroConnected} xeroSyncing={xeroSyncing} onXeroSync={syncFromXero}
          onUpload={handleUpload} uploading={uploading} currentUser={currentUser} onSignOut={()=>supabase.auth.signOut()}/>
        <TabBar active={tab} setActive={setTab} currentUser={currentUser}/>
        <main>
          {tab==="exec"     && <ExecSummary    plRows={plRows} clientRows={clientRows} timeRows={timeRows} staffTimeRows={staffTimeRows} mapping={mapping} referrals={referrals} delegateThreshold={delegateThreshold} agedDebtors={agedDebtors} accruedClients={accruedClients} selectedIdx={selIdx} crmOpps={crmOpps}/>}
          {tab==="sf"       && <BADetailTab    label="Strategic Finance" color={T.sf}    rf="sf_revenue"    cf="sf_cos"    baKey="sf"    plRows={plRows} clientRows={clientRows} timeRows={timeRows} staffTimeRows={staffTimeRows} mapping={mapping} referrals={referrals} delegateThreshold={delegateThreshold} serviceRevRows={serviceRevRows} employees={employees} selectedIdx={selIdx}/>}
          {tab==="legal"    && <BADetailTab    label="Legal"             color={T.legal} rf="legal_revenue" cf="legal_cos" baKey="legal" plRows={plRows} clientRows={clientRows} timeRows={timeRows} staffTimeRows={staffTimeRows} mapping={mapping} referrals={referrals} delegateThreshold={delegateThreshold} serviceRevRows={serviceRevRows} employees={employees} selectedIdx={selIdx}/>}
          {tab==="ihf"      && <BADetailTab    label="In-House Finance"  color={T.ihf}   rf="ihf_revenue"   cf="ihf_cos"   baKey="ihf"   plRows={plRows} clientRows={clientRows} timeRows={timeRows} staffTimeRows={staffTimeRows} mapping={mapping} referrals={referrals} delegateThreshold={delegateThreshold} serviceRevRows={serviceRevRows} employees={employees} selectedIdx={selIdx}/>}
          {tab==="objectives" && <ObjectivesTab/>}
          {tab==="tax"      && <BADetailTab label="Tax" color={T.tax} rf="tax_revenue" cf="tax_cos" baKey="tax" plRows={plRows} clientRows={clientRows} timeRows={timeRows} staffTimeRows={staffTimeRows} mapping={mapping} referrals={referrals} delegateThreshold={delegateThreshold} serviceRevRows={serviceRevRows} employees={employees} selectedIdx={selIdx}/>}
          {tab==="crm"      && <CRMTab opps={crmOpps} setOpps={setCrmOpps} canSeePartners={currentUser?.isPartner || currentUser?.crm_see_partners || false}/>}
          {tab==="standing" && <StandingData   mapping={mapping} setMapping={saveMapping} referrals={referrals} setReferrals={saveReferrals} serviceMap={serviceMap} setServiceMap={saveServiceMap} delegateThreshold={delegateThreshold} setDelegateThreshold={saveThreshold} timeRows={timeRows} employees={employees} setEmployees={saveEmployees}/>}
          {tab==="admin"    && <AdminTab       employees={employees}/>}
          {tab==="onboarding" && <OnboardingTab referrals={referrals}/>}
          {tab==="billing"  && <BillingTab/>}
          {tab==="utilisation" && <UtilisationTab/>}
        </main>
      </div>
      {toast && <Toast msg={toast} onDismiss={() => setToast(null)}/>}
    </>
  );
}
